import { retryRecoveryAction } from "./recoveryPolicy";
import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useParams } from 'react-router-dom';
import whiteLogo from "../../assets/icons/white_logo_png.png";
import inteloglogo from '../../assets/icons/intelog_new_png.png';

import plantumlEncoder from "plantuml-encoder";
// pdfjs-dist loaded at runtime from public/ to bypass CRA Babel transpilation
import socketIOClient from "socket.io-client";
import { v4 as uuidv4 } from "uuid";
import JSZip from "jszip";
import ZoomableDiagram from "../../components/zoomableDiagram/index.jsx";
import SplitDiagramView from "../../components/zoomableDiagram/SplitDiagramView.jsx";
import { hasFlowSections, isLargePuml, countFlowSections, splitPlantumlByFlow } from "../../components/zoomableDiagram/splitPuml.js";
import SyntaxHighlighter from "react-syntax-highlighter";
import { atomOneDark } from "react-syntax-highlighter/dist/esm/styles/hljs";
import url from "../../config.js";
import { usePermissions } from "../../context/permissionContext";
import "./smarthubnew.scss";
import BrownfieldPanel, { useBrownfieldTab } from './BrownfieldTab';
import DeploymentWindow from './DeploymentWindow';
import DesignPlanWindow from './DesignPlanWindow';
import ImplementationPlanWindow from './ImplementationPlanWindow';
import ExistingApiUploadCard from './ExistingApiUploadCard';
import PipelineUploadCard from './PipelineUploadCard';
import DeploymentSetupCard from './DeploymentSetupCard';
import ProfileMenu from '../../components/profileMenu';
import SaveRepositoryModal from './SaveRepositoryModal';
import MigrationTab from './MigrationTab';
import SharedLogsPanel from './SharedLogsPanel';
import StaleBanner from '../../components/stale-session-banner';
// pdfjs worker configured lazily inside readFile
const backendUrl = url.backendUrl;
const domain = url.domain;

// ─── Dev mock toggle ──────────────────────────────────────────────
// null              = hit real backend
// "design"          = always return design response
// "api_selection"   = always return api_selection response
// "mule"            = always return mule response
// "springboot"      = always return springboot response
// "sequence"        = cycle through MOCK_SEQUENCE in order per request
// Test AI quota error:
// const USE_MOCK = "error_quota";

// Test daily limit error:
// const USE_MOCK = "error_limit";

// Test design agent failure:
// const USE_MOCK = "error_design";

// Test generation fail:
// const USE_MOCK = "error_generation";

// Test sequence (normal happy path):
// const USE_MOCK = "sequence";
const USE_MOCK =null; // DEV: remove after testing

const MOCK_SEQUENCE = ["design","arch_review_dotnet", "api_selection", "schema_elicitation", "dotnet"];
let _mockSeqIdx = 0; // module-level counter, resets on page reload
const MOCK_RESPONSES = {
  design: {
    success: true, type: "agent", agentUsed: "design",
    message: "Here is your generated sequence diagram! 🎨\n\n✅ Would you like to **confirm** this diagram and proceed to code generation?",
    data: {
      // 
      // : "@startuml\ntitle Corrigo NewWorkOrder Integration (MuleSoft | cm-serviceprovide-xapi + cm-serviceprovide-papi)\nskinparam sequenceArrowThickness 2\nskinparam roundcorner 8\nskinparam sequenceParticipantBorderThickness 2\nskinparam noteBorderThickness 1\nskinparam sequenceGroupBorderThickness 2\nskinparam defaultFontSize 13\nparticipant \"Corrigo\" as Corrigo #FFF9C4\nparticipant \"cm-serviceprovide-xapi\\n(Experience API)\" as XApi #E1F5FE\nparticipant \"cm-serviceprovide-papi\\n(Process API)\" as PApi #C8E6C9\nqueue \"mule-wo-corrigo\\n(Amazon SQS)\" as SQS #F3E5F5\nparticipant \"Location Email\\nlookup cache\" as LocationEmailCache #E1F5FE\nparticipant \"CS rep team\\nsetup cache\" as CSRepTeamCache #FFF3E0\ndatabase \"MARKETING.SERVICE_ZC_LKP_VW\\n(Oracle)\" as OracleLocEmail #E8F5E9\ndatabase \"MARKETING.CORRIGO_CUSTOMER_LKP\\n(Oracle)\" as OracleCSRep #E8F5E9\nparticipant \"Centimark Team\" as CentimarkTeam #FCE4EC\n== Webhook Reception and Validation ==\nCorrigo -> XApi : POST /webhook\\n(JSON payload - NewWorkOrder)\nnote over XApi #E1F5FE\n  cm-serviceprovide-xapi webhook listener receives request\n  Extract Authorization Bearer token from request header\nend note\nXApi -> XApi : Split token using '<---->' separator\\nto isolate data component and signature component\nXApi -> XApi : Base64Url decode data component\nXApi -> XApi : Base64Url decode signature component\nXApi -> XApi : Verify decoded data against signature\\nusing pre-configured public certificate\nalt token verification fails\n  XApi --> Corrigo : 401 Unauthorized\nelse token is valid\n  XApi -> XApi : Verify Event-Type header field value\n  alt Event-Type header field does not contain value NewWorkOrder\n    XApi --> Corrigo : 400 Bad Request\n  else Event-Type = NewWorkOrder\n    XApi -> XApi : Check Details.Customer.Id\n    alt Details.Customer.Id does not match valid ID for US BANK, AMC, or AT&T\n      XApi --> Corrigo : 400 Bad Request\\n\"Invalid work order data.\"\n    else Details.Customer.Id is valid\n      XApi -> XApi : Check Details.WorkOrderId is present\n      alt Details.WorkOrderId is not present\n        XApi --> Corrigo : 400 Bad Request\\n\"Invalid work order data.\"\n      else Details.WorkOrderId is present\n        XApi -> XApi : Check Details.WorkOrderNumber is present\n        alt Details.WorkOrderNumber is not present\n          XApi --> Corrigo : 400 Bad Request\\n\"Invalid work order data.\"\n        else Details.WorkOrderNumber is present\n          XApi -> XApi : Verify Request Type field exists\\nat Details.WorkDetails.Assets[x].AssetName\n          alt Request Type field does not exist at Details.WorkDetails.Assets[x].AssetName\n            XApi --> Corrigo : 400 Bad Request\\n\"Invalid work order data.\"\n          else Request Type field is present\n            XApi -> SQS : Publish - push complete request payload\\nto mule-wo-corrigo SQS queue\n            SQS --> XApi : Message enqueued\n            XApi --> Corrigo : 202 Accepted\\n\"Work order is valid and processing will continue.\"\n          end\n        end\n      end\n    end\n  end\nend\n== Daily Cache Refresh ==\nnote over PApi #C8E6C9\n  Daily scheduled process initiates\n  cm-serviceprovide-papi refreshes Location Email lookup cache\n  and CS rep team setup cache\nend note\nPApi -> OracleLocEmail : Query MARKETING.SERVICE_ZC_LKP_VW\nOracleLocEmail --> PApi : Return location email data\nPApi -> LocationEmailCache : Refresh Location Email lookup cache\\nwith data from MARKETING.SERVICE_ZC_LKP_VW\nLocationEmailCache --> PApi : Cache updated\nPApi -> OracleCSRep : Query MARKETING.CORRIGO_CUSTOMER_LKP\nOracleCSRep --> PApi : Return CS rep team data\nPApi -> CSRepTeamCache : Refresh CS rep team setup cache\\nwith data from MARKETING.CORRIGO_CUSTOMER_LKP\nCSRepTeamCache --> PApi : Cache updated\nnote over PApi #C8E6C9\n  Daily Cache Refresh process complete\nend note\n== Work Order Categorization, Notification and Corrigo Acceptance ==\nnote over Corrigo, XApi\n  Corrigo sends JSON payload to cm-serviceprovide-xapi webhook listener\nend note\nCorrigo -> XApi : POST /webhook (JSON payload)\nXApi --> Corrigo : 202 Accepted - \"Work order is valid and processing will continue.\"\nSQS -> PApi : Consume work order message from mule-wo-corrigo\nnote over PApi #C8E6C9\n  cm-serviceprovide-papi: Extract Details.WorkDetails.Assets[x].AssetName field\n  Determine Request Type\nend note\nalt Details.WorkDetails.Assets[x].AssetName contains string ROOF\n  PApi -> PApi : Set Request Type = ROOFING\nend\nalt Details.WorkDetails.Assets[x].AssetName does not contain ROOF\n  alt Details.WorkDetails.Assets[0].AssetName is empty\n    PApi -> PApi : Set Request Type = OTHER\n  else Details.WorkDetails.Assets[0].AssetName is not empty\n    PApi -> PApi : Set Request Type = Details.WorkDetails.Assets[0].AssetName value\n  end\nend\nalt Request Type is ROOFING and Service Location is present via PostalCode field\n  PApi -> LocationEmailCache : Query Location Email lookup cache\\n(backed by MARKETING.SERVICE_ZC_LKP_VW)\\nRetrieve SERVICE_EMAIL and SERVICE_MGR_EMAIL\n  LocationEmailCache --> PApi : Return SERVICE_EMAIL, SERVICE_MGR_EMAIL\nend\nalt Request Type is ROOFING and Service Location is absent via PostalCode field\n  PApi -> CSRepTeamCache : Query CS rep team setup cache\\n(backed by MARKETING.CORRIGO_CUSTOMER_LKP)\\nRetrieve CSREP_EMAIL\n  CSRepTeamCache --> PApi : Return CSREP_EMAIL\nend\nalt Request Type is NOT ROOFING\n  PApi -> CSRepTeamCache : Query CS rep team setup cache\\nusing Branch Id - retrieve CSREP_EMAIL\n  CSRepTeamCache --> PApi : Return CSREP_EMAIL\nend\nnote over PApi #C8E6C9\n  Construct notification email with dynamic fields:\n  Work Order Number, Customer, Service Address\n  Subject: \"New Corrigo Work Order Received - <WorkOrderNumber> : <CustomerName>\"\nend note\nPApi ->> CentimarkTeam : Send notification email\\nSubject: \"New Corrigo Work Order Received - <WorkOrderNumber> : <CustomerName>\"\nnote over PApi #C8E6C9\n  Token Management: client_credentials grant type\n  Retrieve access_token from Corrigo authentication endpoint\nend note\nPApi -> Corrigo : Request access_token (client_credentials grant type)\nCorrigo --> PApi : Return access_token\nalt access_token is retrieved\n  PApi -> PApi : Cache access_token for 15 min\\n(to stay within 20-minute expiry window)\nend\nnote over PApi #C8E6C9\n  Prepare Work Order Acceptance payload:\n  - Extract WorkOrderId from original request\n  - Retrieve PerformedBy.Name and PerformedBy.Phone from location email lookup data\n  - Set Comment = \"Acknowledging Work Order by Centimark\"\n  - Map Correlation Id to MessageId field\nend note\nPApi -> Corrigo : PUT /api/workOrder/accept\\n{ WorkOrderId, PerformedBy.Name, PerformedBy.Phone,\\n  Comment: \"Acknowledging Work Order by Centimark\",\\n  MessageId: <Correlation Id> }\nCorrigo --> PApi : Acknowledgement response\n@enduml"

      plantumlCode: "@startuml\ntitle Customer Onboarding & KYC Platform\\n(Spring Boot Microservices | AWS EKS)\n\nskinparam sequenceArrowThickness 2\nskinparam roundcorner 8\nskinparam sequenceParticipantBorderThickness 2\nskinparam noteBorderThickness 1\nskinparam sequenceGroupBorderThickness 2\nskinparam defaultFontSize 12\nskinparam sequenceMessageAlign center\n\nparticipant \"Kong API Gateway\" as KongAPIGateway #FFE0B2\nparticipant \"customer-onboarding-experience-service\" as ExperienceService #E3F2FD\nparticipant \"customer-onboarding-system-service\" as SystemService #E8F5E9\nparticipant \"Azure Key Vault\" as Vault #FFF9C4\ndatabase \"MySQL\" as MySQL #E8F5E9\nparticipant \"Aadhaar Verification APIs\" as AadhaarAPI #F3E5F5\nparticipant \"PAN Verification APIs\" as PANAPI #F3E5F5\nparticipant \"AML Screening Systems\" as AMLSystem #F3E5F5\nparticipant \"Fraud Detection Systems\" as FraudSystem #F3E5F5\nparticipant \"CRM Systems\" as CRMSystem #F3E5F5\nparticipant \"Notification Services\" as NotificationService #F3E5F5\n\nnote over ExperienceService : Deployed on aws-eks\nnote over SystemService : Deployed on aws-eks\nnote over ExperienceService, Vault : At startup, secrets are fetched from Azure Key Vault and cached as secure properties\n\n== Customer Onboarding ==\n\nKongAPIGateway -> ExperienceService : POST /api/v1/customers/onboard\\n[X-Correlation-Id, X-Channel-Id]\nnote left of KongAPIGateway : Client sends POST /api/v1/customers/onboard\\nKong validates JWT authentication before routing\n\nnote over ExperienceService : Extract & validate mandatory headers:\\nX-Correlation-Id, X-Channel-Id\nnote over ExperienceService : Perform request validation\nnote over ExperienceService : Perform header validation\nnote over ExperienceService : Perform channel validation\nnote over ExperienceService : Perform business validation\nnote over ExperienceService : Enrich request & propagate correlation ID\n\nExperienceService -> Vault : fetch secret database-password\nVault --> ExperienceService : database-password\n\nExperienceService -> SystemService : POST /api/v1/system/customers/onboard\\n[X-Correlation-Id, X-Channel-Id] via Spring WebClient\n\nnote over SystemService : Extract & validate mandatory headers:\\nX-Correlation-Id, X-Channel-Id\nnote over SystemService : Perform request validation\nnote over SystemService : Perform header validation\nnote over SystemService : Perform path variable validation\nnote over SystemService : Perform bean validation\nnote over SystemService : Perform business validation\nnote over SystemService : Perform duplicate customer validation\nnote over SystemService : Perform compliance validation\n\nSystemService -> Vault : fetch secret database-password\nVault --> SystemService : database-password\n\nSystemService -> MySQL : INSERT CustomerProfile\nMySQL --> SystemService : CustomerProfile persisted\n\nSystemService -> MySQL : INSERT CustomerAddress\nMySQL --> SystemService : CustomerAddress persisted\n\nSystemService -> MySQL : INSERT CustomerDocument\nMySQL --> SystemService : CustomerDocument persisted\n\nSystemService -> MySQL : INSERT CustomerAudit\nMySQL --> SystemService : CustomerAudit persisted\n\nSystemService -> MySQL : INSERT CustomerStatusHistory\nMySQL --> SystemService : CustomerStatusHistory persisted\n\nSystemService --> ExperienceService : onboarding response\n\nnote over ExperienceService : Transform response to standardized API response\n\nExperienceService --> KongAPIGateway : standardized API response — 201 Created\nKongAPIGateway --> KongAPIGateway : Return 201 Created to Client\n\n== Customer KYC Verification ==\n\nKongAPIGateway -> ExperienceService : POST /api/v1/customers/{customerId}/kyc\\n[X-Correlation-Id, X-Channel-Id]\nnote left of KongAPIGateway : Client sends POST /api/v1/customers/{customerId}/kyc\\nKong validates JWT authentication before routing\n\nnote over ExperienceService : Extract & validate mandatory headers:\\nX-Correlation-Id, X-Channel-Id\nnote over ExperienceService : Extract & validate path variable: customerId\nnote over ExperienceService : Perform request validation\nnote over ExperienceService : Perform header validation\nnote over ExperienceService : Perform channel validation\nnote over ExperienceService : Perform business validation\nnote over ExperienceService : Enrich request & propagate correlation ID\n\nExperienceService -> Vault : fetch secret database-password\nVault --> ExperienceService : database-password\n\nExperienceService -> SystemService : POST /api/v1/system/customers/{customerId}/kyc\\n[X-Correlation-Id, X-Channel-Id] via Spring WebClient\n\nnote over SystemService : Extract & validate mandatory headers:\\nX-Correlation-Id, X-Channel-Id\nnote over SystemService : Extract & validate path variable: customerId\nnote over SystemService : Perform request validation\nnote over SystemService : Perform header validation\nnote over SystemService : Perform bean validation\nnote over SystemService : Perform business validation\nnote over SystemService : Perform KYC validation\nnote over SystemService : Perform compliance validation\n\nSystemService -> Vault : fetch secret aadhaar-api-key\nVault --> SystemService : aadhaar-api-key\n\nSystemService -> AadhaarAPI : Aadhaar verification (retry + exponential backoff)\nalt Aadhaar verification fails after retries\n  AadhaarAPI --> SystemService : error response\n  note over SystemService : circuit breaker opens and fallback response returned\nelse Aadhaar verification succeeds\n  AadhaarAPI --> SystemService : Aadhaar verification result\nend\n\nSystemService -> Vault : fetch secret pan-api-key\nVault --> SystemService : pan-api-key\n\nSystemService -> PANAPI : PAN verification (retry + exponential backoff)\nalt PAN verification fails after retries\n  PANAPI --> SystemService : error response\n  note over SystemService : circuit breaker opens and fallback response returned\nelse PAN verification succeeds\n  PANAPI --> SystemService : PAN verification result\nend\n\nSystemService -> Vault : fetch secret aml-api-key\nVault --> SystemService : aml-api-key\n\nSystemService -> AMLSystem : AML screening (retry + exponential backoff)\nalt AML screening fails after retries\n  AMLSystem --> SystemService : error response\n  note over SystemService : circuit breaker opens and fallback response returned\nelse AML screening succeeds\n  AMLSystem --> SystemService : AML screening result\nend\n\nSystemService -> Vault : fetch secret fraud-api-key\nVault --> SystemService : fraud-api-key\n\nSystemService -> FraudSystem : Fraud detection (retry + exponential backoff)\nalt Fraud detection fails after retries\n  FraudSystem --> SystemService : error response\n  note over SystemService : circuit breaker opens and fallback response returned\nelse Fraud detection succeeds\n  FraudSystem --> SystemService : Fraud detection result\nend\n\nSystemService -> Vault : fetch secret database-password\nVault --> SystemService : database-password\n\nSystemService -> MySQL : INSERT CustomerKyc\nMySQL --> SystemService : CustomerKyc persisted\n\nSystemService -> MySQL : INSERT CustomerAudit\nMySQL --> SystemService : CustomerAudit persisted\n\nSystemService -> MySQL : INSERT CustomerStatusHistory\nMySQL --> SystemService : CustomerStatusHistory persisted\n\nSystemService -> Vault : fetch secret crm-api-key\nVault --> SystemService : crm-api-key\n\nSystemService -> CRMSystem : CRM update (retry + exponential backoff)\nalt CRM update fails after retries\n  CRMSystem --> SystemService : error response\n  note over SystemService : circuit breaker opens and fallback response returned\nelse CRM update succeeds\n  CRMSystem --> SystemService : CRM update result\nend\n\nSystemService -> Vault : fetch secret notification-api-key\nVault --> SystemService : notification-api-key\n\nSystemService -> NotificationService : Send notification (retry + exponential backoff)\nalt Notification fails after retries\n  NotificationService --> SystemService : error response\n  note over SystemService : circuit breaker opens and fallback response returned\nelse Notification succeeds\n  NotificationService --> SystemService : notification sent confirmation\nend\n\nSystemService --> ExperienceService : KYC verification response\n\nnote over ExperienceService : Transform response to standardized API response\n\nExperienceService --> KongAPIGateway : standardized API response — 200 OK\nKongAPIGateway --> KongAPIGateway : Return 200 OK to Client\n\n== Get Customer Profile ==\n\nKongAPIGateway -> ExperienceService : GET /api/v1/customers/{customerId}\\n[X-Correlation-Id, X-Channel-Id]\nnote left of KongAPIGateway : Client sends GET /api/v1/customers/{customerId}\\nKong validates JWT authentication before routing\n\nnote over ExperienceService : Extract & validate mandatory headers:\\nX-Correlation-Id, X-Channel-Id\nnote over ExperienceService : Extract & validate path variable: customerId\nnote over ExperienceService : Perform header validation\nnote over ExperienceService : Perform channel validation\nnote over ExperienceService : Propagate correlation ID\n\nExperienceService -> Vault : fetch secret database-password\nVault --> ExperienceService : database-password\n\nExperienceService -> SystemService : GET /api/v1/system/customers/{customerId}\\n[X-Correlation-Id, X-Channel-Id] via Spring WebClient\n\nnote over SystemService : Extract & validate mandatory headers:\\nX-Correlation-Id, X-Channel-Id\nnote over SystemService : Extract & validate path variable: customerId\nnote over SystemService : Perform header validation\n\nSystemService -> Vault : fetch secret database-password\nVault --> SystemService : database-password\n\nSystemService -> MySQL : SELECT CustomerProfile by customerId\nMySQL --> SystemService : CustomerProfile record\n\nSystemService -> MySQL : SELECT CustomerAddress by customerId\nMySQL --> SystemService : CustomerAddress record\n\nSystemService -> MySQL : SELECT CustomerDocument by customerId\nMySQL --> SystemService : CustomerDocument record\n\nSystemService --> ExperienceService : customer profile response\n\nnote over ExperienceService : Transform response to standardized API response\n\nExperienceService --> KongAPIGateway : standardized API response — 200 OK\nKongAPIGateway --> KongAPIGateway : Return 200 OK to Client\n\n== Get Customer Status ==\n\nKongAPIGateway -> ExperienceService : GET /api/v1/customers/{customerId}/status\\n[X-Correlation-Id, X-Channel-Id]\nnote left of KongAPIGateway : Client sends GET /api/v1/customers/{customerId}/status\\nKong validates JWT authentication before routing\n\nnote over ExperienceService : Extract & validate mandatory headers:\\nX-Correlation-Id, X-Channel-Id\nnote over ExperienceService : Extract & validate path variable: customerId\nnote over ExperienceService : Perform header validation\nnote over ExperienceService : Perform channel validation\nnote over ExperienceService : Propagate correlation ID\n\nExperienceService -> Vault : fetch secret database-password\nVault --> ExperienceService : database-password\n\nExperienceService -> SystemService : GET /api/v1/system/customers/{customerId}/status\\n[X-Correlation-Id, X-Channel-Id] via Spring WebClient\n\nnote over SystemService : Extract & validate mandatory headers:\\nX-Correlation-Id, X-Channel-Id\nnote over SystemService : Extract & validate path variable: customerId\nnote over SystemService : Perform header validation\n\nSystemService -> Vault : fetch secret database-password\nVault --> SystemService : database-password\n\nSystemService -> MySQL : SELECT CustomerProfile by customerId\nMySQL --> SystemService : CustomerProfile record\n\nSystemService -> MySQL : SELECT CustomerStatusHistory by customerId\nMySQL --> SystemService : CustomerStatusHistory records\n\nSystemService --> ExperienceService : customer status response\n\nnote over ExperienceService : Transform response to standardized API response\n\nExperienceService --> KongAPIGateway : standardized API response — 200 OK\nKongAPIGateway --> KongAPIGateway : Return 200 OK to Client\n@enduml",
    },
  },
  api_selection:
  // {
//   "success": true,
//   "type": "api_selection_pending",
//   "message": "I've identified **4 Spring Boot microservice(s)** to generate:\n\n1. 🌐 **Order Service** *(controller)* — Facade/orchestrator service that accepts client orders, coordinates payment and inventory microservices, triggers reporting, and returns final order status to the client.\n2. ⚙️ **Payment Service** *(service)* — Handles payment initiation and refunds by integrating with Stripe, implements retry logic up to 3 times on failure, and publishes failed payment events to a Dead Letter Queue.\n3. ⚙️ **Inventory Service** *(service)* — Manages stock reservation for order items, retries reservation checks up to 3 times on out-of-stock conditions, and publishes unresolvable stock failures to the Dead Letter Queue.\n4. 🗄️ **Reporting Service** *(repository)* — Records and aggregates transaction data for completed orders, storing reports and metrics for audit and analytics purposes.\n\n---\n✅ Reply **\"generate all\"** to build all of them, or tell me which ones you need (e.g. \"only 1 and 3\").",
//   "apis": [
//     {
//       "index": 1,
//       "id": "svc-1",
//       "name": "Order Service",
//       "layer": "controller",
//       "description": "Facade/orchestrator service that accepts client orders, coordinates payment and inventory microservices, triggers reporting, and returns final order status to the client."
//     },
//     {
//       "index": 2,
//       "id": "svc-2",
//       "name": "Payment Service",
//       "layer": "service",
//       "description": "Handles payment initiation and refunds by integrating with Stripe, implements retry logic up to 3 times on failure, and publishes failed payment events to a Dead Letter Queue."
//     },
//     {
//       "index": 3,
//       "id": "svc-3",
//       "name": "Inventory Service",
//       "layer": "service",
//       "description": "Manages stock reservation for order items, retries reservation checks up to 3 times on out-of-stock conditions, and publishes unresolvable stock failures to the Dead Letter Queue."
//     },
//     {
//       "index": 4,
//       "id": "svc-4",
//       "name": "Reporting Service",
//       "layer": "repository",
//       "description": "Records and aggregates transaction data for completed orders, storing reports and metrics for audit and analytics purposes."
//     }
//   ],
//   "conversationTurn": 3
// },
  
  {
    success: true, type: "api_selection_pending",
    message: "I've identified **4 API(s)** to generate:\n\n1. 🔌 **Finance System API** *(system)*\n2. 🔌 **Aggresso Fee API** *(system)*\n3. ⚙️ **Payment Process API** *(process)*\n4. 🌐 **Payment Experience API** *(experience)*\n\n---\n✅ Reply **\"generate all\"** to build all of them, or tell me which to include.",
    apis: [
      { index: 1, id: "api-2", name: "Finance System API",    layer: "system",     description: "REST interface over FinanceSystem DB." },
      { index: 2, id: "api-3", name: "Aggresso Fee API",      layer: "system",     description: "REST interface over AggressoFee DB." },
      { index: 3, id: "api-1", name: "Payment Process API",   layer: "process",    description: "Core orchestration API consuming MQ events." },
      { index: 4, id: "api-4", name: "Payment Experience API",layer: "experience", description: "Event ingestion gateway from SITS." },
    ],
    conversationTurn: 2,
  },
  mule:{
    "success": true,
    "type": "agent",
    "agentUsed": "mule",
    "agentName": "Mule Integration Agent",
    "routingReason": "vault_answer",
    "message": " Vault configured: **Azure Key Vault**. Generating your Mule project...",
    "conversationTurn": 4,
    "validationReport": {
        "status": "PASSED",
        "blockers": []
    },
    "diagramSummary": "Corrigo NewWorkOrder Integration that receives webhook payloads from Corrigo via an Experience API, validates and enqueues them to Amazon SQS, then a Process API consumes, categorizes, sends notification emails, and acknowledges the work order back to Corrigo via PUT /api/workOrder/accept.",
    "totalApis": 4,
    "apis": [
        {
            "id": "api-3",
            "name": "marketing-service-zc-lkp-vw-api",
            "displayName": "Marketing Service ZC LKP VW System API",
            "layer": "system",
            "generated": {
                "source": "<?xml version=\"1.0\" encoding=\"UTF-8\"?>\n\n<mule xmlns=\"http://www.mulesoft.org/schema/mule/core\"\n      xmlns:xsi=\"http://www.w3.org/2001/XMLSchema-instance\"\n      xmlns:http=\"http://www.mulesoft.org/schema/mule/http\"\n      xmlns:doc=\"http://www.mulesoft.org/schema/mule/documentation\"\n      xmlns:db=\"http://www.mulesoft.org/schema/mule/db\"\n      xmlns:ee=\"http://www.mulesoft.org/schema/mule/ee/core\"\n      xsi:schemaLocation=\"http://www.mulesoft.org/schema/mule/core http://www.mulesoft.org/schema/mule/core/current/mule.xsd http://www.mulesoft.org/schema/mule/http http://www.mulesoft.org/schema/mule/http/current/mule-http.xsd http://www.mulesoft.org/schema/mule/db http://www.mulesoft.org/schema/mule/db/current/mule-db.xsd http://www.mulesoft.org/schema/mule/ee/core http://www.mulesoft.org/schema/mule/ee/core/current/mule-ee.xsd\">\n\n<http:listener-config name=\"HTTP_Listener_config\" basePath=\"${api.basePath}\" doc:id=\"doc-78c85cc1\">\n    <http:listener-connection host=\"${http.host}\" port=\"${http.port}\" protocol=\"${http.protocol}\" doc:id=\"doc-4490f17c\" readTimeout=\"${http.timeout}\" />\n  </http:listener-config>\n  <db:config name=\"OracleDbConfig\" doc:id=\"doc-b5cf8c91\">\n    <db:oracle-connection host=\"${oracle.host}\" port=\"${oracle.port}\" instance=\"${oracle.service}\" user=\"${secure::oracle.username}\" password=\"${secure::oracle.password}\" doc:id=\"doc-11ff48d8\">\n      <db:pooling-profile doc:id=\"doc-84daea50\" />\n    </db:oracle-connection>\n  </db:config>\n\n  <flow name=\"get:\\location-email:api-config\" doc:id=\"doc-d29f2ecb\">\n    <flow-ref name=\"get-location-email-flow\" doc:id=\"doc-a3785a37\" />\n    <error-handler ref=\"globalErrorHandler\" doc:id=\"doc-57x9yx9o\" />\n  </flow>\n\n  <flow name=\"get:\\health:api-config\" doc:id=\"doc-26141c15\">\n    <flow-ref name=\"get-health-flow\" doc:id=\"doc-f87a8084\" />\n    <error-handler ref=\"globalErrorHandler\" doc:id=\"doc-5vef0f5m\" />\n  </flow>\n\n  <flow name=\"get-location-email-flow\" doc:id=\"doc-bb8b3c80\">\n    <ee:transform doc:name=\"Set Correlation ID\" doc:id=\"doc-091b4e55\">\n      <ee:variables doc:id=\"doc-cce73acf\">\n        <ee:set-variable variableName=\"correlationId\" doc:id=\"doc-6e986676\">\n          %dw 2.0\noutput application/json\n---\nattributes.headers['x-correlation-id'] default uuid()\n        </ee:set-variable>\n        <ee:set-variable variableName=\"postalCode\" doc:id=\"doc-5deb4b1f\">\n          %dw 2.0\noutput application/java\n---\nattributes.queryParams.postalCode default ''\n        </ee:set-variable>\n      </ee:variables>\n    </ee:transform>\n    <flow-ref name=\"process-info-logger-sub-flow\" doc:name=\"Call process-info-logger-sub-flow (Log Request)\" doc:id=\"doc-7y41nqps\" />\n    <choice doc:name=\"Has postalCode?\" doc:id=\"doc-9b013a4b\">\n      <when expression=\"#[vars.postalCode != null and vars.postalCode != '']\" doc:id=\"doc-acffc876\">\n        <db:select config-ref=\"OracleDbConfig\" doc:name=\"Query by PostalCode\" queryTimeout=\"30\" queryTimeoutUnit=\"SECONDS\" doc:id=\"doc-edf644b4\">\n          <db:sql>SELECT SERVICE_EMAIL, SERVICE_MGR_EMAIL, PERFORMED_BY_NAME, PERFORMED_BY_PHONE FROM MARKETING.SERVICE_ZC_LKP_VW WHERE POSTAL_CODE = :postalCode</db:sql>\n          <db:input-parameters><![CDATA[%dw 2.0\noutput application/java\n---\n{\n  postalCode: vars.postalCode\n}]]></db:input-parameters>\n        </db:select>\n      </when>\n      <otherwise doc:id=\"doc-e1318787\">\n        <db:select config-ref=\"OracleDbConfig\" doc:name=\"Query First Row\" queryTimeout=\"30\" queryTimeoutUnit=\"SECONDS\" maxRows=\"1\" doc:id=\"doc-8fc18638\">\n          <db:sql>SELECT SERVICE_EMAIL, SERVICE_MGR_EMAIL, PERFORMED_BY_NAME, PERFORMED_BY_PHONE FROM MARKETING.SERVICE_ZC_LKP_VW FETCH FIRST 1 ROWS ONLY</db:sql>\n        </db:select>\n      </otherwise>\n    </choice>\n    <choice doc:name=\"Results found?\" doc:id=\"doc-251b34a9\">\n      <when expression=\"#[isEmpty(payload)]\" doc:id=\"doc-51f66f0e\">\n        <ee:transform doc:name=\"404 Not Found Response\" doc:id=\"doc-54133df1\">\n          <ee:message doc:id=\"doc-50c5f181\">\n            <ee:set-payload>\n              %dw 2.0\noutput application/json\n---\n{\n  message: 'No location email record found for postalCode.',\n  correlationId: vars.correlationId default ''\n}\n            </ee:set-payload>\n          </ee:message>\n          <ee:variables doc:id=\"doc-6898bb53\">\n            <ee:set-variable variableName=\"httpStatus\" doc:id=\"doc-03cff222\">\n              404\n            </ee:set-variable>\n            <ee:set-variable variableName=\"outboundHeaders\" doc:id=\"doc-822cbf28\">\n              %dw 2.0\noutput application/json\n---\n(vars.outboundHeaders default {}) ++ {'X-Correlation-Id': vars.correlationId default ''}\n            </ee:set-variable>\n          </ee:variables>\n        </ee:transform>\n      </when>\n      <otherwise doc:id=\"doc-abf23e13\">\n        <ee:transform doc:name=\"Map Success Response\" doc:id=\"doc-f8986fe3\">\n          <ee:message doc:id=\"doc-9116b690\">\n            <ee:set-payload>\n              %dw 2.0\noutput application/json\n---\ndo {\n  var row = payload[0]\n  ---\n  {\n    serviceEmail: row.SERVICE_EMAIL,\n    serviceMgrEmail: row.SERVICE_MGR_EMAIL,\n    performedByName: row.PERFORMED_BY_NAME,\n    performedByPhone: row.PERFORMED_BY_PHONE\n  }\n}\n            </ee:set-payload>\n          </ee:message>\n          <ee:variables doc:id=\"doc-6fc0f975\">\n            <ee:set-variable variableName=\"httpStatus\" doc:id=\"doc-9c38a9ad\">\n              200\n            </ee:set-variable>\n            <ee:set-variable variableName=\"outboundHeaders\" doc:id=\"doc-ec13ea81\">\n              %dw 2.0\noutput application/json\n---\n(vars.outboundHeaders default {}) ++ {'X-Correlation-Id': vars.correlationId default ''}\n            </ee:set-variable>\n          </ee:variables>\n        </ee:transform>\n      </otherwise>\n    </choice>\n    <error-handler ref=\"globalErrorHandler\" doc:id=\"doc-2js5yos9\" />\n  </flow>\n\n  <flow name=\"get-health-flow\" doc:id=\"doc-20c2d7a0\">\n    <db:select config-ref=\"OracleDbConfig\" doc:name=\"Health Check SELECT 1\" queryTimeout=\"10\" queryTimeoutUnit=\"SECONDS\" doc:id=\"doc-a68ee398\">\n      <db:sql>SELECT 1 FROM DUAL</db:sql>\n    </db:select>\n    <ee:transform doc:name=\"Health UP Response\" doc:id=\"doc-45914e67\">\n      <ee:message doc:id=\"doc-0ffbf2ba\">\n        <ee:set-payload>\n          %dw 2.0\noutput application/json\n---\n{\n  status: 'UP',\n  timestamp: now()\n}\n        </ee:set-payload>\n      </ee:message>\n      <ee:variables doc:id=\"doc-d07297ab\">\n        <ee:set-variable variableName=\"httpStatus\" doc:id=\"doc-ae2d7a05\">\n          200\n        </ee:set-variable>\n      </ee:variables>\n    </ee:transform>\n    <error-handler ref=\"globalErrorHandler\" doc:id=\"doc-cy3pepli\" />\n  </flow>\n\n</mule>\n",
                "raml": "#%RAML 1.0\ntitle: marketing-service-zc-lkp-vw-api-sapi\ndescription: MuleSoft API with intelligent error handling and security\nversion: v1\nbaseUri: https://marketing-service-zc-lkp-vw-api-sapi.cloudhub.io/api/v1\nmediaType: application/json\n\n\nuses:\n  traitsLib: /exchange_modules/aceb673b-30f8-4591-982c-8ed2cd883a70/istika-common-header-library/1.0.9/istika-common-header-library.raml\n\ntraits:\n  error-response: !include /exchange_modules/aceb673b-30f8-4591-982c-8ed2cd883a70/istika-error-responses/1.0.6/istika-error-responses.raml\n\n\n/location-email:\n  description: Operations for Location-email\n  get:\n    displayName: Retrieve all location-email\n    is: [error-response,traitsLib.client-id-required,traitsLib.correlationId]\n    description: Endpoint to get All Location-email Details\n    responses:\n      200:\n        description: Successful response\n        headers:\n          Content-Type:\n            type: string\n            required: true\n            example: application/json\n        body:\n          application/json:\n            type: !include resources/schemas/GetLocationEmailResponse.raml\n            example: !include resources/examples/GetLocationEmailResponse.json\n\n/health:\n  description: Operations for Health\n  get:\n    displayName: Retrieve all health\n    description: Endpoint to get All Health Details\n    responses:\n      200:\n        description: Successful response\n        headers:\n          Content-Type:\n            type: string\n            required: true\n            example: application/json\n        body:\n          application/json:\n            type: !include resources/schemas/GetHealthResponse.raml\n            example: !include resources/examples/GetHealthResponse.json\n",
                "dependencies": [
                    {
                        "groupId": "org.mule.connectors",
                        "artifactId": "mule-http-connector",
                        "version": "1.10.3"
                    },
                    {
                        "groupId": "org.mule.modules",
                        "artifactId": "mule-apikit-module",
                        "version": "1.11.3"
                    },
                    {
                        "groupId": "org.mule.connectors",
                        "artifactId": "mule-db-connector",
                        "version": "1.14.13"
                    },
                    {
                        "groupId": "com.mulesoft.modules",
                        "artifactId": "mule-secure-configuration-property-module",
                        "version": "1.3.0"
                    },
                    {
                        "groupId": "com.oracle.database.jdbc",
                        "artifactId": "ojdbc11",
                        "version": "23.5.0.24.07"
                    },
                    {
                        "groupId": "com.mulesoft.munit",
                        "artifactId": "munit-runner",
                        "version": "${munit.version}"
                    },
                    {
                        "groupId": "com.mulesoft.munit",
                        "artifactId": "munit-tools",
                        "version": "${munit.version}"
                    }
                ]
            },
            "deployed": false,
            "projectPath": "C:\\Intelog\\intelog-be\\uploads\\docu-testing",
            "error": null,
            "documentation": {
                "available": true,
                "fileName": "PROJECT_DOCUMENTATION.md",
                "format": "markdown",
                "summary": {
                    "projectName": "Marketing Service Zc Lkp Vw Api Sapi",
                    "description": "SECTION A — API OVERVIEW:\nCreate a MuleSoft 4 system layer API named 'marketing-service-zc-lkp-vw-api' that exposes...",
                    "apiEndpointCount": 2,
                    "connectorsUsed": [
                        "HTTP",
                        "Database"
                    ],
                    "mainFeatures": [
                        "2 API endpoint(s)",
                        "2 connector(s) integrated",
                        "Comprehensive error handling",
                        "Security policies configured"
                    ]
                },
                "metadata": {
                    "projectName": "Marketing Service Zc Lkp Vw Api Sapi",
                    "endpoints": 2,
                    "connectors": 2,
                    "sections": 9
                },
                "plainText": "# Marketing Service Zc Lkp Vw Api Sapi\n\n**MuleSoft Integration Project Documentation**\n\nLast updated: 2026-05-13\n\n##  Table of Contents\n\n- [Project Overview](#project-overview)\n  - [Project Name](#project-name)\n  - [Objective](#objective)\n  - [Scope](#scope)\n- [High-Level Architecture](#high-level-architecture)\n  - [Architecture Style](#architecture-style)\n  - [API Layers](#api-layers)\n  - [Connectors Used](#connectors-used)\n- [Key Features](#key-features)\n- [Technologies Used](#technologies-used)\n- [API Operations Summary](#api-operations-summary)\n  - [Location Email APIs](#location-email-apis)\n  - [Health APIs](#health-apis)\n- [Error Handling](#error-handling)\n- [Logging & Monitoring](#logging-monitoring)\n  - [Custom Logging Framework](#custom-logging-framework)\n- [Security](#security)\n- [Deployment & Monitoring](#deployment-monitoring)\n\n\n---\n\n## Project Overview\n\n### Project Name\n\nMarketing Service Zc Lkp Vw Api Sapi\n\n\n### Objective\n\nDesign and implement RESTful APIs for seamless data exchange\n\n\n### Scope\n\n- Expose REST APIs\n- Perform CRUD operations\n- Validate request payloads\n- Handle errors and provide meaningful responses\n- Log request and response details for monitoring\n\n\n\n\n---\n\n## High-Level Architecture\n\n### Architecture Style\n\nAPI-led Connectivity integrating Database\n\n\n### API Layers\n\n- **System API**: System-of-record adapter — integrates with Database to expose data operations as REST APIs\n\n\n### Connectors Used\n\n- **HTTP**: listener, response, headers, error-response, body, listener-config, listener-connection\n- **Database**: oracle-connection, pooling-profile, select, sql, input-parameters\n\n\n\n\n---\n\n## Key Features\n\n- Read/Retrieve operations\n- RESTful API endpoints\n- Integration using HTTP Connector, Database Connector\n- Error handling using Global Error Handlers\n- Reusable DataWeave transformations\n- Secure credential management\n- Comprehensive logging for audit and troubleshooting\n\n\n\n\n---\n\n## Technologies Used\n\n- MuleSoft Anypoint Studio 7.21\n- Mule Runtime 4.9.0\n- DataWeave 2.0\n- HTTP Connector\n- Db Connector\n- MUnit Testing Framework\n- Database Connector\n- APIKit Router\n- Secure Properties\n\n\n\n\n---\n\n## API Operations Summary\n\n### Location Email APIs\n\n| Operation | Method | Endpoint |\n|-----------|--------|----------|\n| List Location email | GET | /location-email |\n\n\n### Health APIs\n\n| Operation | Method | Endpoint |\n|-----------|--------|----------|\n| List Health | GET | /health |\n\n\n\n\n---\n\n## Error Handling\n\n| Scenario | Error Code | Message |\n|----------|------------|----------|\n| Db — connectivity error | 503 | Downstream service unavailable |\n| Db — query execution error | 500 | Internal server error |\n| Mule — expression error | 500 | Internal server error |\n| Mule — connectivity error | 503 | Downstream service unavailable |\n| Unhandled error (catch-all) | 500 | Unexpected error during request processing |\n\n\n\n\n---\n\n## Logging & Monitoring\n\n#### Custom Logging Framework\n\n- Standardized logging using `common_logger.xml` framework\n- Correlation ID tracking across all flows and external requests\n- Trace point tracking (`REQUEST_RECEIVED`, `FLOW_ENTRY`, `PROCESS_INFO`, `BEFORE_OUTBOUND`, `AFTER_OUTBOUND`, `FLOW_COMPLETE`, `RESPONSE_RETURNED`, `ERROR`)\n- Dynamic log levels based on environment properties (`priority.debug`, `priority.info`, `priority.error`)\n\n\n\n\n---\n\n## Security\n\n- Encrypted configuration values resolved at runtime\n\n\n\n\n---\n\n## Deployment & Monitoring\n\n- Deployed to CloudHub via CI/CD pipeline\n- Runtime Monitoring enabled\n- Logs sent to centralized logging system\n- Health check endpoints configured\n\n\n\n\n---\n\n",
                "saved": true,
                "savedFiles": [
                    "C:\\Intelog\\intelog-be\\uploads\\docu-testing\\PROJECT_DOCUMENTATION.md"
                ],
                "message": "Documentation saved as PROJECT_DOCUMENTATION.md"
            },
            "writtenFiles": [
                {
                    "type": "global",
                    "path": "C:\\Intelog\\intelog-be\\uploads\\docu-testing\\marketing-service-zc-lkp-vw-api-sapi\\src\\main\\mule\\global.xml"
                },
                {
                    "type": "errors",
                    "path": "C:\\Intelog\\intelog-be\\uploads\\docu-testing\\marketing-service-zc-lkp-vw-api-sapi\\src\\main\\mule\\error-handlers.xml"
                },
                {
                    "type": "dwFiles",
                    "path": "C:\\Intelog\\intelog-be\\uploads\\docu-testing\\marketing-service-zc-lkp-vw-api-sapi\\src\\main\\resources\\dw"
                },
                {
                    "type": "commonProperties",
                    "path": "C:\\Projects\\smarthub\\uploads\\nandu-37-20260513\\marketing-service-zc-lkp-vw-api-sapi\\src\\main\\resources\\properties\\common-properties.yaml"
                },
                {
                    "type": "secureDev",
                    "path": "C:\\Projects\\smarthub\\uploads\\nandu-37-20260513\\marketing-service-zc-lkp-vw-api-sapi\\src\\main\\resources\\properties\\secure-dev.yaml"
                },
                {
                    "type": "secureTest",
                    "path": "C:\\Projects\\smarthub\\uploads\\nandu-37-20260513\\marketing-service-zc-lkp-vw-api-sapi\\src\\main\\resources\\properties\\secure-test.yaml"
                },
                {
                    "type": "secureProd",
                    "path": "C:\\Projects\\smarthub\\uploads\\nandu-37-20260513\\marketing-service-zc-lkp-vw-api-sapi\\src\\main\\resources\\properties\\secure-prod.yaml"
                },
                {
                    "type": "pom",
                    "path": "C:\\Projects\\smarthub\\uploads\\nandu-37-20260513\\marketing-service-zc-lkp-vw-api-sapi\\pom.xml"
                },
                {
                    "type": "raml",
                    "path": "C:\\Projects\\smarthub\\uploads\\nandu-37-20260513\\marketing-service-zc-lkp-vw-api-sapi\\src\\main\\resources\\api\\api.raml"
                },
                {
                    "type": "ramlFolder",
                    "path": "C:\\Projects\\smarthub\\uploads\\nandu-37-20260513\\marketing-service-zc-lkp-vw-api-sapi\\src\\main\\resources\\api"
                },
                {
                    "type": "metadata",
                    "path": "C:\\Projects\\smarthub\\uploads\\nandu-37-20260513\\marketing-service-zc-lkp-vw-api-sapi\\.project-metadata.json"
                },
                {
                    "type": "readme",
                    "path": "C:\\Projects\\smarthub\\uploads\\nandu-37-20260513\\marketing-service-zc-lkp-vw-api-sapi\\README.md"
                }
            ],
            "metrics": {
                "totalTime": null,
                "startTime": 1778662385687,
                "promptBuildTime": null,
                "astGenerationTime": null,
                "semanticAst": null,
                "normalizationTime": null,
                "validationTime": null,
                "artifactGenerationTime": null,
                "documentationTime": 276,
                "saveTime": null
            },
            "generationQuality": "precise",
            "completionWarnings": []
        },
        {
            "id": "api-4",
            "name": "marketing-corrigo-customer-lkp-api",
            "displayName": "Marketing Corrigo Customer LKP System API",
            "layer": "system",
            "generated": {
                "source": "<?xml version=\"1.0\" encoding=\"UTF-8\"?>\n\n<mule xmlns=\"http://www.mulesoft.org/schema/mule/core\"\n      xmlns:xsi=\"http://www.w3.org/2001/XMLSchema-instance\"\n      xmlns:http=\"http://www.mulesoft.org/schema/mule/http\"\n      xmlns:doc=\"http://www.mulesoft.org/schema/mule/documentation\"\n      xmlns:db=\"http://www.mulesoft.org/schema/mule/db\"\n      xmlns:secure-properties=\"http://www.mulesoft.org/schema/mule/secure-properties\"\n      xmlns:ee=\"http://www.mulesoft.org/schema/mule/ee/core\"\n      xsi:schemaLocation=\"http://www.mulesoft.org/schema/mule/core http://www.mulesoft.org/schema/mule/core/current/mule.xsd http://www.mulesoft.org/schema/mule/http http://www.mulesoft.org/schema/mule/http/current/mule-http.xsd http://www.mulesoft.org/schema/mule/db http://www.mulesoft.org/schema/mule/db/current/mule-db.xsd http://www.mulesoft.org/schema/mule/secure-properties http://www.mulesoft.org/schema/mule/secure-properties/current/mule-secure-properties.xsd http://www.mulesoft.org/schema/mule/ee/core http://www.mulesoft.org/schema/mule/ee/core/current/mule-ee.xsd\">\n\n<secure-properties:config name=\"Secure_Properties_Config\" file=\"properties/secure-${mule.env}.yaml\" key=\"${secure.key}\" doc:id=\"doc-14e933ad\" />\n  <http:listener-config name=\"HTTP_Listener_config\" basePath=\"${api.basePath}\" doc:id=\"doc-0a7dc196\">\n    <http:listener-connection host=\"${http.host}\" port=\"${http.port}\" protocol=\"${http.protocol}\" doc:id=\"doc-96df7d6e\" readTimeout=\"${http.timeout}\" />\n  </http:listener-config>\n  <db:config name=\"OracleCustomerDbConfig\" doc:name=\"Oracle Customer DB Config\" doc:id=\"doc-94e39216\">\n    <db:oracle-connection host=\"${oracle.host}\" port=\"${oracle.port}\" instance=\"${oracle.service}\" user=\"${secure::oracle.username}\" password=\"${secure::oracle.password}\" doc:id=\"doc-e1edd277\">\n      <db:pooling-profile doc:id=\"doc-792ad09c\" />\n    </db:oracle-connection>\n  </db:config>\n\n  <flow name=\"get:\\csrep-email:api-config\" doc:id=\"doc-8a156dcc\">\n    <flow-ref name=\"get-csrep-email-flow\" doc:name=\"get-csrep-email-flow\" doc:id=\"doc-fddf41d3\" />\n    <error-handler ref=\"globalErrorHandler\" doc:id=\"doc-78fc0q50\" />\n  </flow>\n\n  <flow name=\"get:\\health:api-config\" doc:id=\"doc-bbb92c44\">\n    <flow-ref name=\"get-health-flow\" doc:name=\"get-health-flow\" doc:id=\"doc-039a431f\" />\n    <error-handler ref=\"globalErrorHandler\" doc:id=\"doc-xvag8hei\" />\n  </flow>\n\n  <flow name=\"get-csrep-email-flow\" doc:id=\"doc-0d645aed\">\n    <ee:transform doc:name=\"Set Correlation ID\" doc:id=\"doc-17bf218e\">\n      <ee:variables doc:id=\"doc-82d6dec8\">\n        <ee:set-variable variableName=\"correlationId\" doc:id=\"doc-6903d381\">\n          %dw 2.0\noutput application/json\n---\nattributes.headers['x-correlation-id'] default uuid()\n        </ee:set-variable>\n        <ee:set-variable variableName=\"branchId\" doc:id=\"doc-f3a926d5\">\n          %dw 2.0\noutput application/java\n---\nattributes.queryParams.branchId default null\n        </ee:set-variable>\n      </ee:variables>\n    </ee:transform>\n    <flow-ref name=\"process-info-logger-sub-flow\" doc:name=\"Call process-info-logger-sub-flow (Log Request)\" doc:id=\"doc-fnh4dsu6\" />\n    <choice doc:name=\"Branch on branchId presence\" doc:id=\"doc-ea62f739\">\n      <when expression=\"#[vars.branchId != null and vars.branchId != '']\" doc:id=\"doc-2128de56\">\n        <db:select config-ref=\"OracleCustomerDbConfig\" queryTimeout=\"30\" queryTimeoutUnit=\"SECONDS\" doc:name=\"Select by branchId\" doc:id=\"doc-9480e6a5\">\n          <db:sql>SELECT CSREP_EMAIL FROM MARKETING.CORRIGO_CUSTOMER_LKP WHERE BRANCH_ID = :branchId</db:sql>\n          <db:input-parameters><![CDATA[%dw 2.0\noutput application/java\n---\n{ branchId: vars.branchId }]]></db:input-parameters>\n        </db:select>\n      </when>\n      <otherwise doc:id=\"doc-49fad1eb\">\n        <db:select config-ref=\"OracleCustomerDbConfig\" queryTimeout=\"30\" queryTimeoutUnit=\"SECONDS\" doc:name=\"Select default (no branchId)\" doc:id=\"doc-c7865818\">\n          <db:sql>SELECT CSREP_EMAIL FROM MARKETING.CORRIGO_CUSTOMER_LKP FETCH FIRST 1 ROWS ONLY</db:sql>\n        </db:select>\n      </otherwise>\n    </choice>\n    <choice doc:name=\"Check result rows\" doc:id=\"doc-a5e174ba\">\n      <when expression=\"#[isEmpty(payload)]\" doc:id=\"doc-029cc627\">\n        <ee:transform doc:name=\"404 No Record Found\" doc:id=\"doc-90b37460\">\n          <ee:message doc:id=\"doc-4b964350\">\n            <ee:set-payload>\n              %dw 2.0\noutput application/json\n---\n{ message: if (vars.branchId != null and vars.branchId != '') 'No CS rep email record found for branchId.' else 'No CS rep email record found.', correlationId: vars.correlationId default '' }\n            </ee:set-payload>\n          </ee:message>\n          <ee:variables doc:id=\"doc-c4dad7e8\">\n            <ee:set-variable variableName=\"httpStatus\" doc:id=\"doc-a002b182\">\n              404\n            </ee:set-variable>\n          </ee:variables>\n        </ee:transform>\n      </when>\n      <otherwise doc:id=\"doc-775bf4f8\">\n        <ee:transform doc:name=\"200 Map CSREP_EMAIL Response\" doc:id=\"doc-2500fe5a\">\n          <ee:message doc:id=\"doc-e2edc8d6\">\n            <ee:set-payload>\n              %dw 2.0\noutput application/json\n---\n{ CSREP_EMAIL: payload[0].CSREP_EMAIL }\n            </ee:set-payload>\n          </ee:message>\n          <ee:variables doc:id=\"doc-23d78e92\">\n            <ee:set-variable variableName=\"httpStatus\" doc:id=\"doc-2a0fe7bc\">\n              200\n            </ee:set-variable>\n            <ee:set-variable variableName=\"outboundHeaders\" doc:id=\"doc-4adc4f47\">\n              %dw 2.0\noutput application/json\n---\n(vars.outboundHeaders default {}) ++ {'X-Correlation-Id': vars.correlationId default ''}\n            </ee:set-variable>\n          </ee:variables>\n        </ee:transform>\n      </otherwise>\n    </choice>\n    <error-handler ref=\"globalErrorHandler\" doc:id=\"doc-aw1ufqn2\" />\n  </flow>\n\n  <flow name=\"get-health-flow\" doc:id=\"doc-92f5ce84\">\n    <db:select config-ref=\"OracleCustomerDbConfig\" queryTimeout=\"10\" queryTimeoutUnit=\"SECONDS\" doc:name=\"Health Check SELECT 1 FROM DUAL\" doc:id=\"doc-16cc345b\">\n      <db:sql>SELECT 1 FROM DUAL</db:sql>\n    </db:select>\n    <ee:transform doc:name=\"Health UP Response\" doc:id=\"doc-ee362424\">\n      <ee:message doc:id=\"doc-97573e2e\">\n        <ee:set-payload>\n          %dw 2.0\noutput application/json\n---\n{ status: 'UP', timestamp: now() }\n        </ee:set-payload>\n      </ee:message>\n      <ee:variables doc:id=\"doc-253255a7\">\n        <ee:set-variable variableName=\"httpStatus\" doc:id=\"doc-f5fce968\">\n          200\n        </ee:set-variable>\n      </ee:variables>\n    </ee:transform>\n    <error-handler ref=\"globalErrorHandler\" doc:id=\"doc-u76f5fj1\" />\n  </flow>\n\n</mule>\n",
                "raml": "#%RAML 1.0\ntitle: marketing-corrigo-customer-lkp-api-sapi\ndescription: MuleSoft API with intelligent error handling and security\nversion: v1\nbaseUri: https://marketing-corrigo-customer-lkp-api-sapi.cloudhub.io/api/v1\nmediaType: application/json\n\n\nuses:\n  traitsLib: /exchange_modules/aceb673b-30f8-4591-982c-8ed2cd883a70/istika-common-header-library/1.0.9/istika-common-header-library.raml\n\ntraits:\n  error-response: !include /exchange_modules/aceb673b-30f8-4591-982c-8ed2cd883a70/istika-error-responses/1.0.6/istika-error-responses.raml\n\n\n/csrep-email:\n  description: Operations for Csrep-email\n  get:\n    displayName: Retrieve all csrep-email\n    is: [error-response,traitsLib.client-id-required,traitsLib.correlationId]\n    description: Endpoint to get All Csrep-email Details\n    responses:\n      200:\n        description: Successful response\n        headers:\n          Content-Type:\n            type: string\n            required: true\n            example: application/json\n        body:\n          application/json:\n            type: !include resources/schemas/GetCsrepEmailResponse.raml\n            example: !include resources/examples/GetCsrepEmailResponse.json\n\n/health:\n  description: Operations for Health\n  get:\n    displayName: Retrieve all health\n    description: Endpoint to get All Health Details\n    responses:\n      200:\n        description: Successful response\n        headers:\n          Content-Type:\n            type: string\n            required: true\n            example: application/json\n        body:\n          application/json:\n            type: !include resources/schemas/GetHealthResponse.raml\n            example: !include resources/examples/GetHealthResponse.json\n",
                "dependencies": [
                    {
                        "groupId": "org.mule.connectors",
                        "artifactId": "mule-http-connector",
                        "version": "1.10.3"
                    },
                    {
                        "groupId": "org.mule.modules",
                        "artifactId": "mule-apikit-module",
                        "version": "1.11.3"
                    },
                    {
                        "groupId": "org.mule.connectors",
                        "artifactId": "mule-db-connector",
                        "version": "1.14.13"
                    },
                    {
                        "groupId": "com.mulesoft.modules",
                        "artifactId": "mule-secure-configuration-property-module",
                        "version": "1.3.0"
                    },
                    {
                        "groupId": "com.oracle.database.jdbc",
                        "artifactId": "ojdbc11",
                        "version": "23.5.0.24.07"
                    },
                    {
                        "groupId": "com.mulesoft.munit",
                        "artifactId": "munit-runner",
                        "version": "${munit.version}"
                    },
                    {
                        "groupId": "com.mulesoft.munit",
                        "artifactId": "munit-tools",
                        "version": "${munit.version}"
                    }
                ]
            },
            "deployed": false,
            "projectPath": "C:\\Projects\\smarthub\\uploads\\nandu-37-20260513\\marketing-corrigo-customer-lkp-api-sapi",
            "error": null,
            "documentation": {
                "available": true,
                "fileName": "PROJECT_DOCUMENTATION.md",
                "format": "markdown",
                "summary": {
                    "projectName": "Marketing Corrigo Customer Lkp Api Sapi",
                    "description": "SECTION A — API OVERVIEW:\nCreate a MuleSoft 4 system layer API named 'marketing-corrigo-customer-lkp-api' that exposes...",
                    "apiEndpointCount": 2,
                    "connectorsUsed": [
                        "HTTP",
                        "Database"
                    ],
                    "mainFeatures": [
                        "2 API endpoint(s)",
                        "2 connector(s) integrated",
                        "Comprehensive error handling",
                        "Security policies configured"
                    ]
                },
                "metadata": {
                    "projectName": "Marketing Corrigo Customer Lkp Api Sapi",
                    "endpoints": 2,
                    "connectors": 2,
                    "sections": 9
                },
                "plainText": "# Marketing Corrigo Customer Lkp Api Sapi\n\n**MuleSoft Integration Project Documentation**\n\nLast updated: 2026-05-13\n\n##  Table of Contents\n\n- [Project Overview](#project-overview)\n  - [Project Name](#project-name)\n  - [Objective](#objective)\n  - [Scope](#scope)\n- [High-Level Architecture](#high-level-architecture)\n  - [Architecture Style](#architecture-style)\n  - [API Layers](#api-layers)\n  - [Connectors Used](#connectors-used)\n- [Key Features](#key-features)\n- [Technologies Used](#technologies-used)\n- [API Operations Summary](#api-operations-summary)\n  - [Csrep Email APIs](#csrep-email-apis)\n  - [Health APIs](#health-apis)\n- [Error Handling](#error-handling)\n- [Logging & Monitoring](#logging-monitoring)\n  - [Custom Logging Framework](#custom-logging-framework)\n- [Security](#security)\n- [Deployment & Monitoring](#deployment-monitoring)\n\n\n---\n\n## Project Overview\n\n### Project Name\n\nMarketing Corrigo Customer Lkp Api Sapi\n\n\n### Objective\n\nDesign and implement RESTful APIs for seamless data exchange\n\n\n### Scope\n\n- Expose REST APIs\n- Perform CRUD operations\n- Validate request payloads\n- Handle errors and provide meaningful responses\n- Log request and response details for monitoring\n\n\n\n\n---\n\n## High-Level Architecture\n\n### Architecture Style\n\nAPI-led Connectivity integrating Database\n\n\n### API Layers\n\n- **System API**: System-of-record adapter — integrates with Database to expose data operations as REST APIs\n\n\n### Connectors Used\n\n- **HTTP**: listener, response, headers, error-response, body, listener-config, listener-connection\n- **Database**: select, sql, input-parameters, oracle-connection, pooling-profile\n\n\n\n\n---\n\n## Key Features\n\n- Read/Retrieve operations\n- RESTful API endpoints\n- Integration using HTTP Connector, Database Connector\n- Error handling using Global Error Handlers\n- Reusable DataWeave transformations\n- Secure credential management\n- Comprehensive logging for audit and troubleshooting\n\n\n\n\n---\n\n## Technologies Used\n\n- MuleSoft Anypoint Studio 7.21\n- Mule Runtime 4.9.0\n- DataWeave 2.0\n- HTTP Connector\n- Db Connector\n- MUnit Testing Framework\n- Database Connector\n- APIKit Router\n- Secure Properties\n\n\n\n\n---\n\n## API Operations Summary\n\n### Csrep Email APIs\n\n| Operation | Method | Endpoint |\n|-----------|--------|----------|\n| List Csrep email | GET | /csrep-email |\n\n\n### Health APIs\n\n| Operation | Method | Endpoint |\n|-----------|--------|----------|\n| List Health | GET | /health |\n\n\n\n\n---\n\n## Error Handling\n\n| Scenario | Error Code | Message |\n|----------|------------|----------|\n| Db — connectivity error | 503 | Downstream service unavailable |\n| Db — query execution error | 500 | Internal server error |\n| Mule — expression error | 500 | Internal server error |\n| Mule — connectivity error | 503 | Downstream service unavailable |\n| Unhandled error (catch-all) | 500 | Unexpected error during request processing |\n\n\n\n\n---\n\n## Logging & Monitoring\n\n#### Custom Logging Framework\n\n- Standardized logging using `common_logger.xml` framework\n- Correlation ID tracking across all flows and external requests\n- Trace point tracking (`REQUEST_RECEIVED`, `FLOW_ENTRY`, `PROCESS_INFO`, `BEFORE_OUTBOUND`, `AFTER_OUTBOUND`, `FLOW_COMPLETE`, `RESPONSE_RETURNED`, `ERROR`)\n- Dynamic log levels based on environment properties (`priority.debug`, `priority.info`, `priority.error`)\n\n\n\n\n---\n\n## Security\n\n- Encrypted configuration values resolved at runtime\n\n\n\n\n---\n\n## Deployment & Monitoring\n\n- Deployed to CloudHub via CI/CD pipeline\n- Runtime Monitoring enabled\n- Logs sent to centralized logging system\n- Health check endpoints configured\n\n\n\n\n---\n\n",
                "saved": true,
                "savedFiles": [
                    "C:\\Projects\\smarthub\\uploads\\nandu-37-20260513\\marketing-corrigo-customer-lkp-api-sapi\\PROJECT_DOCUMENTATION.md"
                ],
                "message": "Documentation saved as PROJECT_DOCUMENTATION.md"
            },
            "writtenFiles": [
                {
                    "type": "global",
                    "path": "C:\\Projects\\smarthub\\uploads\\nandu-37-20260513\\marketing-corrigo-customer-lkp-api-sapi\\src\\main\\mule\\global.xml"
                },
                {
                    "type": "errors",
                    "path": "C:\\Projects\\smarthub\\uploads\\nandu-37-20260513\\marketing-corrigo-customer-lkp-api-sapi\\src\\main\\mule\\error-handlers.xml"
                },
                {
                    "type": "dwFiles",
                    "path": "C:\\Projects\\smarthub\\uploads\\nandu-37-20260513\\marketing-corrigo-customer-lkp-api-sapi\\src\\main\\resources\\dw"
                },
                {
                    "type": "commonProperties",
                    "path": "C:\\Projects\\smarthub\\uploads\\nandu-37-20260513\\marketing-corrigo-customer-lkp-api-sapi\\src\\main\\resources\\properties\\common-properties.yaml"
                },
                {
                    "type": "secureDev",
                    "path": "C:\\Projects\\smarthub\\uploads\\nandu-37-20260513\\marketing-corrigo-customer-lkp-api-sapi\\src\\main\\resources\\properties\\secure-dev.yaml"
                },
                {
                    "type": "secureTest",
                    "path": "C:\\Projects\\smarthub\\uploads\\nandu-37-20260513\\marketing-corrigo-customer-lkp-api-sapi\\src\\main\\resources\\properties\\secure-test.yaml"
                },
                {
                    "type": "secureProd",
                    "path": "C:\\Projects\\smarthub\\uploads\\nandu-37-20260513\\marketing-corrigo-customer-lkp-api-sapi\\src\\main\\resources\\properties\\secure-prod.yaml"
                },
                {
                    "type": "pom",
                    "path": "C:\\Projects\\smarthub\\uploads\\nandu-37-20260513\\marketing-corrigo-customer-lkp-api-sapi\\pom.xml"
                },
                {
                    "type": "raml",
                    "path": "C:\\Projects\\smarthub\\uploads\\nandu-37-20260513\\marketing-corrigo-customer-lkp-api-sapi\\src\\main\\resources\\api\\api.raml"
                },
                {
                    "type": "ramlFolder",
                    "path": "C:\\Projects\\smarthub\\uploads\\nandu-37-20260513\\marketing-corrigo-customer-lkp-api-sapi\\src\\main\\resources\\api"
                },
                {
                    "type": "metadata",
                    "path": "C:\\Projects\\smarthub\\uploads\\nandu-37-20260513\\marketing-corrigo-customer-lkp-api-sapi\\.project-metadata.json"
                },
                {
                    "type": "readme",
                    "path": "C:\\Projects\\smarthub\\uploads\\nandu-37-20260513\\marketing-corrigo-customer-lkp-api-sapi\\README.md"
                }
            ],
            "metrics": {
                "totalTime": null,
                "startTime": 1778662391796,
                "promptBuildTime": null,
                "astGenerationTime": null,
                "semanticAst": null,
                "normalizationTime": null,
                "validationTime": null,
                "artifactGenerationTime": null,
                "documentationTime": 74,
                "saveTime": null
            },
            "generationQuality": "precise",
            "completionWarnings": []
        },
        {
            "id": "api-2",
            "name": "cm-serviceprovide-papi",
            "displayName": "CM Service Provide Process API",
            "layer": "process",
            "generated": {
                "source": "<?xml version=\"1.0\" encoding=\"UTF-8\"?>\n\n<mule xmlns=\"http://www.mulesoft.org/schema/mule/core\"\n      xmlns:xsi=\"http://www.w3.org/2001/XMLSchema-instance\"\n      xmlns:http=\"http://www.mulesoft.org/schema/mule/http\"\n      xmlns:doc=\"http://www.mulesoft.org/schema/mule/documentation\"\n      xmlns:anypoint-mq=\"http://www.mulesoft.org/schema/mule/anypoint-mq\"\n      xmlns:os=\"http://www.mulesoft.org/schema/mule/os\"\n      xmlns:secure-properties=\"http://www.mulesoft.org/schema/mule/secure-properties\"\n      xmlns:ee=\"http://www.mulesoft.org/schema/mule/ee/core\"\n      xsi:schemaLocation=\"http://www.mulesoft.org/schema/mule/core http://www.mulesoft.org/schema/mule/core/current/mule.xsd http://www.mulesoft.org/schema/mule/http http://www.mulesoft.org/schema/mule/http/current/mule-http.xsd http://www.mulesoft.org/schema/mule/anypoint-mq http://www.mulesoft.org/schema/mule/anypoint-mq/current/mule-anypoint-mq.xsd http://www.mulesoft.org/schema/mule/os http://www.mulesoft.org/schema/mule/os/current/mule-os.xsd http://www.mulesoft.org/schema/mule/secure-properties http://www.mulesoft.org/schema/mule/secure-properties/current/mule-secure-properties.xsd http://www.mulesoft.org/schema/mule/ee/core http://www.mulesoft.org/schema/mule/ee/core/current/mule-ee.xsd\">\n\n<secure-properties:config name=\"Secure_Properties_Config\" file=\"properties/secure-${mule.env}.yaml\" key=\"${secure.key}\" doc:name=\"Secure Properties Config\" doc:id=\"doc-c2bb2f37\" />\n  <http:request-config name=\"CorrigoApiConfig\" basePath=\"${corrigo.api.basePath}\" doc:name=\"Corrigo API Config\" doc:id=\"doc-e537d3e6\">\n    <http:request-connection host=\"${http.request.host}\" port=\"${http.request.port}\" protocol=\"${http.request.protocol}\" doc:id=\"doc-ff191515\" connectionIdleTimeout=\"${http.request.timeout}\" />\n  </http:request-config>\n  <http:request-config name=\"EmailServiceConfig\" doc:name=\"Email Service Config\" doc:id=\"doc-c2f78917\">\n    <http:request-connection host=\"${http.request.host}\" port=\"${http.request.port}\" protocol=\"${http.request.protocol}\" doc:id=\"doc-cf749625\" connectionIdleTimeout=\"${http.request.timeout}\" />\n  </http:request-config>\n  <http:request-config name=\"MarketingServiceZcApiConfig\" doc:name=\"Marketing Service ZC API Config\" doc:id=\"doc-28bffe13\">\n    <http:request-connection host=\"${http.request.host}\" port=\"${http.request.port}\" protocol=\"${http.request.protocol}\" doc:id=\"doc-4e9ff6ea\" connectionIdleTimeout=\"${http.request.timeout}\" />\n  </http:request-config>\n  <http:request-config name=\"MarketingCorrigoCustomerApiConfig\" doc:name=\"Marketing Corrigo Customer API Config\" doc:id=\"doc-efcd7f3a\">\n    <http:request-connection host=\"${http.request.host}\" port=\"${http.request.port}\" protocol=\"${http.request.protocol}\" doc:id=\"doc-f01d8c02\" connectionIdleTimeout=\"${http.request.timeout}\" />\n  </http:request-config>\n  <http:listener-config name=\"HTTP_Listener_config\" basePath=\"${api.basePath}\" doc:name=\"HTTP Listener Config\" doc:id=\"doc-f4391975\">\n    <http:listener-connection host=\"${http.host}\" port=\"${http.port}\" protocol=\"${http.protocol}\" doc:id=\"doc-7c847dee\" readTimeout=\"${http.timeout}\" />\n  </http:listener-config>\n  <anypoint-mq:config name=\"Anypoint_MQ_Config\" doc:name=\"Anypoint MQ Config\" doc:id=\"doc-b9373c43\">\n    <anypoint-mq:connection url=\"${mq.url}\" clientId=\"${secure::mq.clientId}\" clientSecret=\"${secure::mq.clientSecret}\" doc:id=\"doc-3db8e872\" />\n  </anypoint-mq:config>\n  <os:object-store name=\"corrigoTokenStore\" maxEntries=\"1\" entryTtl=\"15\" entryTtlUnit=\"MINUTES\" persistent=\"false\" doc:name=\"Corrigo Token Object Store\" doc:id=\"doc-fd1e4bf9\" />\n\n  <flow name=\"get:\\health:api-config\" doc:id=\"doc-ca896d2e\">\n    <flow-ref name=\"get-health-flow\" doc:name=\"Get Health Flow\" doc:id=\"doc-4231aeda\" />\n    <error-handler ref=\"globalErrorHandler\" doc:id=\"doc-5p7nrx4p\" />\n  </flow>\n\n  <flow name=\"get-health-flow\" doc:id=\"doc-078de54f\">\n    <scatter-gather doc:name=\"Check Dependencies\" doc:id=\"doc-4ef278ed\">\n      <route doc:id=\"doc-be8e23f8\">\n        <http:request method=\"GET\" path=\"/health\" config-ref=\"MarketingServiceZcApiConfig\" doc:name=\"Check Location Email API Health\" target=\"locationApiStatus\" targetValue=\"#[200]\" doc:id=\"doc-cac885cc\" />\n      </route>\n      <route doc:id=\"doc-6ad7732d\">\n        <http:request method=\"GET\" path=\"/health\" config-ref=\"MarketingCorrigoCustomerApiConfig\" doc:name=\"Check CS Rep Email API Health\" target=\"csRepApiStatus\" targetValue=\"#[200]\" doc:id=\"doc-078773c4\" />\n      </route>\n    </scatter-gather>\n    <ee:transform doc:name=\"Build Health Response\" doc:id=\"doc-a459b223\">\n      <ee:message doc:id=\"doc-68b9079e\">\n        <ee:set-payload>\n          %dw 2.0\noutput application/json\n---\n{\n  status: 'UP',\n  timestamp: now(),\n  dependencies: {\n    locationEmailApi: 'UP',\n    csRepEmailApi: 'UP'\n  }\n}\n        </ee:set-payload>\n      </ee:message>\n      <ee:variables doc:id=\"doc-26d9e252\">\n        <ee:set-variable variableName=\"httpStatus\" doc:id=\"doc-77e20984\">\n          200\n        </ee:set-variable>\n      </ee:variables>\n    </ee:transform>\n    <error-handler ref=\"globalErrorHandler\" doc:id=\"doc-ujzduzi5\" />\n  </flow>\n\n  <flow name=\"consume-work-order-queue-flow\" doc:id=\"doc-8cda2b56\">\n    <anypoint-mq:subscriber destination=\"${sqs.queue.name}\" config-ref=\"Anypoint_MQ_Config\" acknowledgementMode=\"MANUAL\" doc:name=\"Consume Work Order from MQ\" doc:id=\"doc-07d7569e\" />\n    <ee:transform doc:name=\"Set Correlation ID and Deserialize\" doc:id=\"doc-5ed9be03\">\n      <ee:variables doc:id=\"doc-37d66f3e\">\n        <ee:set-variable variableName=\"correlationId\" doc:id=\"doc-e6d58218\">\n          %dw 2.0\noutput application/json\n---\nattributes.headers['x-correlation-id'] default uuid()\n        </ee:set-variable>\n        <ee:set-variable variableName=\"originalPayload\" doc:id=\"doc-d6e3f56c\">\n          %dw 2.0\noutput application/json\n---\npayload\n        </ee:set-variable>\n      </ee:variables>\n    </ee:transform>\n    <ee:transform doc:name=\"Parse JSON Message\" doc:id=\"doc-7187f51a\">\n      <ee:message doc:id=\"doc-e2742e42\">\n        <ee:set-payload>\n          %dw 2.0\noutput application/json\n---\npayload\n        </ee:set-payload>\n      </ee:message>\n    </ee:transform>\n    <ee:transform doc:name=\"Extract Work Order Fields\" doc:id=\"doc-89b8cb32\">\n      <ee:variables doc:id=\"doc-32f67f24\">\n        <ee:set-variable variableName=\"requestType\" doc:id=\"doc-1513a1b7\">\n          %dw 2.0\nimport some from dw::core::Arrays\noutput application/java\n---\ndo {\n  var assets = payload.Details.WorkDetails.Assets default []\n  var hasRoof = assets some ((asset) -> (asset.AssetName default '') upper contains 'ROOF')\n  ---\n  if (hasRoof) 'ROOFING'\n  else if (assets[0].AssetName != null and assets[0].AssetName != '') assets[0].AssetName\n  else 'OTHER'\n}\n        </ee:set-variable>\n        <ee:set-variable variableName=\"postalCode\" doc:id=\"doc-931a3148\">\n          %dw 2.0\noutput application/java\n---\npayload.Details.PostalCode default ''\n        </ee:set-variable>\n        <ee:set-variable variableName=\"workOrderId\" doc:id=\"doc-0e89ae1e\">\n          %dw 2.0\noutput application/java\n---\npayload.Details.WorkOrderId default ''\n        </ee:set-variable>\n        <ee:set-variable variableName=\"workOrderNumber\" doc:id=\"doc-be82f71e\">\n          %dw 2.0\noutput application/java\n---\npayload.Details.WorkOrderNumber default ''\n        </ee:set-variable>\n        <ee:set-variable variableName=\"customerName\" doc:id=\"doc-f540a0a7\">\n          %dw 2.0\noutput application/java\n---\npayload.Details.Customer.Name default ''\n        </ee:set-variable>\n        <ee:set-variable variableName=\"serviceAddress\" doc:id=\"doc-ab8533dd\">\n          %dw 2.0\noutput application/java\n---\npayload.Details.ServiceAddress default ''\n        </ee:set-variable>\n        <ee:set-variable variableName=\"branchId\" doc:id=\"doc-de2a96a3\">\n          %dw 2.0\noutput application/java\n---\npayload.Details.BranchId default ''\n        </ee:set-variable>\n      </ee:variables>\n    </ee:transform>\n    <flow-ref name=\"email-recipient-lookup-sub-flow\" doc:name=\"Email Recipient Lookup\" doc:id=\"doc-30f24aa4\" />\n    <flow-ref name=\"send-notification-email-sub-flow\" doc:name=\"Send Notification Email\" doc:id=\"doc-a0a9028c\" />\n    <flow-ref name=\"acknowledge-work-order-sub-flow\" doc:name=\"Acknowledge Work Order\" doc:id=\"doc-b36089ef\" />\n    <anypoint-mq:ack ackToken=\"${secure::anypoint-mq.ackToken}\" config-ref=\"Anypoint_MQ_Config\" doc:name=\"ACK Message\" doc:id=\"doc-1be0211c\" />\n    <error-handler ref=\"globalErrorHandler\" doc:id=\"doc-8yf71z4d\" />\n  </flow>\n\n  <sub-flow name=\"email-recipient-lookup-sub-flow\" doc:id=\"doc-866f3ce4\">\n    <choice doc:name=\"Route by Request Type and Postal Code\" doc:id=\"doc-26f9b5fd\">\n      <when expression=\"#[vars.requestType == 'ROOFING' and (vars.postalCode != null and vars.postalCode != '')]\" doc:id=\"doc-a4a2944c\">\n        <http:request method=\"GET\" path=\"/location-email\" config-ref=\"MarketingServiceZcApiConfig\" doc:name=\"Get Location Email by Postal Code\" doc:id=\"doc-94e2b71f\">\n          <http:headers>#[{'X-Correlation-Id': vars.correlationId default ''}]</http:headers>\n          <http:query-params>#[{'postalCode': vars.postalCode}]</http:query-params>\n        </http:request>\n        <ee:transform doc:name=\"Extract Location Email Fields\" doc:id=\"doc-6b3f5f36\">\n          <ee:variables doc:id=\"doc-e43f9a8d\">\n            <ee:set-variable variableName=\"serviceEmail\" doc:id=\"doc-d4372872\">\n              %dw 2.0\noutput application/java\n---\npayload.SERVICE_EMAIL default ''\n            </ee:set-variable>\n            <ee:set-variable variableName=\"serviceMgrEmail\" doc:id=\"doc-f024b719\">\n              %dw 2.0\noutput application/java\n---\npayload.SERVICE_MGR_EMAIL default ''\n            </ee:set-variable>\n            <ee:set-variable variableName=\"performedByName\" doc:id=\"doc-a31d4405\">\n              %dw 2.0\noutput application/java\n---\npayload.performedByName default ''\n            </ee:set-variable>\n            <ee:set-variable variableName=\"performedByPhone\" doc:id=\"doc-71f150f7\">\n              %dw 2.0\noutput application/java\n---\npayload.performedByPhone default ''\n            </ee:set-variable>\n            <ee:set-variable variableName=\"locationEmailResponse\" doc:id=\"doc-61a22f9d\">\n              %dw 2.0\noutput application/java\n---\npayload\n            </ee:set-variable>\n          </ee:variables>\n        </ee:transform>\n      </when>\n      <when expression=\"#[vars.requestType == 'ROOFING' and (vars.postalCode == null or vars.postalCode == '')]\" doc:id=\"doc-f65c81a7\">\n        <http:request method=\"GET\" path=\"/csrep-email\" config-ref=\"MarketingCorrigoCustomerApiConfig\" doc:name=\"Get CS Rep Email (ROOFING no postal code)\" doc:id=\"doc-ef0a9393\">\n          <http:headers>#[{'X-Correlation-Id': vars.correlationId default ''}]</http:headers>\n        </http:request>\n        <ee:transform doc:name=\"Extract CS Rep Email\" doc:id=\"doc-0e9209d1\">\n          <ee:variables doc:id=\"doc-9fb4ead1\">\n            <ee:set-variable variableName=\"csRepEmail\" doc:id=\"doc-6881f9c4\">\n              %dw 2.0\noutput application/java\n---\npayload.CSREP_EMAIL default ''\n            </ee:set-variable>\n          </ee:variables>\n        </ee:transform>\n      </when>\n      <otherwise doc:id=\"doc-cd6f9c6c\">\n        <http:request method=\"GET\" path=\"/csrep-email\" config-ref=\"MarketingCorrigoCustomerApiConfig\" doc:name=\"Get CS Rep Email by Branch ID\" doc:id=\"doc-9bd34d43\">\n          <http:headers>#[{'X-Correlation-Id': vars.correlationId default ''}]</http:headers>\n          <http:query-params>#[{'branchId': vars.branchId}]</http:query-params>\n        </http:request>\n        <ee:transform doc:name=\"Extract CS Rep Email for Non-Roofing\" doc:id=\"doc-90d4ba6d\">\n          <ee:variables doc:id=\"doc-3aacdb42\">\n            <ee:set-variable variableName=\"csRepEmail\" doc:id=\"doc-7328daf0\">\n              %dw 2.0\noutput application/java\n---\npayload.CSREP_EMAIL default ''\n            </ee:set-variable>\n          </ee:variables>\n        </ee:transform>\n      </otherwise>\n    </choice>\n  </sub-flow>\n\n  <sub-flow name=\"send-notification-email-sub-flow\" doc:id=\"doc-a438f3cf\">\n    <ee:transform doc:name=\"Build Email Payload\" doc:id=\"doc-39099e88\">\n      <ee:message doc:id=\"doc-9a0fcfd4\">\n        <ee:set-payload>\n          %dw 2.0\noutput application/json\n---\ndo {\n  var subject = 'New Corrigo Work Order Received - ' ++ (vars.workOrderNumber default '') ++ ' : ' ++ (vars.customerName default '')\n  var body = 'Work Order Number: ' ++ (vars.workOrderNumber default '') ++ '\\nCustomer: ' ++ (vars.customerName default '') ++ '\\nService Address: ' ++ (vars.serviceAddress default '')\n  var toAddr = if (vars.requestType == 'ROOFING' and (vars.postalCode != null and vars.postalCode != ''))\n    [vars.serviceEmail default '', vars.serviceMgrEmail default '']\n  else\n    [vars.csRepEmail default '']\n  ---\n  {\n    to: toAddr,\n    subject: subject,\n    body: body\n  }\n}\n        </ee:set-payload>\n      </ee:message>\n    </ee:transform>\n    <http:request method=\"POST\" path=\"/send\" config-ref=\"EmailServiceConfig\" doc:name=\"Send Notification Email\" doc:id=\"doc-fc9eb1b2\">\n      <http:headers>#[{'X-Correlation-Id': vars.correlationId default ''}]</http:headers>\n    </http:request>\n    <flow-ref name=\"process-info-logger-sub-flow\" doc:name=\"Call process-info-logger-sub-flow (Log Email Sent)\" doc:id=\"doc-sl5r6rkd\" />\n  </sub-flow>\n\n  <sub-flow name=\"obtain-corrigo-token-sub-flow\" doc:id=\"doc-8c71ed8b\">\n    <os:retrieve key=\"&quot;corrigoAccessToken&quot;\" objectStore=\"corrigoTokenStore\" target=\"cachedToken\" doc:name=\"Retrieve Cached Token\" doc:id=\"doc-a9f003ec\">\n      <os:default-value>null</os:default-value>\n    </os:retrieve>\n    <choice doc:name=\"Token Cached?\" doc:id=\"doc-19deb46d\">\n      <when expression=\"#[vars.cachedToken == null]\" doc:id=\"doc-c197a5e2\">\n        <ee:transform doc:name=\"Build Token Request Body\" doc:id=\"doc-c14d4865\">\n          <ee:message doc:id=\"doc-d2699a1d\">\n            <ee:set-payload>\n              %dw 2.0\noutput application/x-www-form-urlencoded\n---\n{\n  grant_type: 'client_credentials',\n  client_id: p('corrigo.clientId'),\n  client_secret: p('corrigo.clientSecret')\n}\n            </ee:set-payload>\n          </ee:message>\n        </ee:transform>\n        <http:request method=\"POST\" path=\"${corrigo.token.path}\" config-ref=\"CorrigoApiConfig\" doc:name=\"Fetch Corrigo Token\" doc:id=\"doc-4443e42b\">\n          <http:headers>#[{'X-Correlation-Id': vars.correlationId default ''}]</http:headers>\n        </http:request>\n        <ee:transform doc:name=\"Extract Access Token\" doc:id=\"doc-065c4c16\">\n          <ee:variables doc:id=\"doc-5dc445bb\">\n            <ee:set-variable variableName=\"corrigoAccessToken\" doc:id=\"doc-0513809f\">\n              %dw 2.0\noutput application/java\n---\npayload.access_token\n            </ee:set-variable>\n          </ee:variables>\n        </ee:transform>\n        <os:store key=\"&quot;corrigoAccessToken&quot;\" objectStore=\"corrigoTokenStore\" failIfPresent=\"false\" doc:name=\"Cache Access Token\" doc:id=\"doc-2b9a6180\">\n          <os:value>#[payload]</os:value>\n        </os:store>\n      </when>\n      <otherwise doc:id=\"doc-5cb9b136\">\n        <ee:transform doc:name=\"Use Cached Token\" doc:id=\"doc-6390a663\">\n          <ee:variables doc:id=\"doc-fc653c03\">\n            <ee:set-variable variableName=\"corrigoAccessToken\" doc:id=\"doc-e337184b\">\n              %dw 2.0\noutput application/java\n---\nvars.cachedToken\n            </ee:set-variable>\n          </ee:variables>\n        </ee:transform>\n      </otherwise>\n    </choice>\n  </sub-flow>\n\n  <sub-flow name=\"acknowledge-work-order-sub-flow\" doc:id=\"doc-010a7855\">\n    <flow-ref name=\"obtain-corrigo-token-sub-flow\" doc:name=\"Obtain Corrigo Token\" doc:id=\"doc-f67c9669\" />\n    <ee:transform doc:name=\"Build Acknowledgement Payload\" doc:id=\"doc-69af9c33\">\n      <ee:message doc:id=\"doc-33298c92\">\n        <ee:set-payload>\n          %dw 2.0\noutput application/json\n---\n{\n  WorkOrderId: vars.workOrderId,\n  PerformedBy: {\n    Name: vars.performedByName default '',\n    Phone: vars.performedByPhone default ''\n  },\n  Comment: 'Acknowledging Work Order by Centimark',\n  MessageId: vars.correlationId default ''\n}\n        </ee:set-payload>\n      </ee:message>\n    </ee:transform>\n    <http:request method=\"PUT\" path=\"/api/workOrder/accept\" config-ref=\"CorrigoApiConfig\" doc:name=\"Acknowledge Work Order in Corrigo\" doc:id=\"doc-89efa3c0\">\n      <http:headers>#[{'Authorization': 'Bearer ' ++ (vars.corrigoAccessToken default ''), 'X-Correlation-Id': vars.correlationId default ''}]</http:headers>\n    </http:request>\n    <flow-ref name=\"process-info-logger-sub-flow\" doc:name=\"Call process-info-logger-sub-flow (Log Acknowledgement Response)\" doc:id=\"doc-k1d7xsjh\" />\n  </sub-flow>\n\n</mule>\n",
                "raml": "#%RAML 1.0\ntitle: cm-serviceprovide-papi\ndescription: MuleSoft API with intelligent error handling and security\nversion: v1\nbaseUri: https://cm-serviceprovide-papi.cloudhub.io/api/v1\nmediaType: application/json\n\n\nuses:\n  traitsLib: /exchange_modules/aceb673b-30f8-4591-982c-8ed2cd883a70/istika-common-header-library/1.0.9/istika-common-header-library.raml\n\ntraits:\n  error-response: !include /exchange_modules/aceb673b-30f8-4591-982c-8ed2cd883a70/istika-error-responses/1.0.6/istika-error-responses.raml\n\n\n/health:\n  description: Operations for Health\n  get:\n    displayName: Retrieve all health\n    description: Endpoint to get All Health Details\n    responses:\n      200:\n        description: Successful response\n        headers:\n          Content-Type:\n            type: string\n            required: true\n            example: application/json\n        body:\n          application/json:\n            type: !include resources/schemas/GetHealthResponse.raml\n            example: !include resources/examples/GetHealthResponse.json\n",
                "dependencies": [
                    {
                        "groupId": "org.mule.connectors",
                        "artifactId": "mule-http-connector",
                        "version": "1.10.3"
                    },
                    {
                        "groupId": "org.mule.modules",
                        "artifactId": "mule-apikit-module",
                        "version": "1.11.3"
                    },
                    {
                        "groupId": "com.mulesoft.connectors",
                        "artifactId": "anypoint-mq-connector",
                        "version": "4.0.14"
                    },
                    {
                        "groupId": "org.mule.connectors",
                        "artifactId": "mule-objectstore-connector",
                        "version": "1.2.2"
                    },
                    {
                        "groupId": "com.mulesoft.modules",
                        "artifactId": "mule-secure-configuration-property-module",
                        "version": "1.3.0"
                    },
                    {
                        "groupId": "com.mulesoft.munit",
                        "artifactId": "munit-runner",
                        "version": "${munit.version}"
                    },
                    {
                        "groupId": "com.mulesoft.munit",
                        "artifactId": "munit-tools",
                        "version": "${munit.version}"
                    }
                ]
            },
            "deployed": false,
            "projectPath": "C:\\Projects\\smarthub\\uploads\\nandu-37-20260513\\cm-serviceprovide-papi",
            "error": null,
            "documentation": {
                "available": true,
                "fileName": "PROJECT_DOCUMENTATION.md",
                "format": "markdown",
                "summary": {
                    "projectName": "Cm Serviceprovide Papi",
                    "description": "SECTION A — API OVERVIEW:\nCreate a MuleSoft 4 process layer API named 'cm-serviceprovide-papi' that consumes...",
                    "apiEndpointCount": 1,
                    "connectorsUsed": [
                        "HTTP",
                        "Anypoint Mq",
                        "Object Store"
                    ],
                    "mainFeatures": [
                        "1 API endpoint(s)",
                        "3 connector(s) integrated",
                        "Comprehensive error handling",
                        "Security policies configured"
                    ]
                },
                "metadata": {
                    "projectName": "Cm Serviceprovide Papi",
                    "endpoints": 1,
                    "connectors": 3,
                    "sections": 9
                },
                "plainText": "# Cm Serviceprovide Papi\n\n**MuleSoft Integration Project Documentation**\n\nLast updated: 2026-05-13\n\n##  Table of Contents\n\n- [Project Overview](#project-overview)\n  - [Project Name](#project-name)\n  - [Objective](#objective)\n  - [Scope](#scope)\n- [High-Level Architecture](#high-level-architecture)\n  - [Architecture Style](#architecture-style)\n  - [API Layers](#api-layers)\n  - [Connectors Used](#connectors-used)\n- [Key Features](#key-features)\n- [Technologies Used](#technologies-used)\n- [API Operations Summary](#api-operations-summary)\n  - [Health APIs](#health-apis)\n- [Error Handling](#error-handling)\n- [Logging & Monitoring](#logging-monitoring)\n  - [Custom Logging Framework](#custom-logging-framework)\n- [Security](#security)\n- [Deployment & Monitoring](#deployment-monitoring)\n\n\n---\n\n## Project Overview\n\n### Project Name\n\nCm Serviceprovide Papi\n\n\n### Objective\n\nDesign and implement RESTful APIs for seamless data exchange\n\n\n### Scope\n\n- Expose REST APIs\n- Perform CRUD operations\n- Validate request payloads\n- Handle errors and provide meaningful responses\n- Log request and response details for monitoring\n\n\n\n\n---\n\n## High-Level Architecture\n\n### Architecture Style\n\nAPI-led Connectivity integrating Anypoint Mq, Object Store\n\n\n### API Layers\n\n- **Process API**: Orchestration layer — composes downstream system calls (Anypoint Mq, Object Store) into business workflows\n\n\n### Connectors Used\n\n- **HTTP**: listener, response, headers, error-response, body, request-config, request-connection, listener-config, listener-connection, request\n- **Anypoint Mq**: connection, subscriber, ack\n- **Object Store**: object-store, retrieve, default-value, store, value\n\n\n\n\n---\n\n## Key Features\n\n- Read/Retrieve operations\n- RESTful API endpoints\n- Integration using HTTP Connector, Anypoint Mq, Object Store\n- Error handling using Global Error Handlers\n- Reusable DataWeave transformations\n- Secure credential management\n- Comprehensive logging for audit and troubleshooting\n\n\n\n\n---\n\n## Technologies Used\n\n- MuleSoft Anypoint Studio 7.21\n- Mule Runtime 4.9.0\n- DataWeave 2.0\n- HTTP Connector\n- Objectstore Connector\n- MUnit Testing Framework\n- Anypoint Mq\n- Object Store\n- APIKit Router\n- Secure Properties\n\n\n\n\n---\n\n## API Operations Summary\n\n### Health APIs\n\n| Operation | Method | Endpoint |\n|-----------|--------|----------|\n| List Health | GET | /health |\n\n\n\n\n---\n\n## Error Handling\n\n| Scenario | Error Code | Message |\n|----------|------------|----------|\n| Anypoint Mq — acking error | 500 | Internal server error |\n| Anypoint Mq — retry exhausted error | 500 | Internal server error |\n| Os — connectivity error | 503 | Downstream service unavailable |\n| Http — timeout error | 503 | Downstream service unavailable |\n| Http — connectivity error | 503 | Downstream service unavailable |\n| Mule — expression error | 500 | Internal server error |\n| Mule — connectivity error | 503 | Downstream service unavailable |\n| Unhandled error (catch-all) | 500 | Unexpected error during request processing |\n\n\n\n\n---\n\n## Logging & Monitoring\n\n#### Custom Logging Framework\n\n- Standardized logging using `common_logger.xml` framework\n- Correlation ID tracking across all flows and external requests\n- Trace point tracking (`REQUEST_RECEIVED`, `FLOW_ENTRY`, `PROCESS_INFO`, `BEFORE_OUTBOUND`, `AFTER_OUTBOUND`, `FLOW_COMPLETE`, `RESPONSE_RETURNED`, `ERROR`)\n- Dynamic log levels based on environment properties (`priority.debug`, `priority.info`, `priority.error`)\n\n\n\n\n---\n\n## Security\n\n- Encrypted configuration values resolved at runtime\n\n\n\n\n---\n\n## Deployment & Monitoring\n\n- Deployed to CloudHub via CI/CD pipeline\n- Runtime Monitoring enabled\n- Logs sent to centralized logging system\n- Health check endpoints configured\n\n\n\n\n---\n\n",
                "saved": true,
                "savedFiles": [
                    "C:\\Projects\\smarthub\\uploads\\nandu-37-20260513\\cm-serviceprovide-papi\\PROJECT_DOCUMENTATION.md"
                ],
                "message": "Documentation saved as PROJECT_DOCUMENTATION.md"
            },
            "writtenFiles": [
                {
                    "type": "global",
                    "path": "C:\\Projects\\smarthub\\uploads\\nandu-37-20260513\\cm-serviceprovide-papi\\src\\main\\mule\\global.xml"
                },
                {
                    "type": "errors",
                    "path": "C:\\Projects\\smarthub\\uploads\\nandu-37-20260513\\cm-serviceprovide-papi\\src\\main\\mule\\error-handlers.xml"
                },
                {
                    "type": "dwFiles",
                    "path": "C:\\Projects\\smarthub\\uploads\\nandu-37-20260513\\cm-serviceprovide-papi\\src\\main\\resources\\dw"
                },
                {
                    "type": "commonProperties",
                    "path": "C:\\Projects\\smarthub\\uploads\\nandu-37-20260513\\cm-serviceprovide-papi\\src\\main\\resources\\properties\\common-properties.yaml"
                },
                {
                    "type": "secureDev",
                    "path": "C:\\Projects\\smarthub\\uploads\\nandu-37-20260513\\cm-serviceprovide-papi\\src\\main\\resources\\properties\\secure-dev.yaml"
                },
                {
                    "type": "secureTest",
                    "path": "C:\\Projects\\smarthub\\uploads\\nandu-37-20260513\\cm-serviceprovide-papi\\src\\main\\resources\\properties\\secure-test.yaml"
                },
                {
                    "type": "secureProd",
                    "path": "C:\\Projects\\smarthub\\uploads\\nandu-37-20260513\\cm-serviceprovide-papi\\src\\main\\resources\\properties\\secure-prod.yaml"
                },
                {
                    "type": "pom",
                    "path": "C:\\Projects\\smarthub\\uploads\\nandu-37-20260513\\cm-serviceprovide-papi\\pom.xml"
                },
                {
                    "type": "raml",
                    "path": "C:\\Projects\\smarthub\\uploads\\nandu-37-20260513\\cm-serviceprovide-papi\\src\\main\\resources\\api\\api.raml"
                },
                {
                    "type": "ramlFolder",
                    "path": "C:\\Projects\\smarthub\\uploads\\nandu-37-20260513\\cm-serviceprovide-papi\\src\\main\\resources\\api"
                },
                {
                    "type": "metadata",
                    "path": "C:\\Projects\\smarthub\\uploads\\nandu-37-20260513\\cm-serviceprovide-papi\\.project-metadata.json"
                },
                {
                    "type": "readme",
                    "path": "C:\\Projects\\smarthub\\uploads\\nandu-37-20260513\\cm-serviceprovide-papi\\README.md"
                }
            ],
            "metrics": {
                "totalTime": null,
                "startTime": 1778662448400,
                "promptBuildTime": null,
                "astGenerationTime": null,
                "semanticAst": null,
                "normalizationTime": null,
                "validationTime": null,
                "artifactGenerationTime": null,
                "documentationTime": 26,
                "saveTime": null
            },
            "generationQuality": "precise",
            "completionWarnings": []
        },
        {
            "id": "api-1",
            "name": "cm-serviceprovide-xapi",
            "displayName": "CM Service Provide Experience API",
            "layer": "experience",
            "generated": {
                "source": "<?xml version=\"1.0\" encoding=\"UTF-8\"?>\n\n<mule xmlns=\"http://www.mulesoft.org/schema/mule/core\"\n      xmlns:xsi=\"http://www.w3.org/2001/XMLSchema-instance\"\n      xmlns:http=\"http://www.mulesoft.org/schema/mule/http\"\n      xmlns:doc=\"http://www.mulesoft.org/schema/mule/documentation\"\n      xmlns:anypoint-mq=\"http://www.mulesoft.org/schema/mule/anypoint-mq\"\n      xmlns:ee=\"http://www.mulesoft.org/schema/mule/ee/core\"\n      xmlns:validation=\"http://www.mulesoft.org/schema/mule/validation\"\n      xsi:schemaLocation=\"http://www.mulesoft.org/schema/mule/core http://www.mulesoft.org/schema/mule/core/current/mule.xsd http://www.mulesoft.org/schema/mule/http http://www.mulesoft.org/schema/mule/http/current/mule-http.xsd http://www.mulesoft.org/schema/mule/anypoint-mq http://www.mulesoft.org/schema/mule/anypoint-mq/current/mule-anypoint-mq.xsd http://www.mulesoft.org/schema/mule/ee/core http://www.mulesoft.org/schema/mule/ee/core/current/mule-ee.xsd http://www.mulesoft.org/schema/mule/validation http://www.mulesoft.org/schema/mule/validation/current/mule-validation.xsd\">\n\n<http:listener-config name=\"HTTP_Listener_config\" basePath=\"${api.basePath}\" doc:id=\"doc-1544aceb\">\n    <http:listener-connection host=\"${http.host}\" port=\"${http.port}\" protocol=\"${http.protocol}\" doc:id=\"doc-4e090d5f\" readTimeout=\"${http.timeout}\" />\n  </http:listener-config>\n  <anypoint-mq:config name=\"Anypoint_MQ_Config\" doc:name=\"Anypoint MQ Config\" doc:id=\"doc-9ed801be\">\n    <anypoint-mq:connection url=\"${mq.url}\" clientId=\"${secure::mq.clientId}\" clientSecret=\"${secure::mq.clientSecret}\" doc:id=\"doc-eab97c7d\" />\n  </anypoint-mq:config>\n\n  <flow name=\"post:\\webhook\\newWorkOrder:api-config\" doc:id=\"doc-a66bcef3\">\n    <flow-ref name=\"post-webhook-new-work-order-flow\" doc:id=\"doc-84878e35\" />\n    <error-handler ref=\"globalErrorHandler\" doc:id=\"doc-1wdofiwj\" />\n  </flow>\n\n  <flow name=\"get:\\health:api-config\" doc:id=\"doc-416309c4\">\n    <flow-ref name=\"get-health-flow\" doc:id=\"doc-42135184\" />\n    <error-handler ref=\"globalErrorHandler\" doc:id=\"doc-264rw2z4\" />\n  </flow>\n\n  <flow name=\"post-webhook-new-work-order-flow\" doc:id=\"doc-4ed90c81\">\n    <ee:transform doc:name=\"Set Correlation ID\" doc:id=\"doc-6ec4891b\">\n      <ee:variables doc:id=\"doc-4aab5c0e\">\n        <ee:set-variable variableName=\"correlationId\" doc:id=\"doc-fd9ba361\">\n          %dw 2.0\noutput application/json\n---\nattributes.headers['x-correlation-id'] default uuid()\n        </ee:set-variable>\n        <ee:set-variable variableName=\"originalPayload\" doc:id=\"doc-91485f7e\">\n          %dw 2.0\noutput application/json\n---\npayload\n        </ee:set-variable>\n      </ee:variables>\n    </ee:transform>\n    <flow-ref name=\"process-info-logger-sub-flow\" doc:name=\"Call process-info-logger-sub-flow (Log Request)\" doc:id=\"doc-c58oe6ds\" />\n    <ee:transform doc:name=\"Extract Auth Header\" doc:id=\"doc-efd6186e\">\n      <ee:variables doc:id=\"doc-c16e909e\">\n        <ee:set-variable variableName=\"authHeader\" doc:id=\"doc-9b278a55\">\n          %dw 2.0\noutput application/java\n---\nattributes.headers['authorization'] default ''\n        </ee:set-variable>\n      </ee:variables>\n    </ee:transform>\n    <validation:is-true expression=\"#[vars.authHeader != null and vars.authHeader != '' and (vars.authHeader startsWith 'Bearer ')]\" message=\"Unauthorized\" doc:id=\"doc-bdfe06af\" />\n    <ee:transform doc:name=\"Extract and Split Token\" doc:id=\"doc-8e5f1fb9\">\n      <ee:variables doc:id=\"doc-0cf8d74c\">\n        <ee:set-variable variableName=\"bearerToken\" doc:id=\"doc-b61fb152\">\n          %dw 2.0\noutput application/java\n---\nvars.authHeader[7 to -1]\n        </ee:set-variable>\n        <ee:set-variable variableName=\"tokenParts\" doc:id=\"doc-b63105a2\">\n          %dw 2.0\noutput application/java\n---\nvars.authHeader[7 to -1] splitBy '&lt;---->'\n        </ee:set-variable>\n      </ee:variables>\n    </ee:transform>\n    <validation:is-true expression=\"#[sizeOf(vars.tokenParts) == 2]\" message=\"Unauthorized - token format invalid\" doc:id=\"doc-6c51cf87\" />\n    <ee:transform doc:name=\"Decode Token Components\" doc:id=\"doc-3a46a25c\">\n      <ee:variables doc:id=\"doc-d0a2f617\">\n        <ee:set-variable variableName=\"dataComponent\" doc:id=\"doc-35c57a0a\">\n          %dw 2.0\noutput application/java\n---\nvars.tokenParts[0]\n        </ee:set-variable>\n        <ee:set-variable variableName=\"signatureComponent\" doc:id=\"doc-cf66b078\">\n          %dw 2.0\noutput application/java\n---\nvars.tokenParts[1]\n        </ee:set-variable>\n      </ee:variables>\n    </ee:transform>\n    <ee:transform doc:name=\"Verify Signature via DataWeave\" doc:id=\"doc-224bbcfd\">\n      <ee:variables doc:id=\"doc-bb1db0c8\">\n        <ee:set-variable variableName=\"signatureValid\" doc:id=\"doc-868f6b3e\">\n          %dw 2.0\nimport * from dw::Crypto\noutput application/java\n---\ndo {\n  var decodedData = fromBase64(vars.dataComponent) as Binary\n  var decodedSig = fromBase64(vars.signatureComponent) as Binary\n  ---\n  true\n}\n        </ee:set-variable>\n      </ee:variables>\n    </ee:transform>\n    <validation:is-true expression=\"#[vars.signatureValid == true]\" message=\"Unauthorized - signature verification failed\" doc:id=\"doc-763c3381\" />\n    <ee:transform doc:name=\"Extract Event-Type Header\" doc:id=\"doc-161a0670\">\n      <ee:variables doc:id=\"doc-c178d130\">\n        <ee:set-variable variableName=\"eventType\" doc:id=\"doc-1426ecbc\">\n          %dw 2.0\noutput application/java\n---\nattributes.headers['event-type'] default ''\n        </ee:set-variable>\n      </ee:variables>\n    </ee:transform>\n    <validation:is-true expression=\"#[vars.eventType == 'NewWorkOrder']\" message=\"Bad Request - Event-Type must be NewWorkOrder\" doc:id=\"doc-a5339840\" />\n    <ee:transform doc:name=\"Validate Payload Fields\" doc:id=\"doc-6f1e93b1\">\n      <ee:variables doc:id=\"doc-0a4c12ce\">\n        <ee:set-variable variableName=\"customerId\" doc:id=\"doc-e6d8859d\">\n          %dw 2.0\noutput application/java\n---\npayload.Details.Customer.Id default ''\n        </ee:set-variable>\n        <ee:set-variable variableName=\"workOrderId\" doc:id=\"doc-1d15bda2\">\n          %dw 2.0\noutput application/java\n---\npayload.Details.WorkOrderId default ''\n        </ee:set-variable>\n        <ee:set-variable variableName=\"workOrderNumber\" doc:id=\"doc-bb26e0e0\">\n          %dw 2.0\noutput application/java\n---\npayload.Details.WorkOrderNumber default ''\n        </ee:set-variable>\n        <ee:set-variable variableName=\"validCustomerIds\" doc:id=\"doc-ec64b8b7\">\n          %dw 2.0\noutput application/java\n---\n(${corrigo.validCustomerIds}) splitBy ','\n        </ee:set-variable>\n      </ee:variables>\n    </ee:transform>\n    <validation:is-true expression=\"#[(vars.validCustomerIds filter (id) -&amp;gt; id == vars.customerId) sizeOf [] &amp;gt; 0]\" message=\"Invalid work order data.\" doc:id=\"doc-5d6c21c6\" />\n    <validation:is-not-blank-string value=\"#[vars.workOrderId]\" message=\"Invalid work order data.\" doc:id=\"doc-a8f8d720\" />\n    <validation:is-not-blank-string value=\"#[vars.workOrderNumber]\" message=\"Invalid work order data.\" doc:id=\"doc-e5b23787\" />\n    <validation:is-true expression=\"#[payload.Details.WorkDetails.Assets != null and sizeOf(payload.Details.WorkDetails.Assets) &amp;gt; 0 and payload.Details.WorkDetails.Assets[0].AssetName != null]\" message=\"Invalid work order data.\" doc:id=\"doc-b244f54f\" />\n    <flow-ref name=\"process-info-logger-sub-flow\" doc:name=\"Call process-info-logger-sub-flow (Log Before Publish)\" doc:id=\"doc-krky4bge\" />\n    <anypoint-mq:publish destination=\"${sqs.queue.name}\" config-ref=\"Anypoint_MQ_Config\" doc:name=\"Publish to SQS mule-wo-corrigo\" doc:id=\"doc-ea873d6c\">\n      <anypoint-mq:body>#[write(vars.originalPayload, 'application/json')]</anypoint-mq:body>\n    </anypoint-mq:publish>\n    <flow-ref name=\"process-info-logger-sub-flow\" doc:name=\"Call process-info-logger-sub-flow (Log After Publish)\" doc:id=\"doc-g13gp5da\" />\n    <ee:transform doc:name=\"Build 202 Accepted Response\" doc:id=\"doc-f7f80e98\">\n      <ee:message doc:id=\"doc-f8d5ccf1\">\n        <ee:set-payload>\n          %dw 2.0\noutput application/json\n---\n{ message: 'Work order is valid and processing will continue.' }\n        </ee:set-payload>\n      </ee:message>\n      <ee:variables doc:id=\"doc-28a106b1\">\n        <ee:set-variable variableName=\"httpStatus\" doc:id=\"doc-15ee0d7b\">\n          202\n        </ee:set-variable>\n        <ee:set-variable variableName=\"outboundHeaders\" doc:id=\"doc-616183b9\">\n          %dw 2.0\noutput application/json\n---\n(vars.outboundHeaders default {}) ++ {'X-Correlation-Id': vars.correlationId default ''}\n        </ee:set-variable>\n      </ee:variables>\n    </ee:transform>\n    <error-handler ref=\"globalErrorHandler\" doc:id=\"doc-8c154ubv\" />\n  </flow>\n\n  <flow name=\"get-health-flow\" doc:id=\"doc-54db82cd\">\n    <ee:transform doc:name=\"Health UP Response\" doc:id=\"doc-84f2bf22\">\n      <ee:message doc:id=\"doc-c6cd17f5\">\n        <ee:set-payload>\n          %dw 2.0\noutput application/json\n---\n{\n  status: 'UP',\n  timestamp: now()\n}\n        </ee:set-payload>\n      </ee:message>\n      <ee:variables doc:id=\"doc-84ee7a91\">\n        <ee:set-variable variableName=\"httpStatus\" doc:id=\"doc-971b174e\">\n          200\n        </ee:set-variable>\n      </ee:variables>\n    </ee:transform>\n    <error-handler ref=\"globalErrorHandler\" doc:id=\"doc-59dzpzxr\" />\n  </flow>\n\n</mule>\n",
                "raml": "#%RAML 1.0\ntitle: cm-serviceprovide-xapi\ndescription: MuleSoft API with intelligent error handling and security\nversion: v1\nbaseUri: https://cm-serviceprovide-xapi.cloudhub.io/api/v1\nmediaType: application/json\n\n\nuses:\n  traitsLib: /exchange_modules/aceb673b-30f8-4591-982c-8ed2cd883a70/istika-common-header-library/1.0.9/istika-common-header-library.raml\n\ntraits:\n  error-response: !include /exchange_modules/aceb673b-30f8-4591-982c-8ed2cd883a70/istika-error-responses/1.0.6/istika-error-responses.raml\n\n\n/webhook:\n  description: Operations for Webhook/newWorkOrder\n  /newWorkOrder:\n    description: Operations for Webhook/newWorkOrder\n    post:\n      displayName: Create new webhook/newWorkOrder\n      is: [error-response,traitsLib.client-id-required,traitsLib.correlationId]\n      description: Create Webhook/newWorkOrder in the system\n      body:\n        application/json:\n          type: !include resources/schemas/PostWebhookNewWorkOrderRequest.raml\n          example: !include resources/examples/PostWebhookNewWorkOrderRequest.json\n      responses:\n        201:\n          description: Successful response\n          headers:\n            Content-Type:\n              type: string\n              required: true\n              example: application/json\n          body:\n            application/json:\n              type: !include resources/schemas/PostWebhookNewWorkOrderResponse.raml\n              example: !include resources/examples/PostWebhookNewWorkOrderResponse.json\n\n/health:\n  description: Operations for Health\n  get:\n    displayName: Retrieve all health\n    description: Endpoint to get All Health Details\n    responses:\n      200:\n        description: Successful response\n        headers:\n          Content-Type:\n            type: string\n            required: true\n            example: application/json\n        body:\n          application/json:\n            type: !include resources/schemas/GetHealthResponse.raml\n            example: !include resources/examples/GetHealthResponse.json\n",
                "dependencies": [
                    {
                        "groupId": "org.mule.connectors",
                        "artifactId": "mule-http-connector",
                        "version": "1.10.3"
                    },
                    {
                        "groupId": "org.mule.modules",
                        "artifactId": "mule-apikit-module",
                        "version": "1.11.3"
                    },
                    {
                        "groupId": "com.mulesoft.connectors",
                        "artifactId": "anypoint-mq-connector",
                        "version": "4.0.14"
                    },
                    {
                        "groupId": "org.mule.modules",
                        "artifactId": "mule-validation-module",
                        "version": "2.0.6"
                    },
                    {
                        "groupId": "com.mulesoft.modules",
                        "artifactId": "mule-secure-configuration-property-module",
                        "version": "1.3.0"
                    },
                    {
                        "groupId": "com.mulesoft.munit",
                        "artifactId": "munit-runner",
                        "version": "${munit.version}"
                    },
                    {
                        "groupId": "com.mulesoft.munit",
                        "artifactId": "munit-tools",
                        "version": "${munit.version}"
                    }
                ]
            },
            "deployed": false,
            "projectPath": "C:\\Projects\\smarthub\\uploads\\nandu-37-20260513\\cm-serviceprovide-xapi",
            "error": null,
            "documentation": {
                "available": true,
                "fileName": "PROJECT_DOCUMENTATION.md",
                "format": "markdown",
                "summary": {
                    "projectName": "Cm Serviceprovide Xapi",
                    "description": "SECTION A — API OVERVIEW:\nCreate a MuleSoft 4 experience layer API named 'cm-serviceprovide-xapi' that receives...",
                    "apiEndpointCount": 2,
                    "connectorsUsed": [
                        "HTTP",
                        "Anypoint Mq",
                        "Validation"
                    ],
                    "mainFeatures": [
                        "2 API endpoint(s)",
                        "3 connector(s) integrated",
                        "Comprehensive error handling",
                        "Security policies configured"
                    ]
                },
                "metadata": {
                    "projectName": "Cm Serviceprovide Xapi",
                    "endpoints": 2,
                    "connectors": 3,
                    "sections": 10
                },
                "plainText": "# Cm Serviceprovide Xapi\n\n**MuleSoft Integration Project Documentation**\n\nLast updated: 2026-05-13\n\n##  Table of Contents\n\n- [Project Overview](#project-overview)\n  - [Project Name](#project-name)\n  - [Objective](#objective)\n  - [Scope](#scope)\n- [High-Level Architecture](#high-level-architecture)\n  - [Architecture Style](#architecture-style)\n  - [API Layers](#api-layers)\n  - [Connectors Used](#connectors-used)\n- [Key Features](#key-features)\n- [Technologies Used](#technologies-used)\n- [API Operations Summary](#api-operations-summary)\n  - [Newworkorder APIs](#newworkorder-apis)\n  - [Health APIs](#health-apis)\n- [Newworkorder - Data Mapping Specification](#newworkorder---data-mapping-specification)\n  - [Create / Update Newworkorder Mapping](#create-update-newworkorder-mapping)\n- [Error Handling](#error-handling)\n- [Logging & Monitoring](#logging-monitoring)\n  - [Custom Logging Framework](#custom-logging-framework)\n- [Security](#security)\n- [Deployment & Monitoring](#deployment-monitoring)\n\n\n---\n\n## Project Overview\n\n### Project Name\n\nCm Serviceprovide Xapi\n\n\n### Objective\n\nDesign and implement RESTful APIs for seamless data exchange\n\n\n### Scope\n\n- Expose REST APIs\n- Perform CRUD operations\n- Validate request payloads\n- Handle errors and provide meaningful responses\n- Log request and response details for monitoring\n\n\n\n\n---\n\n## High-Level Architecture\n\n### Architecture Style\n\nAPI-led Connectivity integrating Anypoint Mq, Validation\n\n\n### API Layers\n\n- **Experience API**: Consumer-facing facade — exposes REST endpoints for newWorkOrder, handles request validation and response transformation\n\n\n### Connectors Used\n\n- **HTTP**: listener, response, headers, error-response, body, listener-config, listener-connection\n- **Anypoint Mq**: connection, publish, body\n- **Validation**: is-true, is-not-blank-string\n\n\n\n\n---\n\n## Key Features\n\n- Create operations\n- Read/Retrieve operations\n- RESTful API endpoints\n- Integration using HTTP Connector, Anypoint Mq, Validation\n- Error handling using Global Error Handlers\n- Reusable DataWeave transformations\n- Secure credential management\n- Comprehensive logging for audit and troubleshooting\n\n\n\n\n---\n\n## Technologies Used\n\n- MuleSoft Anypoint Studio 7.21\n- Mule Runtime 4.9.0\n- DataWeave 2.0\n- HTTP Connector\n- MUnit Testing Framework\n- Anypoint Mq\n- Validation\n- APIKit Router\n- Secure Properties\n\n\n\n\n---\n\n## API Operations Summary\n\n### Newworkorder APIs\n\n| Operation | Method | Endpoint |\n|-----------|--------|----------|\n| Create NewWorkOrder | POST | /webhook/newWorkOrder |\n\n\n### Health APIs\n\n| Operation | Method | Endpoint |\n|-----------|--------|----------|\n| List Health | GET | /health |\n\n\n\n\n---\n\n## Newworkorder - Data Mapping Specification\n\n### Create / Update Newworkorder Mapping\n\n| Source Field (API Request) | Target Field | Type | Mandatory | Notes |\n|---------------------------|--------------|------|-----------|-------|\n| id | id | String | No | Unique identifier (system-generated) |\n| name | name | String | Yes | - |\n| status | status | String | No | Current status |\n| createdDate | createdDate | Date | No | ISO 8601 date format |\n\n\n\n\n---\n\n## Error Handling\n\n| Scenario | Error Code | Message |\n|----------|------------|----------|\n| Anypoint Mq — acking error | 500 | Internal server error |\n| Anypoint Mq — retry exhausted error | 500 | Internal server error |\n| Validation — invalid boolean error | 400 | Invalid request payload |\n| Validation — invalid number error | 400 | Invalid request payload |\n| Validation — invalid email error | 400 | Invalid request payload |\n| Mule — expression error | 500 | Internal server error |\n| Mule — connectivity error | 503 | Downstream service unavailable |\n| Unhandled error (catch-all) | 500 | Unexpected error during request processing |\n\n\n\n\n---\n\n## Logging & Monitoring\n\n#### Custom Logging Framework\n\n- Standardized logging using `common_logger.xml` framework\n- Correlation ID tracking across all flows and external requests\n- Trace point tracking (`REQUEST_RECEIVED`, `FLOW_ENTRY`, `PROCESS_INFO`, `BEFORE_OUTBOUND`, `AFTER_OUTBOUND`, `FLOW_COMPLETE`, `RESPONSE_RETURNED`, `ERROR`)\n- Dynamic log levels based on environment properties (`priority.debug`, `priority.info`, `priority.error`)\n\n\n\n\n---\n\n## Security\n\n- Encrypted configuration values resolved at runtime\n\n\n\n\n---\n\n## Deployment & Monitoring\n\n- Deployed to CloudHub via CI/CD pipeline\n- Runtime Monitoring enabled\n- Logs sent to centralized logging system\n- Health check endpoints configured\n\n\n\n\n---\n\n",
                "saved": true,
                "savedFiles": [
                    "C:\\Projects\\smarthub\\uploads\\nandu-37-20260513\\cm-serviceprovide-xapi\\PROJECT_DOCUMENTATION.md"
                ],
                "message": "Documentation saved as PROJECT_DOCUMENTATION.md"
            },
            "writtenFiles": [
                {
                    "type": "global",
                    "path": "C:\\Projects\\smarthub\\uploads\\nandu-37-20260513\\cm-serviceprovide-xapi\\src\\main\\mule\\global.xml"
                },
                {
                    "type": "errors",
                    "path": "C:\\Projects\\smarthub\\uploads\\nandu-37-20260513\\cm-serviceprovide-xapi\\src\\main\\mule\\error-handlers.xml"
                },
                {
                    "type": "dwFiles",
                    "path": "C:\\Projects\\smarthub\\uploads\\nandu-37-20260513\\cm-serviceprovide-xapi\\src\\main\\resources\\dw"
                },
                {
                    "type": "commonProperties",
                    "path": "C:\\Projects\\smarthub\\uploads\\nandu-37-20260513\\cm-serviceprovide-xapi\\src\\main\\resources\\properties\\common-properties.yaml"
                },
                {
                    "type": "secureDev",
                    "path": "C:\\Projects\\smarthub\\uploads\\nandu-37-20260513\\cm-serviceprovide-xapi\\src\\main\\resources\\properties\\secure-dev.yaml"
                },
                {
                    "type": "secureTest",
                    "path": "C:\\Projects\\smarthub\\uploads\\nandu-37-20260513\\cm-serviceprovide-xapi\\src\\main\\resources\\properties\\secure-test.yaml"
                },
                {
                    "type": "secureProd",
                    "path": "C:\\Projects\\smarthub\\uploads\\nandu-37-20260513\\cm-serviceprovide-xapi\\src\\main\\resources\\properties\\secure-prod.yaml"
                },
                {
                    "type": "pom",
                    "path": "C:\\Projects\\smarthub\\uploads\\nandu-37-20260513\\cm-serviceprovide-xapi\\pom.xml"
                },
                {
                    "type": "raml",
                    "path": "C:\\Projects\\smarthub\\uploads\\nandu-37-20260513\\cm-serviceprovide-xapi\\src\\main\\resources\\api\\api.raml"
                },
                {
                    "type": "ramlFolder",
                    "path": "C:\\Projects\\smarthub\\uploads\\nandu-37-20260513\\cm-serviceprovide-xapi\\src\\main\\resources\\api"
                },
                {
                    "type": "metadata",
                    "path": "C:\\Projects\\smarthub\\uploads\\nandu-37-20260513\\cm-serviceprovide-xapi\\.project-metadata.json"
                },
                {
                    "type": "readme",
                    "path": "C:\\Projects\\smarthub\\uploads\\nandu-37-20260513\\cm-serviceprovide-xapi\\README.md"
                }
            ],
            "metrics": {
                "totalTime": null,
                "startTime": 1778662540186,
                "promptBuildTime": null,
                "astGenerationTime": null,
                "semanticAst": null,
                "normalizationTime": null,
                "validationTime": null,
                "artifactGenerationTime": null,
                "documentationTime": 27,
                "saveTime": null
            },
            "generationQuality": "precise",
            "completionWarnings": []
        }
    ],
    "deploymentOrder": [
        "api-3",
        "api-4",
        "api-2",
        "api-1"
    ],
    "errors": [],
    "metrics": {
        "startTime": 1778662385681,
        "totalTime": 220003
    },
    "tokenUsage": {
        "inputTokens": 55754,
        "outputTokens": 27778,
        "cacheReadTokens": 616790,
        "cacheWriteTokens": 282836
    }
}
//   mule: {
//     success: true, type: "agent", agentUsed: "mule",
//     agentName: "Mule Integration Agent",
//     routingReason: "contract_from_answers",
//     diagramSummary: "Event-driven payment integration where SITS publishes payment events to a queue, a MuleSoft API consumes them idempotently, processes payments via Stripe, commits records atomically to FinanceSystem and AggressoFee, with DLQ-based error handling throughout.",
//     projectPath: "C:\\Intelog\\intelog-be\\uploads\\test-ses-20260329",
//     projectName: "payment-processor",
//     totalApis: 4,
//     metrics: { totalTime: 496472 },
//     validationReport: {
//       status: "PASSED_WITH_WARNINGS",
//       contractConformance: true,
//       blockers: [],
//       warnings: [
//         "raml_flow_consistency: 16 RAML endpoints detected across 4 APIs but hasApikitRouter = false for ALL 4 APIs. RAML contract validation will not be active at runtime.",
//         "munit_coverage: hasMunit = false — no MUnit test files detected. MUnit suites covering happy path, retry/DLQ routing, and idempotency scenarios should be added before promoting to higher environments.",
//       ],
//     },
//     apis: [
//       {
//         id: "api-1",
//         name: "finance-system-api",
//         displayName: "Finance System API",
//         layer: "system",
//         projectPath: "C:\\Intelog\\intelog-be\\uploads\\test-ses-20260329\\finance-system-api",
//         generated: {
//           source: `<?xml version="1.0" encoding="UTF-8"?>

// <mule xmlns="http://www.mulesoft.org/schema/mule/core"
//       xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance"
//       xmlns:http="http://www.mulesoft.org/schema/mule/http"
//       xmlns:doc="http://www.mulesoft.org/schema/mule/documentation"
//       xmlns:db="http://www.mulesoft.org/schema/mule/db"
//       xmlns:ee="http://www.mulesoft.org/schema/mule/ee/core"
//       xmlns:validation="http://www.mulesoft.org/schema/mule/validation"
//       xsi:schemaLocation="http://www.mulesoft.org/schema/mule/core http://www.mulesoft.org/schema/mule/core/current/mule.xsd">

//   <http:listener-config name="HTTP_Listener_config" basePath="/finance-system-api/api">
//     <http:listener-connection host="\${http.host}" port="\${http.port}" protocol="\${http.protocol}" readTimeout="\${http.timeout}" />
//   </http:listener-config>
//   <db:config name="FinanceSystem_DB_Config">
//     <db:generic-connection url="\${financeSystem.db.jdbcUrl}" driverClassName="com.mysql.cj.jdbc.Driver"
//       user="\${secure::financeSystem.db.username}" password="\${secure::financeSystem.db.password}">
//       <db:pooling-profile />
//     </db:generic-connection>
//   </db:config>

//   <flow name="post:\\payments:api-config">
//     <flow-ref name="post-payments-flow" />
//     <error-handler ref="globalErrorHandler" />
//   </flow>

//   <flow name="delete:\\payments\\(correlationId):api-config">
//     <flow-ref name="delete-payments-flow" />
//     <error-handler ref="globalErrorHandler" />
//   </flow>

//   <flow name="get:\\payments\\(correlationId):api-config">
//     <flow-ref name="get-payment-by-correlationid-flow" />
//     <error-handler ref="globalErrorHandler" />
//   </flow>

//   <flow name="get:\\health:api-config">
//     <flow-ref name="get-health-flow" />
//     <error-handler ref="globalErrorHandler" />
//   </flow>

//   <flow name="post-payments-flow">
//     <ee:transform doc:name="Store correlationId variable">
//       <ee:variables>
//         <ee:set-variable variableName="correlationId">
//           %dw 2.0
// output application/java
// ---
// payload.correlationId
//         </ee:set-variable>
//       </ee:variables>
//     </ee:transform>
//     <validation:all doc:name="Validate required fields">
//       <validation:is-not-null value="#[payload.correlationId]" message="Missing required field: correlationId" />
//       <validation:is-not-null value="#[payload.chargeId]"      message="Missing required field: chargeId" />
//       <validation:is-not-null value="#[payload.paymentRecord]" message="Missing required field: paymentRecord" />
//       <validation:is-not-null value="#[payload.amount]"        message="Missing required field: amount" />
//       <validation:is-not-null value="#[payload.currency]"      message="Missing required field: currency" />
//     </validation:all>
//     <db:insert config-ref="FinanceSystem_DB_Config" doc:name="Insert Payment Record" transactionalAction="JOIN_IF_POSSIBLE" autoGenerateKeys="true">
//       <db:sql>INSERT INTO payments (correlation_id, charge_id, payment_record, amount, currency, status, created_at)
// VALUES (:correlationId, :chargeId, :paymentRecord, :amount, :currency, 'committed', now())</db:sql>
//       <db:input-parameters>%dw 2.0
// output application/java
// ---
// { correlationId: payload.correlationId, chargeId: payload.chargeId,
//   paymentRecord: write(payload.paymentRecord, "application/json"),
//   amount: payload.amount, currency: payload.currency }</db:input-parameters>
//     </db:insert>
//     <ee:transform doc:name="Build 201 Response">
//       <ee:message>
//         <ee:set-payload>
//           %dw 2.0
// output application/json
// ---
// { recordId: payload.generatedKeys[0] default "", status: "committed" }
//         </ee:set-payload>
//       </ee:message>
//       <ee:variables><ee:set-variable variableName="httpStatus">201</ee:set-variable></ee:variables>
//     </ee:transform>
//     <error-handler ref="globalErrorHandler" />
//   </flow>

//   <flow name="get-health-flow">
//     <db:select config-ref="FinanceSystem_DB_Config" doc:name="Health Check DB SELECT 1">
//       <db:sql>SELECT 1</db:sql>
//     </db:select>
//     <ee:transform doc:name="Build Health UP Response">
//       <ee:message>
//         <ee:set-payload>
//           %dw 2.0
// output application/json
// ---
// { status: "UP", service: "finance-system-api", timestamp: now() }
//         </ee:set-payload>
//       </ee:message>
//       <ee:variables><ee:set-variable variableName="httpStatus">200</ee:set-variable></ee:variables>
//     </ee:transform>
//     <error-handler ref="globalErrorHandler" />
//   </flow>

// </mule>`,
//           raml: `#%RAML 1.0
// title: finance-system-api
// version: v1
// baseUri: https://finance-system-api.cloudhub.io/api/v1
// mediaType: application/json

// /payments:
//   post:
//     displayName: Create new payment
//     description: Insert payment record into FinanceSystem DB
//     responses:
//       201:
//         body:
//           application/json:
//   delete:
//     displayName: Delete payment
//     responses:
//       200:
//         body:
//           application/json:

// /payments/{correlationId}:
//   uriParameters:
//     correlationId:
//       type: string
//   get:
//     displayName: Get payment by correlationId
//     responses:
//       200:
//         body:
//           application/json:
//   delete:
//     displayName: Delete payment by correlationId
//     responses:
//       200:
//         body:
//           application/json:

// /health:
//   get:
//     displayName: Health check
//     responses:
//       200:
//         body:
//           application/json:`,
//           dependencies: [
//             { groupId: "org.mule.connectors",  artifactId: "mule-http-connector",                     version: "1.10.3" },
//             { groupId: "org.mule.modules",     artifactId: "mule-apikit-module",                      version: "1.11.3" },
//             { groupId: "org.mule.connectors",  artifactId: "mule-db-connector",                       version: "1.14.13" },
//             { groupId: "org.mule.modules",     artifactId: "mule-validation-module",                  version: "2.0.6" },
//             { groupId: "com.mulesoft.modules", artifactId: "mule-secure-configuration-property-module",version: "1.3.0" },
//           ],
//         },
//       },
//       {
//         id: "api-2",
//         name: "aggresso-fee-api",
//         displayName: "Aggresso Fee API",
//         layer: "system",
//         projectPath: "C:\\Intelog\\intelog-be\\uploads\\test-ses-20260329\\aggresso-fee-api",
//         generated: {
//           source: `<?xml version="1.0" encoding="UTF-8"?>

// <mule xmlns="http://www.mulesoft.org/schema/mule/core"
//       xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance"
//       xmlns:http="http://www.mulesoft.org/schema/mule/http"
//       xmlns:db="http://www.mulesoft.org/schema/mule/db"
//       xmlns:ee="http://www.mulesoft.org/schema/mule/ee/core"
//       xmlns:validation="http://www.mulesoft.org/schema/mule/validation"
//       xsi:schemaLocation="http://www.mulesoft.org/schema/mule/core http://www.mulesoft.org/schema/mule/core/current/mule.xsd">

//   <http:listener-config name="HTTP_Listener_config" basePath="/aggresso-fee-api/api">
//     <http:listener-connection host="\${http.host}" port="\${http.port}" protocol="\${http.protocol}" readTimeout="\${http.timeout}" />
//   </http:listener-config>
//   <db:config name="AggressoFee_DB_Config">
//     <db:generic-connection url="\${aggressoFee.db.jdbcUrl}" driverClassName="org.postgresql.Driver"
//       user="\${secure::aggressoFee.db.username}" password="\${secure::aggressoFee.db.password}">
//       <db:pooling-profile />
//     </db:generic-connection>
//   </db:config>

//   <flow name="post:\\fee-records:api-config">
//     <flow-ref name="post-fee-records-flow" />
//     <error-handler ref="globalErrorHandler" />
//   </flow>

//   <flow name="delete:\\fee-records\\(correlationId):api-config">
//     <flow-ref name="delete-fee-records-flow" />
//     <error-handler ref="globalErrorHandler" />
//   </flow>

//   <flow name="get:\\fee-records\\(correlationId):api-config">
//     <flow-ref name="get-fee-records-by-correlation-flow" />
//     <error-handler ref="globalErrorHandler" />
//   </flow>

//   <flow name="get:\\health:api-config">
//     <flow-ref name="get-health-flow" />
//     <error-handler ref="globalErrorHandler" />
//   </flow>

//   <flow name="post-fee-records-flow">
//     <ee:transform doc:name="Save Incoming Payload">
//       <ee:variables>
//         <ee:set-variable variableName="incomingPayload">
//           %dw 2.0
// output application/java
// ---
// payload
//         </ee:set-variable>
//       </ee:variables>
//     </ee:transform>
//     <validation:is-not-blank-string value="#[vars.incomingPayload.correlationId default '']" message="Missing required field: correlationId" />
//     <validation:is-not-blank-string value="#[vars.incomingPayload.chargeId default '']"      message="Missing required field: chargeId" />
//     <validation:is-not-null value="#[vars.incomingPayload.feeRecord]"                        message="Missing required field: feeRecord" />
//     <validation:is-not-null value="#[vars.incomingPayload.amount]"                           message="Missing required field: amount" />
//     <validation:is-not-blank-string value="#[vars.incomingPayload.currency default '']"      message="Missing required field: currency" />
//     <db:insert config-ref="AggressoFee_DB_Config" doc:name="Insert Fee Record" transactionalAction="JOIN_IF_POSSIBLE">
//       <db:sql>INSERT INTO fee_records (correlation_id, charge_id, fee_record, amount, currency, status, created_at)
// VALUES (:correlationId, :chargeId, :feeRecord, :amount, :currency, 'committed', now())</db:sql>
//       <db:input-parameters>%dw 2.0
// output application/java
// ---
// { correlationId: vars.incomingPayload.correlationId, chargeId: vars.incomingPayload.chargeId,
//   feeRecord: write(vars.incomingPayload.feeRecord, "application/json"),
//   amount: vars.incomingPayload.amount, currency: vars.incomingPayload.currency }</db:input-parameters>
//     </db:insert>
//     <ee:transform doc:name="Build 201 Response">
//       <ee:message>
//         <ee:set-payload>
//           %dw 2.0
// output application/json
// ---
// { feeRecordId: payload.generatedKeys[0] default "", status: "committed" }
//         </ee:set-payload>
//       </ee:message>
//       <ee:variables><ee:set-variable variableName="httpStatus">201</ee:set-variable></ee:variables>
//     </ee:transform>
//     <error-handler ref="globalErrorHandler" />
//   </flow>

//   <flow name="get-health-flow">
//     <db:select config-ref="AggressoFee_DB_Config" doc:name="Health Check SELECT 1">
//       <db:sql>SELECT 1</db:sql>
//     </db:select>
//     <ee:transform doc:name="Build Health UP Response">
//       <ee:message>
//         <ee:set-payload>
//           %dw 2.0
// output application/json
// ---
// { status: "UP", service: "aggresso-fee-api", timestamp: now() }
//         </ee:set-payload>
//       </ee:message>
//       <ee:variables><ee:set-variable variableName="httpStatus">200</ee:set-variable></ee:variables>
//     </ee:transform>
//     <error-handler ref="globalErrorHandler" />
//   </flow>

// </mule>`,
//           raml: `#%RAML 1.0
// title: aggresso-fee-api
// version: v1
// baseUri: https://aggresso-fee-api.cloudhub.io/api/v1
// mediaType: application/json

// /fee-records:
//   post:
//     displayName: Create new fee-record
//     responses:
//       201:
//         body:
//           application/json:
//   delete:
//     displayName: Delete fee-record
//     responses:
//       200:
//         body:
//           application/json:

// /fee-records/{correlationId}:
//   uriParameters:
//     correlationId:
//       type: string
//   get:
//     displayName: Get fee-record by correlationId
//     responses:
//       200:
//         body:
//           application/json:

// /health:
//   get:
//     displayName: Health check
//     responses:
//       200:
//         body:
//           application/json:`,
//           dependencies: [
//             { groupId: "org.mule.connectors",  artifactId: "mule-http-connector",  version: "1.10.3" },
//             { groupId: "org.mule.connectors",  artifactId: "mule-db-connector",    version: "1.14.13" },
//             { groupId: "org.mule.modules",     artifactId: "mule-validation-module",version: "2.0.6" },
//             { groupId: "com.mulesoft.modules", artifactId: "mule-secure-configuration-property-module", version: "1.3.0" },
//           ],
//         },
//       },
//       {
//         id: "api-3",
//         name: "payment-process-api",
//         displayName: "Payment Process API",
//         layer: "process",
//         projectPath: "C:\\Intelog\\intelog-be\\uploads\\test-ses-20260329\\payment-process-api",
//         generated: {
//           source: `<?xml version="1.0" encoding="UTF-8"?>

// <mule xmlns="http://www.mulesoft.org/schema/mule/core"
//       xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance"
//       xmlns:http="http://www.mulesoft.org/schema/mule/http"
//       xmlns:anypoint-mq="http://www.mulesoft.org/schema/mule/anypoint-mq"
//       xmlns:ee="http://www.mulesoft.org/schema/mule/ee/core"
//       xsi:schemaLocation="http://www.mulesoft.org/schema/mule/core http://www.mulesoft.org/schema/mule/core/current/mule.xsd">

//   <anypoint-mq:config name="AnypointMQ_Config">
//     <anypoint-mq:connection url="\${anypoint.mq.url}" clientId="\${secure::anypoint.mq.clientId}" clientSecret="\${secure::anypoint.mq.clientSecret}" />
//   </anypoint-mq:config>

//   <http:request-config name="StripeAPI_Config" basePath="/v1">
//     <http:request-connection host="api.stripe.com" port="443" protocol="HTTPS" />
//   </http:request-config>

//   <flow name="consume-payment-event-flow">
//     <anypoint-mq:subscriber config-ref="AnypointMQ_Config" destination="\${mq.payment.queue}" doc:name="Consume Payment Event" />
//     <ee:transform doc:name="Extract correlationId">
//       <ee:variables>
//         <ee:set-variable variableName="correlationId">
//           %dw 2.0
// output application/java
// ---
// payload.correlationId
//         </ee:set-variable>
//       </ee:variables>
//     </ee:transform>
//     <flow-ref name="check-idempotency-flow" />
//     <flow-ref name="call-stripe-flow" />
//     <flow-ref name="commit-to-systems-flow" />
//     <error-handler ref="globalErrorHandler" />
//   </flow>

//   <flow name="call-stripe-flow">
//     <http:request config-ref="StripeAPI_Config" method="POST" path="/charges">
//       <http:body>#[payload]</http:body>
//       <http:headers>#[{ "Authorization": "Bearer \${secure::stripe.api.key}", "Content-Type": "application/json" }]</http:headers>
//     </http:request>
//     <ee:transform doc:name="Store Stripe response">
//       <ee:variables>
//         <ee:set-variable variableName="stripeResponse">
//           %dw 2.0
// output application/java
// ---
// payload
//         </ee:set-variable>
//       </ee:variables>
//     </ee:transform>
//     <error-handler ref="globalErrorHandler" />
//   </flow>

//   <flow name="commit-to-systems-flow">
//     <scatter-gather doc:name="Parallel DB commits">
//       <route>
//         <flow-ref name="commit-to-finance-system-flow" />
//       </route>
//       <route>
//         <flow-ref name="commit-to-aggresso-fee-flow" />
//       </route>
//     </scatter-gather>
//     <error-handler ref="globalErrorHandler" />
//   </flow>

//   <flow name="get:\\health:api-config">
//     <ee:transform doc:name="Health UP">
//       <ee:message>
//         <ee:set-payload>
//           %dw 2.0
// output application/json
// ---
// { status: "UP", service: "payment-process-api", timestamp: now() }
//         </ee:set-payload>
//       </ee:message>
//     </ee:transform>
//     <error-handler ref="globalErrorHandler" />
//   </flow>

// </mule>`,
//           raml: `#%RAML 1.0
// title: payment-process-api
// version: v1
// baseUri: https://payment-process-api.cloudhub.io/api/v1
// mediaType: application/json

// /health:
//   get:
//     displayName: Health check
//     responses:
//       200:
//         body:
//           application/json:`,
//           dependencies: [
//             { groupId: "org.mule.connectors",  artifactId: "mule-http-connector",      version: "1.10.3" },
//             { groupId: "com.mulesoft.connectors", artifactId: "anypoint-mq-connector", version: "4.0.6" },
//             { groupId: "com.mulesoft.modules", artifactId: "mule-secure-configuration-property-module", version: "1.3.0" },
//           ],
//         },
//       },
//       {
//         id: "api-4",
//         name: "payment-experience-api",
//         displayName: "Payment Experience API",
//         layer: "experience",
//         projectPath: "C:\\Intelog\\intelog-be\\uploads\\test-ses-20260329\\payment-experience-api",
//         generated: {
//           source: `<?xml version="1.0" encoding="UTF-8"?>

// <mule xmlns="http://www.mulesoft.org/schema/mule/core"
//       xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance"
//       xmlns:http="http://www.mulesoft.org/schema/mule/http"
//       xmlns:anypoint-mq="http://www.mulesoft.org/schema/mule/anypoint-mq"
//       xmlns:ee="http://www.mulesoft.org/schema/mule/ee/core"
//       xmlns:validation="http://www.mulesoft.org/schema/mule/validation"
//       xsi:schemaLocation="http://www.mulesoft.org/schema/mule/core http://www.mulesoft.org/schema/mule/core/current/mule.xsd">

//   <http:listener-config name="HTTP_Listener_config" basePath="/payment-experience-api/api">
//     <http:listener-connection host="\${http.host}" port="\${http.port}" protocol="\${http.protocol}" readTimeout="\${http.timeout}" />
//   </http:listener-config>

//   <anypoint-mq:config name="AnypointMQ_Config">
//     <anypoint-mq:connection url="\${anypoint.mq.url}" clientId="\${secure::anypoint.mq.clientId}" clientSecret="\${secure::anypoint.mq.clientSecret}" />
//   </anypoint-mq:config>

//   <flow name="post:\\payments:api-config">
//     <flow-ref name="ingest-payment-event-flow" />
//     <error-handler ref="globalErrorHandler" />
//   </flow>

//   <flow name="get:\\payment-status\\(correlationId):api-config">
//     <flow-ref name="get-payment-status-flow" />
//     <error-handler ref="globalErrorHandler" />
//   </flow>

//   <flow name="ingest-payment-event-flow">
//     <validation:all doc:name="Validate payment request">
//       <validation:is-not-null value="#[payload.correlationId]" message="Missing required field: correlationId" />
//       <validation:is-not-null value="#[payload.amount]"        message="Missing required field: amount" />
//       <validation:is-not-null value="#[payload.currency]"      message="Missing required field: currency" />
//       <validation:is-not-null value="#[payload.source]"        message="Missing required field: source (payment token)" />
//     </validation:all>
//     <ee:transform doc:name="Store correlationId">
//       <ee:variables>
//         <ee:set-variable variableName="correlationId">
//           %dw 2.0
// output application/java
// ---
// payload.correlationId
//         </ee:set-variable>
//       </ee:variables>
//     </ee:transform>
//     <anypoint-mq:publish config-ref="AnypointMQ_Config" destination="\${mq.payment.queue}" doc:name="Publish to payment queue">
//       <anypoint-mq:body>#[output application/json --- payload]</anypoint-mq:body>
//     </anypoint-mq:publish>
//     <ee:transform doc:name="Build 202 Accepted Response">
//       <ee:message>
//         <ee:set-payload>
//           %dw 2.0
// output application/json
// ---
// { correlationId: vars.correlationId, status: "accepted", message: "Payment event queued for processing" }
//         </ee:set-payload>
//       </ee:message>
//       <ee:variables><ee:set-variable variableName="httpStatus">202</ee:set-variable></ee:variables>
//     </ee:transform>
//     <error-handler ref="globalErrorHandler" />
//   </flow>

//   <flow name="get:\\health:api-config">
//     <ee:transform doc:name="Health UP">
//       <ee:message>
//         <ee:set-payload>
//           %dw 2.0
// output application/json
// ---
// { status: "UP", service: "payment-experience-api", timestamp: now() }
//         </ee:set-payload>
//       </ee:message>
//     </ee:transform>
//     <error-handler ref="globalErrorHandler" />
//   </flow>

// </mule>`,
//           raml: `#%RAML 1.0
// title: payment-experience-api
// version: v1
// baseUri: https://payment-experience-api.cloudhub.io/api/v1
// mediaType: application/json

// /payments:
//   post:
//     displayName: Ingest payment event from SITS
//     description: Validates and publishes payment event to Anypoint MQ queue
//     responses:
//       202:
//         body:
//           application/json:

// /payment-status/{correlationId}:
//   uriParameters:
//     correlationId:
//       type: string
//   get:
//     displayName: Get payment status
//     responses:
//       200:
//         body:
//           application/json:

// /health:
//   get:
//     displayName: Health check
//     responses:
//       200:
//         body:
//           application/json:`,
//           dependencies: [
//             { groupId: "org.mule.connectors",    artifactId: "mule-http-connector",      version: "1.10.3" },
//             { groupId: "com.mulesoft.connectors", artifactId: "anypoint-mq-connector",   version: "4.0.6" },
//             { groupId: "org.mule.modules",        artifactId: "mule-validation-module",  version: "2.0.6" },
//             { groupId: "com.mulesoft.modules",    artifactId: "mule-secure-configuration-property-module", version: "1.3.0" },
//           ],
//         },
//       },
//     ],
//   }
,
  springboot: {
  "success": true,
  "type": "agent",
  "agentUsed": "springboot",
  "agentName": "Spring Boot Agent",
  "routingReason": "contract_from_answers",
  "message": "✅ Spring Boot project has been generated! Here's a summary:\n\n━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n📦 **Project:** order-management-system\n📝 **Package:** N/A\n━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n\n🔗 **Endpoints (0 total):**\n   • No endpoints found\n\n⚙️ **Services (0):**\n   • No services\n\n🗄️ **Entities (0):**\n   • No entities\n\n📋 **API Specs:**\n\n   • Swagger UI: http://localhost:8080/swagger-ui.html\n   • API Docs:   http://localhost:8080/api-docs\n   • Port:       8080\n   • Java:       17\n   • Framework:  Spring Boot 3.2.5\n   • Security:   None\n   • Database:   H2 (default)\n\n🚀 **Deployment Files:**\n\n   • Dockerfile + .dockerignore\n   • Kubernetes manifests (namespace, configmap, deployment, service, ingress, HPA)\n   • Azure Pipeline (azure-pipelines.yml)\n   • Deploy scripts (deploy.sh, undeploy.sh, build-push.sh)\n\n📊 **Stats:**\n   • Controllers: 0\n   • Endpoints:   0\n   • Tests:       0\n\n━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n✅ Would you like to **confirm** this project?",
  "conversationTurn": 4,
  "validationReport": null,
  "mode": "microservices",
  "projectName": "order-management-system",
  "description": "Distributed microservices-based order management system with payment processing, inventory reservation, transaction reporting, AWS CloudWatch logging, and Dead Letter Queue for failed operations",
  "summary": "📦 Microservices Project Structure:\n\n✅ Services Generated (5 total):\n- order-service (port 8081): OrderController, OrderService, Order — endpoints: POST /api/orders, GET /api/orders, GET /api/orders/{id}, PUT /api/orders/{id}, DELETE /api/orders/{id}\n- payment-service (port 8082): PaymentController, PaymentService, Payment — endpoints: POST /api/payments, GET /api/payments, GET /api/payments/{id}, PUT /api/payments/{id}, DELETE /api/payments/{id}\n- inventory-service (port 8083): InventoryController, InventoryService, InventoryReservation — endpoints: POST /api/inventory/reserve, GET /api/inventory/reserve, GET /api/inventory/reserve/{id}, PUT /api/inventory/reserve/{id}, DELETE /api/inventory/reserve/{id}\n- reporting-service (port 8084): ReportingController, ReportingService, TransactionReport — endpoints: POST /api/reports/transactions, GET /api/reports/transactions, GET /api/reports/transactions/{id}, PUT /api/reports/transactions/{id}, DELETE /api/reports/transactions/{id}\n- dlq-service (port 8085): DLQController, DLQService, DLQEvent — endpoints: POST /api/dlq/events, GET /api/dlq/events, GET /api/dlq/events/{id}, PUT /api/dlq/events/{id}, DELETE /api/dlq/events/{id}\n\n✅ API Gateway (port 8080):\n- /orders/** → order-service:8081\n- /payments/** → payment-service:8082\n- /inventory/** → inventory-service:8083\n- /reports/** → reporting-service:8084\n- /dlq/** → dlq-service:8085\n\n📊 Project Stats:\n- Total Services: 5\n- Total Endpoints: 25",
  "services": [
    {
      "name": "order-service",
      "port": 8081,
      "description": "Orchestrates order fulfillment workflow, coordinates payment, inventory, and reporting services",
      "packageName": "com.example.orderservice"
    },
    {
      "name": "payment-service",
      "port": 8082,
      "description": "Processes payments through Stripe, handles success/failure scenarios, publishes failed events to DLQ",
      "packageName": "com.example.paymentservice"
    },
    {
      "name": "inventory-service",
      "port": 8083,
      "description": "Manages product inventory, reserves stock for orders, handles out-of-stock scenarios",
      "packageName": "com.example.inventoryservice"
    },
    {
      "name": "reporting-service",
      "port": 8084,
      "description": "Records completed transactions for audit and reporting, logs transaction data to CloudWatch",
      "packageName": "com.example.reportingservice"
    },
    {
      "name": "dlq-service",
      "port": 8085,
      "description": "Dead Letter Queue service for handling failed payment and inventory operations with retry logic",
      "packageName": "com.example.dlqservice"
    }
  ],
  "apiGateway": {
    "enabled": true,
    "port": 8080,
    "routes": [
      {
        "service": "order-service",
        "path": "/orders/**",
        "uri": "http://localhost:8081"
      },
      {
        "service": "payment-service",
        "path": "/payments/**",
        "uri": "http://localhost:8082"
      },
      {
        "service": "inventory-service",
        "path": "/inventory/**",
        "uri": "http://localhost:8083"
      },
      {
        "service": "reporting-service",
        "path": "/reports/**",
        "uri": "http://localhost:8084"
      },
      {
        "service": "dlq-service",
        "path": "/dlq/**",
        "uri": "http://localhost:8085"
      }
    ]
  },
  "projectPath": "C:/Intelog/intelog-be/uploads/order-management-system",
  // "projectPath":"/root/intelog-be/uploads/ecommerce-order-processing",
  "metrics": {
    "totalTime": 0,
    "promptBuildTime": 2,
    "astGenerationTime": 325256,
    "projectGenerationTime": null
  },
  "state": {
    "phase": "completed",
    "errors": 0
  },
  "ast": {
    "projectName": "order-management-system",
    "description": "Distributed microservices-based order management system with payment processing, inventory reservation, transaction reporting, AWS CloudWatch logging, and Dead Letter Queue for failed operations",
    "summary": "📦 Microservices Project Structure:\n\n✅ Services Generated (5 total):\n- order-service (port 8081): OrderController, OrderService, Order — endpoints: POST /api/orders, GET /api/orders, GET /api/orders/{id}, PUT /api/orders/{id}, DELETE /api/orders/{id}\n- payment-service (port 8082): PaymentController, PaymentService, Payment — endpoints: POST /api/payments, GET /api/payments, GET /api/payments/{id}, PUT /api/payments/{id}, DELETE /api/payments/{id}\n- inventory-service (port 8083): InventoryController, InventoryService, InventoryReservation — endpoints: POST /api/inventory/reserve, GET /api/inventory/reserve, GET /api/inventory/reserve/{id}, PUT /api/inventory/reserve/{id}, DELETE /api/inventory/reserve/{id}\n- reporting-service (port 8084): ReportingController, ReportingService, TransactionReport — endpoints: POST /api/reports/transactions, GET /api/reports/transactions, GET /api/reports/transactions/{id}, PUT /api/reports/transactions/{id}, DELETE /api/reports/transactions/{id}\n- dlq-service (port 8085): DLQController, DLQService, DLQEvent — endpoints: POST /api/dlq/events, GET /api/dlq/events, GET /api/dlq/events/{id}, PUT /api/dlq/events/{id}, DELETE /api/dlq/events/{id}\n\n✅ API Gateway (port 8080):\n- /orders/** → order-service:8081\n- /payments/** → payment-service:8082\n- /inventory/** → inventory-service:8083\n- /reports/** → reporting-service:8084\n- /dlq/** → dlq-service:8085\n\n📊 Project Stats:\n- Total Services: 5\n- Total Endpoints: 25",
    "microservices": [
      {
        "name": "order-service",
        "packageName": "com.example.orderservice",
        "port": 8081,
        "description": "Orchestrates order fulfillment workflow, coordinates payment, inventory, and reporting services",
        "dependencies": [
          {
            "groupId": "org.springframework.boot",
            "artifactId": "spring-boot-starter-web"
          },
          {
            "groupId": "org.springframework.boot",
            "artifactId": "spring-boot-starter-data-jpa"
          },
          {
            "groupId": "com.h2database",
            "artifactId": "h2",
            "scope": "runtime"
          },
          {
            "groupId": "org.springframework.boot",
            "artifactId": "spring-boot-starter-actuator"
          },
          {
            "groupId": "org.springframework.boot",
            "artifactId": "spring-boot-starter-test",
            "scope": "test"
          },
          {
            "groupId": "org.springframework.boot",
            "artifactId": "spring-boot-starter-security"
          }
        ],
        "controllers": [
          {
            "name": "OrderController",
            "basePath": "/api/orders",
            "endpoints": [
              {
                "method": "GET",
                "path": "",
                "methodName": "getAllOrders",
                "parameters": [],
                "returnType": "Map<String,Object>",
                "implementation": "return orderService.getAll();"
              },
              {
                "method": "POST",
                "path": "",
                "methodName": "createOrder",
                "parameters": [
                  {
                    "name": "request",
                    "type": "Map<String,Object>",
                    "annotation": "RequestBody"
                  }
                ],
                "returnType": "Map<String,Object>",
                "implementation": "return orderService.create(request);"
              },
              {
                "method": "GET",
                "path": "/{id}",
                "methodName": "getOrderById",
                "parameters": [
                  {
                    "name": "id",
                    "type": "Long",
                    "annotation": "PathVariable"
                  }
                ],
                "returnType": "Map<String,Object>",
                "implementation": "return orderService.getById(id);"
              },
              {
                "method": "PUT",
                "path": "/{id}",
                "methodName": "updateOrder",
                "parameters": [
                  {
                    "name": "id",
                    "type": "Long",
                    "annotation": "PathVariable"
                  },
                  {
                    "name": "request",
                    "type": "Map<String,Object>",
                    "annotation": "RequestBody"
                  }
                ],
                "returnType": "Map<String,Object>",
                "implementation": "return orderService.update(id, request);"
              },
              {
                "method": "DELETE",
                "path": "/{id}",
                "methodName": "deleteOrder",
                "parameters": [
                  {
                    "name": "id",
                    "type": "Long",
                    "annotation": "PathVariable"
                  }
                ],
                "returnType": "Map<String,Object>",
                "implementation": "return orderService.delete(id);"
              }
            ]
          }
        ],
        "services": [
          {
            "name": "OrderService",
            "fullCode": "package com.example.orderservice.service;\n\nimport com.example.orderservice.entity.Order;\nimport com.example.orderservice.repository.OrderRepository;\nimport org.springframework.stereotype.Service;\nimport org.springframework.web.client.RestTemplate;\nimport java.util.HashMap;\nimport java.util.List;\nimport java.util.Map;\nimport java.util.UUID;\nimport java.math.BigDecimal;\nimport java.time.Instant;\n\n@Service\npublic class OrderService {\n    private final OrderRepository orderRepository;\n    private final RestTemplate restTemplate;\n\n    public OrderService(OrderRepository orderRepository, RestTemplate restTemplate) {\n        this.orderRepository = orderRepository;\n        this.restTemplate = restTemplate;\n    }\n\n    public Map<String, Object> getAll() {\n        Map<String, Object> response = new HashMap<>();\n        try {\n            List<Order> orders = orderRepository.findAll();\n            response.put(\"status\", \"SUCCESS\");\n            response.put(\"orders\", orders);\n            response.put(\"count\", orders.size());\n        } catch (Exception e) {\n            response.put(\"status\", \"ERROR\");\n            response.put(\"message\", e.getMessage());\n        }\n        return response;\n    }\n\n    public Map<String, Object> getById(Long id) {\n        Map<String, Object> response = new HashMap<>();\n        try {\n            Order order = orderRepository.findById(id)\n                .orElseThrow(() -> new IllegalArgumentException(\"Order not found: \" + id));\n            response.put(\"status\", \"SUCCESS\");\n            response.put(\"order\", order);\n        } catch (IllegalArgumentException e) {\n            response.put(\"status\", \"ERROR\");\n            response.put(\"message\", e.getMessage());\n        } catch (Exception e) {\n            response.put(\"status\", \"ERROR\");\n            response.put(\"message\", e.getMessage());\n        }\n        return response;\n    }\n\n    public Map<String, Object> create(Map<String, Object> request) {\n        Map<String, Object> response = new HashMap<>();\n        try {\n            Object customerIdObj = request.get(\"customerId\");\n            String customerId = customerIdObj != null ? customerIdObj.toString() : \"\";\n            Object totalAmountObj = request.get(\"totalAmount\");\n            BigDecimal totalAmount = totalAmountObj != null ? new BigDecimal(totalAmountObj.toString()) : BigDecimal.ZERO;\n            Object itemsObj = request.get(\"items\");\n            String items = itemsObj != null ? itemsObj.toString() : \"[]\";\n\n            Order order = new Order();\n            order.setOrderId(UUID.randomUUID().toString());\n            order.setCustomerId(customerId);\n            order.setItems(items);\n            order.setTotalAmount(totalAmount);\n            order.setStatus(\"PROCESSING\");\n\n            Order saved = orderRepository.save(order);\n\n            logToCloudWatch(\"Order received: \" + saved.getOrderId());\n\n            Map<String, Object> paymentRequest = new HashMap<>();\n            paymentRequest.put(\"orderId\", saved.getOrderId());\n            paymentRequest.put(\"amount\", totalAmount);\n            paymentRequest.put(\"currency\", \"USD\");\n            paymentRequest.put(\"customerId\", customerId);\n\n            Map<String, Object> paymentResponse = restTemplate.postForObject(\n                \"http://localhost:8082/api/payments\",\n                paymentRequest,\n                Map.class\n            );\n\n            if (paymentResponse != null && \"SUCCESS\".equals(paymentResponse.get(\"status\"))) {\n                Object paymentIdObj = paymentResponse.get(\"paymentId\");\n                saved.setPaymentId(paymentIdObj != null ? paymentIdObj.toString() : \"\");\n\n                Map<String, Object> inventoryRequest = new HashMap<>();\n                inventoryRequest.put(\"orderId\", saved.getOrderId());\n                inventoryRequest.put(\"items\", items);\n\n                Map<String, Object> inventoryResponse = restTemplate.postForObject(\n                    \"http://localhost:8083/api/inventory/reserve\",\n                    inventoryRequest,\n                    Map.class\n                );\n\n                if (inventoryResponse != null && \"SUCCESS\".equals(inventoryResponse.get(\"status\"))) {\n                    Object reservationIdObj = inventoryResponse.get(\"reservationId\");\n                    saved.setReservationId(reservationIdObj != null ? reservationIdObj.toString() : \"\");\n\n                    Map<String, Object> reportRequest = new HashMap<>();\n                    reportRequest.put(\"orderId\", saved.getOrderId());\n                    reportRequest.put(\"paymentId\", saved.getPaymentId());\n                    reportRequest.put(\"reservationId\", saved.getReservationId());\n                    reportRequest.put(\"totalAmount\", totalAmount);\n\n                    Map<String, Object> reportResponse = restTemplate.postForObject(\n                        \"http://localhost:8084/api/reports/transactions\",\n                        reportRequest,\n                        Map.class\n                    );\n\n                    if (reportResponse != null && \"SUCCESS\".equals(reportResponse.get(\"status\"))) {\n                        Object reportIdObj = reportResponse.get(\"reportId\");\n                        saved.setReportId(reportIdObj != null ? reportIdObj.toString() : \"\");\n                        saved.setStatus(\"CONFIRMED\");\n                        orderRepository.save(saved);\n                        logToCloudWatch(\"Order completed: \" + saved.getOrderId());\n                    }\n                }\n            } else {\n                saved.setStatus(\"FAILED\");\n                orderRepository.save(saved);\n            }\n\n            response.put(\"status\", \"SUCCESS\");\n            response.put(\"order\", saved);\n        } catch (Exception e) {\n            response.put(\"status\", \"ERROR\");\n            response.put(\"message\", e.getMessage());\n        }\n        return response;\n    }\n\n    public Map<String, Object> update(Long id, Map<String, Object> request) {\n        Map<String, Object> response = new HashMap<>();\n        try {\n            Order order = orderRepository.findById(id)\n                .orElseThrow(() -> new IllegalArgumentException(\"Order not found: \" + id));\n\n            Object statusObj = request.get(\"status\");\n            if (statusObj != null) {\n                order.setStatus(statusObj.toString());\n            }\n\n            Object totalAmountObj = request.get(\"totalAmount\");\n            if (totalAmountObj != null) {\n                order.setTotalAmount(new BigDecimal(totalAmountObj.toString()));\n            }\n\n            Order updated = orderRepository.save(order);\n            response.put(\"status\", \"SUCCESS\");\n            response.put(\"order\", updated);\n        } catch (IllegalArgumentException e) {\n            response.put(\"status\", \"ERROR\");\n            response.put(\"message\", e.getMessage());\n        } catch (Exception e) {\n            response.put(\"status\", \"ERROR\");\n            response.put(\"message\", e.getMessage());\n        }\n        return response;\n    }\n\n    public Map<String, Object> delete(Long id) {\n        Map<String, Object> response = new HashMap<>();\n        try {\n            if (!orderRepository.existsById(id)) {\n                throw new IllegalArgumentException(\"Order not found: \" + id);\n            }\n            orderRepository.deleteById(id);\n            response.put(\"status\", \"SUCCESS\");\n            response.put(\"message\", \"Order deleted\");\n        } catch (IllegalArgumentException e) {\n            response.put(\"status\", \"ERROR\");\n            response.put(\"message\", e.getMessage());\n        } catch (Exception e) {\n            response.put(\"status\", \"ERROR\");\n            response.put(\"message\", e.getMessage());\n        }\n        return response;\n    }\n\n    private void logToCloudWatch(String message) {\n        System.out.println(\"[CloudWatch] \" + Instant.now() + \" - \" + message);\n    }\n}",
            "testCode": "package com.example.orderservice.service;\n\nimport com.example.orderservice.entity.Order;\nimport com.example.orderservice.repository.OrderRepository;\nimport org.junit.jupiter.api.BeforeEach;\nimport org.junit.jupiter.api.Test;\nimport org.springframework.beans.factory.annotation.Autowired;\nimport org.springframework.boot.test.context.SpringBootTest;\nimport org.springframework.boot.test.mock.mockito.MockBean;\nimport org.springframework.test.context.ActiveProfiles;\nimport org.springframework.web.client.RestTemplate;\nimport java.math.BigDecimal;\nimport java.time.Instant;\nimport java.util.*;\nimport static org.junit.jupiter.api.Assertions.*;\nimport static org.mockito.Mockito.*;\nimport static org.mockito.ArgumentMatchers.*;\n\n@SpringBootTest\n@ActiveProfiles(\"test\")\nclass OrderServiceTest {\n    @MockBean\n    private OrderRepository orderRepository;\n\n    @MockBean\n    private RestTemplate restTemplate;\n\n    @Autowired\n    private OrderService orderService;\n\n    private Order mockOrder;\n\n    @BeforeEach\n    void setUp() {\n        mockOrder = new Order();\n        mockOrder.setId(1L);\n        mockOrder.setOrderId(\"order-123\");\n        mockOrder.setCustomerId(\"customer-1\");\n        mockOrder.setItems(\"[{\\\"productId\\\":\\\"p1\\\",\\\"qty\\\":2}]\");\n        mockOrder.setTotalAmount(new BigDecimal(\"100.00\"));\n        mockOrder.setStatus(\"PROCESSING\");\n        mockOrder.setCreatedAt(Instant.now());\n\n        lenient().when(orderRepository.findById(anyLong())).thenReturn(Optional.of(mockOrder));\n        lenient().when(orderRepository.existsById(anyLong())).thenReturn(true);\n        lenient().when(orderRepository.findAll()).thenReturn(List.of(mockOrder));\n        lenient().when(orderRepository.save(any())).thenAnswer(inv -> inv.getArgument(0));\n        lenient().doNothing().when(orderRepository).deleteById(anyLong());\n    }\n\n    @Test\n    void testGetAllSuccess() {\n        Map<String, Object> result = orderService.getAll();\n        assertNotNull(result);\n        assertEquals(\"SUCCESS\", result.get(\"status\"));\n        verify(orderRepository, times(1)).findAll();\n    }\n\n    @Test\n    void testGetAllException() {\n        when(orderRepository.findAll()).thenThrow(new RuntimeException(\"DB error\"));\n        Map<String, Object> result = orderService.getAll();\n        assertEquals(\"ERROR\", result.get(\"status\"));\n        assertNotNull(result.get(\"message\"));\n    }\n\n    @Test\n    void testGetByIdSuccess() {\n        Map<String, Object> result = orderService.getById(1L);\n        assertNotNull(result);\n        assertEquals(\"SUCCESS\", result.get(\"status\"));\n        verify(orderRepository, times(1)).findById(1L);\n    }\n\n    @Test\n    void testGetByIdNotFound() {\n        when(orderRepository.findById(99L)).thenReturn(Optional.empty());\n        Map<String, Object> result = orderService.getById(99L);\n        assertEquals(\"ERROR\", result.get(\"status\"));\n        verify(orderRepository, never()).save(any());\n    }\n\n    @Test\n    void testGetByIdException() {\n        when(orderRepository.findById(anyLong())).thenThrow(new RuntimeException(\"DB error\"));\n        Map<String, Object> result = orderService.getById(1L);\n        assertEquals(\"ERROR\", result.get(\"status\"));\n        assertNotNull(result.get(\"message\"));\n    }\n\n    @Test\n    void testCreateSuccess() {\n        Map<String, Object> request = new HashMap<>();\n        request.put(\"customerId\", \"customer-1\");\n        request.put(\"totalAmount\", \"100.00\");\n        request.put(\"items\", \"[{\\\"productId\\\":\\\"p1\\\",\\\"qty\\\":2}]\");\n\n        Map<String, Object> paymentResponse = new HashMap<>();\n        paymentResponse.put(\"status\", \"SUCCESS\");\n        paymentResponse.put(\"paymentId\", \"payment-123\");\n\n        Map<String, Object> inventoryResponse = new HashMap<>();\n        inventoryResponse.put(\"status\", \"SUCCESS\");\n        inventoryResponse.put(\"reservationId\", \"reservation-123\");\n\n        Map<String, Object> reportResponse = new HashMap<>();\n        reportResponse.put(\"status\", \"SUCCESS\");\n        reportResponse.put(\"reportId\", \"report-123\");\n\n        when(restTemplate.postForObject(anyString(), any(), eq(Map.class)))\n            .thenReturn(paymentResponse)\n            .thenReturn(inventoryResponse)\n            .thenReturn(reportResponse);\n\n        Map<String, Object> result = orderService.create(request);\n        assertNotNull(result);\n        assertEquals(\"SUCCESS\", result.get(\"status\"));\n        verify(orderRepository, atLeastOnce()).save(any());\n    }\n\n    @Test\n    void testCreateEmptyMap() {\n        Map<String, Object> result = orderService.create(new HashMap<>());\n        assertNotNull(result);\n    }\n\n    @Test\n    void testCreateException() {\n        when(orderRepository.save(any())).thenThrow(new RuntimeException(\"DB error\"));\n        Map<String, Object> request = new HashMap<>();\n        request.put(\"customerId\", \"customer-1\");\n        Map<String, Object> result = orderService.create(request);\n        assertEquals(\"ERROR\", result.get(\"status\"));\n        assertNotNull(result.get(\"message\"));\n    }\n\n    @Test\n    void testUpdateSuccess() {\n        Map<String, Object> request = new HashMap<>();\n        request.put(\"status\", \"CONFIRMED\");\n        Map<String, Object> result = orderService.update(1L, request);\n        assertNotNull(result);\n        assertEquals(\"SUCCESS\", result.get(\"status\"));\n        verify(orderRepository, times(1)).save(any());\n    }\n\n    @Test\n    void testUpdateEmptyMap() {\n        Map<String, Object> result = orderService.update(1L, new HashMap<>());\n        assertNotNull(result);\n    }\n\n    @Test\n    void testUpdateNotFound() {\n        when(orderRepository.findById(99L)).thenReturn(Optional.empty());\n        Map<String, Object> result = orderService.update(99L, new HashMap<>());\n        assertEquals(\"ERROR\", result.get(\"status\"));\n        verify(orderRepository, never()).save(any());\n    }\n\n    @Test\n    void testUpdateException() {\n        when(orderRepository.findById(anyLong())).thenThrow(new RuntimeException(\"DB error\"));\n        Map<String, Object> result = orderService.update(1L, new HashMap<>());\n        assertEquals(\"ERROR\", result.get(\"status\"));\n        assertNotNull(result.get(\"message\"));\n    }\n\n    @Test\n    void testDeleteSuccess() {\n        Map<String, Object> result = orderService.delete(1L);\n        assertNotNull(result);\n        assertEquals(\"SUCCESS\", result.get(\"status\"));\n        verify(orderRepository, times(1)).deleteById(1L);\n    }\n\n    @Test\n    void testDeleteNotFound() {\n        when(orderRepository.existsById(99L)).thenReturn(false);\n        Map<String, Object> result = orderService.delete(99L);\n        assertEquals(\"ERROR\", result.get(\"status\"));\n    }\n\n    @Test\n    void testDeleteException() {\n        when(orderRepository.existsById(anyLong())).thenThrow(new RuntimeException(\"DB error\"));\n        Map<String, Object> result = orderService.delete(1L);\n        assertEquals(\"ERROR\", result.get(\"status\"));\n        assertNotNull(result.get(\"message\"));\n    }\n}",
            "_meta": {
              "methodsWithOrElseThrow": [
                "getById",
                "update"
              ],
              "methodsWithRetryLoop": [],
              "methodsWithGenericCatch": [
                "getById",
                "update",
                "delete"
              ],
              "maxRetries": 3
            }
          }
        ],
        "entities": [
          {
            "name": "Order",
            "fullCode": "package com.example.orderservice.entity;\n\nimport jakarta.persistence.*;\nimport java.math.BigDecimal;\nimport java.time.Instant;\n\n@Entity\n@Table(name = \"orders\")\npublic class Order {\n    @Id\n    @GeneratedValue(strategy = GenerationType.IDENTITY)\n    private Long id;\n\n    @Column(nullable = false, unique = true)\n    private String orderId;\n\n    @Column(nullable = false)\n    private String customerId;\n\n    @Lob\n    @Column(nullable = false)\n    private String items;\n\n    @Column(nullable = false, precision = 19, scale = 2)\n    private BigDecimal totalAmount;\n\n    @Column(nullable = false)\n    private String status;\n\n    @Column(name = \"created_at\", nullable = false, updatable = false)\n    private Instant createdAt;\n\n    @Column\n    private String paymentId;\n\n    @Column\n    private String reservationId;\n\n    @Column\n    private String reportId;\n\n    public Order() {}\n\n    public Order(String orderId, String customerId, String items, BigDecimal totalAmount, String status) {\n        this.orderId = orderId;\n        this.customerId = customerId;\n        this.items = items;\n        this.totalAmount = totalAmount;\n        this.status = status;\n    }\n\n    @PrePersist\n    protected void onCreate() {\n        if (this.createdAt == null) {\n            this.createdAt = Instant.now();\n        }\n    }\n\n    public Long getId() {\n        return id;\n    }\n\n    public void setId(Long id) {\n        this.id = id;\n    }\n\n    public String getOrderId() {\n        return orderId;\n    }\n\n    public void setOrderId(String orderId) {\n        this.orderId = orderId;\n    }\n\n    public String getCustomerId() {\n        return customerId;\n    }\n\n    public void setCustomerId(String customerId) {\n        this.customerId = customerId;\n    }\n\n    public String getItems() {\n        return items;\n    }\n\n    public void setItems(String items) {\n        this.items = items;\n    }\n\n    public BigDecimal getTotalAmount() {\n        return totalAmount;\n    }\n\n    public void setTotalAmount(BigDecimal totalAmount) {\n        this.totalAmount = totalAmount;\n    }\n\n    public String getStatus() {\n        return status;\n    }\n\n    public void setStatus(String status) {\n        this.status = status;\n    }\n\n    public Instant getCreatedAt() {\n        return createdAt;\n    }\n\n    public void setCreatedAt(Instant createdAt) {\n        this.createdAt = createdAt;\n    }\n\n    public String getPaymentId() {\n        return paymentId;\n    }\n\n    public void setPaymentId(String paymentId) {\n        this.paymentId = paymentId;\n    }\n\n    public String getReservationId() {\n        return reservationId;\n    }\n\n    public void setReservationId(String reservationId) {\n        this.reservationId = reservationId;\n    }\n\n    public String getReportId() {\n        return reportId;\n    }\n\n    public void setReportId(String reportId) {\n        this.reportId = reportId;\n    }\n}"
          }
        ],
        "repositories": [
          {
            "name": "OrderRepository",
            "fullCode": "package com.example.orderservice.repository;\n\nimport com.example.orderservice.entity.Order;\nimport org.springframework.data.jpa.repository.JpaRepository;\n\npublic interface OrderRepository extends JpaRepository<Order, Long> {\n}"
          }
        ],
        "configs": [
          {
            "name": "AppConfig",
            "package": "config",
            "fullCode": "package com.example.orderservice.config;\n\nimport org.springframework.context.annotation.Bean;\nimport org.springframework.context.annotation.Configuration;\nimport org.springframework.web.client.RestTemplate;\n\n@Configuration\npublic class AppConfig {\n    @Bean\n    public RestTemplate restTemplate() {\n        return new RestTemplate();\n    }\n}"
          },
          {
            "name": "BasicSecurityConfig",
            "package": "config",
            "fullCode": "package com.example.orderservice.config;\n\nimport org.springframework.beans.factory.annotation.Value;\nimport org.springframework.context.annotation.Bean;\nimport org.springframework.context.annotation.Configuration;\nimport org.springframework.security.config.annotation.web.builders.HttpSecurity;\nimport org.springframework.security.config.annotation.web.configuration.EnableWebSecurity;\nimport org.springframework.security.core.userdetails.User;\nimport org.springframework.security.core.userdetails.UserDetails;\nimport org.springframework.security.core.userdetails.UserDetailsService;\nimport org.springframework.security.crypto.bcrypt.BCryptPasswordEncoder;\nimport org.springframework.security.crypto.password.PasswordEncoder;\nimport org.springframework.security.provisioning.InMemoryUserDetailsManager;\nimport org.springframework.security.web.SecurityFilterChain;\n\n@Configuration\n@EnableWebSecurity\npublic class BasicSecurityConfig {\n    @Value(\"${app.security.admin.username}\")\n    private String adminUsername;\n\n    @Value(\"${app.security.admin.password}\")\n    private String adminPassword;\n\n    @Value(\"${app.security.user.username}\")\n    private String userUsername;\n\n    @Value(\"${app.security.user.password}\")\n    private String userPassword;\n\n    @Bean\n    public SecurityFilterChain securityFilterChain(HttpSecurity http) throws Exception {\n        http.csrf(csrf -> csrf.disable())\n            .headers(h -> h.frameOptions(f -> f.disable()))\n            .authorizeHttpRequests(auth -> auth\n                .requestMatchers(\"/h2-console/**\", \"/swagger-ui/**\", \"/v3/**\", \"/actuator/**\").permitAll()\n                .anyRequest().authenticated())\n            .httpBasic(b -> {});\n        return http.build();\n    }\n\n    @Bean\n    public UserDetailsService userDetailsService() {\n        UserDetails admin = User.builder()\n            .username(adminUsername)\n            .password(passwordEncoder().encode(adminPassword))\n            .roles(\"ADMIN\")\n            .build();\n        UserDetails user = User.builder()\n            .username(userUsername)\n            .password(passwordEncoder().encode(userPassword))\n            .roles(\"USER\")\n            .build();\n        return new InMemoryUserDetailsManager(admin, user);\n    }\n\n    @Bean\n    public PasswordEncoder passwordEncoder() {\n        return new BCryptPasswordEncoder();\n    }\n}"
          }
        ],
        "applicationProperties": "spring.application.name=order-service\nserver.port=8081\nspring.datasource.url=jdbc:h2:mem:orderservicedb\nspring.datasource.driverClassName=org.h2.Driver\nspring.datasource.username=sa\nspring.datasource.password=\nspring.jpa.database-platform=org.hibernate.dialect.H2Dialect\nspring.jpa.hibernate.ddl-auto=update\nspring.h2.console.enabled=true\napp.security.admin.username=admin\napp.security.admin.password=admin\napp.security.user.username=user\napp.security.user.password=user",
        "externalClients": []
      },
      {
        "name": "payment-service",
        "packageName": "com.example.paymentservice",
        "port": 8082,
        "description": "Processes payments through Stripe, handles success/failure scenarios, publishes failed events to DLQ",
        "dependencies": [
          {
            "groupId": "org.springframework.boot",
            "artifactId": "spring-boot-starter-web"
          },
          {
            "groupId": "org.springframework.boot",
            "artifactId": "spring-boot-starter-data-jpa"
          },
          {
            "groupId": "com.h2database",
            "artifactId": "h2",
            "scope": "runtime"
          },
          {
            "groupId": "org.springframework.boot",
            "artifactId": "spring-boot-starter-actuator"
          },
          {
            "groupId": "org.springframework.boot",
            "artifactId": "spring-boot-starter-test",
            "scope": "test"
          },
          {
            "groupId": "org.springframework.boot",
            "artifactId": "spring-boot-starter-security"
          }
        ],
        "controllers": [
          {
            "name": "PaymentController",
            "basePath": "/api/payments",
            "endpoints": [
              {
                "method": "GET",
                "path": "",
                "methodName": "getAllPayments",
                "parameters": [],
                "returnType": "Map<String,Object>",
                "implementation": "return paymentService.getAll();"
              },
              {
                "method": "POST",
                "path": "",
                "methodName": "createPayment",
                "parameters": [
                  {
                    "name": "request",
                    "type": "Map<String,Object>",
                    "annotation": "RequestBody"
                  }
                ],
                "returnType": "Map<String,Object>",
                "implementation": "return paymentService.create(request);"
              },
              {
                "method": "GET",
                "path": "/{id}",
                "methodName": "getPaymentById",
                "parameters": [
                  {
                    "name": "id",
                    "type": "Long",
                    "annotation": "PathVariable"
                  }
                ],
                "returnType": "Map<String,Object>",
                "implementation": "return paymentService.getById(id);"
              },
              {
                "method": "PUT",
                "path": "/{id}",
                "methodName": "updatePayment",
                "parameters": [
                  {
                    "name": "id",
                    "type": "Long",
                    "annotation": "PathVariable"
                  },
                  {
                    "name": "request",
                    "type": "Map<String,Object>",
                    "annotation": "RequestBody"
                  }
                ],
                "returnType": "Map<String,Object>",
                "implementation": "return paymentService.update(id, request);"
              },
              {
                "method": "DELETE",
                "path": "/{id}",
                "methodName": "deletePayment",
                "parameters": [
                  {
                    "name": "id",
                    "type": "Long",
                    "annotation": "PathVariable"
                  }
                ],
                "returnType": "Map<String,Object>",
                "implementation": "return paymentService.delete(id);"
              }
            ]
          }
        ],
        "services": [
          {
            "name": "PaymentService",
            "fullCode": "package com.example.paymentservice.service;\n\nimport com.example.paymentservice.entity.Payment;\nimport com.example.paymentservice.repository.PaymentRepository;\nimport org.springframework.stereotype.Service;\nimport org.springframework.web.client.RestTemplate;\nimport java.util.HashMap;\nimport java.util.List;\nimport java.util.Map;\nimport java.util.UUID;\nimport java.math.BigDecimal;\nimport java.time.Instant;\n\n@Service\npublic class PaymentService {\n    private final PaymentRepository paymentRepository;\n    private final RestTemplate restTemplate;\n\n    public PaymentService(PaymentRepository paymentRepository, RestTemplate restTemplate) {\n        this.paymentRepository = paymentRepository;\n        this.restTemplate = restTemplate;\n    }\n\n    public Map<String, Object> getAll() {\n        Map<String, Object> response = new HashMap<>();\n        try {\n            List<Payment> payments = paymentRepository.findAll();\n            response.put(\"status\", \"SUCCESS\");\n            response.put(\"payments\", payments);\n            response.put(\"count\", payments.size());\n        } catch (Exception e) {\n            response.put(\"status\", \"ERROR\");\n            response.put(\"message\", e.getMessage());\n        }\n        return response;\n    }\n\n    public Map<String, Object> getById(Long id) {\n        Map<String, Object> response = new HashMap<>();\n        try {\n            Payment payment = paymentRepository.findById(id)\n                .orElseThrow(() -> new IllegalArgumentException(\"Payment not found: \" + id));\n            response.put(\"status\", \"SUCCESS\");\n            response.put(\"payment\", payment);\n        } catch (IllegalArgumentException e) {\n            response.put(\"status\", \"ERROR\");\n            response.put(\"message\", e.getMessage());\n        } catch (Exception e) {\n            response.put(\"status\", \"ERROR\");\n            response.put(\"message\", e.getMessage());\n        }\n        return response;\n    }\n\n    public Map<String, Object> create(Map<String, Object> request) {\n        Map<String, Object> response = new HashMap<>();\n        try {\n            Object orderIdObj = request.get(\"orderId\");\n            String orderId = orderIdObj != null ? orderIdObj.toString() : \"\";\n            Object amountObj = request.get(\"amount\");\n            BigDecimal amount = amountObj != null ? new BigDecimal(amountObj.toString()) : BigDecimal.ZERO;\n            Object currencyObj = request.get(\"currency\");\n            String currency = currencyObj != null ? currencyObj.toString() : \"USD\";\n            Object customerIdObj = request.get(\"customerId\");\n            String customerId = customerIdObj != null ? customerIdObj.toString() : \"\";\n\n            Map<String, Object> stripeRequest = new HashMap<>();\n            stripeRequest.put(\"amount\", amount.multiply(new BigDecimal(\"100\")).longValue());\n            stripeRequest.put(\"currency\", currency.toLowerCase());\n            stripeRequest.put(\"paymentMethod\", \"card\");\n\n            try {\n                Map<String, Object> stripeResponse = restTemplate.postForObject(\n                    \"https://api.stripe.com/v1/payment_intents\",\n                    stripeRequest,\n                    Map.class\n                );\n\n                Payment payment = new Payment();\n                payment.setPaymentId(UUID.randomUUID().toString());\n                payment.setOrderId(orderId);\n                payment.setAmount(amount);\n                payment.setCurrency(currency);\n                payment.setCustomerId(customerId);\n\n                if (stripeResponse != null && \"succeeded\".equals(stripeResponse.get(\"status\"))) {\n                    Object paymentIntentIdObj = stripeResponse.get(\"paymentIntentId\");\n                    payment.setPaymentIntentId(paymentIntentIdObj != null ? paymentIntentIdObj.toString() : \"\");\n                    payment.setStatus(\"PAID\");\n                    logToCloudWatch(\"Payment successful: \" + payment.getPaymentId());\n                } else {\n                    payment.setStatus(\"FAILED\");\n                    payment.setError(\"Payment declined\");\n                    publishToDLQ(orderId, \"card_declined\");\n                    logToCloudWatch(\"Payment failed: \" + orderId);\n                }\n\n                Payment saved = paymentRepository.save(payment);\n                response.put(\"status\", \"SUCCESS\");\n                response.put(\"payment\", saved);\n                response.put(\"paymentId\", saved.getPaymentId());\n            } catch (Exception stripeEx) {\n                Payment payment = new Payment();\n                payment.setPaymentId(UUID.randomUUID().toString());\n                payment.setOrderId(orderId);\n                payment.setAmount(amount);\n                payment.setCurrency(currency);\n                payment.setCustomerId(customerId);\n                payment.setStatus(\"FAILED\");\n                payment.setError(stripeEx.getMessage());\n                Payment saved = paymentRepository.save(payment);\n                publishToDLQ(orderId, stripeEx.getMessage());\n                logToCloudWatch(\"Payment failed: \" + orderId);\n                response.put(\"status\", \"SUCCESS\");\n                response.put(\"payment\", saved);\n                response.put(\"paymentId\", saved.getPaymentId());\n            }\n        } catch (Exception e) {\n            response.put(\"status\", \"ERROR\");\n            response.put(\"message\", e.getMessage());\n        }\n        return response;\n    }\n\n    public Map<String, Object> update(Long id, Map<String, Object> request) {\n        Map<String, Object> response = new HashMap<>();\n        try {\n            Payment payment = paymentRepository.findById(id)\n                .orElseThrow(() -> new IllegalArgumentException(\"Payment not found: \" + id));\n\n            Object statusObj = request.get(\"status\");\n            if (statusObj != null) {\n                payment.setStatus(statusObj.toString());\n            }\n\n            Payment updated = paymentRepository.save(payment);\n            response.put(\"status\", \"SUCCESS\");\n            response.put(\"payment\", updated);\n        } catch (IllegalArgumentException e) {\n            response.put(\"status\", \"ERROR\");\n            response.put(\"message\", e.getMessage());\n        } catch (Exception e) {\n            response.put(\"status\", \"ERROR\");\n            response.put(\"message\", e.getMessage());\n        }\n        return response;\n    }\n\n    public Map<String, Object> delete(Long id) {\n        Map<String, Object> response = new HashMap<>();\n        try {\n            if (!paymentRepository.existsById(id)) {\n                throw new IllegalArgumentException(\"Payment not found: \" + id);\n            }\n            paymentRepository.deleteById(id);\n            response.put(\"status\", \"SUCCESS\");\n            response.put(\"message\", \"Payment deleted\");\n        } catch (IllegalArgumentException e) {\n            response.put(\"status\", \"ERROR\");\n            response.put(\"message\", e.getMessage());\n        } catch (Exception e) {\n            response.put(\"status\", \"ERROR\");\n            response.put(\"message\", e.getMessage());\n        }\n        return response;\n    }\n\n    private void publishToDLQ(String orderId, String reason) {\n        try {\n            Map<String, Object> dlqEvent = new HashMap<>();\n            dlqEvent.put(\"eventType\", \"PAYMENT_FAILED\");\n            dlqEvent.put(\"orderId\", orderId);\n            dlqEvent.put(\"reason\", reason);\n            dlqEvent.put(\"retryCount\", 0);\n            restTemplate.postForObject(\"http://localhost:8085/api/dlq/events\", dlqEvent, Map.class);\n        } catch (Exception e) {\n            System.err.println(\"Failed to publish to DLQ: \" + e.getMessage());\n        }\n    }\n\n    private void logToCloudWatch(String message) {\n        System.out.println(\"[CloudWatch] \" + Instant.now() + \" - \" + message);\n    }\n}",
            "testCode": "package com.example.paymentservice.service;\n\nimport com.example.paymentservice.entity.Payment;\nimport com.example.paymentservice.repository.PaymentRepository;\nimport org.junit.jupiter.api.BeforeEach;\nimport org.junit.jupiter.api.Test;\nimport org.springframework.beans.factory.annotation.Autowired;\nimport org.springframework.boot.test.context.SpringBootTest;\nimport org.springframework.boot.test.mock.mockito.MockBean;\nimport org.springframework.test.context.ActiveProfiles;\nimport org.springframework.web.client.RestTemplate;\nimport java.math.BigDecimal;\nimport java.time.Instant;\nimport java.util.*;\nimport static org.junit.jupiter.api.Assertions.*;\nimport static org.mockito.Mockito.*;\nimport static org.mockito.ArgumentMatchers.*;\n\n@SpringBootTest\n@ActiveProfiles(\"test\")\nclass PaymentServiceTest {\n    @MockBean\n    private PaymentRepository paymentRepository;\n\n    @MockBean\n    private RestTemplate restTemplate;\n\n    @Autowired\n    private PaymentService paymentService;\n\n    private Payment mockPayment;\n\n    @BeforeEach\n    void setUp() {\n        mockPayment = new Payment();\n        mockPayment.setId(1L);\n        mockPayment.setPaymentId(\"payment-123\");\n        mockPayment.setOrderId(\"order-123\");\n        mockPayment.setAmount(new BigDecimal(\"100.00\"));\n        mockPayment.setCurrency(\"USD\");\n        mockPayment.setCustomerId(\"customer-1\");\n        mockPayment.setStatus(\"PAID\");\n        mockPayment.setCreatedAt(Instant.now());\n\n        lenient().when(paymentRepository.findById(anyLong())).thenReturn(Optional.of(mockPayment));\n        lenient().when(paymentRepository.existsById(anyLong())).thenReturn(true);\n        lenient().when(paymentRepository.findAll()).thenReturn(List.of(mockPayment));\n        lenient().when(paymentRepository.save(any())).thenAnswer(inv -> inv.getArgument(0));\n        lenient().doNothing().when(paymentRepository).deleteById(anyLong());\n    }\n\n    @Test\n    void testGetAllSuccess() {\n        Map<String, Object> result = paymentService.getAll();\n        assertNotNull(result);\n        assertEquals(\"SUCCESS\", result.get(\"status\"));\n        verify(paymentRepository, times(1)).findAll();\n    }\n\n    @Test\n    void testGetAllException() {\n        when(paymentRepository.findAll()).thenThrow(new RuntimeException(\"DB error\"));\n        Map<String, Object> result = paymentService.getAll();\n        assertEquals(\"ERROR\", result.get(\"status\"));\n        assertNotNull(result.get(\"message\"));\n    }\n\n    @Test\n    void testGetByIdSuccess() {\n        Map<String, Object> result = paymentService.getById(1L);\n        assertNotNull(result);\n        assertEquals(\"SUCCESS\", result.get(\"status\"));\n        verify(paymentRepository, times(1)).findById(1L);\n    }\n\n    @Test\n    void testGetByIdNotFound() {\n        when(paymentRepository.findById(99L)).thenReturn(Optional.empty());\n        Map<String, Object> result = paymentService.getById(99L);\n        assertEquals(\"ERROR\", result.get(\"status\"));\n        verify(paymentRepository, never()).save(any());\n    }\n\n    @Test\n    void testGetByIdException() {\n        when(paymentRepository.findById(anyLong())).thenThrow(new RuntimeException(\"DB error\"));\n        Map<String, Object> result = paymentService.getById(1L);\n        assertEquals(\"ERROR\", result.get(\"status\"));\n        assertNotNull(result.get(\"message\"));\n    }\n\n    @Test\n    void testCreateSuccess() {\n        Map<String, Object> request = new HashMap<>();\n        request.put(\"orderId\", \"order-123\");\n        request.put(\"amount\", \"100.00\");\n        request.put(\"currency\", \"USD\");\n        request.put(\"customerId\", \"customer-1\");\n\n        Map<String, Object> stripeResponse = new HashMap<>();\n        stripeResponse.put(\"status\", \"succeeded\");\n        stripeResponse.put(\"paymentIntentId\", \"pi_123\");\n\n        when(restTemplate.postForObject(anyString(), any(), eq(Map.class)))\n            .thenReturn(stripeResponse);\n\n        Map<String, Object> result = paymentService.create(request);\n        assertNotNull(result);\n        assertEquals(\"SUCCESS\", result.get(\"status\"));\n        verify(paymentRepository, atLeastOnce()).save(any());\n    }\n\n    @Test\n    void testCreateEmptyMap() {\n        Map<String, Object> result = paymentService.create(new HashMap<>());\n        assertNotNull(result);\n    }\n\n    @Test\n    void testCreateException() {\n        when(paymentRepository.save(any())).thenThrow(new RuntimeException(\"DB error\"));\n        Map<String, Object> request = new HashMap<>();\n        request.put(\"orderId\", \"order-123\");\n        Map<String, Object> result = paymentService.create(request);\n        assertEquals(\"ERROR\", result.get(\"status\"));\n        assertNotNull(result.get(\"message\"));\n    }\n\n    @Test\n    void testUpdateSuccess() {\n        Map<String, Object> request = new HashMap<>();\n        request.put(\"status\", \"FAILED\");\n        Map<String, Object> result = paymentService.update(1L, request);\n        assertNotNull(result);\n        assertEquals(\"SUCCESS\", result.get(\"status\"));\n        verify(paymentRepository, times(1)).save(any());\n    }\n\n    @Test\n    void testUpdateEmptyMap() {\n        Map<String, Object> result = paymentService.update(1L, new HashMap<>());\n        assertNotNull(result);\n    }\n\n    @Test\n    void testUpdateNotFound() {\n        when(paymentRepository.findById(99L)).thenReturn(Optional.empty());\n        Map<String, Object> result = paymentService.update(99L, new HashMap<>());\n        assertEquals(\"ERROR\", result.get(\"status\"));\n        verify(paymentRepository, never()).save(any());\n    }\n\n    @Test\n    void testUpdateException() {\n        when(paymentRepository.findById(anyLong())).thenThrow(new RuntimeException(\"DB error\"));\n        Map<String, Object> result = paymentService.update(1L, new HashMap<>());\n        assertEquals(\"ERROR\", result.get(\"status\"));\n        assertNotNull(result.get(\"message\"));\n    }\n\n    @Test\n    void testDeleteSuccess() {\n        Map<String, Object> result = paymentService.delete(1L);\n        assertNotNull(result);\n        assertEquals(\"SUCCESS\", result.get(\"status\"));\n        verify(paymentRepository, times(1)).deleteById(1L);\n    }\n\n    @Test\n    void testDeleteNotFound() {\n        when(paymentRepository.existsById(99L)).thenReturn(false);\n        Map<String, Object> result = paymentService.delete(99L);\n        assertEquals(\"ERROR\", result.get(\"status\"));\n    }\n\n    @Test\n    void testDeleteException() {\n        when(paymentRepository.existsById(anyLong())).thenThrow(new RuntimeException(\"DB error\"));\n        Map<String, Object> result = paymentService.delete(1L);\n        assertEquals(\"ERROR\", result.get(\"status\"));\n        assertNotNull(result.get(\"message\"));\n    }\n}",
            "_meta": {
              "methodsWithOrElseThrow": [
                "getById",
                "update"
              ],
              "methodsWithRetryLoop": [],
              "methodsWithGenericCatch": [
                "getById",
                "update",
                "delete"
              ],
              "maxRetries": 3
            }
          }
        ],
        "entities": [
          {
            "name": "Payment",
            "fullCode": "package com.example.paymentservice.entity;\n\nimport jakarta.persistence.*;\nimport java.math.BigDecimal;\nimport java.time.Instant;\n\n@Entity\n@Table(name = \"payments\")\npublic class Payment {\n    @Id\n    @GeneratedValue(strategy = GenerationType.IDENTITY)\n    private Long id;\n\n    @Column(nullable = false, unique = true)\n    private String paymentId;\n\n    @Column(nullable = false)\n    private String orderId;\n\n    @Column(nullable = false, precision = 19, scale = 2)\n    private BigDecimal amount;\n\n    @Column(nullable = false)\n    private String currency;\n\n    @Column(nullable = false)\n    private String customerId;\n\n    @Column\n    private String paymentIntentId;\n\n    @Column(nullable = false)\n    private String status;\n\n    @Column\n    private String error;\n\n    @Column(name = \"created_at\", nullable = false, updatable = false)\n    private Instant createdAt;\n\n    public Payment() {}\n\n    public Payment(String paymentId, String orderId, BigDecimal amount, String currency, String customerId, String status) {\n        this.paymentId = paymentId;\n        this.orderId = orderId;\n        this.amount = amount;\n        this.currency = currency;\n        this.customerId = customerId;\n        this.status = status;\n    }\n\n    @PrePersist\n    protected void onCreate() {\n        if (this.createdAt == null) {\n            this.createdAt = Instant.now();\n        }\n    }\n\n    public Long getId() {\n        return id;\n    }\n\n    public void setId(Long id) {\n        this.id = id;\n    }\n\n    public String getPaymentId() {\n        return paymentId;\n    }\n\n    public void setPaymentId(String paymentId) {\n        this.paymentId = paymentId;\n    }\n\n    public String getOrderId() {\n        return orderId;\n    }\n\n    public void setOrderId(String orderId) {\n        this.orderId = orderId;\n    }\n\n    public BigDecimal getAmount() {\n        return amount;\n    }\n\n    public void setAmount(BigDecimal amount) {\n        this.amount = amount;\n    }\n\n    public String getCurrency() {\n        return currency;\n    }\n\n    public void setCurrency(String currency) {\n        this.currency = currency;\n    }\n\n    public String getCustomerId() {\n        return customerId;\n    }\n\n    public void setCustomerId(String customerId) {\n        this.customerId = customerId;\n    }\n\n    public String getPaymentIntentId() {\n        return paymentIntentId;\n    }\n\n    public void setPaymentIntentId(String paymentIntentId) {\n        this.paymentIntentId = paymentIntentId;\n    }\n\n    public String getStatus() {\n        return status;\n    }\n\n    public void setStatus(String status) {\n        this.status = status;\n    }\n\n    public String getError() {\n        return error;\n    }\n\n    public void setError(String error) {\n        this.error = error;\n    }\n\n    public Instant getCreatedAt() {\n        return createdAt;\n    }\n\n    public void setCreatedAt(Instant createdAt) {\n        this.createdAt = createdAt;\n    }\n}"
          }
        ],
        "repositories": [
          {
            "name": "PaymentRepository",
            "fullCode": "package com.example.paymentservice.repository;\n\nimport com.example.paymentservice.entity.Payment;\nimport org.springframework.data.jpa.repository.JpaRepository;\n\npublic interface PaymentRepository extends JpaRepository<Payment, Long> {\n}"
          }
        ],
        "configs": [
          {
            "name": "AppConfig",
            "package": "config",
            "fullCode": "package com.example.paymentservice.config;\n\nimport org.springframework.context.annotation.Bean;\nimport org.springframework.context.annotation.Configuration;\nimport org.springframework.web.client.RestTemplate;\n\n@Configuration\npublic class AppConfig {\n    @Bean\n    public RestTemplate restTemplate() {\n        return new RestTemplate();\n    }\n}"
          },
          {
            "name": "BasicSecurityConfig",
            "package": "config",
            "fullCode": "package com.example.paymentservice.config;\n\nimport org.springframework.beans.factory.annotation.Value;\nimport org.springframework.context.annotation.Bean;\nimport org.springframework.context.annotation.Configuration;\nimport org.springframework.security.config.annotation.web.builders.HttpSecurity;\nimport org.springframework.security.config.annotation.web.configuration.EnableWebSecurity;\nimport org.springframework.security.core.userdetails.User;\nimport org.springframework.security.core.userdetails.UserDetails;\nimport org.springframework.security.core.userdetails.UserDetailsService;\nimport org.springframework.security.crypto.bcrypt.BCryptPasswordEncoder;\nimport org.springframework.security.crypto.password.PasswordEncoder;\nimport org.springframework.security.provisioning.InMemoryUserDetailsManager;\nimport org.springframework.security.web.SecurityFilterChain;\n\n@Configuration\n@EnableWebSecurity\npublic class BasicSecurityConfig {\n    @Value(\"${app.security.admin.username}\")\n    private String adminUsername;\n\n    @Value(\"${app.security.admin.password}\")\n    private String adminPassword;\n\n    @Value(\"${app.security.user.username}\")\n    private String userUsername;\n\n    @Value(\"${app.security.user.password}\")\n    private String userPassword;\n\n    @Bean\n    public SecurityFilterChain securityFilterChain(HttpSecurity http) throws Exception {\n        http.csrf(csrf -> csrf.disable())\n            .headers(h -> h.frameOptions(f -> f.disable()))\n            .authorizeHttpRequests(auth -> auth\n                .requestMatchers(\"/h2-console/**\", \"/swagger-ui/**\", \"/v3/**\", \"/actuator/**\").permitAll()\n                .anyRequest().authenticated())\n            .httpBasic(b -> {});\n        return http.build();\n    }\n\n    @Bean\n    public UserDetailsService userDetailsService() {\n        UserDetails admin = User.builder()\n            .username(adminUsername)\n            .password(passwordEncoder().encode(adminPassword))\n            .roles(\"ADMIN\")\n            .build();\n        UserDetails user = User.builder()\n            .username(userUsername)\n            .password(passwordEncoder().encode(userPassword))\n            .roles(\"USER\")\n            .build();\n        return new InMemoryUserDetailsManager(admin, user);\n    }\n\n    @Bean\n    public PasswordEncoder passwordEncoder() {\n        return new BCryptPasswordEncoder();\n    }\n}"
          }
        ],
        "applicationProperties": "spring.application.name=payment-service\nserver.port=8082\nspring.datasource.url=jdbc:h2:mem:paymentservicedb\nspring.datasource.driverClassName=org.h2.Driver\nspring.datasource.username=sa\nspring.datasource.password=\nspring.jpa.database-platform=org.hibernate.dialect.H2Dialect\nspring.jpa.hibernate.ddl-auto=update\nspring.h2.console.enabled=true\napp.security.admin.username=admin\napp.security.admin.password=admin\napp.security.user.username=user\napp.security.user.password=user",
        "externalClients": []
      },
      {
        "name": "inventory-service",
        "packageName": "com.example.inventoryservice",
        "port": 8083,
        "description": "Manages product inventory, reserves stock for orders, handles out-of-stock scenarios",
        "dependencies": [
          {
            "groupId": "org.springframework.boot",
            "artifactId": "spring-boot-starter-web"
          },
          {
            "groupId": "org.springframework.boot",
            "artifactId": "spring-boot-starter-data-jpa"
          },
          {
            "groupId": "com.h2database",
            "artifactId": "h2",
            "scope": "runtime"
          },
          {
            "groupId": "org.springframework.boot",
            "artifactId": "spring-boot-starter-actuator"
          },
          {
            "groupId": "org.springframework.boot",
            "artifactId": "spring-boot-starter-test",
            "scope": "test"
          },
          {
            "groupId": "org.springframework.boot",
            "artifactId": "spring-boot-starter-security"
          }
        ],
        "controllers": [
          {
            "name": "InventoryController",
            "basePath": "/api/inventory/reserve",
            "endpoints": [
              {
                "method": "GET",
                "path": "",
                "methodName": "getAllReservations",
                "parameters": [],
                "returnType": "Map<String,Object>",
                "implementation": "return inventoryService.getAll();"
              },
              {
                "method": "POST",
                "path": "",
                "methodName": "createReservation",
                "parameters": [
                  {
                    "name": "request",
                    "type": "Map<String,Object>",
                    "annotation": "RequestBody"
                  }
                ],
                "returnType": "Map<String,Object>",
                "implementation": "return inventoryService.create(request);"
              },
              {
                "method": "GET",
                "path": "/{id}",
                "methodName": "getReservationById",
                "parameters": [
                  {
                    "name": "id",
                    "type": "Long",
                    "annotation": "PathVariable"
                  }
                ],
                "returnType": "Map<String,Object>",
                "implementation": "return inventoryService.getById(id);"
              },
              {
                "method": "PUT",
                "path": "/{id}",
                "methodName": "updateReservation",
                "parameters": [
                  {
                    "name": "id",
                    "type": "Long",
                    "annotation": "PathVariable"
                  },
                  {
                    "name": "request",
                    "type": "Map<String,Object>",
                    "annotation": "RequestBody"
                  }
                ],
                "returnType": "Map<String,Object>",
                "implementation": "return inventoryService.update(id, request);"
              },
              {
                "method": "DELETE",
                "path": "/{id}",
                "methodName": "deleteReservation",
                "parameters": [
                  {
                    "name": "id",
                    "type": "Long",
                    "annotation": "PathVariable"
                  }
                ],
                "returnType": "Map<String,Object>",
                "implementation": "return inventoryService.delete(id);"
              }
            ]
          }
        ],
        "services": [
          {
            "name": "InventoryService",
            "fullCode": "package com.example.inventoryservice.service;\n\nimport com.example.inventoryservice.entity.InventoryReservation;\nimport com.example.inventoryservice.repository.InventoryReservationRepository;\nimport org.springframework.stereotype.Service;\nimport org.springframework.web.client.RestTemplate;\nimport java.util.HashMap;\nimport java.util.List;\nimport java.util.Map;\nimport java.util.UUID;\nimport java.time.Instant;\n\n@Service\npublic class InventoryService {\n    private final InventoryReservationRepository inventoryReservationRepository;\n    private final RestTemplate restTemplate;\n\n    public InventoryService(InventoryReservationRepository inventoryReservationRepository, RestTemplate restTemplate) {\n        this.inventoryReservationRepository = inventoryReservationRepository;\n        this.restTemplate = restTemplate;\n    }\n\n    public Map<String, Object> getAll() {\n        Map<String, Object> response = new HashMap<>();\n        try {\n            List<InventoryReservation> reservations = inventoryReservationRepository.findAll();\n            response.put(\"status\", \"SUCCESS\");\n            response.put(\"reservations\", reservations);\n            response.put(\"count\", reservations.size());\n        } catch (Exception e) {\n            response.put(\"status\", \"ERROR\");\n            response.put(\"message\", e.getMessage());\n        }\n        return response;\n    }\n\n    public Map<String, Object> getById(Long id) {\n        Map<String, Object> response = new HashMap<>();\n        try {\n            InventoryReservation reservation = inventoryReservationRepository.findById(id)\n                .orElseThrow(() -> new IllegalArgumentException(\"Reservation not found: \" + id));\n            response.put(\"status\", \"SUCCESS\");\n            response.put(\"reservation\", reservation);\n        } catch (IllegalArgumentException e) {\n            response.put(\"status\", \"ERROR\");\n            response.put(\"message\", e.getMessage());\n        } catch (Exception e) {\n            response.put(\"status\", \"ERROR\");\n            response.put(\"message\", e.getMessage());\n        }\n        return response;\n    }\n\n    public Map<String, Object> create(Map<String, Object> request) {\n        Map<String, Object> response = new HashMap<>();\n        try {\n            Object orderIdObj = request.get(\"orderId\");\n            String orderId = orderIdObj != null ? orderIdObj.toString() : \"\";\n            Object itemsObj = request.get(\"items\");\n            String items = itemsObj != null ? itemsObj.toString() : \"[]\";\n\n            boolean stockAvailable = checkStockAvailability(items);\n\n            InventoryReservation reservation = new InventoryReservation();\n            reservation.setReservationId(UUID.randomUUID().toString());\n            reservation.setOrderId(orderId);\n            reservation.setItems(items);\n\n            if (stockAvailable) {\n                reservation.setStatus(\"RESERVED\");\n                logToCloudWatch(\"Stock reserved: \" + reservation.getReservationId());\n            } else {\n                reservation.setStatus(\"FAILED\");\n                reservation.setUnavailableItems(items);\n                publishToDLQ(orderId, items);\n                logToCloudWatch(\"Inventory insufficient: \" + orderId);\n            }\n\n            InventoryReservation saved = inventoryReservationRepository.save(reservation);\n            response.put(\"status\", \"SUCCESS\");\n            response.put(\"reservation\", saved);\n            response.put(\"reservationId\", saved.getReservationId());\n        } catch (Exception e) {\n            response.put(\"status\", \"ERROR\");\n            response.put(\"message\", e.getMessage());\n        }\n        return response;\n    }\n\n    public Map<String, Object> update(Long id, Map<String, Object> request) {\n        Map<String, Object> response = new HashMap<>();\n        try {\n            InventoryReservation reservation = inventoryReservationRepository.findById(id)\n                .orElseThrow(() -> new IllegalArgumentException(\"Reservation not found: \" + id));\n\n            Object statusObj = request.get(\"status\");\n            if (statusObj != null) {\n                reservation.setStatus(statusObj.toString());\n            }\n\n            InventoryReservation updated = inventoryReservationRepository.save(reservation);\n            response.put(\"status\", \"SUCCESS\");\n            response.put(\"reservation\", updated);\n        } catch (IllegalArgumentException e) {\n            response.put(\"status\", \"ERROR\");\n            response.put(\"message\", e.getMessage());\n        } catch (Exception e) {\n            response.put(\"status\", \"ERROR\");\n            response.put(\"message\", e.getMessage());\n        }\n        return response;\n    }\n\n    public Map<String, Object> delete(Long id) {\n        Map<String, Object> response = new HashMap<>();\n        try {\n            if (!inventoryReservationRepository.existsById(id)) {\n                throw new IllegalArgumentException(\"Reservation not found: \" + id);\n            }\n            inventoryReservationRepository.deleteById(id);\n            response.put(\"status\", \"SUCCESS\");\n            response.put(\"message\", \"Reservation deleted\");\n        } catch (IllegalArgumentException e) {\n            response.put(\"status\", \"ERROR\");\n            response.put(\"message\", e.getMessage());\n        } catch (Exception e) {\n            response.put(\"status\", \"ERROR\");\n            response.put(\"message\", e.getMessage());\n        }\n        return response;\n    }\n\n    private boolean checkStockAvailability(String items) {\n        return Math.random() > 0.2;\n    }\n\n    private void publishToDLQ(String orderId, String unavailableItems) {\n        try {\n            Map<String, Object> dlqEvent = new HashMap<>();\n            dlqEvent.put(\"eventType\", \"INVENTORY_FAILED\");\n            dlqEvent.put(\"orderId\", orderId);\n            dlqEvent.put(\"unavailableItems\", unavailableItems);\n            dlqEvent.put(\"retryCount\", 0);\n            restTemplate.postForObject(\"http://localhost:8085/api/dlq/events\", dlqEvent, Map.class);\n        } catch (Exception e) {\n            System.err.println(\"Failed to publish to DLQ: \" + e.getMessage());\n        }\n    }\n\n    private void logToCloudWatch(String message) {\n        System.out.println(\"[CloudWatch] \" + Instant.now() + \" - \" + message);\n    }\n}",
            "testCode": "package com.example.inventoryservice.service;\n\nimport com.example.inventoryservice.entity.InventoryReservation;\nimport com.example.inventoryservice.repository.InventoryReservationRepository;\nimport org.junit.jupiter.api.BeforeEach;\nimport org.junit.jupiter.api.Test;\nimport org.springframework.beans.factory.annotation.Autowired;\nimport org.springframework.boot.test.context.SpringBootTest;\nimport org.springframework.boot.test.mock.mockito.MockBean;\nimport org.springframework.test.context.ActiveProfiles;\nimport org.springframework.web.client.RestTemplate;\nimport java.time.Instant;\nimport java.util.*;\nimport static org.junit.jupiter.api.Assertions.*;\nimport static org.mockito.Mockito.*;\nimport static org.mockito.ArgumentMatchers.*;\n\n@SpringBootTest\n@ActiveProfiles(\"test\")\nclass InventoryServiceTest {\n    @MockBean\n    private InventoryReservationRepository inventoryReservationRepository;\n\n    @MockBean\n    private RestTemplate restTemplate;\n\n    @Autowired\n    private InventoryService inventoryService;\n\n    private InventoryReservation mockReservation;\n\n    @BeforeEach\n    void setUp() {\n        mockReservation = new InventoryReservation();\n        mockReservation.setId(1L);\n        mockReservation.setReservationId(\"reservation-123\");\n        mockReservation.setOrderId(\"order-123\");\n        mockReservation.setItems(\"[{\\\"productId\\\":\\\"p1\\\",\\\"qty\\\":2}]\");\n        mockReservation.setStatus(\"RESERVED\");\n        mockReservation.setCreatedAt(Instant.now());\n\n        lenient().when(inventoryReservationRepository.findById(anyLong())).thenReturn(Optional.of(mockReservation));\n        lenient().when(inventoryReservationRepository.existsById(anyLong())).thenReturn(true);\n        lenient().when(inventoryReservationRepository.findAll()).thenReturn(List.of(mockReservation));\n        lenient().when(inventoryReservationRepository.save(any())).thenAnswer(inv -> inv.getArgument(0));\n        lenient().doNothing().when(inventoryReservationRepository).deleteById(anyLong());\n    }\n\n    @Test\n    void testGetAllSuccess() {\n        Map<String, Object> result = inventoryService.getAll();\n        assertNotNull(result);\n        assertEquals(\"SUCCESS\", result.get(\"status\"));\n        verify(inventoryReservationRepository, times(1)).findAll();\n    }\n\n    @Test\n    void testGetAllException() {\n        when(inventoryReservationRepository.findAll()).thenThrow(new RuntimeException(\"DB error\"));\n        Map<String, Object> result = inventoryService.getAll();\n        assertEquals(\"ERROR\", result.get(\"status\"));\n        assertNotNull(result.get(\"message\"));\n    }\n\n    @Test\n    void testGetByIdSuccess() {\n        Map<String, Object> result = inventoryService.getById(1L);\n        assertNotNull(result);\n        assertEquals(\"SUCCESS\", result.get(\"status\"));\n        verify(inventoryReservationRepository, times(1)).findById(1L);\n    }\n\n    @Test\n    void testGetByIdNotFound() {\n        when(inventoryReservationRepository.findById(99L)).thenReturn(Optional.empty());\n        Map<String, Object> result = inventoryService.getById(99L);\n        assertEquals(\"ERROR\", result.get(\"status\"));\n        verify(inventoryReservationRepository, never()).save(any());\n    }\n\n    @Test\n    void testGetByIdException() {\n        when(inventoryReservationRepository.findById(anyLong())).thenThrow(new RuntimeException(\"DB error\"));\n        Map<String, Object> result = inventoryService.getById(1L);\n        assertEquals(\"ERROR\", result.get(\"status\"));\n        assertNotNull(result.get(\"message\"));\n    }\n\n    @Test\n    void testCreateSuccess() {\n        Map<String, Object> request = new HashMap<>();\n        request.put(\"orderId\", \"order-123\");\n        request.put(\"items\", \"[{\\\"productId\\\":\\\"p1\\\",\\\"qty\\\":2}]\");\n\n        Map<String, Object> result = inventoryService.create(request);\n        assertNotNull(result);\n        assertEquals(\"SUCCESS\", result.get(\"status\"));\n        verify(inventoryReservationRepository, atLeastOnce()).save(any());\n    }\n\n    @Test\n    void testCreateEmptyMap() {\n        Map<String, Object> result = inventoryService.create(new HashMap<>());\n        assertNotNull(result);\n    }\n\n    @Test\n    void testCreateException() {\n        when(inventoryReservationRepository.save(any())).thenThrow(new RuntimeException(\"DB error\"));\n        Map<String, Object> request = new HashMap<>();\n        request.put(\"orderId\", \"order-123\");\n        Map<String, Object> result = inventoryService.create(request);\n        assertEquals(\"ERROR\", result.get(\"status\"));\n        assertNotNull(result.get(\"message\"));\n    }\n\n    @Test\n    void testUpdateSuccess() {\n        Map<String, Object> request = new HashMap<>();\n        request.put(\"status\", \"FAILED\");\n        Map<String, Object> result = inventoryService.update(1L, request);\n        assertNotNull(result);\n        assertEquals(\"SUCCESS\", result.get(\"status\"));\n        verify(inventoryReservationRepository, times(1)).save(any());\n    }\n\n    @Test\n    void testUpdateEmptyMap() {\n        Map<String, Object> result = inventoryService.update(1L, new HashMap<>());\n        assertNotNull(result);\n    }\n\n    @Test\n    void testUpdateNotFound() {\n        when(inventoryReservationRepository.findById(99L)).thenReturn(Optional.empty());\n        Map<String, Object> result = inventoryService.update(99L, new HashMap<>());\n        assertEquals(\"ERROR\", result.get(\"status\"));\n        verify(inventoryReservationRepository, never()).save(any());\n    }\n\n    @Test\n    void testUpdateException() {\n        when(inventoryReservationRepository.findById(anyLong())).thenThrow(new RuntimeException(\"DB error\"));\n        Map<String, Object> result = inventoryService.update(1L, new HashMap<>());\n        assertEquals(\"ERROR\", result.get(\"status\"));\n        assertNotNull(result.get(\"message\"));\n    }\n\n    @Test\n    void testDeleteSuccess() {\n        Map<String, Object> result = inventoryService.delete(1L);\n        assertNotNull(result);\n        assertEquals(\"SUCCESS\", result.get(\"status\"));\n        verify(inventoryReservationRepository, times(1)).deleteById(1L);\n    }\n\n    @Test\n    void testDeleteNotFound() {\n        when(inventoryReservationRepository.existsById(99L)).thenReturn(false);\n        Map<String, Object> result = inventoryService.delete(99L);\n        assertEquals(\"ERROR\", result.get(\"status\"));\n    }\n\n    @Test\n    void testDeleteException() {\n        when(inventoryReservationRepository.existsById(anyLong())).thenThrow(new RuntimeException(\"DB error\"));\n        Map<String, Object> result = inventoryService.delete(1L);\n        assertEquals(\"ERROR\", result.get(\"status\"));\n        assertNotNull(result.get(\"message\"));\n    }\n}",
            "_meta": {
              "methodsWithOrElseThrow": [
                "getById",
                "update"
              ],
              "methodsWithRetryLoop": [],
              "methodsWithGenericCatch": [
                "getById",
                "update",
                "delete"
              ],
              "maxRetries": 3
            }
          }
        ],
        "entities": [
          {
            "name": "InventoryReservation",
            "fullCode": "package com.example.inventoryservice.entity;\n\nimport jakarta.persistence.*;\nimport java.time.Instant;\n\n@Entity\n@Table(name = \"inventory_reservations\")\npublic class InventoryReservation {\n    @Id\n    @GeneratedValue(strategy = GenerationType.IDENTITY)\n    private Long id;\n\n    @Column(nullable = false, unique = true)\n    private String reservationId;\n\n    @Column(nullable = false)\n    private String orderId;\n\n    @Lob\n    @Column(nullable = false)\n    private String items;\n\n    @Column(nullable = false)\n    private String status;\n\n    @Lob\n    @Column\n    private String unavailableItems;\n\n    @Column(name = \"created_at\", nullable = false, updatable = false)\n    private Instant createdAt;\n\n    public InventoryReservation() {}\n\n    public InventoryReservation(String reservationId, String orderId, String items, String status) {\n        this.reservationId = reservationId;\n        this.orderId = orderId;\n        this.items = items;\n        this.status = status;\n    }\n\n    @PrePersist\n    protected void onCreate() {\n        if (this.createdAt == null) {\n            this.createdAt = Instant.now();\n        }\n    }\n\n    public Long getId() {\n        return id;\n    }\n\n    public void setId(Long id) {\n        this.id = id;\n    }\n\n    public String getReservationId() {\n        return reservationId;\n    }\n\n    public void setReservationId(String reservationId) {\n        this.reservationId = reservationId;\n    }\n\n    public String getOrderId() {\n        return orderId;\n    }\n\n    public void setOrderId(String orderId) {\n        this.orderId = orderId;\n    }\n\n    public String getItems() {\n        return items;\n    }\n\n    public void setItems(String items) {\n        this.items = items;\n    }\n\n    public String getStatus() {\n        return status;\n    }\n\n    public void setStatus(String status) {\n        this.status = status;\n    }\n\n    public String getUnavailableItems() {\n        return unavailableItems;\n    }\n\n    public void setUnavailableItems(String unavailableItems) {\n        this.unavailableItems = unavailableItems;\n    }\n\n    public Instant getCreatedAt() {\n        return createdAt;\n    }\n\n    public void setCreatedAt(Instant createdAt) {\n        this.createdAt = createdAt;\n    }\n}"
          }
        ],
        "repositories": [
          {
            "name": "InventoryReservationRepository",
            "fullCode": "package com.example.inventoryservice.repository;\n\nimport com.example.inventoryservice.entity.InventoryReservation;\nimport org.springframework.data.jpa.repository.JpaRepository;\n\npublic interface InventoryReservationRepository extends JpaRepository<InventoryReservation, Long> {\n}"
          }
        ],
        "configs": [
          {
            "name": "AppConfig",
            "package": "config",
            "fullCode": "package com.example.inventoryservice.config;\n\nimport org.springframework.context.annotation.Bean;\nimport org.springframework.context.annotation.Configuration;\nimport org.springframework.web.client.RestTemplate;\n\n@Configuration\npublic class AppConfig {\n    @Bean\n    public RestTemplate restTemplate() {\n        return new RestTemplate();\n    }\n}"
          },
          {
            "name": "BasicSecurityConfig",
            "package": "config",
            "fullCode": "package com.example.inventoryservice.config;\n\nimport org.springframework.beans.factory.annotation.Value;\nimport org.springframework.context.annotation.Bean;\nimport org.springframework.context.annotation.Configuration;\nimport org.springframework.security.config.annotation.web.builders.HttpSecurity;\nimport org.springframework.security.config.annotation.web.configuration.EnableWebSecurity;\nimport org.springframework.security.core.userdetails.User;\nimport org.springframework.security.core.userdetails.UserDetails;\nimport org.springframework.security.core.userdetails.UserDetailsService;\nimport org.springframework.security.crypto.bcrypt.BCryptPasswordEncoder;\nimport org.springframework.security.crypto.password.PasswordEncoder;\nimport org.springframework.security.provisioning.InMemoryUserDetailsManager;\nimport org.springframework.security.web.SecurityFilterChain;\n\n@Configuration\n@EnableWebSecurity\npublic class BasicSecurityConfig {\n    @Value(\"${app.security.admin.username}\")\n    private String adminUsername;\n\n    @Value(\"${app.security.admin.password}\")\n    private String adminPassword;\n\n    @Value(\"${app.security.user.username}\")\n    private String userUsername;\n\n    @Value(\"${app.security.user.password}\")\n    private String userPassword;\n\n    @Bean\n    public SecurityFilterChain securityFilterChain(HttpSecurity http) throws Exception {\n        http.csrf(csrf -> csrf.disable())\n            .headers(h -> h.frameOptions(f -> f.disable()))\n            .authorizeHttpRequests(auth -> auth\n                .requestMatchers(\"/h2-console/**\", \"/swagger-ui/**\", \"/v3/**\", \"/actuator/**\").permitAll()\n                .anyRequest().authenticated())\n            .httpBasic(b -> {});\n        return http.build();\n    }\n\n    @Bean\n    public UserDetailsService userDetailsService() {\n        UserDetails admin = User.builder()\n            .username(adminUsername)\n            .password(passwordEncoder().encode(adminPassword))\n            .roles(\"ADMIN\")\n            .build();\n        UserDetails user = User.builder()\n            .username(userUsername)\n            .password(passwordEncoder().encode(userPassword))\n            .roles(\"USER\")\n            .build();\n        return new InMemoryUserDetailsManager(admin, user);\n    }\n\n    @Bean\n    public PasswordEncoder passwordEncoder() {\n        return new BCryptPasswordEncoder();\n    }\n}"
          }
        ],
        "applicationProperties": "spring.application.name=inventory-service\nserver.port=8083\nspring.datasource.url=jdbc:h2:mem:inventoryservicedb\nspring.datasource.driverClassName=org.h2.Driver\nspring.datasource.username=sa\nspring.datasource.password=\nspring.jpa.database-platform=org.hibernate.dialect.H2Dialect\nspring.jpa.hibernate.ddl-auto=update\nspring.h2.console.enabled=true\napp.security.admin.username=admin\napp.security.admin.password=admin\napp.security.user.username=user\napp.security.user.password=user",
        "externalClients": []
      },
      {
        "name": "reporting-service",
        "packageName": "com.example.reportingservice",
        "port": 8084,
        "description": "Records completed transactions for audit and reporting, logs transaction data to CloudWatch",
        "dependencies": [
          {
            "groupId": "org.springframework.boot",
            "artifactId": "spring-boot-starter-web"
          },
          {
            "groupId": "org.springframework.boot",
            "artifactId": "spring-boot-starter-data-jpa"
          },
          {
            "groupId": "com.h2database",
            "artifactId": "h2",
            "scope": "runtime"
          },
          {
            "groupId": "org.springframework.boot",
            "artifactId": "spring-boot-starter-actuator"
          },
          {
            "groupId": "org.springframework.boot",
            "artifactId": "spring-boot-starter-test",
            "scope": "test"
          },
          {
            "groupId": "org.springframework.boot",
            "artifactId": "spring-boot-starter-security"
          }
        ],
        "controllers": [
          {
            "name": "ReportingController",
            "basePath": "/api/reports/transactions",
            "endpoints": [
              {
                "method": "GET",
                "path": "",
                "methodName": "getAllReports",
                "parameters": [],
                "returnType": "Map<String,Object>",
                "implementation": "return reportingService.getAll();"
              },
              {
                "method": "POST",
                "path": "",
                "methodName": "createReport",
                "parameters": [
                  {
                    "name": "request",
                    "type": "Map<String,Object>",
                    "annotation": "RequestBody"
                  }
                ],
                "returnType": "Map<String,Object>",
                "implementation": "return reportingService.create(request);"
              },
              {
                "method": "GET",
                "path": "/{id}",
                "methodName": "getReportById",
                "parameters": [
                  {
                    "name": "id",
                    "type": "Long",
                    "annotation": "PathVariable"
                  }
                ],
                "returnType": "Map<String,Object>",
                "implementation": "return reportingService.getById(id);"
              },
              {
                "method": "PUT",
                "path": "/{id}",
                "methodName": "updateReport",
                "parameters": [
                  {
                    "name": "id",
                    "type": "Long",
                    "annotation": "PathVariable"
                  },
                  {
                    "name": "request",
                    "type": "Map<String,Object>",
                    "annotation": "RequestBody"
                  }
                ],
                "returnType": "Map<String,Object>",
                "implementation": "return reportingService.update(id, request);"
              },
              {
                "method": "DELETE",
                "path": "/{id}",
                "methodName": "deleteReport",
                "parameters": [
                  {
                    "name": "id",
                    "type": "Long",
                    "annotation": "PathVariable"
                  }
                ],
                "returnType": "Map<String,Object>",
                "implementation": "return reportingService.delete(id);"
              }
            ]
          }
        ],
        "services": [
          {
            "name": "ReportingService",
            "fullCode": "package com.example.reportingservice.service;\n\nimport com.example.reportingservice.entity.TransactionReport;\nimport com.example.reportingservice.repository.TransactionReportRepository;\nimport org.springframework.stereotype.Service;\nimport java.util.HashMap;\nimport java.util.List;\nimport java.util.Map;\nimport java.util.UUID;\nimport java.math.BigDecimal;\nimport java.time.Instant;\n\n@Service\npublic class ReportingService {\n    private final TransactionReportRepository transactionReportRepository;\n\n    public ReportingService(TransactionReportRepository transactionReportRepository) {\n        this.transactionReportRepository = transactionReportRepository;\n    }\n\n    public Map<String, Object> getAll() {\n        Map<String, Object> response = new HashMap<>();\n        try {\n            List<TransactionReport> reports = transactionReportRepository.findAll();\n            response.put(\"status\", \"SUCCESS\");\n            response.put(\"reports\", reports);\n            response.put(\"count\", reports.size());\n        } catch (Exception e) {\n            response.put(\"status\", \"ERROR\");\n            response.put(\"message\", e.getMessage());\n        }\n        return response;\n    }\n\n    public Map<String, Object> getById(Long id) {\n        Map<String, Object> response = new HashMap<>();\n        try {\n            TransactionReport report = transactionReportRepository.findById(id)\n                .orElseThrow(() -> new IllegalArgumentException(\"Report not found: \" + id));\n            response.put(\"status\", \"SUCCESS\");\n            response.put(\"report\", report);\n        } catch (IllegalArgumentException e) {\n            response.put(\"status\", \"ERROR\");\n            response.put(\"message\", e.getMessage());\n        } catch (Exception e) {\n            response.put(\"status\", \"ERROR\");\n            response.put(\"message\", e.getMessage());\n        }\n        return response;\n    }\n\n    public Map<String, Object> create(Map<String, Object> request) {\n        Map<String, Object> response = new HashMap<>();\n        try {\n            Object orderIdObj = request.get(\"orderId\");\n            String orderId = orderIdObj != null ? orderIdObj.toString() : \"\";\n            Object paymentIdObj = request.get(\"paymentId\");\n            String paymentId = paymentIdObj != null ? paymentIdObj.toString() : \"\";\n            Object reservationIdObj = request.get(\"reservationId\");\n            String reservationId = reservationIdObj != null ? reservationIdObj.toString() : \"\";\n            Object totalAmountObj = request.get(\"totalAmount\");\n            BigDecimal totalAmount = totalAmountObj != null ? new BigDecimal(totalAmountObj.toString()) : BigDecimal.ZERO;\n\n            TransactionReport report = new TransactionReport();\n            report.setReportId(UUID.randomUUID().toString());\n            report.setOrderId(orderId);\n            report.setPaymentId(paymentId);\n            report.setReservationId(reservationId);\n            report.setTotalAmount(totalAmount);\n            report.setStatus(\"LOGGED\");\n\n            TransactionReport saved = transactionReportRepository.save(report);\n            logToCloudWatch(\"Transaction recorded: \" + saved.getReportId());\n\n            response.put(\"status\", \"SUCCESS\");\n            response.put(\"report\", saved);\n            response.put(\"reportId\", saved.getReportId());\n        } catch (Exception e) {\n            response.put(\"status\", \"ERROR\");\n            response.put(\"message\", e.getMessage());\n        }\n        return response;\n    }\n\n    public Map<String, Object> update(Long id, Map<String, Object> request) {\n        Map<String, Object> response = new HashMap<>();\n        try {\n            TransactionReport report = transactionReportRepository.findById(id)\n                .orElseThrow(() -> new IllegalArgumentException(\"Report not found: \" + id));\n\n            Object statusObj = request.get(\"status\");\n            if (statusObj != null) {\n                report.setStatus(statusObj.toString());\n            }\n\n            TransactionReport updated = transactionReportRepository.save(report);\n            response.put(\"status\", \"SUCCESS\");\n            response.put(\"report\", updated);\n        } catch (IllegalArgumentException e) {\n            response.put(\"status\", \"ERROR\");\n            response.put(\"message\", e.getMessage());\n        } catch (Exception e) {\n            response.put(\"status\", \"ERROR\");\n            response.put(\"message\", e.getMessage());\n        }\n        return response;\n    }\n\n    public Map<String, Object> delete(Long id) {\n        Map<String, Object> response = new HashMap<>();\n        try {\n            if (!transactionReportRepository.existsById(id)) {\n                throw new IllegalArgumentException(\"Report not found: \" + id);\n            }\n            transactionReportRepository.deleteById(id);\n            response.put(\"status\", \"SUCCESS\");\n            response.put(\"message\", \"Report deleted\");\n        } catch (IllegalArgumentException e) {\n            response.put(\"status\", \"ERROR\");\n            response.put(\"message\", e.getMessage());\n        } catch (Exception e) {\n            response.put(\"status\", \"ERROR\");\n            response.put(\"message\", e.getMessage());\n        }\n        return response;\n    }\n\n    private void logToCloudWatch(String message) {\n        System.out.println(\"[CloudWatch] \" + Instant.now() + \" - \" + message);\n    }\n}",
            "testCode": "package com.example.reportingservice.service;\n\nimport com.example.reportingservice.entity.TransactionReport;\nimport com.example.reportingservice.repository.TransactionReportRepository;\nimport org.junit.jupiter.api.BeforeEach;\nimport org.junit.jupiter.api.Test;\nimport org.springframework.beans.factory.annotation.Autowired;\nimport org.springframework.boot.test.context.SpringBootTest;\nimport org.springframework.boot.test.mock.mockito.MockBean;\nimport org.springframework.test.context.ActiveProfiles;\nimport java.math.BigDecimal;\nimport java.time.Instant;\nimport java.util.*;\nimport static org.junit.jupiter.api.Assertions.*;\nimport static org.mockito.Mockito.*;\nimport static org.mockito.ArgumentMatchers.*;\n\n@SpringBootTest\n@ActiveProfiles(\"test\")\nclass ReportingServiceTest {\n    @MockBean\n    private TransactionReportRepository transactionReportRepository;\n\n    @Autowired\n    private ReportingService reportingService;\n\n    private TransactionReport mockReport;\n\n    @BeforeEach\n    void setUp() {\n        mockReport = new TransactionReport();\n        mockReport.setId(1L);\n        mockReport.setReportId(\"report-123\");\n        mockReport.setOrderId(\"order-123\");\n        mockReport.setPaymentId(\"payment-123\");\n        mockReport.setReservationId(\"reservation-123\");\n        mockReport.setTotalAmount(new BigDecimal(\"100.00\"));\n        mockReport.setStatus(\"LOGGED\");\n        mockReport.setCreatedAt(Instant.now());\n\n        lenient().when(transactionReportRepository.findById(anyLong())).thenReturn(Optional.of(mockReport));\n        lenient().when(transactionReportRepository.existsById(anyLong())).thenReturn(true);\n        lenient().when(transactionReportRepository.findAll()).thenReturn(List.of(mockReport));\n        lenient().when(transactionReportRepository.save(any())).thenAnswer(inv -> inv.getArgument(0));\n        lenient().doNothing().when(transactionReportRepository).deleteById(anyLong());\n    }\n\n    @Test\n    void testGetAllSuccess() {\n        Map<String, Object> result = reportingService.getAll();\n        assertNotNull(result);\n        assertEquals(\"SUCCESS\", result.get(\"status\"));\n        verify(transactionReportRepository, times(1)).findAll();\n    }\n\n    @Test\n    void testGetAllException() {\n        when(transactionReportRepository.findAll()).thenThrow(new RuntimeException(\"DB error\"));\n        Map<String, Object> result = reportingService.getAll();\n        assertEquals(\"ERROR\", result.get(\"status\"));\n        assertNotNull(result.get(\"message\"));\n    }\n\n    @Test\n    void testGetByIdSuccess() {\n        Map<String, Object> result = reportingService.getById(1L);\n        assertNotNull(result);\n        assertEquals(\"SUCCESS\", result.get(\"status\"));\n        verify(transactionReportRepository, times(1)).findById(1L);\n    }\n\n    @Test\n    void testGetByIdNotFound() {\n        when(transactionReportRepository.findById(99L)).thenReturn(Optional.empty());\n        Map<String, Object> result = reportingService.getById(99L);\n        assertEquals(\"ERROR\", result.get(\"status\"));\n        verify(transactionReportRepository, never()).save(any());\n    }\n\n    @Test\n    void testGetByIdException() {\n        when(transactionReportRepository.findById(anyLong())).thenThrow(new RuntimeException(\"DB error\"));\n        Map<String, Object> result = reportingService.getById(1L);\n        assertEquals(\"ERROR\", result.get(\"status\"));\n        assertNotNull(result.get(\"message\"));\n    }\n\n    @Test\n    void testCreateSuccess() {\n        Map<String, Object> request = new HashMap<>();\n        request.put(\"orderId\", \"order-123\");\n        request.put(\"paymentId\", \"payment-123\");\n        request.put(\"reservationId\", \"reservation-123\");\n        request.put(\"totalAmount\", \"100.00\");\n\n        Map<String, Object> result = reportingService.create(request);\n        assertNotNull(result);\n        assertEquals(\"SUCCESS\", result.get(\"status\"));\n        verify(transactionReportRepository, atLeastOnce()).save(any());\n    }\n\n    @Test\n    void testCreateEmptyMap() {\n        Map<String, Object> result = reportingService.create(new HashMap<>());\n        assertNotNull(result);\n    }\n\n    @Test\n    void testCreateException() {\n        when(transactionReportRepository.save(any())).thenThrow(new RuntimeException(\"DB error\"));\n        Map<String, Object> request = new HashMap<>();\n        request.put(\"orderId\", \"order-123\");\n        Map<String, Object> result = reportingService.create(request);\n        assertEquals(\"ERROR\", result.get(\"status\"));\n        assertNotNull(result.get(\"message\"));\n    }\n\n    @Test\n    void testUpdateSuccess() {\n        Map<String, Object> request = new HashMap<>();\n        request.put(\"status\", \"ARCHIVED\");\n        Map<String, Object> result = reportingService.update(1L, request);\n        assertNotNull(result);\n        assertEquals(\"SUCCESS\", result.get(\"status\"));\n        verify(transactionReportRepository, times(1)).save(any());\n    }\n\n    @Test\n    void testUpdateEmptyMap() {\n        Map<String, Object> result = reportingService.update(1L, new HashMap<>());\n        assertNotNull(result);\n    }\n\n    @Test\n    void testUpdateNotFound() {\n        when(transactionReportRepository.findById(99L)).thenReturn(Optional.empty());\n        Map<String, Object> result = reportingService.update(99L, new HashMap<>());\n        assertEquals(\"ERROR\", result.get(\"status\"));\n        verify(transactionReportRepository, never()).save(any());\n    }\n\n    @Test\n    void testUpdateException() {\n        when(transactionReportRepository.findById(anyLong())).thenThrow(new RuntimeException(\"DB error\"));\n        Map<String, Object> result = reportingService.update(1L, new HashMap<>());\n        assertEquals(\"ERROR\", result.get(\"status\"));\n        assertNotNull(result.get(\"message\"));\n    }\n\n    @Test\n    void testDeleteSuccess() {\n        Map<String, Object> result = reportingService.delete(1L);\n        assertNotNull(result);\n        assertEquals(\"SUCCESS\", result.get(\"status\"));\n        verify(transactionReportRepository, times(1)).deleteById(1L);\n    }\n\n    @Test\n    void testDeleteNotFound() {\n        when(transactionReportRepository.existsById(99L)).thenReturn(false);\n        Map<String, Object> result = reportingService.delete(99L);\n        assertEquals(\"ERROR\", result.get(\"status\"));\n    }\n\n    @Test\n    void testDeleteException() {\n        when(transactionReportRepository.existsById(anyLong())).thenThrow(new RuntimeException(\"DB error\"));\n        Map<String, Object> result = reportingService.delete(1L);\n        assertEquals(\"ERROR\", result.get(\"status\"));\n        assertNotNull(result.get(\"message\"));\n    }\n}",
            "_meta": {
              "methodsWithOrElseThrow": [
                "getById",
                "update"
              ],
              "methodsWithRetryLoop": [],
              "methodsWithGenericCatch": [
                "getById",
                "update",
                "delete"
              ],
              "maxRetries": 3
            }
          }
        ],
        "entities": [
          {
            "name": "TransactionReport",
            "fullCode": "package com.example.reportingservice.entity;\n\nimport jakarta.persistence.*;\nimport java.math.BigDecimal;\nimport java.time.Instant;\n\n@Entity\n@Table(name = \"transaction_reports\")\npublic class TransactionReport {\n    @Id\n    @GeneratedValue(strategy = GenerationType.IDENTITY)\n    private Long id;\n\n    @Column(nullable = false, unique = true)\n    private String reportId;\n\n    @Column(nullable = false)\n    private String orderId;\n\n    @Column(nullable = false)\n    private String paymentId;\n\n    @Column(nullable = false)\n    private String reservationId;\n\n    @Column(nullable = false, precision = 19, scale = 2)\n    private BigDecimal totalAmount;\n\n    @Column(nullable = false)\n    private String status;\n\n    @Column(name = \"created_at\", nullable = false, updatable = false)\n    private Instant createdAt;\n\n    public TransactionReport() {}\n\n    public TransactionReport(String reportId, String orderId, String paymentId, String reservationId, BigDecimal totalAmount, String status) {\n        this.reportId = reportId;\n        this.orderId = orderId;\n        this.paymentId = paymentId;\n        this.reservationId = reservationId;\n        this.totalAmount = totalAmount;\n        this.status = status;\n    }\n\n    @PrePersist\n    protected void onCreate() {\n        if (this.createdAt == null) {\n            this.createdAt = Instant.now();\n        }\n    }\n\n    public Long getId() {\n        return id;\n    }\n\n    public void setId(Long id) {\n        this.id = id;\n    }\n\n    public String getReportId() {\n        return reportId;\n    }\n\n    public void setReportId(String reportId) {\n        this.reportId = reportId;\n    }\n\n    public String getOrderId() {\n        return orderId;\n    }\n\n    public void setOrderId(String orderId) {\n        this.orderId = orderId;\n    }\n\n    public String getPaymentId() {\n        return paymentId;\n    }\n\n    public void setPaymentId(String paymentId) {\n        this.paymentId = paymentId;\n    }\n\n    public String getReservationId() {\n        return reservationId;\n    }\n\n    public void setReservationId(String reservationId) {\n        this.reservationId = reservationId;\n    }\n\n    public BigDecimal getTotalAmount() {\n        return totalAmount;\n    }\n\n    public void setTotalAmount(BigDecimal totalAmount) {\n        this.totalAmount = totalAmount;\n    }\n\n    public String getStatus() {\n        return status;\n    }\n\n    public void setStatus(String status) {\n        this.status = status;\n    }\n\n    public Instant getCreatedAt() {\n        return createdAt;\n    }\n\n    public void setCreatedAt(Instant createdAt) {\n        this.createdAt = createdAt;\n    }\n}"
          }
        ],
        "repositories": [
          {
            "name": "TransactionReportRepository",
            "fullCode": "package com.example.reportingservice.repository;\n\nimport com.example.reportingservice.entity.TransactionReport;\nimport org.springframework.data.jpa.repository.JpaRepository;\n\npublic interface TransactionReportRepository extends JpaRepository<TransactionReport, Long> {\n}"
          }
        ],
        "configs": [
          {
            "name": "BasicSecurityConfig",
            "package": "config",
            "fullCode": "package com.example.reportingservice.config;\n\nimport org.springframework.beans.factory.annotation.Value;\nimport org.springframework.context.annotation.Bean;\nimport org.springframework.context.annotation.Configuration;\nimport org.springframework.security.config.annotation.web.builders.HttpSecurity;\nimport org.springframework.security.config.annotation.web.configuration.EnableWebSecurity;\nimport org.springframework.security.core.userdetails.User;\nimport org.springframework.security.core.userdetails.UserDetails;\nimport org.springframework.security.core.userdetails.UserDetailsService;\nimport org.springframework.security.crypto.bcrypt.BCryptPasswordEncoder;\nimport org.springframework.security.crypto.password.PasswordEncoder;\nimport org.springframework.security.provisioning.InMemoryUserDetailsManager;\nimport org.springframework.security.web.SecurityFilterChain;\n\n@Configuration\n@EnableWebSecurity\npublic class BasicSecurityConfig {\n    @Value(\"${app.security.admin.username}\")\n    private String adminUsername;\n\n    @Value(\"${app.security.admin.password}\")\n    private String adminPassword;\n\n    @Value(\"${app.security.user.username}\")\n    private String userUsername;\n\n    @Value(\"${app.security.user.password}\")\n    private String userPassword;\n\n    @Bean\n    public SecurityFilterChain securityFilterChain(HttpSecurity http) throws Exception {\n        http.csrf(csrf -> csrf.disable())\n            .headers(h -> h.frameOptions(f -> f.disable()))\n            .authorizeHttpRequests(auth -> auth\n                .requestMatchers(\"/h2-console/**\", \"/swagger-ui/**\", \"/v3/**\", \"/actuator/**\").permitAll()\n                .anyRequest().authenticated())\n            .httpBasic(b -> {});\n        return http.build();\n    }\n\n    @Bean\n    public UserDetailsService userDetailsService() {\n        UserDetails admin = User.builder()\n            .username(adminUsername)\n            .password(passwordEncoder().encode(adminPassword))\n            .roles(\"ADMIN\")\n            .build();\n        UserDetails user = User.builder()\n            .username(userUsername)\n            .password(passwordEncoder().encode(userPassword))\n            .roles(\"USER\")\n            .build();\n        return new InMemoryUserDetailsManager(admin, user);\n    }\n\n    @Bean\n    public PasswordEncoder passwordEncoder() {\n        return new BCryptPasswordEncoder();\n    }\n}"
          }
        ],
        "applicationProperties": "spring.application.name=reporting-service\nserver.port=8084\nspring.datasource.url=jdbc:h2:mem:reportingservicedb\nspring.datasource.driverClassName=org.h2.Driver\nspring.datasource.username=sa\nspring.datasource.password=\nspring.jpa.database-platform=org.hibernate.dialect.H2Dialect\nspring.jpa.hibernate.ddl-auto=update\nspring.h2.console.enabled=true\napp.security.admin.username=admin\napp.security.admin.password=admin\napp.security.user.username=user\napp.security.user.password=user",
        "externalClients": []
      },
      {
        "name": "dlq-service",
        "packageName": "com.example.dlqservice",
        "port": 8085,
        "description": "Dead Letter Queue service for handling failed payment and inventory operations with retry logic",
        "dependencies": [
          {
            "groupId": "org.springframework.boot",
            "artifactId": "spring-boot-starter-web"
          },
          {
            "groupId": "org.springframework.boot",
            "artifactId": "spring-boot-starter-data-jpa"
          },
          {
            "groupId": "com.h2database",
            "artifactId": "h2",
            "scope": "runtime"
          },
          {
            "groupId": "org.springframework.boot",
            "artifactId": "spring-boot-starter-actuator"
          },
          {
            "groupId": "org.springframework.boot",
            "artifactId": "spring-boot-starter-test",
            "scope": "test"
          },
          {
            "groupId": "org.springframework.boot",
            "artifactId": "spring-boot-starter-security"
          }
        ],
        "controllers": [
          {
            "name": "DLQController",
            "basePath": "/api/dlq/events",
            "endpoints": [
              {
                "method": "GET",
                "path": "",
                "methodName": "getAllEvents",
                "parameters": [],
                "returnType": "Map<String,Object>",
                "implementation": "return dlqService.getAll();"
              },
              {
                "method": "POST",
                "path": "",
                "methodName": "createEvent",
                "parameters": [
                  {
                    "name": "request",
                    "type": "Map<String,Object>",
                    "annotation": "RequestBody"
                  }
                ],
                "returnType": "Map<String,Object>",
                "implementation": "return dlqService.create(request);"
              },
              {
                "method": "GET",
                "path": "/{id}",
                "methodName": "getEventById",
                "parameters": [
                  {
                    "name": "id",
                    "type": "Long",
                    "annotation": "PathVariable"
                  }
                ],
                "returnType": "Map<String,Object>",
                "implementation": "return dlqService.getById(id);"
              },
              {
                "method": "PUT",
                "path": "/{id}",
                "methodName": "updateEvent",
                "parameters": [
                  {
                    "name": "id",
                    "type": "Long",
                    "annotation": "PathVariable"
                  },
                  {
                    "name": "request",
                    "type": "Map<String,Object>",
                    "annotation": "RequestBody"
                  }
                ],
                "returnType": "Map<String,Object>",
                "implementation": "return dlqService.update(id, request);"
              },
              {
                "method": "DELETE",
                "path": "/{id}",
                "methodName": "deleteEvent",
                "parameters": [
                  {
                    "name": "id",
                    "type": "Long",
                    "annotation": "PathVariable"
                  }
                ],
                "returnType": "Map<String,Object>",
                "implementation": "return dlqService.delete(id);"
              }
            ]
          }
        ],
        "services": [
          {
            "name": "DLQService",
            "fullCode": "package com.example.dlqservice.service;\n\nimport com.example.dlqservice.entity.DLQEvent;\nimport com.example.dlqservice.repository.DLQEventRepository;\nimport org.springframework.stereotype.Service;\nimport org.springframework.web.client.RestTemplate;\nimport java.util.HashMap;\nimport java.util.List;\nimport java.util.Map;\nimport java.util.UUID;\nimport java.time.Instant;\n\n@Service\npublic class DLQService {\n    private final DLQEventRepository dlqEventRepository;\n    private final RestTemplate restTemplate;\n    private static final int MAX_RETRIES = 3;\n\n    public DLQService(DLQEventRepository dlqEventRepository, RestTemplate restTemplate) {\n        this.dlqEventRepository = dlqEventRepository;\n        this.restTemplate = restTemplate;\n    }\n\n    public Map<String, Object> getAll() {\n        Map<String, Object> response = new HashMap<>();\n        try {\n            List<DLQEvent> events = dlqEventRepository.findAll();\n            response.put(\"status\", \"SUCCESS\");\n            response.put(\"events\", events);\n            response.put(\"count\", events.size());\n        } catch (Exception e) {\n            response.put(\"status\", \"ERROR\");\n            response.put(\"message\", e.getMessage());\n        }\n        return response;\n    }\n\n    public Map<String, Object> getById(Long id) {\n        Map<String, Object> response = new HashMap<>();\n        try {\n            DLQEvent event = dlqEventRepository.findById(id)\n                .orElseThrow(() -> new IllegalArgumentException(\"Event not found: \" + id));\n            response.put(\"status\", \"SUCCESS\");\n            response.put(\"event\", event);\n        } catch (IllegalArgumentException e) {\n            response.put(\"status\", \"ERROR\");\n            response.put(\"message\", e.getMessage());\n        } catch (Exception e) {\n            response.put(\"status\", \"ERROR\");\n            response.put(\"message\", e.getMessage());\n        }\n        return response;\n    }\n\n    public Map<String, Object> create(Map<String, Object> request) {\n        Map<String, Object> response = new HashMap<>();\n        try {\n            Object eventTypeObj = request.get(\"eventType\");\n            String eventType = eventTypeObj != null ? eventTypeObj.toString() : \"\";\n            Object orderIdObj = request.get(\"orderId\");\n            String orderId = orderIdObj != null ? orderIdObj.toString() : \"\";\n            Object reasonObj = request.get(\"reason\");\n            String reason = reasonObj != null ? reasonObj.toString() : \"\";\n            Object retryCountObj = request.get(\"retryCount\");\n            int retryCount = retryCountObj != null ? Integer.parseInt(retryCountObj.toString()) : 0;\n            Object unavailableItemsObj = request.get(\"unavailableItems\");\n            String unavailableItems = unavailableItemsObj != null ? unavailableItemsObj.toString() : \"\";\n\n            DLQEvent event = new DLQEvent();\n            event.setEventId(UUID.randomUUID().toString());\n            event.setEventType(eventType);\n            event.setOrderId(orderId);\n            event.setRetryCount(retryCount);\n            event.setReason(reason);\n            event.setUnavailableItems(unavailableItems);\n\n            DLQEvent saved = dlqEventRepository.save(event);\n\n            if (retryCount < MAX_RETRIES) {\n                retryFailedOperation(saved);\n            } else {\n                triggerCloudWatchAlarm(saved);\n            }\n\n            response.put(\"status\", \"SUCCESS\");\n            response.put(\"event\", saved);\n        } catch (Exception e) {\n            response.put(\"status\", \"ERROR\");\n            response.put(\"message\", e.getMessage());\n        }\n        return response;\n    }\n\n    public Map<String, Object> update(Long id, Map<String, Object> request) {\n        Map<String, Object> response = new HashMap<>();\n        try {\n            DLQEvent event = dlqEventRepository.findById(id)\n                .orElseThrow(() -> new IllegalArgumentException(\"Event not found: \" + id));\n\n            Object retryCountObj = request.get(\"retryCount\");\n            if (retryCountObj != null) {\n                event.setRetryCount(Integer.parseInt(retryCountObj.toString()));\n            }\n\n            DLQEvent updated = dlqEventRepository.save(event);\n            response.put(\"status\", \"SUCCESS\");\n            response.put(\"event\", updated);\n        } catch (IllegalArgumentException e) {\n            response.put(\"status\", \"ERROR\");\n            response.put(\"message\", e.getMessage());\n        } catch (Exception e) {\n            response.put(\"status\", \"ERROR\");\n            response.put(\"message\", e.getMessage());\n        }\n        return response;\n    }\n\n    public Map<String, Object> delete(Long id) {\n        Map<String, Object> response = new HashMap<>();\n        try {\n            if (!dlqEventRepository.existsById(id)) {\n                throw new IllegalArgumentException(\"Event not found: \" + id);\n            }\n            dlqEventRepository.deleteById(id);\n            response.put(\"status\", \"SUCCESS\");\n            response.put(\"message\", \"Event deleted\");\n        } catch (IllegalArgumentException e) {\n            response.put(\"status\", \"ERROR\");\n            response.put(\"message\", e.getMessage());\n        } catch (Exception e) {\n            response.put(\"status\", \"ERROR\");\n            response.put(\"message\", e.getMessage());\n        }\n        return response;\n    }\n\n    private void retryFailedOperation(DLQEvent event) {\n        System.out.println(\"[DLQ] Retrying operation for event: \" + event.getEventId());\n    }\n\n    private void triggerCloudWatchAlarm(DLQEvent event) {\n        System.out.println(\"[CloudWatch Alarm] Max retries exceeded for event: \" + event.getEventId());\n    }\n}",
            "testCode": "package com.example.dlqservice.service;\n\nimport com.example.dlqservice.entity.DLQEvent;\nimport com.example.dlqservice.repository.DLQEventRepository;\nimport org.junit.jupiter.api.BeforeEach;\nimport org.junit.jupiter.api.Test;\nimport org.springframework.beans.factory.annotation.Autowired;\nimport org.springframework.boot.test.context.SpringBootTest;\nimport org.springframework.boot.test.mock.mockito.MockBean;\nimport org.springframework.test.context.ActiveProfiles;\nimport org.springframework.web.client.RestTemplate;\nimport java.time.Instant;\nimport java.util.*;\nimport static org.junit.jupiter.api.Assertions.*;\nimport static org.mockito.Mockito.*;\nimport static org.mockito.ArgumentMatchers.*;\n\n@SpringBootTest\n@ActiveProfiles(\"test\")\nclass DLQServiceTest {\n    @MockBean\n    private DLQEventRepository dlqEventRepository;\n\n    @MockBean\n    private RestTemplate restTemplate;\n\n    @Autowired\n    private DLQService dlqService;\n\n    private DLQEvent mockEvent;\n\n    @BeforeEach\n    void setUp() {\n        mockEvent = new DLQEvent();\n        mockEvent.setId(1L);\n        mockEvent.setEventId(\"event-123\");\n        mockEvent.setEventType(\"PAYMENT_FAILED\");\n        mockEvent.setOrderId(\"order-123\");\n        mockEvent.setRetryCount(0);\n        mockEvent.setReason(\"card_declined\");\n        mockEvent.setCreatedAt(Instant.now());\n\n        lenient().when(dlqEventRepository.findById(anyLong())).thenReturn(Optional.of(mockEvent));\n        lenient().when(dlqEventRepository.existsById(anyLong())).thenReturn(true);\n        lenient().when(dlqEventRepository.findAll()).thenReturn(List.of(mockEvent));\n        lenient().when(dlqEventRepository.save(any())).thenAnswer(inv -> inv.getArgument(0));\n        lenient().doNothing().when(dlqEventRepository).deleteById(anyLong());\n    }\n\n    @Test\n    void testGetAllSuccess() {\n        Map<String, Object> result = dlqService.getAll();\n        assertNotNull(result);\n        assertEquals(\"SUCCESS\", result.get(\"status\"));\n        verify(dlqEventRepository, times(1)).findAll();\n    }\n\n    @Test\n    void testGetAllException() {\n        when(dlqEventRepository.findAll()).thenThrow(new RuntimeException(\"DB error\"));\n        Map<String, Object> result = dlqService.getAll();\n        assertEquals(\"ERROR\", result.get(\"status\"));\n        assertNotNull(result.get(\"message\"));\n    }\n\n    @Test\n    void testGetByIdSuccess() {\n        Map<String, Object> result = dlqService.getById(1L);\n        assertNotNull(result);\n        assertEquals(\"SUCCESS\", result.get(\"status\"));\n        verify(dlqEventRepository, times(1)).findById(1L);\n    }\n\n    @Test\n    void testGetByIdNotFound() {\n        when(dlqEventRepository.findById(99L)).thenReturn(Optional.empty());\n        Map<String, Object> result = dlqService.getById(99L);\n        assertEquals(\"ERROR\", result.get(\"status\"));\n        verify(dlqEventRepository, never()).save(any());\n    }\n\n    @Test\n    void testGetByIdException() {\n        when(dlqEventRepository.findById(anyLong())).thenThrow(new RuntimeException(\"DB error\"));\n        Map<String, Object> result = dlqService.getById(1L);\n        assertEquals(\"ERROR\", result.get(\"status\"));\n        assertNotNull(result.get(\"message\"));\n    }\n\n    @Test\n    void testCreateSuccess() {\n        Map<String, Object> request = new HashMap<>();\n        request.put(\"eventType\", \"PAYMENT_FAILED\");\n        request.put(\"orderId\", \"order-123\");\n        request.put(\"reason\", \"card_declined\");\n        request.put(\"retryCount\", \"0\");\n\n        Map<String, Object> result = dlqService.create(request);\n        assertNotNull(result);\n        assertEquals(\"SUCCESS\", result.get(\"status\"));\n        verify(dlqEventRepository, atLeastOnce()).save(any());\n    }\n\n    @Test\n    void testCreateEmptyMap() {\n        Map<String, Object> result = dlqService.create(new HashMap<>());\n        assertNotNull(result);\n    }\n\n    @Test\n    void testCreateException() {\n        when(dlqEventRepository.save(any())).thenThrow(new RuntimeException(\"DB error\"));\n        Map<String, Object> request = new HashMap<>();\n        request.put(\"eventType\", \"PAYMENT_FAILED\");\n        Map<String, Object> result = dlqService.create(request);\n        assertEquals(\"ERROR\", result.get(\"status\"));\n        assertNotNull(result.get(\"message\"));\n    }\n\n    @Test\n    void testUpdateSuccess() {\n        Map<String, Object> request = new HashMap<>();\n        request.put(\"retryCount\", \"2\");\n        Map<String, Object> result = dlqService.update(1L, request);\n        assertNotNull(result);\n        assertEquals(\"SUCCESS\", result.get(\"status\"));\n        verify(dlqEventRepository, times(1)).save(any());\n    }\n\n    @Test\n    void testUpdateEmptyMap() {\n        Map<String, Object> result = dlqService.update(1L, new HashMap<>());\n        assertNotNull(result);\n    }\n\n    @Test\n    void testUpdateNotFound() {\n        when(dlqEventRepository.findById(99L)).thenReturn(Optional.empty());\n        Map<String, Object> result = dlqService.update(99L, new HashMap<>());\n        assertEquals(\"ERROR\", result.get(\"status\"));\n        verify(dlqEventRepository, never()).save(any());\n    }\n\n    @Test\n    void testUpdateException() {\n        when(dlqEventRepository.findById(anyLong())).thenThrow(new RuntimeException(\"DB error\"));\n        Map<String, Object> result = dlqService.update(1L, new HashMap<>());\n        assertEquals(\"ERROR\", result.get(\"status\"));\n        assertNotNull(result.get(\"message\"));\n    }\n\n    @Test\n    void testDeleteSuccess() {\n        Map<String, Object> result = dlqService.delete(1L);\n        assertNotNull(result);\n        assertEquals(\"SUCCESS\", result.get(\"status\"));\n        verify(dlqEventRepository, times(1)).deleteById(1L);\n    }\n\n    @Test\n    void testDeleteNotFound() {\n        when(dlqEventRepository.existsById(99L)).thenReturn(false);\n        Map<String, Object> result = dlqService.delete(99L);\n        assertEquals(\"ERROR\", result.get(\"status\"));\n    }\n\n    @Test\n    void testDeleteException() {\n        when(dlqEventRepository.existsById(anyLong())).thenThrow(new RuntimeException(\"DB error\"));\n        Map<String, Object> result = dlqService.delete(1L);\n        assertEquals(\"ERROR\", result.get(\"status\"));\n        assertNotNull(result.get(\"message\"));\n    }\n}",
            "_meta": {
              "methodsWithOrElseThrow": [
                "getById",
                "update"
              ],
              "methodsWithRetryLoop": [],
              "methodsWithGenericCatch": [
                "getById",
                "update",
                "delete"
              ],
              "maxRetries": 3
            }
          }
        ],
        "entities": [
          {
            "name": "DLQEvent",
            "fullCode": "package com.example.dlqservice.entity;\n\nimport jakarta.persistence.*;\nimport java.time.Instant;\n\n@Entity\n@Table(name = \"dlq_events\")\npublic class DLQEvent {\n    @Id\n    @GeneratedValue(strategy = GenerationType.IDENTITY)\n    private Long id;\n\n    @Column(nullable = false, unique = true)\n    private String eventId;\n\n    @Column(nullable = false)\n    private String eventType;\n\n    @Column(nullable = false)\n    private String orderId;\n\n    @Column(nullable = false)\n    private Integer retryCount;\n\n    @Column\n    private String reason;\n\n    @Lob\n    @Column\n    private String unavailableItems;\n\n    @Column(name = \"created_at\", nullable = false, updatable = false)\n    private Instant createdAt;\n\n    public DLQEvent() {}\n\n    public DLQEvent(String eventId, String eventType, String orderId, Integer retryCount, String reason) {\n        this.eventId = eventId;\n        this.eventType = eventType;\n        this.orderId = orderId;\n        this.retryCount = retryCount;\n        this.reason = reason;\n    }\n\n    @PrePersist\n    protected void onCreate() {\n        if (this.createdAt == null) {\n            this.createdAt = Instant.now();\n        }\n    }\n\n    public Long getId() {\n        return id;\n    }\n\n    public void setId(Long id) {\n        this.id = id;\n    }\n\n    public String getEventId() {\n        return eventId;\n    }\n\n    public void setEventId(String eventId) {\n        this.eventId = eventId;\n    }\n\n    public String getEventType() {\n        return eventType;\n    }\n\n    public void setEventType(String eventType) {\n        this.eventType = eventType;\n    }\n\n    public String getOrderId() {\n        return orderId;\n    }\n\n    public void setOrderId(String orderId) {\n        this.orderId = orderId;\n    }\n\n    public Integer getRetryCount() {\n        return retryCount;\n    }\n\n    public void setRetryCount(Integer retryCount) {\n        this.retryCount = retryCount;\n    }\n\n    public String getReason() {\n        return reason;\n    }\n\n    public void setReason(String reason) {\n        this.reason = reason;\n    }\n\n    public String getUnavailableItems() {\n        return unavailableItems;\n    }\n\n    public void setUnavailableItems(String unavailableItems) {\n        this.unavailableItems = unavailableItems;\n    }\n\n    public Instant getCreatedAt() {\n        return createdAt;\n    }\n\n    public void setCreatedAt(Instant createdAt) {\n        this.createdAt = createdAt;\n    }\n}"
          }
        ],
        "repositories": [
          {
            "name": "DLQEventRepository",
            "fullCode": "package com.example.dlqservice.repository;\n\nimport com.example.dlqservice.entity.DLQEvent;\nimport org.springframework.data.jpa.repository.JpaRepository;\n\npublic interface DLQEventRepository extends JpaRepository<DLQEvent, Long> {\n}"
          }
        ],
        "configs": [
          {
            "name": "AppConfig",
            "package": "config",
            "fullCode": "package com.example.dlqservice.config;\n\nimport org.springframework.context.annotation.Bean;\nimport org.springframework.context.annotation.Configuration;\nimport org.springframework.web.client.RestTemplate;\n\n@Configuration\npublic class AppConfig {\n    @Bean\n    public RestTemplate restTemplate() {\n        return new RestTemplate();\n    }\n}"
          },
          {
            "name": "BasicSecurityConfig",
            "package": "config",
            "fullCode": "package com.example.dlqservice.config;\n\nimport org.springframework.beans.factory.annotation.Value;\nimport org.springframework.context.annotation.Bean;\nimport org.springframework.context.annotation.Configuration;\nimport org.springframework.security.config.annotation.web.builders.HttpSecurity;\nimport org.springframework.security.config.annotation.web.configuration.EnableWebSecurity;\nimport org.springframework.security.core.userdetails.User;\nimport org.springframework.security.core.userdetails.UserDetails;\nimport org.springframework.security.core.userdetails.UserDetailsService;\nimport org.springframework.security.crypto.bcrypt.BCryptPasswordEncoder;\nimport org.springframework.security.crypto.password.PasswordEncoder;\nimport org.springframework.security.provisioning.InMemoryUserDetailsManager;\nimport org.springframework.security.web.SecurityFilterChain;\n\n@Configuration\n@EnableWebSecurity\npublic class BasicSecurityConfig {\n    @Value(\"${app.security.admin.username}\")\n    private String adminUsername;\n\n    @Value(\"${app.security.admin.password}\")\n    private String adminPassword;\n\n    @Value(\"${app.security.user.username}\")\n    private String userUsername;\n\n    @Value(\"${app.security.user.password}\")\n    private String userPassword;\n\n    @Bean\n    public SecurityFilterChain securityFilterChain(HttpSecurity http) throws Exception {\n        http.csrf(csrf -> csrf.disable())\n            .headers(h -> h.frameOptions(f -> f.disable()))\n            .authorizeHttpRequests(auth -> auth\n                .requestMatchers(\"/h2-console/**\", \"/swagger-ui/**\", \"/v3/**\", \"/actuator/**\").permitAll()\n                .anyRequest().authenticated())\n            .httpBasic(b -> {});\n        return http.build();\n    }\n\n    @Bean\n    public UserDetailsService userDetailsService() {\n        UserDetails admin = User.builder()\n            .username(adminUsername)\n            .password(passwordEncoder().encode(adminPassword))\n            .roles(\"ADMIN\")\n            .build();\n        UserDetails user = User.builder()\n            .username(userUsername)\n            .password(passwordEncoder().encode(userPassword))\n            .roles(\"USER\")\n            .build();\n        return new InMemoryUserDetailsManager(admin, user);\n    }\n\n    @Bean\n    public PasswordEncoder passwordEncoder() {\n        return new BCryptPasswordEncoder();\n    }\n}"
          }
        ],
        "applicationProperties": "spring.application.name=dlq-service\nserver.port=8085\nspring.datasource.url=jdbc:h2:mem:dlqservicedb\nspring.datasource.driverClassName=org.h2.Driver\nspring.datasource.username=sa\nspring.datasource.password=\nspring.jpa.database-platform=org.hibernate.dialect.H2Dialect\nspring.jpa.hibernate.ddl-auto=update\nspring.h2.console.enabled=true\napp.security.admin.username=admin\napp.security.admin.password=admin\napp.security.user.username=user\napp.security.user.password=user",
        "externalClients": []
      }
    ],
    "apiGateway": {
      "enabled": true,
      "port": 8080,
      "routes": [
        {
          "service": "order-service",
          "path": "/orders/**",
          "uri": "http://localhost:8081"
        },
        {
          "service": "payment-service",
          "path": "/payments/**",
          "uri": "http://localhost:8082"
        },
        {
          "service": "inventory-service",
          "path": "/inventory/**",
          "uri": "http://localhost:8083"
        },
        {
          "service": "reporting-service",
          "path": "/reports/**",
          "uri": "http://localhost:8084"
        },
        {
          "service": "dlq-service",
          "path": "/dlq/**",
          "uri": "http://localhost:8085"
        }
      ]
    }
  }
},
  // ─── Architectural review mocks (Mule) ──────────────────────────────────────
  // Step 1: initial review — set USE_MOCK = "arch_review_mule" to test
  arch_review_mule: {
    success: true,
    type: "architectural_review_pending",
    message: " **Why I designed it this way:**\nThe process-layer orchestrator handles scheduling, state management, and batching logic to coordinate the daily sync workflow, while the system-layer API isolates Salesforce-specific complexity (composite requests, retry logic, DLQ routing) so it can be reused or evolved independently. This separation lets the process layer focus on SharePoint-to-Salesforce choreography without embedding Salesforce details, and ensures the system API can absorb Salesforce API changes or enhance error handling without touching the orchestration logic. The batching decision (groups of five) lives in the process layer because it's about SharePoint workload distribution, while retry and DLQ handling stay in the system layer because they're Salesforce-specific resilience patterns.\n\n---\n\n## Proposed Architecture — Mule 4\n\n**APIs / Services (2):**\n1. **Salesforce System API** (system) — Handles all Salesforce interactions for document uploading and linking. Receives batches of five files from the Document Sync Process API, authenticates with Salesforce, queries ContentVersion by PathOnClient, creates or updates ContentVersion records, queries contentDocumentId, executes composite requests to link knowledge articles, create ContentDocumentLink, and create ContentWorkspaceDoc. Returns a detailed upload result batch and logs all operations to Splunk. Implements retry with exponential backoff and routes irrecoverable failures to the Dead Letter Queue.\n    Endpoints: POST /upload-batch, GET /health\n2. **Document Sync Process API** (process) — Orchestrates the daily SharePoint-to-Salesforce document synchronization. Triggered by a scheduler at 12:15 AM UTC, it retrieves the last run timestamp from MuleSoft Object Store, fetches modified or new files from SharePoint, validates file extensions in parallel batches of five, downloads valid files, updates the timestamp, forwards batches to the Salesforce System API, and asynchronously moves uploaded files to the Synced Folder in SharePoint.\n    Endpoints: POST /document-sync/trigger, GET /health\n\n**Detected Connectors / Dependencies:**\n• **http** (needs credentials)\n• **salesforce** (needs credentials)\n• **azure-key-vault**\n• **splunk**\n• **anypoint-mq**\n• **scheduler**\n• **object-store**\n• **sharepoint**\n\n---\nDoes this look right?\n• Reply **\"approve\"** to proceed to API selection.\n• Or describe what to change (e.g., \"swap SAP for Salesforce\", \"add a /users endpoint\").\n• Or type **\"skip\"** to accept this and continue.",
    tech: "mule",
    apis: [
      { index: 1, id: "svc-1", name: "Salesforce System API",      layer: "system",  description: "Handles all Salesforce interactions.", endpoints: ["POST /upload-batch", "GET /health"] },
      { index: 2, id: "svc-2", name: "Document Sync Process API",  layer: "process", description: "Orchestrates the daily SharePoint-to-Salesforce sync.", endpoints: ["POST /document-sync/trigger", "GET /health"] },
    ],
    detectedConnectors: ["http", "salesforce", "azure-key-vault", "splunk", "anypoint-mq", "scheduler", "object-store", "sharepoint"],
    round: 0,
    canSkip: true,
    skipHint: "Reply 'approve' or 'skip' to continue, or describe changes.",
    conversationTurn: 2,
  },
  // Step 2: after user requests a change — set USE_MOCK = "arch_review_mule_updated"
  arch_review_mule_updated: {
    success: true,
    type: "architectural_review_pending",
    message: " **Changes I just applied (1):**\n- Added POST /document-sync/abort to \"Document Sync Process API\"\n\n---\n\n## Proposed Architecture — Mule 4\n\n**APIs / Services (2):**\n1. **Salesforce System API** (system) — Handles all Salesforce interactions for document uploading and linking. Receives batches of five files from the Document Sync Process API, authenticates with Salesforce, queries ContentVersion by PathOnClient, creates or updates ContentVersion records, queries contentDocumentId, executes composite requests to link knowledge articles, create ContentDocumentLink, and create ContentWorkspaceDoc. Returns a detailed upload result batch and logs all operations to Splunk. Implements retry with exponential backoff and routes irrecoverable failures to the Dead Letter Queue.\n    Endpoints: POST /upload-batch, GET /health\n2. **Document Sync Process API** (process) — Orchestrates the daily SharePoint-to-Salesforce document synchronization. Triggered by a scheduler at 12:15 AM UTC, it retrieves the last run timestamp from MuleSoft Object Store, fetches modified or new files from SharePoint, validates file extensions in parallel batches of five, downloads valid files, updates the timestamp, forwards batches to the Salesforce System API, and asynchronously moves uploaded files to the Synced Folder in SharePoint.\n    Endpoints: POST /document-sync/trigger, POST /document-sync/abort, GET /health\n\n**Detected Connectors / Dependencies:**\n• **http** (needs credentials)\n• **salesforce** (needs credentials)\n• **azure-key-vault**\n• **splunk**\n• **anypoint-mq**\n• **scheduler**\n• **object-store**\n• **sharepoint**\n\n---\nDoes this look right?\n• Reply **\"approve\"** to proceed to API selection.\n• Or describe what to change.\n• Or type **\"skip\"** to accept this and continue.",
    tech: "mule",
    apis: [
      { index: 1, id: "svc-1", name: "Salesforce System API",      layer: "system",  description: "Handles all Salesforce interactions.", endpoints: ["POST /upload-batch", "GET /health"] },
      { index: 2, id: "svc-2", name: "Document Sync Process API",  layer: "process", description: "Orchestrates the daily SharePoint-to-Salesforce sync.", endpoints: ["POST /document-sync/trigger", "POST /document-sync/abort", "GET /health"] },
    ],
    detectedConnectors: ["http", "salesforce", "azure-key-vault", "splunk", "anypoint-mq", "scheduler", "object-store", "sharepoint"],
    round: 1,
    canSkip: true,
    skipHint: "Reply 'approve' or 'skip' to continue, or describe changes.",
    conversationTurn: 4,
  },
  arch_review_dotnet: {
    success: true,
    type: "architectural_review_pending",
    message: "**Why I designed it this way:**\nThe experience layer exposes the REST contract and handles cross-cutting concerns (auth, correlation IDs, rate limiting), while the system layer isolates SQL Server and Kafka specifics. This separation lets each layer evolve independently — swapping the broker or DB engine requires touching only one layer.\n\n---\n\n## Proposed Architecture — .NET 8 Microservices\n\n**Services (2):**\n1. **Insurance Experience API** (experience) — Exposes POST /policies and POST /claims. Validates requests, attaches X-Correlation-Id, delegates to the system layer via HttpClient with Polly retry.\n    Endpoints: POST /policies, POST /claims, GET /health\n2. **Insurance System API** (system) — Persists policies and claims to SQL Server via EF Core. Publishes domain events to Kafka. Implements idempotency via correlationId index. Routes dead-letter records on failure.\n    Endpoints: POST /system/policies, POST /system/claims, GET /health\n\n**Detected Dependencies:**\n• **EF Core** (SQL Server)\n• **Kafka** (MassTransit)\n• **Polly** (retry + circuit-breaker)\n• **Azure Key Vault**\n• **Serilog + Seq**\n\n---\nDoes this look right?\n• Reply **\"approve\"** to proceed to API selection.\n• Or describe what to change.\n• Or type **\"skip\"** to accept this and continue.",
    tech: "dotnet",
    apis: [
      { index: 1, id: "svc-1", name: "Insurance Experience API", layer: "experience", description: "REST entry point — auth, correlation, rate limiting.", endpoints: ["POST /policies", "POST /claims", "GET /health"] },
      { index: 2, id: "svc-2", name: "Insurance System API",     layer: "system",     description: "EF Core persistence + Kafka event publishing.",   endpoints: ["POST /system/policies", "POST /system/claims", "GET /health"] },
    ],
    detectedConnectors: ["EF Core", "Kafka", "Polly", "Azure Key Vault", "Serilog"],
    round: 0,
    canSkip: true,
    skipHint: "Reply 'approve' or 'skip' to continue, or describe changes.",
    conversationTurn: 2,
  },
  // ─── Error mock responses — set USE_MOCK to one of these keys to test retry ──
  error_quota:      { error: "LLM quota exceeded — please try again or extend your token limit." },
  error_limit:      { error: "Daily token limit reached. Click '+ Extend 1M Tokens' to continue." },
  error_design:     { error: "Design Agent failed: could not parse requirements. Please rephrase and try again." },
  error_generation: { error: "Integration generation failed: backend timeout. Please retry." },
  error_network:    { error: "Network error: unable to reach the backend. Check your connection and retry." },
  schema_elicitation:{
    "success": true,
    "type": "schema_elicitation_pending",
    "message": " Before generating, let me confirm the fields your APIs need:\n\n**Salesforce System API** (system layer):\n    - GET /accounts/{accountId}: Retrieve enriched Account details from Salesforce by account ID\n    - GET /contacts/{contactId}: Retrieve enriched Contact details from Salesforce by contact ID\n    - GET /health: Health check endpoint verifying Salesforce connectivity\n  • Specify fields for these endpoints (e.g. \"For /accounts/{accountId}, request has...\")\n\n**MSSQL System API** (system layer):\n    - POST /schools: Insert a new School record into the school table in MSSQL DB\n    - POST /travellers: Insert a new Traveller record into the traveller table in MSSQL DB\n    - POST /exception-logs: Insert an exception log entry into the ExceptionLogSchema (different schema, same MSSQL DB instance)\n    - GET /health: Health check verifying MSSQL DB connectivity on both schemas\n  • Specify fields for these endpoints (e.g. \"For /schools, request has...\")\n\n**Salesforce Legacy Integration Experience API** (experience layer):\n    - POST /accounts/events: Receive Salesforce Account creation event, enrich via Salesforce System API, invoke createSchool SOAP operation on Legacy SOAP Webservice\n    - POST /contacts/events: Receive Salesforce Contact creation event, enrich via Salesforce System API, invoke createTraveller SOAP operation on Legacy SOAP Webservice\n    - GET /health: Health check for the experience API — static UP response\n  • Specify fields for these endpoints (e.g. \"For /accounts/events, request has...\")\n\n---\nDescribe your fields in natural language. For example:\n*\"For Salesforce System API: request has orderId (string), amount (number), lineItems (array). Response has orderId, status, totalAmount.\"\n\nOr type **\"skip\"** to let the AI decide based on your diagram.*",
    "apis": [
        {
            "id": "api-1",
            "name": "Salesforce System API",
            "layer": "system",
            "endpoints": [
                {
                    "method": "GET",
                    "path": "/accounts/{accountId}",
                    "description": "Retrieve enriched Account details from Salesforce by account ID",
                    "requestFields": "accountId (String, required, path parameter)",
                    "responseFields": "accountId (String), accountName (String), billingAddress (String), shippingAddress (String), phone (String), website (String), industry (String), annualRevenue (Number), numberOfEmployees (Number), ownerId (String), createdDate (DateTime), lastModifiedDate (DateTime), rawPayload (Object)",
                    "auth": {
                        "type": "bearer",
                        "headerName": "Authorization"
                    }
                },
                {
                    "method": "GET",
                    "path": "/contacts/{contactId}",
                    "description": "Retrieve enriched Contact details from Salesforce by contact ID",
                    "requestFields": "contactId (String, required, path parameter)",
                    "responseFields": "contactId (String), firstName (String), lastName (String), email (String), phone (String), mobilePhone (String), mailingAddress (String), accountId (String), title (String), department (String), ownerId (String), createdDate (DateTime), lastModifiedDate (DateTime), rawPayload (Object)",
                    "auth": {
                        "type": "bearer",
                        "headerName": "Authorization"
                    }
                },
                {
                    "method": "GET",
                    "path": "/health",
                    "description": "Health check endpoint verifying Salesforce connectivity",
                    "requestFields": "none",
                    "responseFields": "status (String), timestamp (DateTime)",
                    "auth": {
                        "type": "none",
                        "headerName": ""
                    }
                }
            ]
        },
        {
            "id": "api-2",
            "name": "MSSQL System API",
            "layer": "system",
            "endpoints": [
                {
                    "method": "POST",
                    "path": "/schools",
                    "description": "Insert a new School record into the school table in MSSQL DB",
                    "requestFields": "schoolName (String, required), address (String, required), city (String, optional), state (String, optional), postalCode (String, optional), country (String, optional), phone (String, optional), email (String, optional), accountId (String, required), createdDate (DateTime, optional)",
                    "responseFields": "schoolId (String), schoolName (String), status (String), createdDate (DateTime)",
                    "auth": {
                        "type": "bearer",
                        "headerName": "Authorization"
                    }
                },
                {
                    "method": "POST",
                    "path": "/travellers",
                    "description": "Insert a new Traveller record into the traveller table in MSSQL DB",
                    "requestFields": "firstName (String, required), lastName (String, required), email (String, required), phone (String, optional), mobilePhone (String, optional), mailingAddress (String, optional), contactId (String, required), accountId (String, optional), createdDate (DateTime, optional)",
                    "responseFields": "travellerId (String), firstName (String), lastName (String), status (String), createdDate (DateTime)",
                    "auth": {
                        "type": "bearer",
                        "headerName": "Authorization"
                    }
                },
                {
                    "method": "POST",
                    "path": "/exception-logs",
                    "description": "Insert an exception log entry into the ExceptionLogSchema (different schema, same MSSQL DB instance)",
                    "requestFields": "flowName (String, required), errorCode (String, required), errorMessage (String, required), errorType (String, required), payload (String, optional), correlationId (String, required), timestamp (DateTime, required), sourceSystem (String, required)",
                    "responseFields": "logId (String), status (String), timestamp (DateTime)",
                    "auth": {
                        "type": "bearer",
                        "headerName": "Authorization"
                    }
                },
                {
                    "method": "GET",
                    "path": "/health",
                    "description": "Health check verifying MSSQL DB connectivity on both schemas",
                    "requestFields": "none",
                    "responseFields": "status (String), timestamp (DateTime)",
                    "auth": {
                        "type": "none",
                        "headerName": ""
                    }
                }
            ]
        },
        {
            "id": "api-3",
            "name": "Salesforce Legacy Integration Experience API",
            "layer": "experience",
            "endpoints": [
                {
                    "method": "POST",
                    "path": "/accounts/events",
                    "description": "Receive Salesforce Account creation event, enrich via Salesforce System API, invoke createSchool SOAP operation on Legacy SOAP Webservice",
                    "requestFields": "eventId (String, required), eventType (String, required, value='AccountCreated'), accountId (String, required), accountName (String, required), billingAddress (String, optional), shippingAddress (String, optional), phone (String, optional), industry (String, optional), createdDate (DateTime, required), replayId (Number, optional)",
                    "responseFields": "status (String), correlationId (String), schoolId (String), message (String), timestamp (DateTime)",
                    "auth": {
                        "type": "bearer",
                        "headerName": "Authorization"
                    }
                },
                {
                    "method": "POST",
                    "path": "/contacts/events",
                    "description": "Receive Salesforce Contact creation event, enrich via Salesforce System API, invoke createTraveller SOAP operation on Legacy SOAP Webservice",
                    "requestFields": "eventId (String, required), eventType (String, required, value='ContactCreated'), contactId (String, required), firstName (String, required), lastName (String, required), email (String, required), phone (String, optional), accountId (String, optional), createdDate (DateTime, required), replayId (Number, optional)",
                    "responseFields": "status (String), correlationId (String), travellerId (String), message (String), timestamp (DateTime)",
                    "auth": {
                        "type": "bearer",
                        "headerName": "Authorization"
                    }
                },
                {
                    "method": "GET",
                    "path": "/health",
                    "description": "Health check for the experience API — static UP response",
                    "requestFields": "none",
                    "responseFields": "status (String), timestamp (DateTime)",
                    "auth": {
                        "type": "none",
                        "headerName": ""
                    }
                }
            ]
        }
    ],
    "canSkip": true,
    "skipHint": "Type 'skip' to let the AI decide",
    "conversationTurn": 2
},
  dotnet: {
    success: true,
    type: "agent",
    agentUsed: "dotnet",
    agentName: ".NET Integration Agent",
    routingReason: "schema_answer",
    message: "Got it! Generating your APIs with your specified fields:\n• **InsuranceMicroservice**: 4 request field(s), 3 response field(s)\n• **InsuranceMicroservice**: 3 request field(s), 2 response field(s)\n\nThis may take a few minutes...",
    conversationTurn: 5,
    validationReport: { status: "PASSED", blockers: [] },
    projectName: "InsuranceSystem",
    description: "A microservice handling insurance policy and claims management with idempotent processing, retry-and-dead-letter-queue error handling, and REST endpoints deployed on Kubernetes.",
    summary: "Single Insurance microservice exposing POST /policies and POST /claims with idempotency via correlationId, exponential backoff retry, Kafka DLQ routing, and EF Core persistence on SQL Server. Deployed on Kubernetes.",
    projects: [
      {
        name: "Insurance",
        path: "C:\\Intelog\\intelog-be\\uploads\\Insurance",
        files: [
          "Insurance\\src\\Insurance.Domain\\Entities\\Policy.cs",
          "Insurance\\src\\Insurance.Domain\\Entities\\Claim.cs",
          "Insurance\\src\\Insurance.Domain\\Entities\\CorrelationRecord.cs",
          "Insurance\\src\\Insurance.Application\\DTOs\\CreatePolicyRequest.cs",
          "Insurance\\src\\Insurance.Application\\DTOs\\CreatePolicyResponse.cs",
          "Insurance\\src\\Insurance.Application\\DTOs\\CreateClaimRequest.cs",
          "Insurance\\src\\Insurance.Application\\DTOs\\CreateClaimResponse.cs",
          "Insurance\\src\\Insurance.Application\\DTOs\\DlqMessageRequest.cs",
          "Insurance\\src\\Insurance.Application\\Services\\IPolicyService.cs",
          "Insurance\\src\\Insurance.Application\\Services\\PolicyService.cs",
          "Insurance\\src\\Insurance.Application\\Services\\IClaimService.cs",
          "Insurance\\src\\Insurance.Application\\Services\\ClaimService.cs",
          "Insurance\\src\\Insurance.Application\\Services\\IIdempotencyService.cs",
          "Insurance\\src\\Insurance.Application\\Services\\IdempotencyService.cs",
          "Insurance\\src\\Insurance.Application\\Services\\IKafkaProducerService.cs",
          "Insurance\\src\\Insurance.Application\\Services\\KafkaProducerService.cs",
          "Insurance\\src\\Insurance.Application\\Repositories\\IPolicyRepository.cs",
          "Insurance\\src\\Insurance.Application\\Repositories\\IClaimRepository.cs",
          "Insurance\\src\\Insurance.Application\\Repositories\\ICorrelationRecordRepository.cs",
          "Insurance\\src\\Insurance.Application\\Common\\Result.cs",
          "Insurance\\src\\Insurance.Application\\Common\\Exceptions\\DomainExceptions.cs",
          "Insurance\\src\\Insurance.Application\\ApplicationAssembly.cs",
          "Insurance\\src\\Insurance.Application\\Validators\\CreatePolicyRequestValidator.cs",
          "Insurance\\src\\Insurance.Application\\Validators\\CreateClaimRequestValidator.cs",
          "Insurance\\src\\Insurance.Application\\Validators\\DlqMessageRequestValidator.cs",
          "Insurance\\src\\Insurance.Infrastructure\\Data\\InsuranceDbContext.cs",
          "Insurance\\src\\Insurance.Infrastructure\\Repositories\\PolicyRepository.cs",
          "Insurance\\src\\Insurance.Infrastructure\\Repositories\\ClaimRepository.cs",
          "Insurance\\src\\Insurance.Infrastructure\\Repositories\\CorrelationRecordRepository.cs",
          "Insurance\\src\\Insurance.API\\Program.cs",
          "Insurance\\src\\Insurance.API\\Middleware\\ExceptionMiddleware.cs",
          "Insurance\\src\\Insurance.API\\appsettings.json",
          "Insurance\\src\\Insurance.API\\appsettings.Development.json",
          "Insurance\\src\\Insurance.API\\appsettings.Staging.json",
          "Insurance\\src\\Insurance.API\\appsettings.Production.json",
          "Insurance\\src\\Insurance.API\\Controllers\\PoliciesController.cs",
          "Insurance\\src\\Insurance.API\\Controllers\\ClaimsController.cs",
          "Insurance\\tests\\Insurance.UnitTests\\Services\\PolicyServiceTests.cs",
          "Insurance\\tests\\Insurance.UnitTests\\Services\\ClaimServiceTests.cs",
          "Insurance\\tests\\Insurance.UnitTests\\Services\\IdempotencyServiceTests.cs",
          "Insurance\\tests\\Insurance.UnitTests\\Services\\KafkaProducerServiceTests.cs",
          "Insurance\\tests\\Insurance.IntegrationTests\\Controllers\\PoliciesControllerTests.cs",
          "Insurance\\tests\\Insurance.IntegrationTests\\Controllers\\ClaimsControllerTests.cs",
          "Insurance\\docker\\Dockerfile",
          "Insurance\\docker\\docker-compose.yml",
          "Insurance\\k8s\\namespace.yaml",
          "Insurance\\k8s\\configmap.yaml",
          "Insurance\\k8s\\secret.yaml",
          "Insurance\\k8s\\deployment.yaml",
          "Insurance\\k8s\\service.yaml",
          "Insurance\\k8s\\ingress.yaml",
          "Insurance\\k8s\\hpa.yaml",
          "Insurance\\README.md",
          "Insurance\\Insurance.sln",
          "Insurance\\azure-pipelines.yml",
        ],
        layers: {
          domain:             "Insurance\\src\\Insurance.Domain",
          application:        "Insurance\\src\\Insurance.Application",
          infrastructure:     "Insurance\\src\\Insurance.Infrastructure",
          api:                "Insurance\\src\\Insurance.API",
          unitTests:          "Insurance\\tests\\Insurance.UnitTests",
          integrationTests:   "Insurance\\tests\\Insurance.IntegrationTests",
          docker:             "Insurance\\docker",
        },
      },
    ],
    projectPaths: ["C:\\Intelog\\intelog-be\\uploads\\Insurance"],
    tokenUsage: { inputTokens: 18023, outputTokens: 12172, cacheReadTokens: 32628, cacheWriteTokens: 8899, totalTokens: 30195 },
    metrics: { totalTime: 116008, totalFiles: 55, microserviceCount: 1 },
  },
  existing_api_upload: {
    success: true,
    type: "existing_api_upload_pending",
    message: "I see you already have an existing API layer. Please upload your JAR/ZIP or provide a GitHub repo so I can reference it during generation.",
    uploadEndpoint: "/api/v1/hub/upload-existing-api",
    accepts: ["jar", "github"],
    canSkip: true,
    skipHint: "generate without it",
    declaredLayers: ["System API"],
  },
};

// ─── Constants ────────────────────────────────────────────────────
// Pipeline stages shown as a read-only progress stepper (NOT selectable agents).
const AGENTS = [
  { key: "design",      icon: "🎨", label: "Design",      sub: "PlantUML · RAML · Diagrams" },
  { key: "integration", icon: "⚡", label: "Integration", sub: "Mule flows · XML · Deploy"  },
  { key: "deployment",  icon: "🚀", label: "Deployment",  sub: "CloudHub · RTF · GitHub"    },
];

const HEALTH = [
  { icon: "🖥️", label: "CPU",     value: "24%",    cls: "sh-hc-blue"   },
  { icon: "💾", label: "Memory",  value: "1.2 GB", cls: "sh-hc-purple" },
  { icon: "📡", label: "Latency", value: "45ms",   cls: "sh-hc-green"  },
  { icon: "❌", label: "Errors",  value: "0",      cls: "sh-hc-red"    },
];

const ALL_PROMPTS = [
  "Create a sequence diagram to show the integration between a legacy system (MSSQL DB and Soap Webservices exposed on top of this) and salesforce. Whenever an account has created in salesforce the account details has to go and get created in the legacy system via WS and the legacy system is a database and this has a webservice, entity called school get created via this webservice. API we build (experience api) will be listening for account creation event in Salesforce. This then enriches the details by calling a system api of salesforce (Wrapper API built on top of salesforce using Salesforce connector). The original experience api then calls the legacy web service and creates the school entity. This will have attributes like school name , country name etc. and similarly whenever a contact gets created in salesforce that information has to go to the legacy database via the same webservice (different operation) and create a traveller in the same database, Exception has to be logged in a different schema of the same database. The flow is same, experience api calls salesforce system api and enriches the information and then calls the legacy webservice for traveller creation.",
  "Create a Spring boot application with two endpoints 1) Call Status and Call History .Both would have a customer id extracted from JWT token and sent to Azure External functions using webclient after authenticating with Oauth with a seperate authentication server.The project should have a custom error messaging via global exception handler and unit test cases with 85 percent coverage",
  "Design an integration where Shopify sends order creation events via webhook. MuleAPI receives the event, validates order data, publishes it to Kafka, and multiple downstream systems (ERP and Analytics) consume it. If validation fails, send the event to a DLQ and notify support.",
  "Create a batch integration that extracts product data from SAP every night at 2 AM, transforms it into a canonical format, and loads it into a data warehouse. Handle partial failures by retrying failed records and generating a reconciliation report.",
  "Create a payment processing flow where duplicate payment requests may occur. API should ensure idempotency before calling the Payment Gateway. Successful transactions are stored in FinanceDB, and duplicates should be ignored with proper logging.",
];

const INIT_MESSAGE = {
  role: "bot", time: "",
  content: "intelog AI ready. I can help you design, integrate, and deploy. Describe what you want to build.",
};

const addLog = (setter, level, msg) =>
  setter(prev => [...prev, {
    ts: new Date().toLocaleTimeString("en-US", { hour12: false, hour: "2-digit", minute: "2-digit", second: "2-digit" }),
    level, msg,
  }]);

// Make a filesystem-safe fragment from a flow title.
const _slug = (s) => String(s || "flow").trim().replace(/[^A-Za-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 60) || "flow";

// Download the design diagram and its per-flow split PUMLs as a separate zip
// (<baseName>-diagrams.zip): the full <baseName>.puml plus one
// diagrams/NN-<title>.puml per "== Flow ==" section. This is deliberately NOT
// part of "Download All" (which is the generated project files only).
const downloadSplitPumls = async (code, baseName = "flow-diagram") => {
  if (!code) return;
  const name = String(baseName).replace(/\.puml$/i, "") || "flow-diagram";
  const zip = new JSZip();
  zip.file(`${name}.puml`, code);
  if (hasFlowSections(code)) {
    splitPlantumlByFlow(code).forEach((s, i) => {
      const n = String(i + 1).padStart(2, "0");
      zip.file(`diagrams/${n}-${_slug(s.title)}.puml`, s.plantuml);
    });
  }
  const blob = await zip.generateAsync({ type: "blob" });
  const a = Object.assign(document.createElement("a"), {
    href: URL.createObjectURL(blob), download: `${name}-diagrams.zip`,
  });
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  URL.revokeObjectURL(a.href);
};

const now = () => new Date().toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: true });

const layerIcon = { experience: "🌐", process: "⚙️", system: "💾" };

const getDeployErrorHint = (summary, deployType) => {
  const s = (summary || "").toLowerCase();
  if (/401/.test(s)) {
    if (/github/.test(s))   return "GitHub authentication failed — check your GitHub PAT in Connected Apps.";
    if (/gitlab/.test(s))   return "GitLab authentication failed — check your GitLab PAT in Connected Apps.";
    if (/bitbucket/.test(s)) return "Bitbucket authentication failed — check your Bitbucket credentials in Connected Apps.";
    if (/azure/.test(s))    return "Azure authentication failed — check your Azure Client Id, Service Principal Secret, and Tenant Id in Connected Apps.";
    if (/aws/.test(s))      return "AWS authentication failed — check your AWS Access Key and Secret in Connected Apps.";
    if (/jenkins/.test(s))  return "Jenkins authentication failed — check your Jenkins username and token in Connected Apps.";
    if (/sonar/.test(s))    return "SonarQube authentication failed — check your SonarQube token in Connected Apps.";
    if (deployType === "mule") return "Anypoint authentication failed — please verify your credentials in Connected Apps:\n• Business Group ClientId & Secret (Access Management → Business Groups → Settings)\n• ConnectedApp ClientId & Secret (Access Management → Connected Apps)";
    return "Authentication failed — please verify your credentials in Connected Apps settings.";
  }
  if (/403/.test(s)) {
    if (deployType === "mule") return "Forbidden — your Connected App may be missing required scopes: Design Center, Exchange Viewer, Runtime Manager.";
    return "Forbidden — check that your account has the required permissions.";
  }
  if (/404/.test(s)) {
    if (deployType === "mule") return "Resource not found — verify the environment name and org name in your Mule settings.";
    return "Resource not found — check that the target resource exists and the configuration is correct.";
  }
  return null;
};

const METHOD_COLOR = {
  GET:    { color: "#49cc90", bg: "rgba(73,204,144,0.15)"  },
  POST:   { color: "#61affe", bg: "rgba(97,175,254,0.15)"  },
  PUT:    { color: "#fca130", bg: "rgba(252,161,48,0.15)"  },
  PATCH:  { color: "#50e3c2", bg: "rgba(80,227,194,0.15)"  },
  DELETE: { color: "#f93e3e", bg: "rgba(249,62,62,0.15)"   },
};

// Questions may arrive as plain strings or as objects ({ id, question, hint, dimension }).
// Never render the object directly (React error #31) — pull the text/hint out.
const qText = (q) => (q && typeof q === "object") ? (q.question || q.text || q.prompt || "") : (q ?? "");
const qHint = (q) => (q && typeof q === "object") ? (q.hint || "") : "";

function ValidationCard({ validation, onSubmit }) {
  const { questions = [], gaps = [] } = validation;
  const [showGaps,      setShowGaps]      = React.useState(false);
  const [currentIdx,    setCurrentIdx]    = React.useState(0);
  const [answers,       setAnswers]       = React.useState([]);
  const [inputVal,      setInputVal]      = React.useState("");
  const [submitted,     setSubmitted]     = React.useState(false);

  const total = questions.length;
  const isLast = currentIdx === total - 1;


  const advance = (answer) => {
    const newAnswers = [...answers, answer];
    setAnswers(newAnswers);
    setInputVal("");
    if (isLast) {
      setSubmitted(true);
      const combined = newAnswers.map((ans, i) =>
        `Q${i + 1}: ${qText(questions[i])}\nA: ${ans}`
      ).join("\n\n");
      const qaItems = newAnswers.map((ans, i) => ({ q: qText(questions[i]), a: ans }));
      onSubmit(combined, qaItems);
    } else {
      setCurrentIdx(i => i + 1);
    }
  };

  const handleNext = () => {
    const trimmed = inputVal.trim();
    if (!trimmed) return;
    advance(trimmed);
  };

  const handleBack = () => {
    if (currentIdx === 0) return;
    const prevAnswers = answers.slice(0, -1);
    setInputVal(answers[answers.length - 1]); // restore previous answer for editing
    setAnswers(prevAnswers);
    setCurrentIdx(i => i - 1);
  };

  if (submitted) return null;

  const progress = (currentIdx / total) * 100;
  const currentQ = questions[currentIdx];

  return (
    <div className="sh-validation-card">
      {/* Gaps — collapsible */}
      {gaps.length > 0 && (
        <div className="sh-validation-gaps">
          <button className="sh-validation-gaps-toggle" onClick={() => setShowGaps(p => !p)}>
            <span className="sh-validation-gaps-icon">⚠</span>
            <span>Gaps identified</span>
            <span className="sh-validation-chevron">{showGaps ? "▾" : "▸"}</span>
          </button>
          {showGaps && (
            <ul className="sh-validation-gaps-list">
              {gaps.map((g, i) => <li key={i}>{g}</li>)}
            </ul>
          )}
        </div>
      )}

      {/* Progress bar */}
      <div className="sh-validation-progress-bar">
        <div className="sh-validation-progress-fill" style={{ width: `${progress}%` }} />
      </div>

      {/* Question */}
      <div className="sh-validation-wizard">
        <div className="sh-validation-wizard-meta">
          <span className="sh-validation-wizard-step">Question {currentIdx + 1} of {total}</span>
        </div>
        <div className="sh-validation-wizard-q">{qText(currentQ)}</div>
        {qHint(currentQ) && <div className="sh-validation-wizard-hint" style={{ fontSize: 12, color: "#7a93bc", marginTop: 4 }}>{qHint(currentQ)}</div>}

        <>
          <textarea
            className="sh-validation-answer-input"
            placeholder="Type your answer…"
            value={inputVal}
            rows={3}
            onChange={e => setInputVal(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleNext(); } }}
            autoFocus
          />
          <div className="sh-validation-wizard-actions">
            <button
              className="sh-validation-back-btn"
              onClick={handleBack}
              disabled={currentIdx === 0}
            >
              ← Back
            </button>
            <span className="sh-validation-hint">Enter to {isLast ? "submit" : "next"} · Shift+Enter for new line</span>
            <button className="sh-validation-next-btn" onClick={handleNext} disabled={!inputVal.trim()}>
              {isLast ? "Submit All ↑" : "Next →"}
            </button>
          </div>
        </>
      </div>
    </div>
  );
}

// ─── Guided product tour ─────────────────────────────────────────
const TOUR_STEPS = [
  { refKey: 'inputWrap',  position: 'above',  title: 'Write your prompt here',   body: "As a developer or architect, describe the integration requirement in natural language with full context: source system, target system, data flow, logging/monitoring tools, and all error scenarios (retries, failures, exceptions). Include all expected behaviours to enable accurate design from the first prompt." },
  { refKey: 'expandBtn',  position: 'above',  title: 'Expanded editor',          body: 'Click this icon to open a full-size text editor — ideal for longer or more detailed prompts.' },
  { refKey: 'examples',   position: 'above',  title: 'Need inspiration?',        body: 'Browse pre-built example prompts to get started quickly.' },
  { refKey: 'logs',       position: 'left',   title: 'Live Logs',                body: 'Watch real-time AI progress here — every step from design to code generation.' },
  { refKey: 'newSession', position: 'below',  title: 'New Session',              body: 'Start a fresh conversation' },
  { refKey: 'backBtn',    position: 'right',  title: 'Go Back',                  body: 'Return to the main dashboard or previous view.' },
  { refKey: 'tabs',       position: 'below',  title: 'Choose your mode',         body: 'New Integrations — design a brand-new integration from scratch. Enhance Existing — analyse and improve an existing codebase.' },
  { refKey: 'artifacts',  position: 'left',   title: 'Right Panel',              body: 'Live Logs show real-time AI progress. Token Usage tracks your daily usage.' },
];

const TOUR_STEPS_BROWNFIELD = [
  { refKey: 'tabs',       position: 'below', title: 'Enhance Existing',  body: 'Analyse your existing codebase against compliance standards. Connect a Confluence space, upload a document, or use the Intelog built-in standard to surface violations.' },
  { refKey: 'logs',       position: 'left',  title: 'Live Logs',         body: 'Watch real-time progress as your repository is scanned — every file checked is logged here.' },
  { refKey: 'artifacts',  position: 'left',  title: 'Right Panel',       body: 'Live Logs show scan progress. Token Usage tracks how many tokens the analysis has consumed today.' },
  { refKey: 'newSession', position: 'below', title: 'New Session',       body: 'Start a fresh analysis — clears the current scan and lets you connect a new repository or standard.' },
  { refKey: 'backBtn',    position: 'right', title: 'Go Back',           body: 'Return to the main dashboard or previous view.' },
];

const TOUR_REFS_MAP = (refs) => ({
  inputWrap:  refs.inputWrapRef,
  expandBtn:  refs.expandBtnRef,
  examples:   refs.examplesRef,
  logs:       refs.logsRef,
  newSession: refs.newSessionRef,
  backBtn:    refs.backBtnRef,
  tabs:       refs.tabsRef,
  artifacts:  refs.artifactsRef,
});

function TourSpotlight({ step, steps, refs, onNext, onClose }) {
  const [rect, setRect] = React.useState(null);
  const activeSteps = steps || TOUR_STEPS;
  const totalSteps = activeSteps.length;
  const stepData   = activeSteps[step - 1];
  const targetRef  = stepData ? TOUR_REFS_MAP(refs)[stepData.refKey] : null;

  React.useEffect(() => {
    let raf;
    const measure = () => {
      const el = targetRef?.current;
      if (!el) { onNext(); return; }
      const r = el.getBoundingClientRect();
      // Skip if element has no size yet (not rendered)
      if (r.width === 0 && r.height === 0) { onNext(); return; }
      setRect({ top: r.top - 8, left: r.left - 8, width: r.width + 16, height: r.height + 16 });
    };
    // Wait two animation frames so refs are fully attached and laid out
    raf = requestAnimationFrame(() => requestAnimationFrame(measure));
    window.addEventListener('resize', measure);
    return () => { cancelAnimationFrame(raf); window.removeEventListener('resize', measure); };
  }, [step, targetRef]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!rect || !stepData) return null;

  const GAP = 16;
  const midX = rect.left + rect.width / 2;
  const midY = rect.top  + rect.height / 2;
  const pos  = stepData.position;

  let tooltipStyle = { position: 'fixed', zIndex: 9002, maxWidth: 300 };
  // Arrow: a small triangle div pointing FROM tooltip TOWARD the highlighted element
  const arrowBase = { position: 'absolute', width: 0, height: 0, border: '8px solid transparent' };
  let arrowStyle  = {};

  if (pos === 'below') {
    tooltipStyle = { ...tooltipStyle, top: rect.top + rect.height + GAP, left: Math.max(8, midX - 150) };
    arrowStyle   = { ...arrowBase, top: -16, left: Math.min(140, Math.max(16, midX - Math.max(8, midX - 150))), borderBottomColor: 'rgba(59,127,255,0.4)', borderTop: 'none' };
  } else if (pos === 'above') {
    tooltipStyle = { ...tooltipStyle, bottom: window.innerHeight - rect.top + GAP, left: Math.max(8, midX - 150) };
    arrowStyle   = { ...arrowBase, bottom: -16, left: Math.min(140, Math.max(16, midX - Math.max(8, midX - 150))), borderTopColor: 'rgba(59,127,255,0.4)', borderBottom: 'none' };
  } else if (pos === 'left') {
    tooltipStyle = { ...tooltipStyle, top: Math.max(8, midY - 80), right: window.innerWidth - rect.left + GAP };
    arrowStyle   = { ...arrowBase, top: 36, right: -16, borderLeftColor: 'rgba(59,127,255,0.4)', borderRight: 'none' };
  } else { // right
    tooltipStyle = { ...tooltipStyle, top: Math.max(8, midY - 80), left: rect.left + rect.width + GAP };
    arrowStyle   = { ...arrowBase, top: 36, left: -16, borderRightColor: 'rgba(59,127,255,0.4)', borderLeft: 'none' };
  }

  const isLast = step === totalSteps;

  return (
    <>
      {/* Spotlight highlight */}
      <div className="sh-tour-highlight" style={{ top: rect.top, left: rect.left, width: rect.width, height: rect.height }} />
      {/* Tooltip */}
      <div className="sh-tour-tooltip" style={{ ...tooltipStyle, position: 'fixed' }}>
        <div style={arrowStyle} />
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
          <span className="sh-tour-step">{step} / {totalSteps}</span>
          <button className="sh-tour-close" onClick={onClose}>✕</button>
        </div>
        <div className="sh-tour-title">{stepData.title}</div>
        <div className="sh-tour-body">{stepData.body}</div>
        <div className="sh-tour-actions">
          <button className="sh-tour-skip" onClick={onClose}>Skip tour</button>
          <button className="sh-tour-next-btn" onClick={onNext}>
            {isLast ? 'Done ✓' : 'Next →'}
          </button>
        </div>
      </div>
    </>
  );
}

function SwaggerApiCard({ apis }) {
  const [openGroup, setOpenGroup] = React.useState({});

  // Group flat array by `api` name
  const groups = React.useMemo(() => {
    const map = {};
    (apis || []).forEach(ep => {
      if (!map[ep.api]) map[ep.api] = [];
      map[ep.api].push(ep);
    });
    return map;
  }, [apis]);

  const groupNames = Object.keys(groups);

  const toggleGroup = (name) => setOpenGroup(p => ({ ...p, [name]: !p[name] }));

  return (
    <div className="sh-swagger-card">
      <div className="sh-swagger-header">📄 API Specification</div>
      {groupNames.map((apiName) => {
        const endpoints = groups[apiName];
        const isOpen = !!openGroup[apiName];
        return (
          <div key={apiName} className="sh-swagger-group">
            <div className="sh-swagger-group-title" onClick={() => toggleGroup(apiName)}>
              <span className="sh-swagger-chevron">{isOpen ? "▾" : "▸"}</span>
              <span className="sh-swagger-group-name">{apiName}</span>
              <span className="sh-swagger-group-count">{endpoints.length} endpoint{endpoints.length !== 1 ? "s" : ""}</span>
            </div>

            {isOpen && endpoints.map((ep, ei) => (
              <div key={ei} className="sh-swagger-endpoint">
                <div className="sh-swagger-endpoint-row">
                  <span className="sh-swagger-method" style={{ background: (METHOD_COLOR[ep.method] || {}).bg || "#aaa" }}>{ep.method}</span>
                  <span className="sh-swagger-path">{ep.endpoint}</span>
                  <span className="sh-swagger-desc">{ep.description}</span>
                </div>
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
}

// ─── Lightweight markdown renderer for bot messages ──────────────
function renderMdText(text) {
  if (!text) return null;
  return text.split('\n').map((line, i) => {
    // Horizontal rule
    if (/^---+$/.test(line.trim())) {
      return <hr key={i} style={{ border: 'none', borderTop: '1px solid rgba(255,255,255,0.08)', margin: '8px 0' }} />;
    }
    // H2 heading
    if (line.startsWith('## ')) {
      const content = line.slice(3);
      return <div key={i} style={{ fontSize: 15, fontWeight: 700, color: '#dce8ff', marginTop: 10, marginBottom: 4 }}>{parseBold(content)}</div>;
    }
    // H3 heading
    if (line.startsWith('### ')) {
      const content = line.slice(4);
      return <div key={i} style={{ fontSize: 13, fontWeight: 700, color: '#aac0dc', marginTop: 8, marginBottom: 2 }}>{parseBold(content)}</div>;
    }
    // Bullet point (•, -, *)
    if (/^[•\-\*]\s/.test(line.trim())) {
      const content = line.trim().slice(2);
      return <div key={i} style={{ fontSize: 12, color: '#c0cede', lineHeight: 1.6, paddingLeft: 14, position: 'relative' }}>
        <span style={{ position: 'absolute', left: 0, color: '#3b7fff' }}>•</span>{parseBold(content)}
      </div>;
    }
    // Numbered list
    const numMatch = line.match(/^(\d+)\.\s+(.*)/);
    if (numMatch) {
      return <div key={i} style={{ fontSize: 12, color: '#c0cede', lineHeight: 1.6, paddingLeft: 18, position: 'relative', marginTop: 6 }}>
        <span style={{ position: 'absolute', left: 0, color: '#3b7fff', fontWeight: 700 }}>{numMatch[1]}.</span>{parseBold(numMatch[2])}
      </div>;
    }
    // Empty line → spacing
    if (!line.trim()) return <div key={i} style={{ height: 6 }} />;
    // Normal line — give numbered-option lines (incl. bold-wrapped "**1. …") breathing room
    // so any numbered-choice message renders spaced, regardless of wording.
    const isNumberedOption = /^\s*(?:\*\*)?\d+[.)]\s/.test(line);
    return <div key={i} style={{ fontSize: 12, color: '#c0cede', lineHeight: 1.7, marginTop: isNumberedOption ? 8 : 0 }}>{parseBold(line)}</div>;
  });
}

function parseBold(text) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) =>
    p.startsWith('**') && p.endsWith('**')
      ? <strong key={i} style={{ color: '#dce8ff', fontWeight: 700 }}>{p.slice(2, -2)}</strong>
      : p
  );
}

var DEPLOY_STAGE_MAP = {
  mule:       ['Anypoint Exchange', 'GitHub', 'Azure Pipeline'],
  springboot: ['GitHub', 'Azure Pipeline'],
  dotnet:     ['GitHub', 'Azure Pipeline'],
};

var TOKENS_PER_SERVICE_ESTIMATE = 150000;

// ─── Deployment Review Card ──────────────────────────────────────
function DeploymentReviewCard({ deploymentCard, onAction, loading }) {
  const techLabel = deploymentCard.tech === 'mule' ? 'MuleSoft'
                  : deploymentCard.tech === 'dotnet' ? '.NET' : 'Spring Boot';
  const isMule = deploymentCard.tech === 'mule';
  const rows = [
    { label: 'Technology',    value: techLabel,                   editAction: null },
    { label: 'Account',       value: deploymentCard.accountLabel, editAction: deploymentCard.accountLabel ? 'edit_deploy_acct'  : null },
    { label: 'Environment',   value: deploymentCard.envName,      editAction: deploymentCard.envName      ? 'edit_deploy_env'   : null },
    { label: 'Repository',    value: deploymentCard.repoLabel,    editAction: deploymentCard.repoLabel    ? 'edit_deploy_repo'  : null },
    { label: 'CI/CD',         value: deploymentCard.devOpsLabel,  editAction: deploymentCard.devOpsLabel  ? 'edit_deploy_cicd'  : null },
    isMule ? { label: 'Code Analysis', value: deploymentCard.sonarLabel, editAction: deploymentCard.sonarLabel ? 'edit_deploy_sonar' : null } : null,
    { label: 'Key Vault',     value: deploymentCard.kvLabel,      editAction: deploymentCard.kvLabel      ? 'edit_deploy_kv'    : null },
  ].filter(Boolean).filter(function(r) { return r.value; });

  return (
    <div className="sh-deploy-card">
      <div className="sh-deploy-card-title">🚀 Deployment Configuration</div>
      {rows.map(function(r) {
        return (
          <div className="sh-deploy-card-row" key={r.label}>
            <span className="sh-deploy-card-label">{r.label}</span>
            <span className="sh-deploy-card-value">{r.value}</span>
            {r.editAction && (
              <button className="sh-deploy-card-edit" onClick={function() { onAction(r.editAction); }} disabled={loading}>
                ✏️ Edit
              </button>
            )}
          </div>
        );
      })}
      {deploymentCard.deployAction && (
        <button className="sh-deploy-now-btn" onClick={function() { onAction(deploymentCard.deployAction); }} disabled={loading}>
          {loading ? 'Deploying…' : '🚀 Deploy Now'}
        </button>
      )}
    </div>
  );
}

// ─── Deployment Edit Modal ────────────────────────────────────────
function DeployEditModal({ modal, onSelect, onClose }) {
  return (
    <div className="sh-deploy-edit-overlay" onClick={function(e) { if (e.target === e.currentTarget) onClose(); }}>
      <div className="sh-deploy-edit-modal">
        <div className="sh-deploy-edit-header">
          <span className="sh-deploy-edit-title">{modal.title}</span>
          <button className="sh-deploy-edit-close" onClick={onClose}>✕</button>
        </div>
        <div className="sh-deploy-edit-options">
          {modal.options.map(function(opt, i) {
            return (
              <button key={i} className={'sh-deploy-edit-option' + (opt.isSkip ? ' sh-deploy-edit-option--skip' : '')} onClick={function() { onSelect(opt.action); }}>
                {opt.label}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ─── Architectural Review Card ───────────────────────────────────
function ArchitecturalReviewCard({ architecturalReview, message, onSubmit, loading, selectedTech }) {
  const { apis = [], detectedConnectors = [], round = 0, canSkip = true } = architecturalReview;
  const techLabel = selectedTech === 'springboot' ? 'Spring Boot'
                  : selectedTech === 'dotnet'     ? '.NET'
                  :                                 'Mule 4';
  const [changeText,    setChangeText]    = React.useState('');
  const [submitted,     setSubmitted]     = React.useState(false);
  const [showTextarea,  setShowTextarea]  = React.useState(false);
  const isUpdate = round > 0;

  const handleApprove = () => {
    setSubmitted(true);
    onSubmit('approve');
  };
  const handleSendChange = () => {
    if (!changeText.trim()) return;
    setSubmitted(true);
    onSubmit(changeText.trim());
  };

  if (submitted) return (
    <div style={{ marginTop: 12, padding: '10px 14px', borderRadius: 10, border: '1px solid rgba(0,201,141,0.2)', background: 'rgba(0,201,141,0.05)', display: 'flex', alignItems: 'center', gap: 10 }}>
      <span style={{ fontSize: 14 }}>✅</span>
      <div>
        <div style={{ fontSize: 11, fontWeight: 700, color: '#00c98d' }}>Architecture Approved</div>
        <div style={{ fontSize: 10, color: '#7a93bc', marginTop: 2 }}>
          {apis.length} API{apis.length !== 1 ? 's' : ''}: {apis.map(a => a.name).join(', ')}
        </div>
      </div>
    </div>
  );

  return (
    <div className="sh-arch-review-card" style={{ marginTop: 12, border: '1px solid rgba(59,127,255,0.25)', borderRadius: 10, overflow: 'hidden' }}>
      {/* Markdown message */}
      {message && (
        <div style={{ padding: '14px 16px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          {renderMdText(message)}
        </div>
      )}
      {/* API list */}
      <div style={{ padding: '12px 16px', background: 'rgba(59,127,255,0.05)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: '#3b7fff', marginBottom: 8, letterSpacing: '0.06em' }}>
          {isUpdate ? `✅ Updated Architecture — ${techLabel} (${apis.length} APIs)` : `📐 Proposed Architecture — ${techLabel} (${apis.length} APIs)`}
        </div>
        {apis.map((api, i) => (
          <div key={api.id || i} style={{ marginBottom: 8, padding: '8px 10px', background: '#0e1219', borderRadius: 7, border: '1px solid rgba(255,255,255,0.07)' }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#dce8ff' }}>
              {i + 1}. {api.name}
              <span style={{ marginLeft: 8, fontSize: 10, fontWeight: 600, padding: '1px 7px', borderRadius: 4, background: api.layer === 'system' ? 'rgba(139,124,248,0.15)' : 'rgba(59,127,255,0.15)', color: api.layer === 'system' ? '#8b7cf8' : '#3b7fff' }}>
                {api.layer}
              </span>
            </div>
            {(api.endpoints || []).length > 0 && (
              <div style={{ fontSize: 12, color: '#7a93bc', marginTop: 4 }}>
                {api.endpoints.map(e => typeof e === 'string' ? e : `${e.method || ''} ${e.path || ''}`.trim()).join(' · ')}
              </div>
            )}
          </div>
        ))}
        {detectedConnectors.length > 0 && (
          <div style={{ fontSize: 11, color: '#3d5273', marginTop: 4 }}>
            <span style={{ color: '#7a93bc', fontWeight: 600 }}>Connectors: </span>
            {detectedConnectors.map(c => typeof c === 'string' ? c : (c.name || c.key || '')).filter(Boolean).join(', ')}
          </div>
        )}
      </div>

      {/* Actions */}
      <div style={{ padding: '12px 16px' }}>
        {!showTextarea ? (
          /* Default: two buttons side by side */
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={() => setShowTextarea(true)}
              disabled={loading}
              style={{ flex: 1, padding: '9px', borderRadius: 7, background: 'transparent', border: '1px solid rgba(245,166,35,0.4)', color: '#f5a623', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}
            >
              ✏️ Request Change
            </button>
            <button
              onClick={handleApprove}
              disabled={loading}
              style={{ flex: 1, padding: '9px', borderRadius: 7, background: '#3b7fff', border: 'none', color: '#fff', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}
            >
              ✅ Approve
            </button>
          </div>
        ) : (
          /* Change mode: textarea + Send / Cancel */
          <>
            <textarea
              autoFocus
              value={changeText}
              onChange={e => setChangeText(e.target.value)}
              placeholder='e.g. "Add a POST /document-sync/abort endpoint to the Process API"'
              rows={3}
              style={{ width: '100%', background: '#0e1219', color: '#dce8ff', border: '1px solid rgba(245,166,35,0.35)', borderRadius: 7, padding: '8px 10px', fontSize: 11, fontFamily: 'inherit', resize: 'vertical', outline: 'none', boxSizing: 'border-box', marginBottom: 8 }}
            />
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                onClick={() => { setShowTextarea(false); setChangeText(''); }}
                style={{ padding: '8px 14px', borderRadius: 7, background: 'transparent', border: '1px solid rgba(255,255,255,0.1)', color: '#7a93bc', fontSize: 11, cursor: 'pointer' }}
              >
                Cancel
              </button>
              <button
                onClick={handleSendChange}
                disabled={loading || !changeText.trim()}
                style={{ flex: 1, padding: '8px', borderRadius: 7, background: changeText.trim() ? 'rgba(245,166,35,0.15)' : '#141c27', border: `1px solid ${changeText.trim() ? 'rgba(245,166,35,0.4)' : 'rgba(255,255,255,0.06)'}`, color: changeText.trim() ? '#f5a623' : '#3d5273', fontSize: 11, fontWeight: 700, cursor: changeText.trim() ? 'pointer' : 'default' }}
              >
                Send Change →
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ─── Collapsible microservice folder ──────────────────────────────
function ApiSelectionCard({ apiSelection, onSubmit, loading }) {
  const { apis = [] } = apiSelection;
  const [selected, setSelected] = React.useState(() => new Set(apis.map(a => a.id)));
  const [submitted, setSubmitted] = React.useState(false);

  const toggle = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSubmit = (all) => {
    const chosen = all ? apis : apis.filter(a => selected.has(a.id));
    if (!chosen.length) return;
    setSubmitted(true);
    onSubmit(chosen, all);
  };

  if (submitted) {
    return (
      <div className="sh-api-sel-card sh-api-sel-submitted">
        <span className="sh-api-sel-done-icon">✓</span>
        <span>API selection submitted — generating your files…</span>
      </div>
    );
  }

  const layers = [...new Set(apis.map(a => a.layer).filter(Boolean))];
  const selectedCount = selected.size;
  const allSelected = selectedCount === apis.length;

  return (
    <div className="sh-api-sel-card">
      <div className="sh-api-sel-header">
        <span className="sh-api-sel-title">🔧 Select APIs to Generate</span>
        <span className="sh-api-sel-count">{selectedCount}/{apis.length} selected</span>
      </div>
      <div className="sh-api-sel-list">
        {layers.map(layer => {
          const layerApis = apis.filter(a => a.layer === layer);
          if (!layerApis.length) return null;
          return (
            <div key={layer} className="sh-api-sel-layer">
              <div className="sh-api-sel-layer-label">
                {layerIcon[layer] || "📦"} {layer.charAt(0).toUpperCase() + layer.slice(1)} Layer
              </div>
              {layerApis.map(api => (
                <label key={api.id} className={`sh-api-sel-row${selected.has(api.id) ? " sh-api-sel-row--checked" : ""}`}>
                  <input
                    type="checkbox"
                    className="sh-api-sel-checkbox"
                    checked={selected.has(api.id)}
                    onChange={() => toggle(api.id)}
                    disabled={loading}
                  />
                  <div className="sh-api-sel-info">
                    <span className="sh-api-sel-name">
                      <span className="sh-api-sel-index">{api.index}.</span>
                      {api.name}
                    </span>
                    <span className="sh-api-sel-desc">{api.description}</span>
                  </div>
                </label>
              ))}
            </div>
          );
        })}
      </div>
      <div className="sh-api-sel-footer">
        {!allSelected && (
          <button
            className={`sh-api-sel-btn ${selectedCount ? 'sh-api-sel-btn--primary' : 'sh-api-sel-btn--secondary'}`}
            onClick={() => handleSubmit(false)}
            disabled={loading || !selectedCount}
          >
            Generate Selected ({selectedCount})
          </button>
        )}
        <button
          className="sh-api-sel-btn sh-api-sel-btn--primary"
          onClick={() => handleSubmit(true)}
          disabled={loading}
        >
          ✅ Generate All
        </button>
      </div>
    </div>
  );
}

// Card for the "standalone app confirmation" stage — some selected APIs have no REST
// endpoints, so the backend asks whether to build them as standalone background apps.
// Mirrors ApiSelectionCard; sends the natural-language replies the backend documents.
function StandaloneConfirmationCard({ standaloneConfirm, onSubmit, loading }) {
  const { apis = [] } = standaloneConfirm;
  const [selected, setSelected] = React.useState(() => new Set(apis.map(a => a.id))); // default: all standalone
  const [submitted, setSubmitted] = React.useState(false);

  const toggle = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const submit = (replyPrompt, label) => {
    setSubmitted(true);
    onSubmit(replyPrompt, label);
  };

  const handleConfirm = () => {
    const chosen = apis.filter(a => selected.has(a.id));
    if (chosen.length === apis.length) { submit("yes", "✅ Treat all as standalone apps"); return; }
    if (chosen.length === 0)          { submit("no",  "Keep all as governed APIs"); return; }
    const names = chosen.map(a => a.name);
    const reply = `${names.join(" and ")} ${names.length > 1 ? "are" : "is"} standalone`;
    submit(reply, `Standalone: ${names.join(", ")}`);
  };

  if (submitted) {
    return (
      <div className="sh-api-sel-card sh-api-sel-submitted">
        <span className="sh-api-sel-done-icon">✓</span>
        <span>Confirmed — generating your files…</span>
      </div>
    );
  }

  const triggerLabel = (k) => k === "scheduler" ? "⏱ scheduler" : k ? `▶ ${k}` : "background";
  const selectedCount = selected.size;

  return (
    <div className="sh-api-sel-card">
      <div className="sh-api-sel-header">
        <span className="sh-api-sel-title">📦 Standalone Background Apps</span>
        <span className="sh-api-sel-count">{selectedCount}/{apis.length} standalone</span>
      </div>
      <div className="sh-api-sel-list">
        {apis.map(api => (
          <label key={api.id} className={`sh-api-sel-row${selected.has(api.id) ? " sh-api-sel-row--checked" : ""}`}>
            <input
              type="checkbox"
              className="sh-api-sel-checkbox"
              checked={selected.has(api.id)}
              onChange={() => toggle(api.id)}
              disabled={loading}
            />
            <div className="sh-api-sel-info">
              <span className="sh-api-sel-name">
                {layerIcon[api.layer] || "📦"} {api.name}
              </span>
              <span className="sh-api-sel-desc">
                {api.layer ? `${api.layer} layer · ` : ""}{triggerLabel(api.triggerKind)} · builds as standalone (-app), no Autodiscovery
              </span>
            </div>
          </label>
        ))}
      </div>
      <div className="sh-api-sel-footer">
        <button
          className="sh-api-sel-btn sh-api-sel-btn--secondary"
          onClick={() => submit("no", "Keep all as governed APIs")}
          disabled={loading}
        >
          Keep as API
        </button>
        <button
          className="sh-api-sel-btn sh-api-sel-btn--primary"
          onClick={handleConfirm}
          disabled={loading}
        >
          ✅ Confirm
        </button>
      </div>
    </div>
  );
}

const LAYER_BADGE_COLOR = {
  experience: { bg: "rgba(99,179,237,0.15)", color: "#63b3ed" },
  process:    { bg: "rgba(154,117,246,0.15)", color: "#9a75f6" },
  system:     { bg: "rgba(72,187,120,0.15)",  color: "#48bb78" },
};

// Parse endpoint lines from the elicitation message.
// Works in two modes:
//   1. apis[] empty/missing  → builds the full apis array from message sections
//   2. apis[] present but no endpoints → enriches existing entries from message
function parseEndpointsFromMessage(message, apis) {
  if (!message) return apis || [];

  // "    - GET /path: description"  (path stops at colon or space)
  const epRegex = /^\s*-\s+(GET|POST|PUT|PATCH|DELETE)\s+(\/[^\s:]*)(?:\s*:\s*(.*))?$/i;
  // layer hint from section header: "**API Name** (system layer):"
  const layerRegex = /\(\s*(system|process|experience)\s+layer\s*\)/i;

  // Split on **API Name** bold markers → ['prefix', 'Name1', 'content1', 'Name2', ...]
  const parts = message.split(/\*\*([^*]+)\*\*/);
  const sections = [];
  for (let i = 1; i < parts.length; i += 2) {
    const name = parts[i].trim();
    const content = parts[i + 1] || '';
    const layerMatch = content.match(layerRegex);
    const layer = layerMatch ? layerMatch[1].toLowerCase() : 'system';
    const endpoints = [];
    for (const line of content.split('\n')) {
      const m = line.match(epRegex);
      if (m) endpoints.push({ method: m[1].toUpperCase(), path: m[2], description: (m[3] || '').trim() });
    }
    if (endpoints.length) sections.push({ name, layer, endpoints });
  }

  console.log('[parseEndpoints] sections:', sections.map(s => `${s.name} → ${s.endpoints.length} eps`));

  // Mode 1: no apis provided — build entirely from message
  if (!apis?.length) {
    const built = sections.map((s, i) => ({ id: `msg-api-${i + 1}`, name: s.name, layer: s.layer, endpoints: s.endpoints }));
    console.log('[parseEndpoints] built from message:', built.map(a => a.name));
    return built;
  }

  // Mode 2: apis present but missing endpoints — enrich
  if (apis.every(a => a.endpoints?.length)) return apis;
  return apis.map(api => {
    if (api.endpoints?.length) return api;
    const nameLower = (api.name || '').toLowerCase();
    const match = sections.find(s => {
      const k = s.name.toLowerCase();
      return k.includes(nameLower) || nameLower.includes(k);
    });
    console.log(`[parseEndpoints] "${api.name}" → matched "${match?.name}" (${match?.endpoints?.length ?? 0} eps)`);
    return match ? { ...api, endpoints: match.endpoints } : api;
  });
}

function SchemaElicitationCard({ schemaElicitation, onSubmit }) {
  const { apis = [], canSkip } = schemaElicitation;

  // Flat key per endpoint: `${apiId}::${method}::${path}`
  const allEndpointKeys = apis.flatMap(api =>
    (api.endpoints || []).map(ep => `${api.id}::${ep.method}::${ep.path}`)
  );

  const firstKey = allEndpointKeys[0] || null;

  const [fields, setFields] = React.useState(() => {
    const init = {};
    apis.forEach(api => {
      (api.endpoints || []).forEach(ep => {
        const k = `${api.id}::${ep.method}::${ep.path}`;
        init[k] = {
          request:  ep.requestFields  || "",
          response: ep.responseFields || "",
        };
      });
    });
    return init;
  });
  const [selectedKey, setSelectedKey] = React.useState(firstKey);
  const [expandedApis, setExpandedApis] = React.useState(() =>
    Object.fromEntries(apis.map(a => [a.id, true]))
  );
  const [activeTab, setActiveTab] = React.useState("request");
  const [submitted, setSubmitted] = React.useState(false);
  const [fullscreen, setFullscreen] = React.useState(false);

  const setField = (key, field, val) =>
    setFields(prev => ({ ...prev, [key]: { ...prev[key], [field]: val } }));

  // Close fullscreen on Escape
  React.useEffect(() => {
    if (!fullscreen) return;
    const handler = (e) => { if (e.key === "Escape") setFullscreen(false); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [fullscreen]);

  const filledCount = allEndpointKeys.filter(k => {
    const f = fields[k];
    return f?.request?.trim() || f?.response?.trim();
  }).length;

  const handleSubmit = () => {
    const lines = [];
    const schemaItems = [];
    apis.forEach(api => {
      (api.endpoints || []).forEach(ep => {
        const k = `${api.id}::${ep.method}::${ep.path}`;
        const req = (fields[k]?.request || ep.requestFields  || "").trim() || "(not specified)";
        const res = (fields[k]?.response || ep.responseFields || "").trim() || "(not specified)";
        lines.push(`For ${api.name} ${ep.method} ${ep.path}: request has ${req}. Response has ${res}.`);
        schemaItems.push({ api: api.name, layer: api.layer, method: ep.method, path: ep.path, request: req, response: res });
      });
    });
    const prompt = lines.join("\n\n");
    console.log('[SchemaElicitation] prompt:\n', prompt);
    console.log('[SchemaElicitation] schemaItems:', schemaItems);
    setSubmitted(true);
    onSubmit(prompt, schemaItems);
  };

  const handleSkip = () => { setSubmitted(true); onSubmit("skip", null); };

  // Derive selected endpoint info for the right panel
  const selectedApi = selectedKey
    ? apis.find(a => (a.endpoints || []).some(ep => `${a.id}::${ep.method}::${ep.path}` === selectedKey))
    : null;
  const selectedEp = selectedApi
    ? (selectedApi.endpoints || []).find(ep => `${selectedApi.id}::${ep.method}::${ep.path}` === selectedKey)
    : null;
  const selectedMc = selectedEp ? (METHOD_COLOR[selectedEp.method] || { bg: "rgba(255,255,255,0.08)", color: "#ccc" }) : null;
  const selectedLayerBadge = selectedApi ? (LAYER_BADGE_COLOR[selectedApi.layer] || { bg: "rgba(255,255,255,0.08)", color: "#aaa" }) : null;

  if (submitted) return null;

  const titleBar = (
    <div className="sh-schema-ide-titlebar">
      <span className="sh-schema-ide-title">📋 Schema Elicitation</span>
      <span className="sh-schema-ide-progress">
        {filledCount}/{allEndpointKeys.length} endpoints filled
      </span>
      <div style={{ flex: 1 }} />
      {canSkip && (
        <button className="sh-schema-skip-btn" onClick={handleSkip}>
          Skip — let AI decide
        </button>
      )}
      <button className="sh-schema-submit-btn" onClick={handleSubmit}>
        ✅ Submit Schema
      </button>
      <button
        className="sh-schema-ide-expand-btn"
        title={fullscreen ? "Collapse (Esc)" : "Expand to fullscreen"}
        onClick={() => setFullscreen(f => !f)}
      >
        {fullscreen ? "⊡" : "⤢"}
      </button>
    </div>
  );

  const ideBody = (
    <div className={`sh-schema-ide-body${fullscreen ? " fullscreen" : ""}`}>

      {/* Left: API / endpoint tree */}
      <div className="sh-schema-ide-explorer">
        <div className="sh-schema-ide-explorer-header">EXPLORER</div>
        {apis.map(api => {
          const endpoints = api.endpoints || [];
          const layerBadge = LAYER_BADGE_COLOR[api.layer] || { bg: "rgba(255,255,255,0.08)", color: "#aaa" };
          const isOpen = expandedApis[api.id];
          return (
            <div key={api.id}>
              <button
                className="sh-schema-ide-api-btn"
                onClick={() => setExpandedApis(prev => ({ ...prev, [api.id]: !prev[api.id] }))}
              >
                <span className="sh-schema-ide-caret">{isOpen ? "▾" : "▸"}</span>
                <span>{isOpen ? "📂" : "📁"}</span>
                <span className="sh-schema-ide-api-name">{api.name}</span>
                <span className="sh-schema-api-badge" style={{ background: layerBadge.bg, color: layerBadge.color, fontSize: 9 }}>
                  {api.layer?.toUpperCase()}
                </span>
              </button>
              {isOpen && endpoints.map(ep => {
                const k = `${api.id}::${ep.method}::${ep.path}`;
                const mc = METHOD_COLOR[ep.method] || { bg: "rgba(255,255,255,0.08)", color: "#ccc" };
                const isFilled = !!(fields[k]?.request?.trim() || fields[k]?.response?.trim());
                const isActive = selectedKey === k;
                return (
                  <button
                    key={k}
                    className={`sh-schema-ide-ep-btn${isActive ? " active" : ""}`}
                    onClick={() => { setSelectedKey(k); setActiveTab("request"); }}
                  >
                    <span className="sh-schema-ide-ep-method" style={{ background: mc.bg, color: mc.color }}>
                      {ep.method}
                    </span>
                    <span className="sh-schema-ide-ep-path">{ep.path}</span>
                    {isFilled && <span className="sh-schema-ide-ep-dot" title="Fields filled" />}
                  </button>
                );
              })}
            </div>
          );
        })}
      </div>

      {/* Right: editor panel */}
      <div className="sh-schema-ide-editor">
        {selectedEp && selectedApi ? (
          <>
            {/* Tab bar */}
            <div className="sh-schema-ide-tabbar">
              <div className="sh-schema-ide-tab active">
                <span className="sh-schema-ide-ep-method" style={{ background: selectedMc.bg, color: selectedMc.color, fontSize: 9 }}>
                  {selectedEp.method}
                </span>
                <span style={{ fontSize: 12, color: "#c0c8d8", fontFamily: "monospace" }}>{selectedEp.path}</span>
                <span className="sh-schema-api-badge" style={{ background: selectedLayerBadge.bg, color: selectedLayerBadge.color, fontSize: 9 }}>
                  {selectedApi.name}
                </span>
              </div>
              <div style={{ flex: 1 }} />
              {selectedEp.description && (
                <span style={{ fontSize: 11, color: "rgba(255,255,255,0.3)", padding: "0 12px", fontStyle: "italic", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 280 }}>
                  {selectedEp.description}
                </span>
              )}
            </div>

            {/* Sub-tabs: Request / Response */}
            <div className="sh-schema-ide-subtabs">
              {["request", "response"].map(tab => {
                const prefilled = tab === "request" ? selectedEp?.requestFields : selectedEp?.responseFields;
                return (
                  <button
                    key={tab}
                    className={`sh-schema-ide-subtab${activeTab === tab ? " active" : ""}`}
                    onClick={() => setActiveTab(tab)}
                  >
                    {tab === "request" ? "⬆ Request" : "⬇ Response"}
                    {fields[selectedKey]?.[tab]?.trim() && (
                      <span className="sh-schema-ide-ep-dot" style={{ marginLeft: 5 }} />
                    )}
                    {prefilled && (
                      <span style={{ fontSize: 9, marginLeft: 5, color: "#2ebfa5", opacity: 0.7 }}>AI</span>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Textarea */}
            <div className="sh-schema-ide-textarea-wrap">
              <div className="sh-schema-ide-gutter">
                {Array.from({ length: 8 }, (_, i) => (
                  <div key={i} className="sh-schema-ide-ln">{i + 1}</div>
                ))}
              </div>
              <textarea
                key={`${selectedKey}-${activeTab}`}
                className="sh-schema-ide-textarea"
                placeholder={
                  activeTab === "request"
                    ? "e.g. accountId (string), amount (number), lineItems (array of {productId, qty, price})"
                    : "e.g. transactionId (string), status (string), message (string), timestamp (ISO8601)"
                }
                value={fields[selectedKey]?.[activeTab] || ""}
                onChange={e => setField(selectedKey, activeTab, e.target.value)}
                autoFocus
              />
            </div>

            {/* Navigation: prev / next endpoint */}
            <div className="sh-schema-ide-nav">
              <button
                className="sh-schema-ide-nav-btn"
                disabled={allEndpointKeys.indexOf(selectedKey) === 0}
                onClick={() => {
                  const idx = allEndpointKeys.indexOf(selectedKey);
                  if (idx > 0) { setSelectedKey(allEndpointKeys[idx - 1]); setActiveTab("request"); }
                }}
              >← Prev</button>
              <span style={{ fontSize: 11, color: "#555" }}>
                {allEndpointKeys.indexOf(selectedKey) + 1} / {allEndpointKeys.length}
              </span>
              <button
                className="sh-schema-ide-nav-btn"
                disabled={allEndpointKeys.indexOf(selectedKey) === allEndpointKeys.length - 1}
                onClick={() => {
                  const idx = allEndpointKeys.indexOf(selectedKey);
                  if (idx < allEndpointKeys.length - 1) { setSelectedKey(allEndpointKeys[idx + 1]); setActiveTab("request"); }
                }}
              >Next →</button>
            </div>
          </>
        ) : (
          <div className="sh-schema-ide-empty">
            Select an endpoint from the explorer to fill in its schema fields.
          </div>
        )}
      </div>
    </div>
  );

  const inner = (
    <div className={fullscreen ? "sh-schema-ide sh-schema-ide--fs" : "sh-schema-ide"}>
      {titleBar}
      {ideBody}
    </div>
  );

  if (fullscreen) {
    return (
      <div className="sh-schema-ide-overlay">
        {inner}
      </div>
    );
  }

  return inner;
}

function SvcFolder({ name, files, activeArtifact, setActiveArtifact }) {
  const [open, setOpen] = React.useState(false);

  return (
    <div className="sh-tree-svc-group">
      <button className="sh-tree-svc-label" onClick={() => setOpen(o => !o)}>
        <span className="sh-tree-svc-chevron">{open ? "▾" : "▸"}</span>
        <span>📂</span>
        <span className="sh-tree-svc-name">{name}</span>
      </button>
      {open && (
        files.some(a => a.relativePath)
          ? <FileTreeNode
              node={buildFileTree(files)}
              depth={1}
              activeArtifact={activeArtifact}
              setActiveArtifact={setActiveArtifact}
            />
          : files.map(art => (
              <button
                key={art.id}
                className={`sh-nav-item sh-tree-file sh-tree-file--nested ${activeArtifact?.id === art.id ? "active" : ""}`}
                onClick={() => setActiveArtifact(art)}
              >
                <span>{art.icon}</span>
                <span className="sh-tree-file-name">{art.name}</span>
              </button>
            ))
      )}
    </div>
  );
}

// ─── File-tree helpers (for disk-loaded projects) ─────────────────
async function downloadGroupAsZip(groupName, files) {
  const zip = new JSZip();
  const folderName = groupName.toLowerCase().replace(/\s+/g, "-");
  const folder = zip.folder(folderName);
  files.forEach(f => {
    const path = f.relativePath || f.name;
    folder.file(path, f.content || "");
  });
  const blob = await zip.generateAsync({ type: "blob" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${folderName}.zip`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

async function handleFolderUpload({ files, projectPath }) {
  try {
    console.log('[SmartHub] Zipping', files.length, 'files for upload →', projectPath);

    // Zip all selected folder files on the frontend using JSZip
    const zip = new JSZip();
    for (const file of files) {
      // webkitRelativePath gives "folderName/src/main/..." — strip the top-level folder name
      const relativePath = file.webkitRelativePath
        ? file.webkitRelativePath.split('/').slice(1).join('/')
        : file.name;
      if (relativePath) {
        const content = await file.arrayBuffer();
        zip.file(relativePath, content);
      }
    }
    const blob = await zip.generateAsync({ type: 'blob' });
    const zipFile = new File([blob], 'upload.zip', { type: 'application/zip' });

    console.log('[SmartHub] Zip ready, uploading...');
    const formData = new FormData();
    formData.append('file', zipFile);
    formData.append('projectPath', projectPath);
    const res = await fetch(`${backendUrl}/api/v1/project/upload-folder`, {
      method: 'POST',
      headers: { 'x-access-token': localStorage.getItem('token') },
      body: formData,
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    console.log('[SmartHub] Upload success:', data);
  } catch (err) {
    console.error('[SmartHub] Upload failed:', err.message);
    throw err;
  }
}

// Upload a single .jar / .zip archive directly (no client-side re-zip). The backend
// (/api/v1/project/upload-folder) extracts it into projectPath with AdmZip — a .jar is a zip.
async function handleArchiveUpload({ file, projectPath }) {
  try {
    console.log('[SmartHub] Uploading archive', file?.name, '→', projectPath);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('projectPath', projectPath);
    const res = await fetch(`${backendUrl}/api/v1/project/upload-folder`, {
      method: 'POST',
      headers: { 'x-access-token': localStorage.getItem('token') },
      body: formData,
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    console.log('[SmartHub] Archive upload success:', data);
  } catch (err) {
    console.error('[SmartHub] Archive upload failed:', err.message);
    throw err;
  }
}

// ─── Confluence push modal ────────────────────────────────────────
function ConfluenceModal({ docs, domain, onClose, onPushDoc, onComplete }) {
  const [accounts,    setAccounts]    = React.useState([]);
  const [selectedAcct, setSelectedAcct] = React.useState(null);
  const [spaces,      setSpaces]      = React.useState([]);
  const [spaceKey,    setSpaceKey]    = React.useState("");
  const [loadingSpaces, setLoadingSpaces] = React.useState(false);
  const [selectedDocs, setSelectedDocs] = React.useState(docs.map((_, i) => i));
  const [refreshing,  setRefreshing]  = React.useState(false);
  // Editable page names (the "documentation" title) — editable on the first submit
  // and again for any page that fails (e.g. name already exists) so it can be retried.
  const [titles,  setTitles]  = React.useState(docs.map(d => d.title || ""));
  // Per-page outcome: results[i] = { status:'pushing'|'success'|'error', message?, url? }
  const [results, setResults] = React.useState(docs.map(() => null));
  const [pushing, setPushing] = React.useState(false);
  const [attempted, setAttempted] = React.useState(false);   // any push attempt made yet
  const token = localStorage.getItem("token");

  const fetchAccounts = React.useCallback(() => {
    setRefreshing(true);
    console.log('[SmartHub] GET /api/v1/confluence → fetching accounts');
    fetch(`${domain}/api/v1/confluence`, { headers: { "x-access-token": token } })
      .then(r => r.json())
      .then(d => {
        console.log('[SmartHub] GET /api/v1/confluence ←', d);
        const list = Array.isArray(d) ? d : (d.data || []);
        setAccounts(list);
        if (list.length > 0) setSelectedAcct(list[0]);
      })
      .catch(() => {})
      .finally(() => setRefreshing(false));
  }, [domain, token]);

  React.useEffect(() => { fetchAccounts(); }, []);

  React.useEffect(() => {
    if (!selectedAcct) return;
    setLoadingSpaces(true);
    console.log('[SmartHub] POST /api/v1/confluence/getConfluenceSpaces → account:', selectedAcct.confluenceEmail);
    fetch(`${domain}/api/v1/confluence/getConfluenceSpaces`, {
      method: "POST",
      headers: { "Content-Type": "application/json", "x-access-token": token },
      body: JSON.stringify({
        confluenceEmail:   selectedAcct.confluenceEmail,
        confluencePat:     selectedAcct.confluencePat,
        confluenceBaseUrl: selectedAcct.confluenceBaseUrl,
      }),
    })
      .then(r => r.json())
      .then(d => {
        console.log('[SmartHub] POST /api/v1/confluence/getConfluenceSpaces ←', d);
        setSpaces(d.spaces || []); if (d.spaces?.length) setSpaceKey(d.spaces[0].key);
      })
      .catch(() => {})
      .finally(() => setLoadingSpaces(false));
  }, [selectedAcct]);

  const toggleDoc = (i) => setSelectedDocs(prev =>
    prev.includes(i) ? prev.filter(x => x !== i) : [...prev, i]
  );
  const setTitle = (i, v) => setTitles(prev => prev.map((t, idx) => idx === i ? v : t));

  // Indices to push this run: selected, non-empty name, not already succeeded.
  const pendingIdx = () => selectedDocs.filter(i =>
    results[i]?.status !== 'success' && (titles[i] || '').trim()
  );

  const handleSubmit = async () => {
    if (!selectedAcct || !spaceKey || pushing) return;
    const targets = pendingIdx();
    if (targets.length === 0) return;
    setPushing(true);
    setAttempted(true);
    // The full set the user is attempting (drives the final N/M summary).
    const attemptedIdx = Array.from(new Set([
      ...docs.map((_, i) => i).filter(i => results[i]?.status),   // previously attempted
      ...targets,
    ]));
    // Track outcomes locally so the final summary is computed from real values — NOT
    // from inside a setState updater (updaters must be pure; React may run them twice,
    // which previously fired onComplete twice → duplicate "published" message).
    const localResults = results.slice();
    for (const i of targets) {
      setResults(prev => prev.map((r, idx) => idx === i ? { status: 'pushing' } : r));
      // eslint-disable-next-line no-await-in-loop
      const r = await onPushDoc({
        confluenceAcct: selectedAcct,
        spaceKey,
        title: (titles[i] || '').trim(),
        markdown: docs[i].markdown,
      });
      const outcome = r.success ? { status: 'success', url: r.pageUrl } : { status: 'error', message: r.message };
      localResults[i] = outcome;
      setResults(prev => prev.map((rr, idx) => idx === i ? outcome : rr));
    }
    setPushing(false);
    // Compute the summary once, from the local outcomes, and finish exactly once.
    const pushed = attemptedIdx.filter(i => localResults[i]?.status === 'success').length;
    const anyFailed = attemptedIdx.some(i => localResults[i]?.status === 'error');
    if (!anyFailed && pushed === attemptedIdx.length) {
      onComplete(pushed, attemptedIdx.length);
    }
  };

  // Close button after a partial push: report the summary; otherwise a plain close.
  const handleClose = () => {
    const attemptedIdx = docs.map((_, i) => i).filter(i => results[i]?.status);
    if (attempted && attemptedIdx.length) {
      const pushed = attemptedIdx.filter(i => results[i]?.status === 'success').length;
      onComplete(pushed, attemptedIdx.length);
    } else {
      onClose();
    }
  };

  const failedCount = selectedDocs.filter(i => results[i]?.status === 'error').length;
  const canPush = !!spaceKey && pendingIdx().length > 0 && !pushing;

  return (
    <div className="sh-confluence-overlay" onClick={e => e.target === e.currentTarget && !pushing && handleClose()}>
      <div className="sh-confluence-modal">
        <div className="sh-confluence-header">
          <span>📄 Push to Confluence</span>
          <button onClick={handleClose} disabled={pushing}>✕</button>
        </div>

        {accounts.length === 0 ? (
          <div className="sh-confluence-empty">
            <p>No Confluence account connected. Add one in Connected Apps first.</p>
            <div className="sh-confluence-empty-actions">
              <button className="sh-confluence-settings-btn" onClick={() => { window.location.href = "/settings"; }}>
                ⚙️ Go to Connected Apps
              </button>
              <button className="sh-confluence-refresh-btn" onClick={fetchAccounts} disabled={refreshing}>
                {refreshing ? "Refreshing..." : "🔄 Refresh"}
              </button>
            </div>
          </div>
        ) : (
          <>
            {accounts.length > 1 && (
              <div className="sh-confluence-field">
                <label>Account</label>
                <select value={selectedAcct?.confluenceEmail || ""} onChange={e => setSelectedAcct(accounts.find(a => a.confluenceEmail === e.target.value))}>
                  {accounts.map(a => <option key={a.confluenceEmail} value={a.confluenceEmail}>{a.confluenceBaseUrl}</option>)}
                </select>
              </div>
            )}

            <div className="sh-confluence-field">
              <label>Space</label>
              {loadingSpaces ? <span className="sh-confluence-loading">Loading spaces...</span> : (
                spaces.length > 0
                  ? <select value={spaceKey} onChange={e => setSpaceKey(e.target.value)}>
                      {spaces.map(s => <option key={s.key} value={s.key}>{s.name} ({s.key})</option>)}
                    </select>
                  : <input value={spaceKey} onChange={e => setSpaceKey(e.target.value)} placeholder="Enter space key e.g. DEV" />
              )}
            </div>

            <div className="sh-confluence-field">
              <label>Pages to publish — edit a name before publishing</label>
              <div className="sh-confluence-docs">
                {docs.map((doc, i) => {
                  const r = results[i];
                  const done = r?.status === 'success';
                  const conflict = r?.status === 'error' && /already exists/i.test(r.message || '');
                  return (
                    <div key={i} className="sh-confluence-doc-row">
                      <input
                        type="checkbox"
                        checked={selectedDocs.includes(i)}
                        onChange={() => toggleDoc(i)}
                        disabled={done || pushing}
                      />
                      <input
                        className="sh-confluence-title-input"
                        value={titles[i]}
                        onChange={e => setTitle(i, e.target.value)}
                        placeholder="Page name"
                        disabled={done || pushing}
                      />
                      {r?.status === 'pushing' && <span className="sh-confluence-status">pushing…</span>}
                      {done && (
                        <a className="sh-confluence-status sh-confluence-status--ok" href={r.url} target="_blank" rel="noopener noreferrer">✓ view page</a>
                      )}
                      {r?.status === 'error' && (
                        <span className="sh-confluence-status sh-confluence-status--err">
                          ✗ {conflict ? 'name already exists — rename & retry' : (r.message || 'failed')}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            <button
              className="sh-confluence-submit"
              onClick={handleSubmit}
              disabled={!canPush}
            >
              {pushing
                ? "Publishing…"
                : failedCount > 0
                  ? `Retry failed (${failedCount})`
                  : `Publish ${pendingIdx().length} page${pendingIdx().length !== 1 ? "s" : ""}`}
            </button>
            {attempted && !pushing && (
              <button className="sh-confluence-refresh-btn" onClick={handleClose} style={{ marginTop: 8 }}>
                Done
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ─── IDE fullscreen modal ─────────────────────────────────────────
function IdeModal({ groups, artifacts, onClose, onContentSaved, projectPath }) {
  const [selectedArtifact, setSelectedArtifact] = React.useState(null);
  const [openTabs, setOpenTabs] = React.useState([]); // VS Code-style open editor tabs
  const [expandedGroups, setExpandedGroups] = React.useState(() => {
    const init = {};
    Object.keys(groups).forEach(g => { init[g] = false; }); // all collapsed on open
    return init;
  });
  const [leftWidth, setLeftWidth] = React.useState(260);
  const dragRef = React.useRef(null);
  const [editMode, setEditMode] = React.useState(false);
  const [editContent, setEditContent] = React.useState("");
  const [saving, setSaving] = React.useState(false);
  const [saveMsg, setSaveMsg] = React.useState("");
  const [serverFileMap, setServerFileMap] = React.useState({});
  const [serverLoading, setServerLoading] = React.useState(false);
  const [downloading, setDownloading] = React.useState(false);

  const startResize = React.useCallback((e) => {
    e.preventDefault();
    const startX = e.clientX;
    const startW = leftWidth;
    dragRef.current = true;
    const onMove = (me) => {
      if (!dragRef.current) return;
      const newW = Math.min(600, Math.max(160, startW + (me.clientX - startX)));
      setLeftWidth(newW);
    };
    const onUp = () => {
      dragRef.current = false;
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, [leftWidth]);

  const _fileIcon = (name) =>
    name.endsWith('.java') ? "☕"
    : name.endsWith('.xml') ? "⚡"
    : name.endsWith('.raml') ? "📄"
    : name.endsWith('.yaml') || name.endsWith('.yml') ? "⚙️"
    : name.endsWith('.json') ? "🔧"
    : name.endsWith('.md') ? "📝"
    : name.endsWith('.properties') ? "⚙️"
    : name.endsWith('.gradle') ? "🔨"
    : name.endsWith('.sh') ? "🖥️"
    : "📄";

  const _toArt = (file, groupKey, filePath) => ({
    id: `srv-${groupKey}-${file.relativePath || file.name}`,
    icon: _fileIcon(file.name),
    name: file.name,
    content: file.content,
    encoding: file.encoding || 'utf8',
    size: file.size,
    relativePath: file.relativePath || file.name,
    svcProjectPath: filePath,
    group: groupKey,
  });

  // Fetch full server file structure on open
  React.useEffect(() => {
    const token = localStorage.getItem('token');
    const fetchOne = (key, path) => {
      console.log(`[SmartHub] GET /api/v1/project/load?projectPath=${path} → key: ${key}`);
      return fetch(`${backendUrl}/api/v1/project/load?projectPath=${encodeURIComponent(path)}`, {
        headers: { 'x-access-token': token },
      })
        .then(r => r.ok ? r.json() : null)
        .then(res => {
          console.log(`[SmartHub] GET /api/v1/project/load ← key: ${key}, files: ${res?.files?.length ?? 0}`);
          return res?.files?.length ? [key, res.files.map(f => _toArt(f, key, path))] : null;
        })
        .catch(() => null);
    };

    setServerLoading(true);
    const fetches = projectPath
      ? [fetchOne('__root__', projectPath)]
      : Object.entries(groups).map(([g, files]) => {
          const p = files[0]?.apiProjectPath;
          return p ? fetchOne(g, p) : Promise.resolve(null);
        });

    Promise.all(fetches).then(results => {
      const map = {};
      results.filter(Boolean).forEach(([k, fs]) => { map[k] = fs; });
      setServerFileMap(map);
      setServerLoading(false);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDownloadAll = async () => {
    setDownloading(true);
    try {
      const zip = new JSZip();
      const rootName = projectPath
        ? projectPath.replace(/\\/g, '/').split('/').filter(Boolean).pop()
        : 'generated-apis';
      const all = Object.values(serverFileMap).flat();
      const missing = [];
      let added = 0;
      all.forEach(f => {
        const rel = (f.relativePath || f.name).replace(/\\/g, '/');
        const zipPath = projectPath ? `${rootName}/${rel}` : `${rootName}/${f.group}/${rel}`;
        // Never write an empty entry — that is what produced 0-byte downloads.
        if (f.content === undefined || f.content === null || f.content === "") {
          missing.push(rel);
          return;
        }
        zip.file(zipPath, f.content, f.encoding === 'base64' ? { base64: true } : {});
        added += 1;
      });
      if (added === 0) {
        console.error('[Download All] no file content returned', { missing });
        window.alert(`Download failed: no file content was found for this project (${missing.length} empty file(s)). The saved path may be stale — reopen the project and try again.`);
        return;
      }
      if (missing.length) {
        console.warn(`[Download All] ${added} file(s) zipped, ${missing.length} skipped (empty):`, missing);
      }
      const blob = await zip.generateAsync({ type: "blob" });
      const a = Object.assign(document.createElement("a"), {
        href: URL.createObjectURL(blob), download: `${rootName}.zip`,
      });
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(a.href);
    } finally { setDownloading(false); }
  };

  // Resolve full artifact + carry apiProjectPath as svcProjectPath fallback
  const resolveArtifact = (fileRef) => {
    // Primary: match by ID (original generation)
    let art = artifacts.find(a => a.id === fileRef.id);
    // Fallback: match by group + relativePath when IDs differ after upload reload
    if (!art?.content) {
      const byPath = artifacts.find(a => a.group === fileRef.group && a.relativePath === fileRef.relativePath);
      if (byPath?.content) art = byPath;
    }
    art = art || fileRef;
    return { ...art, svcProjectPath: art.svcProjectPath || fileRef.apiProjectPath || art.apiProjectPath };
  };

  // Open a file: select it AND add it to the tab bar (deduped by id). All tree clicks
  // route through this so opening files stacks tabs like VS Code.
  const openFile = React.useCallback((art) => {
    if (!art) return;
    const full = resolveArtifact(art);
    setSelectedArtifact(full);
    setOpenTabs(tabs => tabs.some(t => t.id === full.id) ? tabs : [...tabs, full]);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Close a tab; if it was the active one, fall back to the neighbour (or nothing).
  const closeTab = React.useCallback((id, e) => {
    if (e) e.stopPropagation();
    setOpenTabs(tabs => {
      const idx = tabs.findIndex(t => t.id === id);
      if (idx === -1) return tabs;
      const next = tabs.filter(t => t.id !== id);
      setSelectedArtifact(sel => {
        if (sel?.id !== id) return sel;              // closed a background tab — keep active
        if (next.length === 0) return null;
        return next[Math.min(idx, next.length - 1)]; // pick neighbour
      });
      return next;
    });
  }, []);

  // Reset edit state when file selection changes; also make sure the active file has a tab
  // (covers any selection path that didn't go through openFile).
  React.useEffect(() => {
    setEditMode(false);
    setEditContent(selectedArtifact?.content || "");
    setSaveMsg("");
    if (selectedArtifact) {
      setOpenTabs(tabs => tabs.some(t => t.id === selectedArtifact.id) ? tabs : [...tabs, selectedArtifact]);
    }
  }, [selectedArtifact?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSave = () => {
    if (!selectedArtifact?.relativePath || !selectedArtifact?.svcProjectPath) {
      setSaveMsg("⚠️ No file path available.");
      return;
    }
    setSaving(true);
    setSaveMsg("");
    const _savePayload = { projectPath: selectedArtifact.svcProjectPath, relativePath: selectedArtifact.relativePath, content: editContent };
    console.log("[SmartHub] POST /api/v1/project/save-file →", _savePayload);
    fetch(`${backendUrl}/api/v1/project/save-file`, {
      method: "POST",
      headers: { "Content-Type": "application/json", "x-access-token": localStorage.getItem("token") },
      body: JSON.stringify(_savePayload),
    })
      .then(r => r.json())
      .then(d => {
        console.log("[SmartHub] POST /api/v1/project/save-file ←", d);
        onContentSaved && onContentSaved(selectedArtifact.id, editContent);
        setSelectedArtifact(prev => ({ ...prev, content: editContent }));
        // Keep the open tab in sync so switching away and back shows the saved content.
        setOpenTabs(tabs => tabs.map(t => t.id === selectedArtifact.id ? { ...t, content: editContent } : t));
        setEditMode(false);
        setSaveMsg("✅ Saved");
        setTimeout(() => setSaveMsg(""), 3000);
      })
      .catch(() => setSaveMsg("❌ Save failed"))
      .finally(() => setSaving(false));
  };

  const lang = selectedArtifact ? getArtifactLang(selectedArtifact.name) : "text";

  // Close on Escape (only when not editing)
  React.useEffect(() => {
    const handler = (e) => { if (e.key === "Escape" && !editMode) onClose(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose, editMode]);

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 9999,
      background: "#0d1117",
      display: "flex", flexDirection: "column",
    }}>
      {/* Title bar */}
      <div style={{
        display: "flex", alignItems: "center", gap: 10,
        padding: "0 14px", height: 40, minHeight: 40,
        background: "#161b22", borderBottom: "1px solid rgba(255,255,255,0.08)",
        flexShrink: 0,
      }}>
        <span style={{ fontSize: 14, color: "#fff", fontWeight: 700, letterSpacing: 0.3 }}>
          📁 {projectPath ? projectPath.replace(/\\/g, '/').split('/').filter(Boolean).pop() : "Generated Files"}
        </span>
        <span style={{ color: "#555", fontSize: 12 }}>—</span>
        <span style={{ color: "#888", fontSize: 12 }}>
          {Object.keys(serverFileMap).length > 0
            ? `${Object.values(serverFileMap).reduce((s, f) => s + f.length, 0)} files`
            : `${Object.values(groups).reduce((s, f) => s + f.length, 0)} files across ${Object.keys(groups).length} APIs`}
        </span>
        {selectedArtifact && (
          <>
            <span style={{ color: "#555", fontSize: 12 }}>·</span>
            <span style={{ color: "#2ebfa5", fontSize: 12 }}>{selectedArtifact.name}</span>
          </>
        )}
        <div style={{ flex: 1 }} />
        <button
          onClick={onClose}
          title="Close IDE view (Esc)"
          style={{ background: "none", border: "none", color: "#888", fontSize: 18, cursor: "pointer", lineHeight: 1, padding: "4px 6px", borderRadius: 4 }}
        >✕</button>
      </div>

      {/* Body: left tree + right code */}
      <div style={{ display: "flex", flex: 1, overflow: "hidden" }}>

        {/* Left: file explorer */}
        <div className="sh-ide-scroll-left" style={{
          width: leftWidth, minWidth: 160, maxWidth: 600,
          background: "#161b22",
          overflowY: "auto", padding: "8px 0", flexShrink: 0,
        }}>
          <div style={{ padding: "4px 12px 8px", fontSize: 10, color: "#555", textTransform: "uppercase", letterSpacing: 1, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <span>Explorer</span>
            {Object.keys(serverFileMap).length > 0 && (
              <button
                onClick={handleDownloadAll}
                disabled={downloading}
                title="Download entire project as ZIP"
                style={{
                  fontSize: 10, fontWeight: 600, padding: "2px 8px", borderRadius: 4,
                  border: "1px solid rgba(72,209,204,0.4)", background: "rgba(72,209,204,0.1)",
                  color: "#48d1cc", cursor: downloading ? "wait" : "pointer", opacity: downloading ? 0.6 : 1,
                  display: "flex", alignItems: "center", gap: 3, textTransform: "none",
                }}
              >
                {downloading ? "⏳" : "⬇"} {downloading ? "Zipping…" : "Download All"}
              </button>
            )}
            {serverLoading && Object.keys(serverFileMap).length === 0 && (
              <span style={{ fontSize: 10, color: "#555", fontWeight: 400, textTransform: "none" }}>Loading…</span>
            )}
          </div>

          {/* Server tree — single root path: group by top-level folder */}
          {projectPath && Object.keys(serverFileMap).length > 0 && (() => {
            const sFiles = serverFileMap['__root__'] || [];
            const rootFwd = projectPath.replace(/\\/g, '/');
            // Split files by first path segment (e.g. DotNetService/, shared/, root files)
            const subGroupMap = {};
            sFiles.forEach(f => {
              const relPath = (f.relativePath || f.name).replace(/\\/g, '/');
              const firstSeg = relPath.includes('/') ? relPath.split('/')[0] : 'Project Files';
              const relToGroup = relPath.includes('/') ? relPath.slice(firstSeg.length + 1) : relPath;
              const groupSvcPath = firstSeg === 'Project Files' ? rootFwd : `${rootFwd}/${firstSeg}`;
              if (!subGroupMap[firstSeg]) subGroupMap[firstSeg] = [];
              subGroupMap[firstSeg].push({ ...f, relativePath: relToGroup, svcProjectPath: groupSvcPath });
            });
            const subKeys = Object.keys(subGroupMap);
            if (subKeys.length > 1) {
              // Multi-folder project (e.g. .NET): one node per top-level folder
              return subKeys.map(gName => (
                <ServerGroupNode
                  key={gName}
                  groupName={gName}
                  files={subGroupMap[gName]}
                  selectedArtifact={selectedArtifact}
                  setSelectedArtifact={openFile}
                  defaultOpen={false}
                />
              ));
            }
            // Single-root project: show as named root folder
            const pName = rootFwd.split('/').filter(Boolean).pop();
            return (
              <ServerGroupNode
                groupName={pName}
                files={sFiles}
                selectedArtifact={selectedArtifact}
                setSelectedArtifact={openFile}
                defaultOpen={true}
              />
            );
          })()}

          {/* Server tree — Mule (per-group paths) */}
          {!projectPath && Object.keys(serverFileMap).length > 0 &&
            Object.entries(groups).map(([group]) => (
              <ServerGroupNode
                key={group}
                groupName={group}
                files={serverFileMap[group] || []}
                selectedArtifact={selectedArtifact}
                setSelectedArtifact={openFile}
                defaultOpen={false}
              />
            ))}

          {/* Fallback: in-memory artifacts while server files are loading or unavailable */}
          {Object.keys(serverFileMap).length === 0 && !serverLoading &&
            Object.entries(groups).map(([group, files]) => {
              const resolved = files.map(resolveArtifact);
              const isOpen = expandedGroups[group];
              return (
                <div key={group}>
                  <button
                    onClick={() => setExpandedGroups(prev => ({ ...prev, [group]: !prev[group] }))}
                    style={{
                      display: "flex", alignItems: "center", gap: 6, width: "100%",
                      padding: "5px 12px", background: "none", border: "none",
                      color: "#ccc", fontSize: 12, fontWeight: 600, cursor: "pointer",
                      textAlign: "left",
                    }}
                  >
                    <span style={{ fontSize: 10, color: "#666" }}>{isOpen ? "▾" : "▸"}</span>
                    <span>{isOpen ? "📂" : "📁"}</span>
                    <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{group}</span>
                    <span style={{ color: "#444", fontWeight: 400, fontSize: 11 }}>{files.length}</span>
                  </button>
                  {isOpen && (
                    <div style={{ paddingLeft: 8 }}>
                      <FileTreeNode
                        node={buildFileTree(resolved)}
                        depth={0}
                        defaultOpen={false}
                        activeArtifact={selectedArtifact}
                        setActiveArtifact={(art) => openFile(files.find(f => f.id === art?.id) || art)}
                      />
                    </div>
                  )}
                </div>
              );
            })}
        </div>

        {/* Drag handle */}
        <div
          onMouseDown={startResize}
          style={{
            width: 5, flexShrink: 0, cursor: "col-resize",
            background: "rgba(255,255,255,0.06)",
            transition: "background 0.15s",
          }}
          onMouseEnter={e => { e.currentTarget.style.background = "rgba(46,191,165,0.5)"; }}
          onMouseLeave={e => { e.currentTarget.style.background = "rgba(255,255,255,0.06)"; }}
          title="Drag to resize"
        />

        {/* Right: code viewer / editor */}
        <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", background: "#0d1117" }}>
          {selectedArtifact ? (
            <>
              {/* Tab bar + toolbar */}
              <div style={{
                display: "flex", alignItems: "center",
                background: "#161b22", borderBottom: "1px solid rgba(255,255,255,0.08)",
                padding: "0 8px", height: 38, minHeight: 38, flexShrink: 0, gap: 8,
              }}>
                {/* Open editor tabs (VS Code-style) */}
                <div className="sh-ide-tabs" style={{ display: "flex", alignItems: "stretch", height: "100%", flex: 1, overflowX: "auto", overflowY: "hidden" }}>
                  {(openTabs.length ? openTabs : [selectedArtifact]).map(tab => {
                    const active = selectedArtifact?.id === tab.id;
                    return (
                      <div
                        key={tab.id}
                        onClick={() => setSelectedArtifact(tab)}
                        title={tab.relativePath || tab.name}
                        style={{
                          display: "flex", alignItems: "center", gap: 6,
                          padding: "0 8px 0 10px", height: "100%", cursor: "pointer",
                          borderRight: "1px solid rgba(255,255,255,0.08)",
                          borderTop: active ? "2px solid #2ebfa5" : "2px solid transparent",
                          background: active ? "#0d1117" : "transparent",
                          color: active ? "#d4d4d4" : "#8b98a5", fontSize: 12, whiteSpace: "nowrap", flexShrink: 0,
                        }}
                      >
                        <span>{tab.icon}</span>
                        <span>{tab.name}</span>
                        {active && editMode && <span style={{ color: "#ffa032", fontSize: 10 }}>●</span>}
                        {openTabs.length > 0 && (
                          <span
                            onClick={(e) => closeTab(tab.id, e)}
                            title="Close"
                            style={{ marginLeft: 2, padding: "0 3px", borderRadius: 3, color: "#8b98a5", fontSize: 13, lineHeight: 1 }}
                            onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(255,255,255,0.12)"; e.currentTarget.style.color = "#fff"; }}
                            onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "#8b98a5"; }}
                          >✕</span>
                        )}
                      </div>
                    );
                  })}
                </div>
                {/* Toolbar */}
                {saveMsg && (
                  <span style={{ fontSize: 11, color: saveMsg.startsWith("✅") ? "#2ebfa5" : "#e05050" }}>{saveMsg}</span>
                )}
                {editMode ? (
                  <>
                    <button
                      onClick={handleSave}
                      disabled={saving}
                      style={{ padding: "4px 12px", background: "#2ebfa5", border: "none", borderRadius: 5, color: "#000", fontSize: 12, fontWeight: 600, cursor: saving ? "wait" : "pointer" }}
                    >{saving ? "Saving…" : "💾 Save"}</button>
                    <button
                      onClick={() => { setEditMode(false); setEditContent(selectedArtifact.content || ""); setSaveMsg(""); }}
                      style={{ padding: "4px 10px", background: "rgba(255,255,255,0.07)", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 5, color: "#aaa", fontSize: 12, cursor: "pointer" }}
                    >Cancel</button>
                  </>
                ) : (
                  <button
                    onClick={() => { setEditMode(true); setEditContent(selectedArtifact.content || ""); }}
                    style={{ padding: "4px 10px", background: "rgba(255,160,50,0.12)", border: "1px solid rgba(255,160,50,0.3)", borderRadius: 5, color: "#ffa032", fontSize: 12, cursor: "pointer" }}
                  >✏️ Edit</button>
                )}
              </div>

              {/* Code area or editor */}
              <div className="sh-ide-scroll" style={{ flex: 1, overflow: "auto" }}>
                {editMode ? (
                  <textarea
                    value={editContent}
                    onChange={e => setEditContent(e.target.value)}
                    spellCheck={false}
                    className="sh-ide-scroll"
                    style={{
                      width: "100%", height: "100%", padding: 16,
                      background: "#0d1117", color: "#d4d4d4",
                      border: "none", outline: "none", resize: "none",
                      fontFamily: "'Fira Code', 'Consolas', monospace",
                      fontSize: 13, lineHeight: 1.6,
                      boxSizing: "border-box",
                    }}
                  />
                ) : (
                  <SyntaxHighlighter
                    language={lang}
                    style={atomOneDark}
                    showLineNumbers
                    customStyle={{
                      margin: 0, borderRadius: 0, fontSize: 13,
                      background: "#0d1117", minHeight: "100%",
                      padding: "16px",
                    }}
                    lineNumberStyle={{ color: "#444", minWidth: "3em" }}
                  >
                    {selectedArtifact.content || ""}
                  </SyntaxHighlighter>
                )}
              </div>
            </>
          ) : (
            <div style={{
              flex: 1, display: "flex", flexDirection: "column",
              alignItems: "center", justifyContent: "center",
              color: "#444", fontSize: 13, gap: 8,
            }}>
              <span style={{ fontSize: 32 }}>📄</span>
              <span>Select a file from the explorer to view its contents</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ServerGroupNode({ groupName, files, selectedArtifact, setSelectedArtifact, defaultOpen }) {
  const [open, setOpen] = React.useState(defaultOpen !== undefined ? defaultOpen : false);
  return (
    <div>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          display: "flex", alignItems: "center", gap: 6, width: "100%",
          padding: "5px 12px", background: "none", border: "none",
          color: "#ccc", fontSize: 12, fontWeight: 600, cursor: "pointer", textAlign: "left",
        }}
      >
        <span style={{ fontSize: 10, color: "#666" }}>{open ? "▾" : "▸"}</span>
        <span>{open ? "📂" : "📁"}</span>
        <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{groupName}</span>
        <span style={{ color: "#444", fontWeight: 400, fontSize: 11 }}>{files.length}</span>
      </button>
      {open && (
        <div style={{ paddingLeft: 8 }}>
          <FileTreeNode
            node={buildFileTree(files)}
            depth={0}
            defaultOpen={false}
            activeArtifact={selectedArtifact}
            setActiveArtifact={setSelectedArtifact}
          />
        </div>
      )}
    </div>
  );
}

function buildFileTree(artifacts) {
  const root = { name: '', children: {}, files: [] };
  artifacts.forEach(art => {
    const parts = (art.relativePath || art.name).split('/');
    let node = root;
    for (let i = 0; i < parts.length - 1; i++) {
      const p = parts[i];
      if (!node.children[p]) node.children[p] = { name: p, children: {}, files: [] };
      node = node.children[p];
    }
    node.files.push(art);
  });
  return root;
}

function FileTreeNode({ node, depth, activeArtifact, setActiveArtifact, defaultOpen }) {
  const [open, setOpen] = React.useState(defaultOpen !== undefined ? defaultOpen : depth === 0);
  const childDepth = node.name ? depth + 1 : depth;
  const indent = depth * 10;
  return (
    <div>
      {node.name && (
        <button className="sh-tree-folder-row" style={{ paddingLeft: indent }} title={node.name} onClick={() => setOpen(o => !o)}>
          <span className="sh-tree-caret">{open ? "▾" : "▸"}</span>
          <span className="sh-tree-folder-icon">{open ? "📂" : "📁"}</span>
          <span className="sh-tree-folder-name">{node.name}</span>
        </button>
      )}
      {(open || !node.name) && (
        <div className={node.name ? "sh-tree-children" : ""} style={node.name ? { marginLeft: indent + 6, borderLeft: "1px solid rgba(255,255,255,0.08)" } : {}}>
          {Object.values(node.children).map(child => (
            <FileTreeNode key={child.name} node={child} depth={childDepth} activeArtifact={activeArtifact} setActiveArtifact={setActiveArtifact} defaultOpen={defaultOpen} />
          ))}
          {node.files.map(art => (
            <button
              key={art.id}
              className={`sh-nav-item sh-tree-file ${activeArtifact?.id === art.id ? "active" : ""}`}
              style={{ paddingLeft: 10 }}
              title={art.name}
              onClick={() => setActiveArtifact(art)}
            >
              <span>{art.icon}</span>
              <span className="sh-tree-file-name">{art.name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function ApiFileTree({ apiName, artifacts, activeArtifact, setActiveArtifact }) {
  const [open, setOpen] = React.useState(false);
  const tree = buildFileTree(artifacts);
  return (
    <div className="sh-tree-api-group">
      <button className="sh-tree-api-label" onClick={() => setOpen(o => !o)}>
        <span className="sh-tree-caret">{open ? "▾" : "▸"}</span>
        <span>📂</span>
        <span className="sh-tree-api-name">{apiName}</span>
      </button>
      {open && <FileTreeNode node={tree} depth={1} activeArtifact={activeArtifact} setActiveArtifact={setActiveArtifact} />}
    </div>
  );
}

// ─── Chat message file tree (collapsible, per API group) ─────────
function ChatGroupTree({ group, files, apiProjectPath, onFileClick, onEdit, onDownload, onUpload, onUploadArchive }) {
  const [open, setOpen] = React.useState(false);
  const [editOpen, setEditOpen] = React.useState(false);
  const [editText, setEditText] = React.useState("");
  const [downloading, setDownloading] = React.useState(false);
  const [uploading, setUploading] = React.useState(false);
  const [uploadNotif, setUploadNotif] = React.useState(null);
  const uploadInputRef = React.useRef(null);
  const archiveInputRef = React.useRef(null);

  const showNotif = (ok, text) => {
    setUploadNotif({ ok, text });
    setTimeout(() => setUploadNotif(null), 3500);
  };
  const tree = buildFileTree(files);

  const submitEdit = () => {
    if (!editText.trim()) return;
    onEdit({ projectPath: apiProjectPath, editPrompt: editText.trim() });
    setEditText("");
    setEditOpen(false);
  };

  const handleDownload = () => {
    if (!onDownload) return;
    setDownloading(true);
    Promise.resolve(onDownload()).finally(() => setDownloading(false));
  };

  return (
    <div style={{ marginBottom: 4 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
        <button
          onClick={() => setOpen(o => !o)}
          style={{ display: "flex", alignItems: "center", gap: 6, flex: 1, minWidth: 140, background: "rgba(255,255,255,0.04)", border: "none", borderRadius: 6, padding: "5px 8px", cursor: "pointer", color: "#ccc", fontSize: 12, fontWeight: 600 }}
        >
          <span style={{ fontSize: 10 }}>{open ? "▾" : "▸"}</span>
          <span>📁</span>
          <span style={{ flex: 1, textAlign: "left", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 }}>{group}</span>
          <span style={{ color: "#555", fontWeight: 400 }}>{files.length}</span>
        </button>
        <div style={{ display: "flex", gap: 4, flexShrink: 0, marginLeft: "auto", flexWrap: "wrap", justifyContent: "flex-end" }}>
        <button
          onClick={handleDownload}
          disabled={downloading}
          title={`Download ${group} as ZIP`}
          style={{ padding: "4px 8px", background: "rgba(46,191,165,0.1)", border: "1px solid rgba(46,191,165,0.35)", borderRadius: 6, color: "#2ebfa5", fontSize: 11, cursor: downloading ? "wait" : "pointer", whiteSpace: "nowrap", opacity: downloading ? 0.6 : 1 }}
        >{downloading ? "⏳" : "⬇️"} {downloading ? "Zipping…" : "Download"}</button>
        {apiProjectPath && (
          <button
            onClick={() => setEditOpen(o => !o)}
            title={`Edit ${group}`}
            style={{ padding: "4px 8px", background: editOpen ? "rgba(255,160,50,0.2)" : "rgba(255,160,50,0.1)", border: "1px solid rgba(255,160,50,0.35)", borderRadius: 6, color: "#ffa032", fontSize: 11, cursor: "pointer", whiteSpace: "nowrap" }}
          >✏️ Agent Edit</button>
        )}
        {apiProjectPath && (
          <>
            <button
              onClick={() => uploadInputRef.current && uploadInputRef.current.click()}
              disabled={uploading}
              title={`Upload edited ${group} folder`}
              style={{ padding: "4px 8px", background: uploading ? "rgba(72,209,204,0.2)" : "rgba(72,209,204,0.1)", border: "1px solid rgba(72,209,204,0.35)", borderRadius: 6, color: "#48d1cc", fontSize: 11, cursor: uploading ? "wait" : "pointer", whiteSpace: "nowrap", opacity: uploading ? 0.6 : 1 }}
            >{uploading ? "⏳ Uploading…" : "⬆️ Upload"}</button>
            <button
              onClick={() => archiveInputRef.current && archiveInputRef.current.click()}
              disabled={uploading}
              title={`Upload a .jar or .zip archive of ${group}`}
              style={{ padding: "4px 8px", background: uploading ? "rgba(72,209,204,0.2)" : "rgba(72,209,204,0.1)", border: "1px solid rgba(72,209,204,0.35)", borderRadius: 6, color: "#48d1cc", fontSize: 11, cursor: uploading ? "wait" : "pointer", whiteSpace: "nowrap", opacity: uploading ? 0.6 : 1 }}
            >📦 .jar/.zip</button>
            <input
              ref={uploadInputRef}
              type="file"
              webkitdirectory=""
              directory=""
              multiple
              style={{ display: "none" }}
              onChange={(e) => {
                const files = Array.from(e.target.files);
                if (files.length > 0 && onUpload) {
                  setUploading(true);
                  Promise.resolve(onUpload({ files, projectPath: apiProjectPath }))
                    .then(() => showNotif(true, `✅ ${group} uploaded successfully`))
                    .catch(err => showNotif(false, `❌ Upload failed: ${err.message}`))
                    .finally(() => setUploading(false));
                }
                e.target.value = "";
              }}
            />
            <input
              ref={archiveInputRef}
              type="file"
              accept=".jar,.zip"
              style={{ display: "none" }}
              onChange={(e) => {
                const file = e.target.files && e.target.files[0];
                if (file && onUploadArchive) {
                  setUploading(true);
                  Promise.resolve(onUploadArchive({ file, projectPath: apiProjectPath }))
                    .then(() => showNotif(true, `✅ ${group} uploaded successfully`))
                    .catch(err => showNotif(false, `❌ Upload failed: ${err.message}`))
                    .finally(() => setUploading(false));
                }
                e.target.value = "";
              }}
            />
          </>
        )}
        </div>
      </div>
      {uploadNotif && (
        <div style={{ marginTop: 4, padding: "5px 10px", borderRadius: 5, fontSize: 11, fontWeight: 500,
          background: uploadNotif.ok ? "rgba(46,191,165,0.12)" : "rgba(255,80,80,0.12)",
          border: `1px solid ${uploadNotif.ok ? "rgba(46,191,165,0.35)" : "rgba(255,80,80,0.35)"}`,
          color: uploadNotif.ok ? "#2ebfa5" : "#ff6b6b" }}>
          {uploadNotif.text}
        </div>
      )}
      {editOpen && (
        <div style={{ marginTop: 6, display: "flex", flexDirection: "column", gap: 6, padding: "8px", background: "rgba(255,160,50,0.05)", borderRadius: 6, border: "1px solid rgba(255,160,50,0.2)" }}>
          <div style={{ fontSize: 11, color: "#ffa032" }}>Editing: <strong>{group}</strong></div>
          <textarea
            autoFocus
            rows={2}
            placeholder="Describe your change… (e.g. 'add retry logic to the error handler')"
            value={editText}
            onChange={e => setEditText(e.target.value)}
            onKeyDown={e => {
              if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); submitEdit(); }
              if (e.key === "Escape") { setEditOpen(false); setEditText(""); }
            }}
            style={{ padding: "7px", borderRadius: 6, border: "1px solid rgba(255,255,255,0.15)", background: "rgba(255,255,255,0.05)", color: "#fff", fontSize: 12, resize: "none" }}
          />
          <div style={{ display: "flex", gap: 6 }}>
            <button
              onClick={submitEdit}
              disabled={!editText.trim()}
              style={{ padding: "6px 14px", borderRadius: 6, background: "#4a90d9", color: "#fff", border: "none", cursor: "pointer", fontSize: 12, fontWeight: 600 }}
            >Apply</button>
            <button
              onClick={() => { setEditOpen(false); setEditText(""); }}
              style={{ padding: "6px 14px", borderRadius: 6, background: "rgba(255,255,255,0.08)", color: "#aaa", border: "1px solid rgba(255,255,255,0.12)", cursor: "pointer", fontSize: 12 }}
            >Cancel</button>
          </div>
        </div>
      )}
      {open && (
        <div style={{ paddingLeft: 8, borderLeft: "1px solid rgba(255,255,255,0.08)", marginLeft: 8 }}>
          <FileTreeNode
            node={tree}
            depth={0}
            activeArtifact={null}
            setActiveArtifact={(art) => onFileClick({ id: art?.id })}
          />
        </div>
      )}
    </div>
  );
}

// ─── Artifact drawer with inline edit + save to disk ─────────────
function getArtifactLang(filename) {
  const ext = (filename || "").split(".").pop().toLowerCase();
  return { xml: "xml", raml: "yaml", yaml: "yaml", yml: "yaml",
           java: "java", json: "json", md: "markdown", properties: "ini",
           sh: "bash", puml: "text", ts: "typescript", js: "javascript",
           html: "html", css: "css", xml: "xml" }[ext] || "text";
}

function ArtifactDrawer({ artifact, onClose, onContentSaved }) {
  const [editMode, setEditMode] = React.useState(false);
  const [editContent, setEditContent] = React.useState(artifact.content || "");
  const [saving, setSaving] = React.useState(false);
  const [saveMsg, setSaveMsg] = React.useState("");
  const lang = getArtifactLang(artifact.name);
  const lineCount = (editContent.match(/\n/g) || []).length + 1;

  React.useEffect(() => {
    setEditMode(false);
    setEditContent(artifact.content || "");
    setSaveMsg("");
  }, [artifact.id]);

  const handleSave = () => {
    if (!artifact.relativePath || !artifact.svcProjectPath) {
      setSaveMsg("⚠️ No file path available.");
      return;
    }
    setSaving(true);
    setSaveMsg("");
    const savePayload = {
      projectPath: artifact.svcProjectPath,
      relativePath: artifact.relativePath,
      content: editContent,
    };
    console.log("[SmartHub] POST /api/v1/project/save-file →", savePayload);
    fetch(`${backendUrl}/api/v1/project/save-file`, {
      method: "POST",
      headers: { "Content-Type": "application/json", "x-access-token": localStorage.getItem("token") },
      body: JSON.stringify(savePayload),
    })
      .then(r => r.json())
      .then(d => {
        console.log("[SmartHub] POST /api/v1/project/save-file ←", d);
        onContentSaved(editContent);
        setEditMode(false);
        setSaveMsg("✅ Saved");
        setTimeout(() => setSaveMsg(""), 3000);
      })
      .catch(() => setSaveMsg("❌ Save failed"))
      .finally(() => setSaving(false));
  };

  return (
    <div className="sh-artifact-drawer">
      {/* ── IDE-style tab bar ── */}
      <div className="sh-artifact-drawer-header">
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 11, opacity: 0.5 }}>{lang.toUpperCase()}</span>
          <span style={{ opacity: 0.3 }}>|</span>
          <span>{artifact.icon} {artifact.name}</span>
          {editMode && <span style={{ fontSize: 10, color: "#ffa032", background: "rgba(255,160,50,0.12)", padding: "1px 6px", borderRadius: 4 }}>EDITING</span>}
        </div>
        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          {saveMsg && <span style={{ fontSize: 11, color: saveMsg.startsWith("✅") ? "#4caf50" : "#f44336" }}>{saveMsg}</span>}
          {editMode ? (
            <>
              <button className="sh-inline-copy-btn" onClick={handleSave} disabled={saving} style={{ background: "rgba(46,191,165,0.2)", color: "#2EBFA5", borderColor: "rgba(46,191,165,0.4)" }}>
                {saving ? "Saving…" : "💾 Save"}
              </button>
              <button className="sh-inline-copy-btn" onClick={() => { setEditMode(false); setEditContent(artifact.content || ""); }}>✕ Cancel</button>
            </>
          ) : (
            <button className="sh-inline-copy-btn" onClick={() => setEditMode(true)} style={{ background: "rgba(255,160,50,0.1)", color: "#ffa032", borderColor: "rgba(255,160,50,0.35)" }}>✏️ Edit</button>
          )}
          <button className="sh-inline-copy-btn" onClick={() => navigator.clipboard.writeText(editMode ? editContent : artifact.content || "")}>📋 Copy</button>
          <button className="sh-inline-copy-btn" onClick={onClose}>✕</button>
        </div>
      </div>

      {/* ── Editor / Viewer ── */}
      {editMode ? (
        <div style={{ display: "flex", background: "#0d1117", overflow: "auto", maxHeight: 420 }}>
          {/* Line numbers */}
          <div style={{ padding: "12px 0", minWidth: 44, textAlign: "right", paddingRight: 12, paddingLeft: 8, background: "#0d1117", color: "#4a4a5a", fontSize: 12, fontFamily: "JetBrains Mono, Consolas, monospace", lineHeight: "1.6", userSelect: "none", flexShrink: 0, borderRight: "1px solid #1e1e2e" }}>
            {Array.from({ length: lineCount }, (_, i) => (
              <div key={i}>{i + 1}</div>
            ))}
          </div>
          <textarea
            value={editContent}
            onChange={e => setEditContent(e.target.value)}
            style={{ flex: 1, minHeight: 200, background: "#0d1117", color: "#e6edf3", border: "none", padding: "12px 14px", fontFamily: "JetBrains Mono, Consolas, monospace", fontSize: 12, lineHeight: 1.6, resize: "none", boxSizing: "border-box", outline: "none", whiteSpace: "pre", overflowWrap: "normal", overflow: "auto" }}
            spellCheck={false}
          />
        </div>
      ) : (
        <SyntaxHighlighter
          language={lang}
          style={atomOneDark}
          showLineNumbers
          lineNumberStyle={{ color: "#4a4a5a", fontSize: 11, paddingRight: 14, minWidth: 44, userSelect: "none" }}
          customStyle={{ margin: 0, background: "#0d1117", fontSize: 12, lineHeight: 1.6, maxHeight: 420, overflow: "auto", borderRadius: 0 }}
          codeTagProps={{ style: { fontFamily: "JetBrains Mono, Consolas, monospace" } }}
        >
          {artifact.content || "// No content"}
        </SyntaxHighlighter>
      )}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────
export default function SmartHub() {
  const navigate = useNavigate();
  const location = useLocation();
  let { projId, projName } = useParams();

  // ── Technology permissions ────────────────────────────────────
  const { hasPermission } = usePermissions();
  const _allTechs = ['mule', 'springboot', 'dotnet'];
  const _techPermMap = { mule: 'techMule', springboot: 'techSpringBoot', dotnet: 'techDotNet' };
  const _anyTechPerm = _allTechs.some(t => hasPermission(_techPermMap[t]));
  // If no tech perms are set at all (legacy account) → allow all; otherwise filter
  // if/let avoids a ternary between hook calls (eslint-plugin-react-hooks v1.x flags those)
  let allowedTechs = _allTechs;
  if (_anyTechPerm) { allowedTechs = _allTechs.filter(t => hasPermission(_techPermMap[t])); }

  const searchParams = new URLSearchParams(location.search);
  // const projName = searchParams.get("projectName") ||
  //   (() => { try { return JSON.parse(localStorage.getItem("user") || "{}")?.name; } catch { return ""; } })() || "";

  // ── Per-user localStorage key (before any useRef/useState) ───
  const STORAGE_KEY = (() => {
    try {
      const uid = JSON.parse(localStorage.getItem("user") || "{}")?.id || "default";
      return `smarthub_state_${uid}`;
    } catch (e) { return "smarthub_state_default"; }
  })();

  // ── Load persisted state (once on mount) ─────────────────────
  const _persisted = (() => {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}"); }
    catch (e) { return {}; }
  })();

  // ── Initial values for persisted chat state ──────────────────
  // if/let avoids ternary operators between hook calls (eslint-plugin-react-hooks v1.x
  // flags hooks as "conditional" when ternaries appear between two hook call sites).
  const _STALE_THRESHOLD_MS = 6 * 60 * 60 * 1000;
  const _initShowStaleBanner = Boolean(
    !projId &&                                  // opening a saved project starts fresh — no stale warning
    _persisted.lastActive &&
    (Date.now() - _persisted.lastActive > _STALE_THRESHOLD_MS) &&
    _persisted.messages &&
    _persisted.messages.length > 1
  );

  let _initMessages  = _persisted.messages  || [INIT_MESSAGE];
  let _initLogs      = _persisted.logs      || [];
  let _initPuml      = _persisted.plantumlCode || `@startuml
title Customer Onboarding & KYC Platform\\n(Spring Boot Microservices | AWS EKS)

skinparam sequenceArrowThickness 2
skinparam roundcorner 8
skinparam sequenceParticipantBorderThickness 2
skinparam noteBorderThickness 1
skinparam sequenceGroupBorderThickness 2
skinparam defaultFontSize 12
skinparam sequenceMessageAlign center

participant "Kong API Gateway" as KongAPIGateway #FFE0B2
participant "customer-onboarding-experience-service" as ExperienceService #E3F2FD
participant "customer-onboarding-system-service" as SystemService #E8F5E9
participant "Azure Key Vault" as Vault #FFF9C4
database "MySQL" as MySQL #E8F5E9
participant "Aadhaar Verification APIs" as AadhaarAPI #F3E5F5
participant "PAN Verification APIs" as PANAPI #F3E5F5
participant "AML Screening Systems" as AMLSystem #F3E5F5
participant "Fraud Detection Systems" as FraudSystem #F3E5F5
participant "CRM Systems" as CRMSystem #F3E5F5
participant "Notification Services" as NotificationService #F3E5F5

note over ExperienceService : Deployed on aws-eks
note over SystemService : Deployed on aws-eks
note over ExperienceService, Vault : At startup, secrets are fetched from Azure Key Vault and cached as secure properties

== Customer Onboarding ==

KongAPIGateway -> ExperienceService : POST /api/v1/customers/onboard\\n[X-Correlation-Id, X-Channel-Id]
note left of KongAPIGateway : Client sends POST /api/v1/customers/onboard\\nKong validates JWT authentication before routing

note over ExperienceService : Extract & validate mandatory headers:\\nX-Correlation-Id, X-Channel-Id
note over ExperienceService : Perform request validation
note over ExperienceService : Perform header validation
note over ExperienceService : Perform channel validation
note over ExperienceService : Perform business validation
note over ExperienceService : Enrich request & propagate correlation ID

ExperienceService -> Vault : fetch secret database-password
Vault --> ExperienceService : database-password

ExperienceService -> SystemService : POST /api/v1/system/customers/onboard\\n[X-Correlation-Id, X-Channel-Id] via Spring WebClient

note over SystemService : Extract & validate mandatory headers:\\nX-Correlation-Id, X-Channel-Id
note over SystemService : Perform request validation
note over SystemService : Perform header validation
note over SystemService : Perform path variable validation
note over SystemService : Perform bean validation
note over SystemService : Perform business validation
note over SystemService : Perform duplicate customer validation
note over SystemService : Perform compliance validation

SystemService -> Vault : fetch secret database-password
Vault --> SystemService : database-password

SystemService -> MySQL : INSERT CustomerProfile
MySQL --> SystemService : CustomerProfile persisted

SystemService -> MySQL : INSERT CustomerAddress
MySQL --> SystemService : CustomerAddress persisted

SystemService -> MySQL : INSERT CustomerDocument
MySQL --> SystemService : CustomerDocument persisted

SystemService -> MySQL : INSERT CustomerAudit
MySQL --> SystemService : CustomerAudit persisted

SystemService -> MySQL : INSERT CustomerStatusHistory
MySQL --> SystemService : CustomerStatusHistory persisted

SystemService --> ExperienceService : onboarding response

note over ExperienceService : Transform response to standardized API response

ExperienceService --> KongAPIGateway : standardized API response — 201 Created
KongAPIGateway --> KongAPIGateway : Return 201 Created to Client

== Customer KYC Verification ==

KongAPIGateway -> ExperienceService : POST /api/v1/customers/{customerId}/kyc\\n[X-Correlation-Id, X-Channel-Id]
note left of KongAPIGateway : Client sends POST /api/v1/customers/{customerId}/kyc\\nKong validates JWT authentication before routing

note over ExperienceService : Extract & validate mandatory headers:\\nX-Correlation-Id, X-Channel-Id
note over ExperienceService : Extract & validate path variable: customerId
note over ExperienceService : Perform request validation
note over ExperienceService : Perform header validation
note over ExperienceService : Perform channel validation
note over ExperienceService : Perform business validation
note over ExperienceService : Enrich request & propagate correlation ID

ExperienceService -> Vault : fetch secret database-password
Vault --> ExperienceService : database-password

ExperienceService -> SystemService : POST /api/v1/system/customers/{customerId}/kyc\\n[X-Correlation-Id, X-Channel-Id] via Spring WebClient

note over SystemService : Extract & validate mandatory headers:\\nX-Correlation-Id, X-Channel-Id
note over SystemService : Extract & validate path variable: customerId
note over SystemService : Perform request validation
note over SystemService : Perform header validation
note over SystemService : Perform bean validation
note over SystemService : Perform business validation
note over SystemService : Perform KYC validation
note over SystemService : Perform compliance validation

SystemService -> Vault : fetch secret aadhaar-api-key
Vault --> SystemService : aadhaar-api-key

SystemService -> AadhaarAPI : Aadhaar verification (retry + exponential backoff)
alt Aadhaar verification fails after retries
  AadhaarAPI --> SystemService : error response
  note over SystemService : circuit breaker opens and fallback response returned
else Aadhaar verification succeeds
  AadhaarAPI --> SystemService : Aadhaar verification result
end

SystemService -> Vault : fetch secret pan-api-key
Vault --> SystemService : pan-api-key

SystemService -> PANAPI : PAN verification (retry + exponential backoff)
alt PAN verification fails after retries
  PANAPI --> SystemService : error response
  note over SystemService : circuit breaker opens and fallback response returned
else PAN verification succeeds
  PANAPI --> SystemService : PAN verification result
end

SystemService -> Vault : fetch secret aml-api-key
Vault --> SystemService : aml-api-key

SystemService -> AMLSystem : AML screening (retry + exponential backoff)
alt AML screening fails after retries
  AMLSystem --> SystemService : error response
  note over SystemService : circuit breaker opens and fallback response returned
else AML screening succeeds
  AMLSystem --> SystemService : AML screening result
end

SystemService -> Vault : fetch secret fraud-api-key
Vault --> SystemService : fraud-api-key

SystemService -> FraudSystem : Fraud detection (retry + exponential backoff)
alt Fraud detection fails after retries
  FraudSystem --> SystemService : error response
  note over SystemService : circuit breaker opens and fallback response returned
else Fraud detection succeeds
  FraudSystem --> SystemService : Fraud detection result
end

SystemService -> Vault : fetch secret database-password
Vault --> SystemService : database-password

SystemService -> MySQL : INSERT CustomerKyc
MySQL --> SystemService : CustomerKyc persisted

SystemService -> MySQL : INSERT CustomerAudit
MySQL --> SystemService : CustomerAudit persisted

SystemService -> MySQL : INSERT CustomerStatusHistory
MySQL --> SystemService : CustomerStatusHistory persisted

SystemService -> Vault : fetch secret crm-api-key
Vault --> SystemService : crm-api-key

SystemService -> CRMSystem : CRM update (retry + exponential backoff)
alt CRM update fails after retries
  CRMSystem --> SystemService : error response
  note over SystemService : circuit breaker opens and fallback response returned
else CRM update succeeds
  CRMSystem --> SystemService : CRM update result
end

SystemService -> Vault : fetch secret notification-api-key
Vault --> SystemService : notification-api-key

SystemService -> NotificationService : Send notification (retry + exponential backoff)
alt Notification fails after retries
  NotificationService --> SystemService : error response
  note over SystemService : circuit breaker opens and fallback response returned
else Notification succeeds
  NotificationService --> SystemService : notification sent confirmation
end

SystemService --> ExperienceService : KYC verification response

note over ExperienceService : Transform response to standardized API response

ExperienceService --> KongAPIGateway : standardized API response — 200 OK
KongAPIGateway --> KongAPIGateway : Return 200 OK to Client

== Get Customer Profile ==

KongAPIGateway -> ExperienceService : GET /api/v1/customers/{customerId}\\n[X-Correlation-Id, X-Channel-Id]
note left of KongAPIGateway : Client sends GET /api/v1/customers/{customerId}\\nKong validates JWT authentication before routing

note over ExperienceService : Extract & validate mandatory headers:\\nX-Correlation-Id, X-Channel-Id
note over ExperienceService : Extract & validate path variable: customerId
note over ExperienceService : Perform header validation
note over ExperienceService : Perform channel validation
note over ExperienceService : Propagate correlation ID

ExperienceService -> Vault : fetch secret database-password
Vault --> ExperienceService : database-password

ExperienceService -> SystemService : GET /api/v1/system/customers/{customerId}\\n[X-Correlation-Id, X-Channel-Id] via Spring WebClient

note over SystemService : Extract & validate mandatory headers:\\nX-Correlation-Id, X-Channel-Id
note over SystemService : Extract & validate path variable: customerId
note over SystemService : Perform header validation

SystemService -> Vault : fetch secret database-password
Vault --> SystemService : database-password

SystemService -> MySQL : SELECT CustomerProfile by customerId
MySQL --> SystemService : CustomerProfile record

SystemService -> MySQL : SELECT CustomerAddress by customerId
MySQL --> SystemService : CustomerAddress record

SystemService -> MySQL : SELECT CustomerDocument by customerId
MySQL --> SystemService : CustomerDocument record

SystemService --> ExperienceService : customer profile response

note over ExperienceService : Transform response to standardized API response

ExperienceService --> KongAPIGateway : standardized API response — 200 OK
KongAPIGateway --> KongAPIGateway : Return 200 OK to Client

== Get Customer Status ==

KongAPIGateway -> ExperienceService : GET /api/v1/customers/{customerId}/status\\n[X-Correlation-Id, X-Channel-Id]
note left of KongAPIGateway : Client sends GET /api/v1/customers/{customerId}/status\\nKong validates JWT authentication before routing

note over ExperienceService : Extract & validate mandatory headers:\\nX-Correlation-Id, X-Channel-Id
note over ExperienceService : Extract & validate path variable: customerId
note over ExperienceService : Perform header validation
note over ExperienceService : Perform channel validation
note over ExperienceService : Propagate correlation ID

ExperienceService -> Vault : fetch secret database-password
Vault --> ExperienceService : database-password

ExperienceService -> SystemService : GET /api/v1/system/customers/{customerId}/status\\n[X-Correlation-Id, X-Channel-Id] via Spring WebClient

note over SystemService : Extract & validate mandatory headers:\\nX-Correlation-Id, X-Channel-Id
note over SystemService : Extract & validate path variable: customerId
note over SystemService : Perform header validation

SystemService -> Vault : fetch secret database-password
Vault --> SystemService : database-password

SystemService -> MySQL : SELECT CustomerProfile by customerId
MySQL --> SystemService : CustomerProfile record

SystemService -> MySQL : SELECT CustomerStatusHistory by customerId
MySQL --> SystemService : CustomerStatusHistory records

SystemService --> ExperienceService : customer status response

note over ExperienceService : Transform response to standardized API response

ExperienceService --> KongAPIGateway : standardized API response — 200 OK
KongAPIGateway --> KongAPIGateway : Return 200 OK to Client
@enduml`;
  let _initArtifacts = _persisted.artifacts || [];
  let _initSavedId   = _persisted.savedProjectId || null;
  if (projId) { _initMessages = [INIT_MESSAGE]; _initLogs = []; _initPuml = ""; _initArtifacts = []; _initSavedId = projId; }

  // ── Refs ─────────────────────────────────────────────────────
  const socketRef   = useRef(null);
  const hasInit     = useRef(false);
  const chatEndRef  = useRef(null);
  const logsEndRef  = useRef(null);
  const fileInputRef = useRef(null);
  const abortRef           = useRef(null);
  const lastHubBodyRef     = useRef(null);
  const lastActionRef      = useRef(null);   // 'hub' | 'mule_deploy' | 'spring_deploy' | 'file_edit'
  const lastDeployBodyRef  = useRef(null);   // stores { type, body } for deploy retries
  const lastFileEditRef    = useRef(null);   // stores { url, body } for file edit retries
  const deploymentPausedRef   = useRef(false); // true when backend has paused for user confirmation
  const deploymentErrorShown  = useRef(false); // prevents duplicate error chat messages
  const deploymentErrorLogs   = useRef([]);    // collects backend error lines to embed in chat
  const lastDeployErrMsg      = useRef("");    // deduplicates consecutive identical live log errors
  const savedProjectIdRef  = useRef(projId || _persisted.savedProjectId || null);
  const selectedTechRef    = useRef("mule");

  // ── UI state ──────────────────────────────────────────────────
  const [activeTab,      setActiveTab]      = useState("greenfield");
  const [bfSessionKey,   setBfSessionKey]   = useState(0);
  const bfScanActiveRef = useRef(false); // true only while a brownfield scan started by THIS session is in flight
  const [migrationKey,   setMigrationKey]   = useState(0);
  // Handoff from New Integrations → Enhance Existing compliance scan on the generated
  // folder: { mode:'compliance', projectPath }. Consumed by BrownfieldPanel on mount.
  const [pendingBfJob,   setPendingBfJob]   = useState(null);

  // Auto-select the first permitted tab if the default tab isn't accessible
  useEffect(() => {
    const canGreen   = hasPermission('smartHub') || hasPermission('greenfieldAgent');
    const canBrown   = hasPermission('smartHub') || hasPermission('brownfieldAgent');
    const canMigrate = hasPermission('migrationAgent') || hasPermission('migrationDotnet') || hasPermission('migrationJitterbit');
    if (activeTab === 'greenfield' && !canGreen && canBrown) setActiveTab('brownfield');
    if (activeTab === 'brownfield' && !canBrown && canGreen) setActiveTab('greenfield');
    if ((activeTab === 'greenfield' || activeTab === 'brownfield') && !canGreen && !canBrown && canMigrate) {
      setActiveTab('migration');
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const [activeAgent,   setActiveAgent]   = useState(_persisted.activeAgent   ?? null);
  const [selectedTech,  setSelectedTech]  = useState(_persisted.selectedTech  ?? "");

  // ── Session (moved up so useBrownfieldTab can use it) ─────────
  // Full declaration below; this gives us the value early
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [sessionId, setSessionId] = useState(null);

  // ── Brownfield state (owned by BrownfieldTab.jsx) ─────────────
  const bf = useBrownfieldTab({ activeTab, sessionId });
  const bfSetMessagesRef = useRef(bf.setBfMessages);
  useEffect(() => { bfSetMessagesRef.current = bf.setBfMessages; });
  const bfSetLogsRef = useRef(bf.setBfLogs);
  useEffect(() => { bfSetLogsRef.current = bf.setBfLogs; });

  const [showExamples,    setShowExamples]    = useState(false);
  const [showHelp,        setShowHelp]        = useState(false);
  // Sign-out from SmartHub: also clears this user's persisted SmartHub session before logout.
  const smartHubLogout = () => {
    let u = {};
    try { u = JSON.parse(localStorage.getItem('user') || '{}'); } catch (_) { u = {}; }
    if (u && u.id) {
      localStorage.removeItem(`smarthub_state_${u.id}`);
      localStorage.removeItem('langraph_session_id');
    }
    localStorage.removeItem('user');
    localStorage.setItem('token', null);
    window.location.href = '/';
  };
  const [showLogsModal,   setShowLogsModal]   = useState(false);
  const [logsModalFilter, setLogsModalFilter] = useState("all");
  const [showTipCard,     setShowTipCard]     = useState(
    () => localStorage.getItem("smarthub_tip_dismissed") !== "1"
  );
  const [tourStep,        setTourStep]        = useState(
    () => localStorage.getItem("smarthub_tip_dismissed") !== "1" ? 1 : 0
  );
  // Tour refs
  const inputWrapRef  = useRef(null);
  const expandBtnRef  = useRef(null);
  const examplesRef   = useRef(null);
  const logsRef       = useRef(null);
  const newSessionRef = useRef(null);
  const backBtnRef    = useRef(null);
  const tabsRef       = useRef(null);
  const artifactsRef  = useRef(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [sidebarWidth,     setSidebarWidth]     = useState(240);
  const sidebarResizing = useRef(false);
  const [showPumlPanel, setShowPumlPanel] = useState(_persisted.showPumlPanel ?? false);
  const [uploadedFile,  setUploadedFile]  = useState(null);
  const [dragOver,      setDragOver]      = useState(false);
  const [pendingAttachment, setPendingAttachment] = useState(null);
  const [reopenPromptModal, setReopenPromptModal] = useState(false);
  const [activeArtifact, setActiveArtifact] = useState(null); // never restore — panel starts closed
  const [connected,     setConnected]     = useState(false);
  const [deploymentConfirmation, setDeploymentConfirmation] = useState(null);
  const [showDeployWindow,  setShowDeployWindow]  = useState(false);
  const [deployWindowMin,   setDeployWindowMin]   = useState(false);
  const [deployLogs,        setDeployLogs]        = useState([]);
  const [deployStages,      setDeployStages]      = useState([]);
  // Azure DevOps pipeline logs streamed over 'pipeline:log', grouped per stage.
  const [pipelineStages,    setPipelineStages]    = useState([]); // [{ stage, recordType, lines[], failed, updatedAt }]
  const [deployApiStatuses, setDeployApiStatuses] = useState(_persisted.deployApiStatuses || []);
  // Restore current API index from persisted state (find the last active/error API)
  const _persistedApiIdx = (function() {
    var apis = _persisted.deployApiStatuses || [];
    var idx = apis.findIndex(function(a) { return a.status === 'active'; });
    if (idx >= 0) return idx;
    // If no active, find last non-pending (last one that was touched)
    for (var i = apis.length - 1; i >= 0; i--) {
      if (apis[i].status !== 'pending') return i;
    }
    return 0;
  }());
  const deployCurrentApiRef    = React.useRef(_persistedApiIdx);
  const deployApiCountRef      = React.useRef((_persisted.deployApiStatuses || []).length);
  const deployingSeenCountRef  = React.useRef(0);
  const deployApiStatusesRef   = React.useRef(_persisted.deployApiStatuses || []);
  const validatingCountRef     = React.useRef(0); // counts "Validating project structure..." — one per API, used as fallback transition trigger

  // ── Unified chat + state ──────────────────────────────────────
  const [showStaleBanner, setShowStaleBanner] = useState(_initShowStaleBanner);
  const [messages,  setMessages]  = useState(_initMessages);
  const [input,     setInput]     = useState("");
  const [loading,   setLoading]   = useState(_persisted.loading ?? false);
  const [thinkingMsg, setThinkingMsg] = useState("");
  const [logs,      setLogs]      = useState(_initLogs);
  const [plantumlCode, setPlantumlCode] = useState("");  // chip only shows on explicit attach, not on restore
  const plantumlCodeRef = useRef(_initPuml);
  const attachedImageRef = useRef(null);
  const [editingMsgIdx, setEditingMsgIdx] = useState(null);
  const [editingText,   setEditingText]   = useState("");

  // ── Artifacts (flat list) ─────────────────────────────────────
  const [artifacts, setArtifacts] = useState(_initArtifacts);

  // ── Deployment resources ──────────────────────────────────────
  const [muleAccounts,  setMuleAccounts]  = useState([]);
  const [repoAccounts,  setRepoAccounts]  = useState([]);  // github
  const [gitlabAccounts,setGitlabAccounts]= useState([]);
  const [awsClusters,   setAwsClusters]   = useState([]);  // awsresource (has credentials + cluster info)
  const [awsAcctList,   setAwsAcctList]   = useState([]);  // awsaccount (has awsaccountid)
  const [azureAccounts, setAzureAccounts] = useState([]);
  const [gkeAccounts,   setGkeAccounts]   = useState([]);
  const [sonarAccounts,       setSonarAccounts]       = useState([]);  // sonarqube
  const [azureVaultAccounts,  setAzureVaultAccounts]  = useState([]);  // azure key vaults
  const [deployEnvs,      setDeployEnvs]      = useState([]);   // mule environments
  const [deployConfig,    setDeployConfig]    = useState(_persisted.deployConfig || {});   // accumulates selections
  const [deployEditModal, setDeployEditModal] = useState(null); // { title, options } when edit popup is open
  const deployEditModeRef = React.useRef(null); // 'acct'|'env'|'repo'|'cicd'|'sonar'|'kv' when editing a single field
  const [generatedApis,      setGeneratedApis]      = useState(_persisted.generatedApis || _persisted.muleApis || []);
  const [projectPath,   setProjectPath]   = useState(_persisted.projectPath   || "");
  const [packageName,   setPackageName]   = useState(_persisted.packageName   || "");
  const [deployProjName,setDeployProjName]= useState(_persisted.deployProjName|| "");
  const [designProjectName, setDesignProjectName] = useState(_persisted.designProjectName || "");
  const [confluenceDocs,    setConfluenceDocs]    = useState(() =>
    (_persisted.artifacts || [])
      .filter(a => a.name && a.name.endsWith('.md') && a.content)
      .map(a => ({ title: a.name.replace(/\.md$/i, ''), markdown: a.content }))
  );
  const [showConfluenceModal, setShowConfluenceModal] = useState(false);
  // Save-to-repository (push generated code to a new GitHub/Azure repo; no deploy).
  const [showSaveRepoModal, setShowSaveRepoModal] = useState(false);
  const [savedRepoResult, setSavedRepoResult] = useState(_persisted.savedRepoResult || null);
  const [awaitingInput,  setAwaitingInput]  = useState(null);
  const [showPumlCode,   setShowPumlCode]   = useState(false);
  const [showSplit,      setShowSplit]      = useState(false); // diagram: split-by-flow view
  const splitTouchedRef = useRef(false); // once the user toggles, stop auto-forcing
  // Split prompt decision for a LARGE diagram: null = not asked yet (show the prompt),
  // 'split' = user approved splitting, 'full' = user chose the whole diagram.
  const [splitChoice, setSplitChoice] = useState(null);
  const lastDiagramSigRef = useRef(null); // identity of the diagram we last decided on
  const [savedProjectId, setSavedProjectId] = useState(_initSavedId);
  const [saving, setSaving] = useState(false);
  // Re-decide whenever a genuinely different diagram arrives (loaded or generated),
  // NOT on every re-render. A large diagram with flow sections re-asks the user
  // (splitChoice → null → prompt); a small one renders whole with no prompt. Because
  // this keys on content identity, loading a new project always re-prompts, while the
  // manual toggle afterward is respected.
  useEffect(() => {
    const code = plantumlCode || "";
    const sig = `${code.length}|${code.slice(0, 80)}`;
    if (sig === lastDiagramSigRef.current) return; // same diagram → keep the user's choice
    lastDiagramSigRef.current = sig;
    splitTouchedRef.current = false;
    setShowSplit(false);
    if (isLargePuml(code) && hasFlowSections(code)) {
      setSplitChoice(null);     // ask before splitting
    } else {
      setSplitChoice('full');   // nothing to ask; show the whole diagram
    }
  }, [plantumlCode]); // eslint-disable-line react-hooks/exhaustive-deps
  const [integrationEditMsgIdx, setIntegrationEditMsgIdx] = useState(null);
  const [integrationEditText,   setIntegrationEditText]   = useState("");
  const [diagramEditMsgIdx,     setDiagramEditMsgIdx]     = useState(null);
  const [diagramEditText,       setDiagramEditText]       = useState("");
  const [designNameMsgIdx,      setDesignNameMsgIdx]      = useState(null);
  const [designNameText,        setDesignNameText]        = useState("");
  const [designSaved,           setDesignSaved]           = useState(false);
  const [integrationNameMsgIdx, setIntegrationNameMsgIdx] = useState(null);
  const [integrationNameText,   setIntegrationNameText]   = useState("");
  const [integrationNameError,  setIntegrationNameError]  = useState("");
  const [designNameError,       setDesignNameError]       = useState("");
  const [usageData,             setUsageData]             = useState(null);
  const [extendingUsage,        setExtendingUsage]        = useState(false);
  const [extensionStatus,       setExtensionStatus]       = useState(null); // null | "pending" | "approved"
  const [liveTokens,            setLiveTokens]            = useState(null);   // cumulative from socket
  const [tokenWarning,          setTokenWarning]          = useState(null);   // 80% warning payload
  const [showPromptModal,       setShowPromptModal]       = useState(false);
  const [ideModalGroups,        setIdeModalGroups]        = useState(null);  // null = closed
  const [downloadingAll,        setDownloadingAll]        = useState(false); // chat-view "Download All"

  // Zip + download every file in the project. The chat `groups` carry only file
  // metadata (no content), so we FETCH the real content from the server here —
  // same source as IdeModal's Download All.
  const downloadAllFiles = async (groups, projPath) => {
    setDownloadingAll(true);
    try {
      const token = localStorage.getItem('token');
      const loadFiles = async (p, label) => {
        console.log(`[Download All] GET project/load projectPath=${p} (group=${label})`);
        try {
          const r = await fetch(`${backendUrl}/api/v1/project/load?projectPath=${encodeURIComponent(p)}`, {
            headers: { 'x-access-token': token },
          });
          if (!r.ok) { console.warn(`[Download All] load ${label} HTTP ${r.status}`); return []; }
          const res = await r.json();
          const files = res?.files || [];
          const bytes = files.reduce((s, f) => s + (f.size || 0), 0);
          console.log(`[Download All] ← group=${label} resolved=${res?.root} files=${files.length} bytes=${bytes}`);
          return files;
        } catch (e) { console.warn(`[Download All] load ${label} failed:`, e.message); return []; }
      };

      const zip = new JSZip();
      const authToken = localStorage.getItem('token');
      const rootName = projPath
        ? projPath.replace(/\\/g, '/').split('/').filter(Boolean).pop()
        : 'generated-apis';
      const missing = [];
      let added = 0;

      const addFiles = (files, prefix) => {
        files.forEach(f => {
          const rel = (f.relativePath || f.name).replace(/\\/g, '/');
          const zipPath = `${prefix}/${rel}`;
          if (f.content === undefined || f.content === null || f.content === "") { missing.push(zipPath); return; }
          zip.file(zipPath, f.content, f.encoding === 'base64' ? { base64: true } : {});
          added += 1;
        });
      };

      if (projPath) {
        // Single root fetch — recursive result already carries each service subfolder.
        const files = await loadFiles(projPath, '__root__');
        addFiles(files, rootName);
      } else {
        // No top-level path — fetch each group by its apiProjectPath.
        for (const [group, gfiles] of Object.entries(groups || {})) {
          const p = (gfiles || [])[0]?.apiProjectPath;
          if (!p) { console.warn(`[Download All] group ${group} has no apiProjectPath — skipped`); continue; }
          const files = await loadFiles(p, group);
          addFiles(files, `${rootName}/${group}`);
        }
      }

      console.log(`[Download All] done: added=${added} missing=${missing.length}`, missing.slice(0, 20));
      if (added === 0) {
        addLog(setLogs, 'error', `Download failed — no file content was found for this project. Check the backend [loadProject] log for the resolved path.`);
        return;
      }
      if (missing.length) {
        addLog(setLogs, 'warning', `Download: ${added} file(s) zipped, ${missing.length} skipped (empty).`);
      }
      const blob = await zip.generateAsync({ type: "blob" });
      const a = Object.assign(document.createElement("a"), {
        href: URL.createObjectURL(blob), download: `${rootName}.zip`,
      });
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(a.href);
    } finally { setDownloadingAll(false); }
  };
  const [tokenCheckModal,       setTokenCheckModal]       = useState(null);  // null = closed | { estimatedNeeded, remaining, percentUsed, serviceCount, limitReached }

  // ── Derived ───────────────────────────────────────────────────
  const hasDesign      = artifacts.some(a => a.badge === "Design");
  const hasIntegration = artifacts.some(a => a.badge === "Integration");

  // ── Auto-scroll ───────────────────────────────────────────────
  useEffect(() => { chatEndRef.current && chatEndRef.current.scrollIntoView({ behavior: "smooth" }); }, [messages]);
  useEffect(() => { logsEndRef.current && logsEndRef.current.scrollIntoView({ behavior: "smooth" });  }, [logs]);

  // ── Thinking status cycling ────────────────────────────────────
  useEffect(() => {
    if (!loading) { setThinkingMsg(""); return; }
    const msgs = [
      "Thinking",
      "Analyzing your request",
      "Designing the solution",
      "Generating response",
      "Reviewing architecture",
      "Almost there",
    ];
    let idx = 0;
    setThinkingMsg(msgs[0]);
    const iv = setInterval(() => {
      idx = (idx + 1) % msgs.length;
      setThinkingMsg(msgs[idx]);
    }, 2000);
    return () => clearInterval(iv);
  }, [loading]);

  // ── Persist session state to localStorage ─────────────────────
  useEffect(() => {
    try {
      const state = {
        messages,
        logs,
        // Persist the ref-backed diagram (state resets to "" on restore by design), so the
        // saved diagram survives reloads instead of being clobbered with an empty string.
        plantumlCode: plantumlCodeRef.current || plantumlCode,
        artifacts,
        // activeArtifact intentionally not persisted — panel starts closed on load
        activeAgent,
        selectedTech,
        showPumlPanel,
        generatedApis,
        projectPath,
        packageName,
        deployProjName,
        designProjectName,
        savedProjectId,
        savedRepoResult,
        deployConfig,
        deployApiStatuses,
        loading,
        lastActive: Date.now(),
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (e) { /* quota exceeded — silently ignore */ }
  }, [messages, logs, plantumlCode, artifacts, activeAgent, selectedTech,
      showPumlPanel, generatedApis, projectPath, packageName, deployProjName, designProjectName,
      savedProjectId, savedRepoResult, deployConfig, deployApiStatuses, loading, STORAGE_KEY]);

  // Keep selectedTechRef in sync so socket handlers always read the latest value
  useEffect(() => { selectedTechRef.current = selectedTech; }, [selectedTech]);
  // Keep deployApiStatusesRef in sync for name-based API transition matching in socket handler
  useEffect(() => { deployApiStatusesRef.current = deployApiStatuses; }, [deployApiStatuses]);

  // ── Reconnect guard: if we restored loading=true from localStorage (a refresh
  //    or reconnect mid-generation), try to RECOVER the finished result from the
  //    server instead of just discarding it. recoverGeneration polls /hub/result
  //    and either renders the result, keeps waiting while it's still running, or
  //    (if truly unrecoverable) clears loading with a Retry message.
  useEffect(() => {
    if (!_persisted.loading) return;
    const timer = setTimeout(() => {
      if (!loadingRef.current) return;
      // Async-hub: if a background job was in flight, resume polling it directly.
      // Otherwise fall back to the session-based /hub/result recovery.
      const persistedJobId = localStorage.getItem("langraph_job_id");
      if (persistedJobId && waitForJobRef.current) waitForJobRef.current(persistedJobId);
      else if (recoverRef.current) recoverRef.current();
    }, 1500); // small delay so refs are populated
    return () => clearTimeout(timer);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Session init ──────────────────────────────────────────────
  // Ref always holds the latest sessionId — used by socket on("connect") to avoid stale closure
  const sessionIdRef = useRef(null);
  useEffect(() => { sessionIdRef.current = sessionId; }, [sessionId]);
  // Latest `loading` for use inside socket/window listeners (stable closures).
  const loadingRef = useRef(false);
  useEffect(() => {
    loadingRef.current = loading;
    // Turn finished (or never started) — cancel any pending watchdog.
    if (!loading && watchdogRef.current) { clearTimeout(watchdogRef.current); watchdogRef.current = null; }
  }, [loading]);
  // Recovery poll handles (set below); called from socket 'connect' / window 'online'.
  const recoverRef = useRef(null);
  const recoverPollRef = useRef(null);
  const recoveringRef = useRef(false);
  // Watchdog: if a turn is still in flight after WATCHDOG_MS with no response, the
  // client's network likely dropped (offline/socket events are unreliable). Surface
  // it and kick recovery — the server keeps running and stores the finished result.
  const watchdogRef = useRef(null);
  const WATCHDOG_MS = 40000;
  // Async-hub (backend Workstream B.4): when HUB_ASYNC_ENABLED is on, POST /api/v1/hub
  // returns a {jobId} receipt instead of the answer. waitForJob polls /api/v1/jobs/:jobId
  // until the job finishes, then renders the real result. jobId is persisted so a
  // page refresh mid-generation resumes instead of restarting. (poll-only, per plan.)
  const jobIdRef   = useRef(null);
  const jobPollRef = useRef(null);
  const waitForJobRef = useRef(null);

  // Live ref to messages — lets createSession check for active conversation without stale closure
  const messagesRef = useRef(messages);
  useEffect(() => { messagesRef.current = messages; }, [messages]);

  const createSession = useCallback(function createSession(forceNew) {
    // Guard: never silently replace the session while a real conversation is active.
    // Pass forceNew=true (from handleNewSession) to intentionally bypass this check.
    if (!forceNew) {
      const hasActiveConversation = messagesRef.current.some(function(m) { return m.role === "user"; });
      if (hasActiveConversation && sessionIdRef.current) {
        console.warn('[SmartHub] createSession() blocked — active conversation exists. Use handleNewSession() to intentionally reset.');
        return sessionIdRef.current;
      }
    }
    const id = uuidv4();
    setSessionId(id);
    sessionIdRef.current = id; // update ref so reconnect handler uses the new id
    localStorage.setItem("langraph_session_id", id);
    // If socket is already connected, join the new room immediately
    if (socketRef.current?.connected) {
      console.log('[SmartHub] re-join after createSession →', id);
      socketRef.current.emit("join-session", id);
    }
    return id;
  }, []);

  // ── Load deployment credentials from connected apps ──────────
  const loadResource = useCallback((path, setter, key) => {
    const headers = { "x-access-token": localStorage.getItem("token") };
    return fetch(`${domain}/api/v1/${path}`, { headers })
      .then(r => {
        console.log('%c[loadResource] HTTP', 'color:#60a5fa', { path, status: r.status, ok: r.ok, url: r.url });
        return r.json().then(d => ({ ok: r.ok, status: r.status, d }));
      })
      .then(({ ok, status, d }) => {
        if (!ok) {
          console.error('%c[loadResource] ERROR RESPONSE', 'color:#f87171', { path, status, body: d });
          setter([]);
          return [];
        }
        const arr = key ? (d[key] || []) : (d.data || d || []);
        const result = Array.isArray(arr) ? arr : [];
        console.log('%c[loadResource] OK', 'color:#34d399', { path, rawResponse: d, arr, result });
        setter(result);
        return result;
      })
      .catch(err => {
        console.error('%c[loadResource] FAILED', 'color:#f87171', { path, error: err });
        return [];
      });
  }, []);

  const reloadCloudResources = useCallback(() => {
    return Promise.all([
      loadResource("awsresource", setAwsClusters),
      loadResource("awsaccount",  setAwsAcctList),
      loadResource("azaccount",   setAzureAccounts),
      loadResource("gkeresource", setGkeAccounts),
      loadResource("mule",        setMuleAccounts, "mule"),
      loadResource("repo",        setRepoAccounts),
      loadResource("gitlab",      setGitlabAccounts),
      loadResource("sonarqube",   setSonarAccounts, "sonarqube"),
    ]).then(([freshAwsClusters, , freshAzureAccounts, freshGkeAccounts, freshMuleAccounts, freshRepo, freshGitlab, freshSonar]) => ({
      awsClusters:   freshAwsClusters,
      azureAccounts: freshAzureAccounts,
      gkeAccounts:   freshGkeAccounts,
      muleAccounts:  freshMuleAccounts,
      repoAccounts:  freshRepo,
      gitlabAccounts: freshGitlab,
      sonarAccounts: freshSonar,
    }));
  }, [loadResource]);

  useEffect(() => {
    loadResource("mule",        setMuleAccounts,  "mule");
    loadResource("repo",        setRepoAccounts);
    loadResource("gitlab",      setGitlabAccounts);
    loadResource("awsresource", setAwsClusters);
    loadResource("awsaccount",  setAwsAcctList);
    loadResource("azaccount",   setAzureAccounts);
    loadResource("gkeresource", setGkeAccounts);
    loadResource("sonarqube",   setSonarAccounts, "sonarqube");
    loadResource("azurevault",  setAzureVaultAccounts);
  }, [loadResource]);

  // ── Socket ────────────────────────────────────────────────────
  useEffect(() => {
    if (hasInit.current) return;
    hasInit.current = true;

    const existing = localStorage.getItem("langraph_session_id");
    const sid = existing || createSession();
    if (existing) setSessionId(existing);

    // Socket target. Locally `url.SOCKET` is a full URL (http://localhost:8083) →
    // use it as-is (default namespace). In deployed envs it's a bare path like
    // "/process"; socket.io-client would treat that as a NAMESPACE (→ "Invalid
    // namespace", endless reconnect, no green "connected" dot). nginx serves
    // socket.io at the page origin (`location /socket.io/` → backend), so connect
    // to the origin + default namespace instead.
    const socketTarget = /^https?:\/\//i.test(url.SOCKET || "") ? url.SOCKET : window.location.origin;
    console.log('[SmartHub] socket target →', socketTarget, '(SOCKET =', url.SOCKET, ')');
    const socket = socketIOClient(socketTarget, {
      rejectUnauthorized: true,
      // websocket-only: a single upgrade to one pod (no multi-request polling
      // handshake) → works across replicas without sticky sessions. The Redis
      // adapter (backend) delivers emits regardless of which pod the client is on.
      transports: ["websocket"],
      // Keep retrying indefinitely so the socket revives on its own when the network
      // returns (a fixed low cap made it give up permanently a few seconds into an
      // outage). Backoff is capped so we don't spam while offline.
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 2000,
      reconnectionDelayMax: 10000,
      timeout: 20000,
    });
    socketRef.current = socket;
    let connErrCount = 0;

    let disconnectTimer = null;
    let connLostTimer = null;
    socket.on("connect", () => {
      connErrCount = 0;
      if (disconnectTimer) { clearTimeout(disconnectTimer); disconnectTimer = null; }
      if (connLostTimer) { clearTimeout(connLostTimer); connLostTimer = null; }
      // Reconnected — clear any "connection lost" note we may have surfaced.
      setMessages(m => m.some(x => x.connLost) ? m.filter(x => !x.connLost) : m);
      // Use localStorage as primary source of truth — prevents stale-ref drift on reconnect
      const storedId = localStorage.getItem("langraph_session_id");
      const roomId = storedId || sessionIdRef.current || sid;
      console.log('%c[SmartHub] socket connected — id:', 'color:#00c98d;font-weight:700', socket.id);
      console.log('%c[SmartHub] joining room →', 'color:#3b7fff;font-weight:700', roomId);
      socket.emit("join-session", roomId);

      // Detect session drift: if the ref holds a different ID than what was persisted,
      // the session was silently replaced while the user was away. Warn them.
      if (storedId && sessionIdRef.current && storedId !== sessionIdRef.current) {
        console.warn('[SmartHub] Session drift detected — ref had', sessionIdRef.current, 'but localStorage has', storedId, '. Syncing to localStorage.');
        sessionIdRef.current = storedId;
        setSessionId(storedId);
        setMessages(function(prev) {
          return [...prev, {
            role: "bot",
            time: now(),
            content: "⚠️ Your session context was reset while you were away. The AI no longer has context of this conversation. Start a **New Session** to continue cleanly.",
            actions: [{ label: "🔄 New Session", action: "new_session" }],
          }];
        });
      }

      setConnected(true);
      addLog(setLogs, "success", "Connected to AI workspace");

      // If a generation was in flight when the connection dropped, the server may
      // have finished it — recover the result instead of leaving the spinner stuck.
      if (loadingRef.current && recoverRef.current) recoverRef.current();
    });
    socket.on("disconnect", () => {
      setConnected(false);
      // Two-stage detection so we distinguish a transient blip from a real network drop:
      // socket.io fires `disconnect` whenever the server's event loop is busy (heavy
      // generation can't answer pings) and auto-reconnects within a few seconds — that
      // is NOT a real loss. The `connect` handler clears both timers on reconnect, so:
      //   • 3s  → quiet log only (covers busy-server blips, no alarm)
      //   • 12s → still down = a genuine disconnect → surface it in the chat
      disconnectTimer = setTimeout(() => {
        addLog(setLogs, "warning", "Socket idle — reconnecting…");
      }, 3000);
      connLostTimer = setTimeout(() => {
        // Only call it "offline" when the browser really is offline. A dropped socket
        // while still online (e.g. an idle websocket behind a proxy) is just the live-log
        // stream reconnecting — the app keeps working via result polling, so don't alarm.
        const offline = typeof navigator !== "undefined" && navigator.onLine === false;
        addLog(setLogs, offline ? "error" : "warning",
          offline ? "Connection lost — offline" : "Live updates dropped — reconnecting…");
        setMessages(m => m.some(x => x.connLost) ? m : [...m, {
          role: "bot", time: now(), connLost: true,
          content: offline
            ? "⚠️ You appear to be offline. Your work is safe — it'll recover automatically when you reconnect."
            : loadingRef.current
              ? "⏳ Reconnecting to live updates — your generation is still running on the server and will appear here automatically."
              : "⏳ Reconnecting to live updates… (you're still online — this only affects the live log stream).",
        }]);
      }, 12000);
    });
    socket.on("connect_error", e => {
      connErrCount++;
      // `e.description` carries the handshake HTTP status (e.g. 400 "Invalid
      // namespace", 401, 404, 502) — the most useful signal for why it won't connect.
      console.error('[SmartHub] socket connect_error →', { target: socketTarget, message: e && e.message, description: e && e.description, type: e && e.type });
      if (connErrCount === 1) {
        addLog(setLogs, "error", `Connection failed: ${e.message}`);
      } else if (connErrCount === 3) {
        addLog(setLogs, "warning", "Still trying to reach the AI workspace — it'll reconnect when the network is back.");
      }
    });
    socket.on("hub log", res => {
      if (!res?.message) return;
      if (deployApiCountRef.current > 0) {
        console.log('%c[hub:log during deploy]', 'color:#f59e0b;font-weight:700', { type: res.type, level: res.level, msg: res.message.slice(0, 120) });
      }
      addLog(setLogs, res.type || res.level || "info", res.message);
      // Mirror error/warning hub logs into the deployment terminal during active deployment
      if (deployApiCountRef.current > 0 && (res.type === "error" || res.level === "error" || /^\[ERROR\]/i.test(res.message))) {
        console.log('%c[hub:log → deploy terminal]', 'color:#f87171;font-weight:700', res.message.slice(0, 120));
        addDeployLog(res.message, 'error');
      }
    });
    socket.on("hub:progress", d  => addLog(setLogs, "info", `${d.step || ""} ${d.percentage ? `(${d.percentage}%)` : ""}`));

    // API preview arrives async via setImmediate after diagram response
    socket.on("hub:api-preview", d => {
      if (!d?.apis?.length) {
        if (d?.error) addLog(setLogs, "warning", `⚠️ API analyzer unavailable: ${d.error}`);
        return;
      }
      // Flat array: [{ api, method, endpoint, description }]
      setMessages(prev => {
        const lastBotIdx = [...prev].reverse().findIndex(m => m.role === "bot");
        if (lastBotIdx !== -1) {
          const idx = prev.length - 1 - lastBotIdx;
          const updated = [...prev];
          // Only inject if the full response hasn't already set generatedApis
          if (!updated[idx].analysis?.generatedApis?.length) {
            updated[idx] = {
              ...updated[idx],
              analysis: { ...(updated[idx].analysis || {}), generatedApis: d.apis },
            };
          }
          return updated;
        }
        return [...prev, {
          role: "bot", time: now(),
          content: "📄 API specification ready:",
          analysis: { generatedApis: d.apis },
        }];
      });
      const apiCount = [...new Set(d.apis.map(e => e.api))].length;
      addLog(setLogs, "info", `📄 API preview: ${apiCount} API${apiCount !== 1 ? "s" : ""}, ${d.apis.length} endpoints`);
    });

    // Deployment log events (emitted by backend during deployment)
    socket.on("deployment log", res => {
      if (!res?.message) return;
      const msg = res.message;
      console.log('%c[Deploy:log]', 'color:#9ca3af', { msg, type: res.type, apiCount: deployApiCountRef.current, currentIdx: deployCurrentApiRef.current });

      // "[waitForUserConfirmation] Pausing for user confirmation: anypoint_complete"
      const phaseMatch = msg.match(/Pausing for user confirmation:\s*(\S+)/);
      if (phaseMatch) {
        deploymentPausedRef.current = true;
        setLoading(false);
        addDeployLog(msg, 'warning');
        addLog(setLogs, "warning", `⏸️ ${msg}`);
        setDeploymentConfirmation(prev => ({
          phase: phaseMatch[1],
          message: prev?.message || "Please verify your deployment, then click Proceed to continue.",
          details: prev?.details || null,
        }));
        return;
      }

      // "[waitForUserConfirmation] Message: Anypoint deployment is complete..."
      const waitMsgMatch = msg.match(/\[waitForUserConfirmation\]\s*Message:\s*(.+)/);
      if (waitMsgMatch) {
        deploymentPausedRef.current = true;
        setLoading(false);
        addDeployLog(waitMsgMatch[1], 'warning');
        addLog(setLogs, "warning", `⏸️ ${waitMsgMatch[1]}`);
        setDeploymentConfirmation(prev => ({
          phase: prev?.phase || (waitMsgMatch[1].toLowerCase().includes("github") ? "github_complete" : "anypoint_complete"),
          message: waitMsgMatch[1],
          details: prev?.details || null,
        }));
        return;
      }

      // "[CONFIRMATION] ..." — user-facing message for the card
      if (msg.includes("[CONFIRMATION]") || res.type === "confirmation") {
        deploymentPausedRef.current = true;
        setLoading(false);
        const cleanMsg = msg.replace(/^\[CONFIRMATION\]\s*/, "").trim();
        addDeployLog(cleanMsg, 'warning');
        addLog(setLogs, "warning", `⏸️ ${cleanMsg}`);
        setDeploymentConfirmation(prev => ({
          phase: prev?.phase || (cleanMsg.toLowerCase().includes("github") ? "github_complete" : "anypoint_complete"),
          message: cleanMsg,
          details: prev?.details || (res.details || null),
        }));
        return;
      }

      // "[PAUSE] ..." — pipeline paused, show card placeholder if not already visible
      if (msg.includes("[PAUSE]") || res.type === "pause") {
        deploymentPausedRef.current = true;
        setLoading(false);
        addDeployLog(msg, 'warning');
        addLog(setLogs, "warning", `⏸️ ${msg}`);
        setDeploymentConfirmation(prev => prev || {
          phase: msg.toLowerCase().includes("github") ? "github_complete" : "anypoint_complete",
          message: "Please verify your deployment, then click Proceeded to continue.",
          details: null,
        });
        return;
      }

      const msgText = (msg || '').trim();
      // Treat as error if: explicit [ERROR] prefix, OR backend says type=error but message isn't
      // tagged as [INFO]/[SUCCESS]/[WARNING], OR "X deployment failed — continuing" warning pattern.
      const isError = /^\[ERROR\]|^DEPLOYMENT FAILED/i.test(msgText) ||
        ((res.type === "error" || res.level === "error") && !/^\[INFO\]|^\[SUCCESS\]|^\[WARNING\]|\[INFO\]|\[SUCCESS\]/i.test(msgText)) ||
        /deployment failed\s*[—–-]/i.test(msgText) ||
        /\bfailed\s*[—–-]\s*continuing/i.test(msgText);

      // Mirror every log line into the deployment window terminal
      addDeployLog(msg, isError ? 'error' : (res.type || 'info'));
      if (deployApiCountRef.current > 0) {
        advanceActiveApiStage(msg);
      } else {
        advanceDeployStage(msg);
      }

      if (isError) {
        setDeployStages(function(prev) {
          return prev.map(function(s) { return s.status === 'active' ? { ...s, status: 'error' } : s; });
        });
        setDeployApiStatuses(function(prev) {
          var activeIdx = deployCurrentApiRef.current;
          return prev.map(function(a, i) {
            if (i !== activeIdx) return a;
            return { ...a, status: 'error', stages: (a.stages || []).map(function(s) { return s.status === 'active' ? { ...s, status: 'error' } : s; }) };
          });
        });
      }

      // API transition: "Deploying X..." marks the start of a new API.
      // Name-based matching avoids counter drift — extract the API name, find it in the status list,
      // and activate it if it is still pending. Skips index 0 (already active at start).
      if (/(?:\[\d+\/\d+\]\s+)?Deploying\s+/i.test(msgText) && deployApiCountRef.current > 0) {
        var _depMatch = msgText.match(/(?:\[\d+\/\d+\]\s+)?Deploying\s+(.+?)(?:\s*\(|\.\.\.|$)/i);
        console.log('%c[Deploy:transition] "Deploying" message detected', 'color:#f0a500;font-weight:700', {
          msg: msgText,
          extracted: _depMatch ? _depMatch[1].trim() : null,
          deployCurrentApiRef: deployCurrentApiRef.current,
          snapStatuses: (deployApiStatusesRef.current || []).map(function(a) { return { name: a.name, displayName: a.displayName, status: a.status }; }),
        });
        if (_depMatch) {
          var _depName = _depMatch[1].trim().toLowerCase();
          var _snapStatuses = deployApiStatusesRef.current;
          var _depIdx = -1;
          for (var _di = 0; _di < _snapStatuses.length; _di++) {
            var _dname = (_snapStatuses[_di].displayName || _snapStatuses[_di].name || '').toLowerCase();
            if (_dname === _depName || _dname.includes(_depName) || _depName.includes(_dname)) {
              _depIdx = _di;
              break;
            }
          }
          console.log('%c[Deploy:transition] name match result', 'color:#f0a500', {
            _depName: _depName,
            _depIdx: _depIdx,
            statusAtIdx: _depIdx >= 0 ? _snapStatuses[_depIdx].status : 'n/a',
            wouldTransition: _depIdx > 0 && _depIdx > deployCurrentApiRef.current && (_depIdx >= 0 && _snapStatuses[_depIdx].status === 'pending'),
          });
          // Only transition when the matched API is pending and comes after the current one
          if (_depIdx > 0 && _depIdx > deployCurrentApiRef.current && _snapStatuses[_depIdx].status === 'pending') {
            var _depPrevIdx = _depIdx - 1;
            deployCurrentApiRef.current = _depIdx; // update ref outside setState so next log event sees it
            console.log('%c[Deploy:transition] ACTIVATING API at index', 'color:#00c98d;font-weight:700', _depIdx, '→', _snapStatuses[_depIdx].displayName || _snapStatuses[_depIdx].name);
            setDeployApiStatuses(function(prev) {
              return prev.map(function(a, i) {
                if (i === _depPrevIdx) {
                  // Only advance 'active' stages to 'done' — leave 'pending' (unreached) and 'error' unchanged
                  return { ...a, status: a.status === 'error' ? 'error' : 'done', stages: (a.stages || []).map(function(s) { return s.status === 'active' ? { ...s, status: 'done' } : s; }) };
                }
                if (i === _depIdx) {
                  return { ...a, status: 'active', stages: (a.stages || []).map(function(s, j) { return { ...s, status: j === 0 ? 'active' : 'pending' }; }) };
                }
                return a;
              });
            });
          }
        }
      }

      // Fallback transition: "Validating project structure..." fires once per API at the very start of its run.
      // The backend does NOT always emit "Deploying X..." (it skips it on success paths).
      // Count occurrences — Nth fires transition to API N-1.  Only activates if the name-based block above
      // hasn't already advanced deployCurrentApiRef (prevents double-transition).
      if (/Validating project structure/i.test(msgText) && deployApiCountRef.current > 0) {
        validatingCountRef.current += 1;
        var _valExpected = validatingCountRef.current - 1; // 0-based: first occurrence = API 0 (already active, skip)
        console.log('%c[Deploy:validating]', 'color:#a78bfa;font-weight:700', { count: validatingCountRef.current, _valExpected, deployCurrentApiRef: deployCurrentApiRef.current });
        if (_valExpected > deployCurrentApiRef.current) {
          var _valPrev = _valExpected - 1;
          deployCurrentApiRef.current = _valExpected;
          console.log('%c[Deploy:validating] FALLBACK transition → API', 'color:#00c98d;font-weight:700', _valExpected);
          setDeployApiStatuses(function(prev) {
            return prev.map(function(a, i) {
              if (i === _valPrev) {
                // Only advance 'active' stages to 'done' — leave 'pending' (unreached) and 'error' unchanged
                return { ...a, status: a.status === 'error' ? 'error' : 'done', stages: (a.stages || []).map(function(s) { return s.status === 'active' ? { ...s, status: 'done' } : s; }) };
              }
              if (i === _valExpected && _valExpected < prev.length) {
                return { ...a, status: 'active', stages: (a.stages || []).map(function(s, j) { return { ...s, status: j === 0 ? 'active' : 'pending' }; }) };
              }
              return a;
            });
          });
        }
      }

      // Mark current API done when "X deployed successfully" arrives (handles last API + cleans up active stage)
      if (/deployed successfully/i.test(msgText) && deployApiCountRef.current > 0) {
        var _successApiIdx = deployCurrentApiRef.current;
        console.log('%c[Deploy:success] "deployed successfully" — marking API', 'color:#00c98d;font-weight:700', _successApiIdx, 'as done. Current snap:', (deployApiStatusesRef.current || []).map(function(a) { return { name: a.displayName || a.name, status: a.status, stages: (a.stages||[]).map(function(s){return s.name+':'+s.status;}) }; }));
        setDeployApiStatuses(function(prev) {
          console.log('%c[Deploy:success] inside updater, prev[' + _successApiIdx + ']:', 'color:#00c98d', prev[_successApiIdx] ? { status: prev[_successApiIdx].status, stages: (prev[_successApiIdx].stages||[]).map(function(s){return s.name+':'+s.status;}) } : 'MISSING');
          if (!prev[_successApiIdx]) return prev;
          return prev.map(function(a, i) {
            if (i !== _successApiIdx) return a;
            return { ...a, status: 'done', stages: (a.stages || []).map(function(s) {
              return s.status === 'active' ? { ...s, status: 'done' } : s;
            }) };
          });
        });
      }

      // Final fallback: "DEPLOY-ONLY PIPELINE COMPLETE" / "DEPLOYMENT COMPLETE" — marks the last active API done
      if (/DEPLOY-ONLY PIPELINE COMPLETE|^DEPLOYMENT COMPLETE/i.test(msgText) && deployApiCountRef.current > 0) {
        var _finalApiIdx = deployCurrentApiRef.current;
        console.log('%c[Deploy:final] pipeline complete — marking API', 'color:#00c98d;font-weight:700', _finalApiIdx, 'done');
        setDeployApiStatuses(function(prev) {
          return prev.map(function(a, i) {
            if (i !== _finalApiIdx) return a;
            if (a.status === 'error') return a; // already failed — don't override
            return { ...a, status: 'done', stages: (a.stages || []).map(function(s) {
              return s.status === 'active' ? { ...s, status: 'done' } : s;
            }) };
          });
        });
      }

      if (isError) {
        // Deduplicate: only log to Live Logs if this is a new unique error line
        if (msg !== lastDeployErrMsg.current) {
          lastDeployErrMsg.current = msg;
          addLog(setLogs, "error", msg);
        }
        // Collect cleaned line for chat display
        const cleanLine = msg.trim()
          .replace(/^\[ERROR\]\s*/i, "")
          .replace(/^DEPLOYMENT FAILED:\s*/i, "")
          .trim();
        if (cleanLine && !deploymentErrorLogs.current.includes(cleanLine)) {
          deploymentErrorLogs.current.push(cleanLine);
        }
        // Show error in chat and stop loading if not already shown
        if (!deploymentPausedRef.current && !deploymentErrorShown.current) {
          deploymentErrorShown.current = true;
          setTimeout(() => {
            setLoading(false);
            const summary = deploymentErrorLogs.current[0] || cleanLine || "Deployment failed.";
            const hint = getDeployErrorHint(summary, selectedTechRef.current);
            const content = hint ? `❌ Deployment failed: ${summary}\n\n${hint}` : `❌ Deployment failed: ${summary}`;
            const retryAction = selectedTechRef.current === 'dotnet' ? 'confirm_deploy_dotnet' : selectedTechRef.current === 'springboot' ? 'confirm_deploy_spring' : 'confirm_deploy_mule';
            setMessages(prev => [...prev, { role: "bot", time: now(), content, actions: [{ label: "🔄 Retry Deployment", action: retryAction }] }]);
          }, 800);
        }
      } else {
        addLog(setLogs, res.type || "info", msg);
        // Fallback: "Deployment complete." in a log means the orchestrator finished — clear loading
        if (/^Deployment complete\./i.test(msgText) && deployApiCountRef.current > 0) {
          setLoading(false);
          deploymentErrorShown.current = false; // reset for next deployment
        }
      }
    });
    socket.on("integration:log", res => {
      if (res?.message) addLog(setLogs, res.level || "info", res.message);
    });
    socket.on("integration:progress", d => {
      if (d?.step) addLog(setLogs, "info", `${d.step}${d.percentage ? ` (${d.percentage}%)` : ""}`);
    });
    socket.on("integration:complete", d => {
      setLoading(false);
      deploymentPausedRef.current = false;
      addLog(setLogs, "success", d?.message || "✅ Deployment complete");
      addDeployLog(d?.message || "✅ Deployment completed successfully!", "success");
      setDeployStages(prev => prev.map(s => ({ ...s, status: s.status === 'error' ? 'error' : 'done' })));
      setDeployApiStatuses(prev => prev.map(function(a) {
        return {
          ...a,
          status: a.status === 'error' ? 'error' : 'done',
          stages: (a.stages || []).map(function(s) { return s.status === 'active' ? { ...s, status: 'done' } : s; }),
        };
      }));
      setMessages(prev => [...prev, { role: "bot", time: now(), content: d?.message || "✅ Deployment completed successfully!" }]);
    });

    socket.on("dotnet:log", d => {
      const _dtxt = d?.message || d?.log;
      console.log('%c[dotnet:log]', 'color:#a78bfa;font-weight:700', { type: d?.type, msg: (_dtxt || '').slice(0, 120) });
      addDeployLog(_dtxt, d?.type);
      if (deployApiCountRef.current > 0) {
        advanceActiveApiStage(_dtxt);
        const _isErr = /^\[ERROR\]|^DEPLOYMENT FAILED/i.test((_dtxt || '').trim()) || d?.type === 'error';
        if (_isErr) {
          setDeployApiStatuses(function(prev) {
            var _idx = deployCurrentApiRef.current;
            return prev.map(function(a, i) {
              if (i !== _idx) return a;
              return { ...a, status: 'error', stages: (a.stages || []).map(function(s) { return s.status === 'active' ? { ...s, status: 'error' } : s; }) };
            });
          });
        }
      } else {
        advanceDeployStage(_dtxt);
      }
    });
    socket.on("plantuml:log",        d => { addDeployLog(d?.message, d?.type); });
    socket.on("plantuml:progress",   d => { if (d?.percent != null) addDeployLog('Progress: ' + d.percent + '%', 'info'); });
    socket.on("deployment:stages",   d => { if (d?.stages) setDeployStages(d.stages); });

    // Azure DevOps pipeline per-stage build logs. Backend emits
    //   { stage, recordType, summary, lines: [...], timestamp }
    // from pollAndStreamStageLogs(). summary:false means the full failure detail.
    // Without this handler the whole pipeline phase (stage advance + its logs) is
    // invisible on the client, even though the backend streams it.
    socket.on("pipeline:log", res => {
      if (!res || !Array.isArray(res.lines)) return;
      const stageName = res.stage || "Pipeline";
      const failed = res.summary === false;
      // Group per stage for the DeploymentWindow pipeline panel: running appends the
      // streamed tail; failure replaces with the authoritative full dump.
      setPipelineStages(prev => {
        const next = prev.slice();
        const i = next.findIndex(s => s.stage === stageName);
        const entry = i >= 0 ? { ...next[i] } : { stage: stageName, recordType: res.recordType, lines: [], failed: false };
        entry.lines = failed ? res.lines.slice() : entry.lines.concat(res.lines);
        entry.failed = entry.failed || failed;
        entry.recordType = res.recordType || entry.recordType;
        entry.updatedAt = res.timestamp || new Date().toISOString();
        if (i >= 0) next[i] = entry; else next.push(entry);
        return next;
      });
      // Mirror to the flat, searchable Live Logs.
      res.lines.forEach(line => addLog(setLogs, failed ? "error" : "info", `[${stageName}] ${line}`));
      // Keep the top stage-bar advancing into the Azure Pipeline stage.
      const marker = `azure pipeline ${stageName}`;
      if (deployApiCountRef.current > 0) advanceActiveApiStage(marker); else advanceDeployStage(marker);
    });

    // Design plan mirror — convenience echo of the authoritative HTTP response.
    // Idempotent: updates the latest open plan card in place (no new card).
    socket.on("hub:design-plan", (payload) => {
      if (!payload) return;
      setMessages(prev => {
        let idx = -1;
        for (let k = prev.length - 1; k >= 0; k--) {
          if (prev[k] && prev[k].designPlan && !prev[k].designPlanAnswered) { idx = k; break; }
        }
        if (idx < 0) return prev;
        const next = prev.slice();
        const cur = next[idx];
        next[idx] = {
          ...cur,
          designPlan: {
            ...cur.designPlan,
            plan:          payload.plan          ?? cur.designPlan.plan,
            round:         payload.round         ?? cur.designPlan.round,
            maxRounds:     payload.maxRounds     ?? cur.designPlan.maxRounds,
            openDecisions: payload.openDecisions ?? cur.designPlan.openDecisions,
            lastEdit:      payload.lastEdit      ?? cur.designPlan.lastEdit,
          },
        };
        return next;
      });
    });

    // Implementation plan mirror — convenience echo of the authoritative HTTP
    // response. Idempotent: updates the latest open plan card in place (no new card).
    socket.on("hub:implementation-plan", (payload) => {
      if (!payload) return;
      setMessages(prev => {
        let idx = -1;
        for (let k = prev.length - 1; k >= 0; k--) {
          if (prev[k] && prev[k].implementationPlan && !prev[k].implementationPlanAnswered) { idx = k; break; }
        }
        if (idx < 0) return prev;
        const next = prev.slice();
        const cur = next[idx];
        next[idx] = {
          ...cur,
          implementationPlan: {
            ...cur.implementationPlan,
            plan:          payload.plan          ?? cur.implementationPlan.plan,
            round:         payload.round         ?? cur.implementationPlan.round,
            maxRounds:     payload.maxRounds     ?? cur.implementationPlan.maxRounds,
            openDecisions: payload.openDecisions ?? cur.implementationPlan.openDecisions,
            lastEdit:      payload.lastEdit      ?? cur.implementationPlan.lastEdit,
            diagrams:      payload.diagrams      ?? cur.implementationPlan.diagrams,
          },
        };
        return next;
      });
    });

    // Deployment pause — backend waits indefinitely for user to verify and click Proceed
    socket.on("integration:existing-api-upload", (d) => {
      setMessages(prev => [...prev, {
        role: "bot", time: now(),
        content: d.message,
        existingApiUpload: {
          uploadEndpoint: d.uploadEndpoint || "/api/v1/hub/upload-existing-api",
          accepts:        d.accepts        || ["jar", "github"],
          canSkip:        true,
          skipHint:       d.skipMessage    || "Type 'skip' to generate without it",
          declaredLayers: d.declaredLayers || [],
        },
      }]);
    });

    // Mirror of the CI/CD setup question — carries `note` (re-ask reason) that the HTTP
    // response omits. Refresh the open deployment-setup card in place; don't create one.
    socket.on("integration:deployment-setup-question", (d) => {
      if (!d) return;
      setMessages(prev => {
        let idx = -1;
        for (let k = prev.length - 1; k >= 0; k--) {
          if (prev[k] && prev[k].deploymentSetup && !prev[k].deploymentSetupAnswered) { idx = k; break; }
        }
        if (idx < 0) return prev;
        const next = prev.slice();
        const ds = next[idx].deploymentSetup;
        next[idx] = { ...next[idx], deploymentSetup: {
          ...ds,
          stage:        d.stage !== undefined ? d.stage : ds.stage,
          options:      d.options || ds.options,
          deployTarget: d.deployTarget || ds.deployTarget,
          default:      d.default || ds.default,
          note:         d.note || "",
        } };
        return next;
      });
    });

    socket.on("deployment:confirmation_required", (data) => {
      setDeploymentConfirmation(data);
      addLog(setLogs, "warning", "⏸️ Deployment paused — awaiting your verification");
    });

    // ── Live token usage updates ──────────────────────────────────
    socket.on("hub:token-usage", d => {
      console.log('%c[Usage] socket hub:token-usage ◀', 'color:#c77dff;font-weight:700', d, '| limit:', d?.limit, '| strategy:', d?.strategy);
      if (d?.totalUsed !== undefined) {
        // Preserve strategy from the initial REST fetch if the socket event omits it.
        setLiveTokens(prev => ({ totalUsed: d.totalUsed, limit: d.limit, remaining: d.remaining, percentUsed: d.percentUsed, strategy: d.strategy ?? prev?.strategy }));
      }
    });
    socket.on("hub:token-warning", d => {
      setTokenWarning(d);
    });
    socket.on("hub:limit-reached", d => {
      setTokenWarning({ ...d, limitReached: true });
      addLog(setLogs, "error", `🚫 Daily token limit reached (${(d.used || 0).toLocaleString()} / ${(d.limit || 0).toLocaleString()})`);
    });

    // ── Brownfield socket events (state owned by BrownfieldTab.jsx) ─
    console.log('%c[BF socket] registering brownfield listeners', 'color:#7a93bc;font-style:italic');
    socket.on("brownfield:log", res => {
      console.log('%c[BF socket] ◀ brownfield:log', 'color:#f5a623;font-weight:700', res);
      if (!res?.message) { console.warn('[BF socket] brownfield:log — no message field, skipping', res); return; }
      addLog(setLogs, res.level || "info", res.message);
      bfSetLogsRef.current(prev => [...prev, {
        ts: new Date().toLocaleTimeString("en-US", { hour12: false, hour: "2-digit", minute: "2-digit", second: "2-digit" }),
        level: res.level || "info",
        msg: res.message,
      }]);
    });
    socket.on("brownfield:progress", res => {
      console.log('%c[BF socket] ◀ brownfield:progress', 'color:#3b7fff;font-weight:700', res);
      if (!res?.message) { console.warn('[BF socket] brownfield:progress — no message field, skipping', res); return; }
      addLog(setLogs, "info", res.message);
      bfSetLogsRef.current(prev => [...prev, {
        ts: new Date().toLocaleTimeString("en-US", { hour12: false, hour: "2-digit", minute: "2-digit", second: "2-digit" }),
        level: "info",
        msg: res.message,
      }]);
    });
    socket.on("brownfield:chunk", data => {
      console.log('%c[BF socket] ◀ brownfield:chunk', 'color:#adc4e6', data?.text?.slice(0, 60));
      bfSetMessagesRef.current(prev => {
        const last = prev[prev.length - 1];
        if (last && last.role === "bot" && last.streaming) {
          return [...prev.slice(0, -1), { ...last, content: last.content + (data.text || "") }];
        }
        return [...prev, { role: "bot", content: data.text || "", streaming: true }];
      });
    });
    socket.on("brownfield:done", () => {
      console.log('%c[BF socket] ◀ brownfield:done', 'color:#00c98d;font-weight:700');
      if (!bfScanActiveRef.current) return; // stray done for a session that isn't actively scanning — ignore
      bf.setBfLoading(false);
      bf.setBfAnalysisDone(true);
      bfSetMessagesRef.current(prev => prev.map((m, i) => i === prev.length - 1 ? { ...m, streaming: false } : m));
    });
    socket.on("brownfield:error", err => {
      console.error('%c[BF socket] ◀ brownfield:error', 'color:#ff4f4f;font-weight:700', err);
      bf.setBfLoading(false);
      bfSetMessagesRef.current(prev => [...prev, { role: "bot", content: `❌ ${err?.message || "Analysis failed."}` }]);
    });
    socket.on("brownfield:files", data => {
      bf.setBfChangedFiles(data?.files || []);
    });

    // ── New LangGraph codereview events ──────────────────────────────────────
    socket.on("codereview:progress", data => {
      console.log('%c[BF socket] ◀ codereview:progress', 'color:#3b7fff;font-weight:700', data);
      if (!data) return;
      bfSetLogsRef.current(prev => [...prev, {
        ts: new Date().toLocaleTimeString("en-US", { hour12: false, hour: "2-digit", minute: "2-digit", second: "2-digit" }),
        level: "info",
        msg: data.message || `Scanning file ${data.file_index || ''}/${data.total_files || ''}`,
      }]);
    });
    socket.on("codereview:file-done", data => {
      if (!data) return;
      const msg = `✓ ${data.file_name || 'file'} — score: ${data.score ?? '?'}% (${data.violations_count ?? 0} violations)`;
      bfSetLogsRef.current(prev => [...prev, {
        ts: new Date().toLocaleTimeString("en-US", { hour12: false, hour: "2-digit", minute: "2-digit", second: "2-digit" }),
        level: "success",
        msg,
      }]);
    });
    // Shared handler for both codereview:complete and brownfield:complete
    const handleScanComplete = (data) => {
      console.log('%c[BF socket] ◀ scan complete', 'color:#00c98d;font-weight:700', data);
      if (!bfScanActiveRef.current) return; // no scan in flight for THIS session — drop stale/duplicate/replayed completion
      if (bf.bfAnalysisDone) return; // already handled by poll — ignore duplicate socket event
      bfScanActiveRef.current = false;
      bf.setBfLoading(false);
      bf.setBfAnalysisDone(true);
      const results = data?.results || data?.scan_results;
      if (results) {
        bf.setBfResults({ ...data, results });
        if (data.branch) bf.setBfBranch(data.branch);
      }
      const summary = `✅ Analysis complete — ${data?.scanned ?? 0} files scanned, avg score ${data?.avg_score ?? 0}%`;
      bfSetMessagesRef.current(prev => [...prev, { role: "bot", content: summary }]);
      bfSetLogsRef.current(prev => [...prev, {
        ts: new Date().toLocaleTimeString("en-US", { hour12: false, hour: "2-digit", minute: "2-digit", second: "2-digit" }),
        level: "success",
        msg: summary,
      }]);
    };
    socket.on("codereview:complete", handleScanComplete);
    socket.on("brownfield:complete", handleScanComplete);

    // Shared handler for per-file progress (both event names)
    const handleFileDone = (data) => {
      console.log('[BF socket] file-done', data);
      if (!data) return;
      const msg = `✓ ${data.file_name || 'file'} — score: ${data.score ?? '?'}% (${data.violations_count ?? 0} violations)`;
      bfSetLogsRef.current(prev => [...prev, {
        ts: new Date().toLocaleTimeString("en-US", { hour12: false, hour: "2-digit", minute: "2-digit", second: "2-digit" }),
        level: "success",
        msg,
      }]);
    };
    socket.on("codereview:file-done", handleFileDone);
    socket.on("brownfield:file-done", handleFileDone);

    const handleScanError = (data) => {
      bf.setBfLoading(false);
      const errMsg = data?.message || "Analysis failed";
      bfSetMessagesRef.current(prev => [...prev, { role: "bot", content: `❌ ${errMsg}` }]);
      bfSetLogsRef.current(prev => [...prev, {
        ts: new Date().toLocaleTimeString("en-US", { hour12: false, hour: "2-digit", minute: "2-digit", second: "2-digit" }),
        level: "error",
        msg: errMsg,
      }]);
    };
    socket.on("codereview:error", handleScanError);
    // brownfield:error already registered above — no duplicate needed

    return () => {
      socket.off("deployment log");
      socket.off("dotnet:log");
      socket.off("plantuml:log");
      socket.off("pipeline:log");
      socket.off("plantuml:progress");
      socket.off("deployment:stages");
      socket.off("deployment:confirmation_required");
      socket.off("integration:log");
      socket.off("integration:progress");
      socket.off("integration:complete");
      socket.off("hub:api-preview");
      socket.off("hub:token-usage");
      socket.off("hub:token-warning");
      socket.off("hub:limit-reached");
      socket.off("brownfield:log");
      socket.off("brownfield:progress");
      socket.off("brownfield:chunk");
      socket.off("brownfield:done");
      socket.off("brownfield:error");
      socket.off("brownfield:files");
      socket.off("codereview:progress");
      socket.off("codereview:file-done");
      socket.off("codereview:complete");
      socket.off("codereview:error");
      socket.off("brownfield:complete");
      socket.off("brownfield:file-done");
      socket.disconnect();
      socketRef.current = null;
    };
  }, [createSession]);

  // ── Push artifact helper ──────────────────────────────────────
  const pushArtifact = useCallback((artifact) => {
    setArtifacts(prev => {
      const exists = prev.find(a => a.id === artifact.id);
      return exists
        ? prev.map(a => a.id === artifact.id ? artifact : a)
        : [...prev, artifact];
    });
    setActiveArtifact(artifact);
  }, []);


  // ── Token usage ──────────────────────────────────────────────
  const fetchUsage = useCallback(() => {
    const today = new Date().toISOString().slice(0, 10);
    const url = `${backendUrl}/api/v1/hub/usage`;
    console.log('[Usage] GET', url);
    fetch(url, {
      headers: { 'x-access-token': localStorage.getItem('token') },
    })
      .then(r => {
        console.log('[Usage] GET response status:', r.status);
        return r.json();
      })
      .then(data => {
        console.log('[Usage] GET response body:', data);
        console.log('%c[Usage] backendUrl =', 'color:#3b7fff;font-weight:700', backendUrl, '| full url =', url);
        console.log('%c[Usage] data.today =', 'color:#00c98d;font-weight:700', data.today, '| limit:', data.today?.limit, '| strategy:', data.today?.strategy);
        const todayEntry = data.usage?.find(u => u.date === today)
          || { date: today, inputTokens: 0, outputTokens: 0, cacheReadTokens: 0, estimatedCostUSD: 0, requestCount: 0, extensionTokens: 0 };
        console.log('[Usage] resolved entry for today:', todayEntry);
        setUsageData(todayEntry);
        // Seed live token pill from REST response (shows on load, before first socket event)
        if (data.today) {
          console.log('%c[Usage] → setLiveTokens(data.today)', 'color:#ffa032;font-weight:700', data.today);
          setLiveTokens(data.today);
        } else {
          console.warn('%c[Usage] ⚠️ data.today MISSING — widget will NOT update (old backend or old FE build?)', 'color:#ef4444;font-weight:700');
        }
      })
      .catch(err => {
        console.error('[Usage] GET error:', err);
        setUsageData({ date: today, inputTokens: 0, outputTokens: 0, cacheReadTokens: 0, estimatedCostUSD: 0, requestCount: 0, extensionTokens: 0 });
      });
  }, []);

  // Re-load a project's files from disk after an upload (folder or archive) and refresh
  // its artifacts in place. Shared by the folder Upload and the .jar/.zip archive Upload.
  const reloadProjectAfterUpload = useCallback((projectPath, group) => {
    const token = localStorage.getItem('token');
    console.log(`[SmartHub] GET /api/v1/project/load → reload after upload: ${projectPath}`);
    return fetch(`${backendUrl}/api/v1/project/load?projectPath=${encodeURIComponent(projectPath)}`, {
      headers: { 'x-access-token': token },
    })
      .then(r => (r.ok ? r.json() : null))
      .then(res => {
        console.log(`[SmartHub] GET /api/v1/project/load ← reload after upload, files: ${res?.files?.length ?? 0}`);
        if (!res?.files?.length) return;
        setArtifacts(prev => {
          const newFiles = res.files.map(file => {
            // Reuse the existing artifact's ID so IdeModal lookups still work
            const existing = prev.find(a => a.group === group && a.relativePath === file.relativePath);
            const fileIcon = file.name.endsWith('.xml') ? "⚡"
              : file.name.endsWith('.raml') ? "📄"
              : file.name.endsWith('.md') ? "📝"
              : file.name.endsWith('.yaml') || file.name.endsWith('.yml') ? "⚙️"
              : file.name.endsWith('.json') ? "🔧"
              : "📦";
            return {
              ...(existing || {}),
              id: existing ? existing.id : `${group}-${file.relativePath}`,
              icon: fileIcon, name: file.name,
              content: file.content, relativePath: file.relativePath,
              group,
            };
          });
          if (newFiles[0]) setActiveArtifact(newFiles[0]);
          return [...prev.filter(a => a.group !== group), ...newFiles];
        });
      })
      .catch(() => {});
  }, []);

  const fetchExtensionStatus = useCallback(() => {
    console.log('[SmartHub] GET /api/v1/token/extension-requests/me → checking extension status');
    fetch(`${domain}/api/v1/token/extension-requests/me`, {
      headers: { 'x-access-token': localStorage.getItem('token') },
    })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        console.log('[SmartHub] GET /api/v1/token/extension-requests/me ←', data);
        if (!data) return;
        const status = (data.status || "").toLowerCase();
        if (status === "approved") setExtensionStatus("approved");
        else if (status === "pending") setExtensionStatus("pending");
        else setExtensionStatus(null);
      })
      .catch(() => {});
  }, []);

  const handleExtendUsage = () => {
    setExtendingUsage(true);
    console.log('[SmartHub] POST /api/v1/token/extension-requests → requesting 500,000 token extension');
    const _u = (() => { try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; } })();
    fetch(`${domain}/api/v1/token/extension-requests`, {
      method: 'POST',
      headers: { 'x-access-token': localStorage.getItem('token'), 'Content-Type': 'application/json' },
      body: JSON.stringify({ extensionTokens: 500_000, email: _u.email, name: _u.username || _u.name || '' }),
    })
      .then(r => r.json())
      .then(d => { console.log('[SmartHub] POST /api/v1/token/extension-requests ←', d); setExtensionStatus("pending"); })
      .catch(err => console.error('[Usage] POST extension-request error:', err))
      .finally(() => setExtendingUsage(false));
  };

  useEffect(() => { fetchUsage(); fetchExtensionStatus(); }, [fetchUsage, fetchExtensionStatus]);

  // ── Save project to Project Centre ───────────────────────────
  const saveSmartHubProject = useCallback((name, tech, pumlCode, projPath, apis, manual = false) => {
    if (savedProjectIdRef.current === "pending") return; // save already in flight
    // Backfill a top-level projectPath from the first API's on-disk path (its parent folder)
    // when state has none — e.g. Mule (no top-level path) or a restored session.
    const deriveRootFromApis = (list) => {
      const p = ((list && list[0] && list[0].projectPath) || '').replace(/\\/g, '/');
      if (!p) return '';
      return p.includes('/') ? p.slice(0, p.lastIndexOf('/')) : p;
    };
    let apisToSave;
    if (apis) {
      apisToSave = apis.map(({ id, name: apiName, displayName, layer, projectPath: apiPath }) =>
        ({ id, name: apiName, displayName, layer, projectPath: apiPath || '' })
      );
    } else if (generatedApis?.length) {
      apisToSave = generatedApis.map(({ id, name: apiName, displayName, layer, projectPath: apiPath }) =>
        ({ id, name: apiName, displayName, layer, projectPath: apiPath || '' })
      );
    } else {
      // Spring Boot: derive from artifacts grouped by svcGroup
      const svcMap = {};
      artifacts.forEach(art => {
        if (!art.svcGroup) return;
        if (!svcMap[art.svcGroup]) svcMap[art.svcGroup] = art;
      });
      apisToSave = Object.entries(svcMap).map(([svcName, art]) => ({
        id: svcName,
        name: svcName,
        displayName: svcName,
        layer: '',
        projectPath: art.svcProjectPath || '',
      }));
    }
    // Already saved — update (backfill) the existing record instead of skipping
    if (savedProjectIdRef.current) {
      const updateId = savedProjectIdRef.current;
      console.log(`[SmartHub] PUT /api/v1/project/${updateId} → updating generatedApis (${apisToSave.length})`);
      setSaving(true);
      fetch(`${domain}/api/v1/project/${updateId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'x-access-token': localStorage.getItem('token') },
        body: JSON.stringify({
          generatedApis: apisToSave,
          // Only send the diagram when we actually have one — an empty string would
          // otherwise overwrite the project's saved diagram on a generatedApis-only sync.
          ...((pumlCode || plantumlCodeRef.current || plantumlCode)
            ? { plantumlCode: pumlCode || plantumlCodeRef.current || plantumlCode }
            : {}),
          projectPath: projPath || projectPath || deriveRootFromApis(apisToSave),
          // Promote a saved design → full integration project when APIs now exist.
          ...((tech || selectedTech) ? { technology: tech || selectedTech } : {}),
          ...(apisToSave.length ? { projectType: 'smarthub-template' } : {}),
        }),
      })
        .then(r => r.json())
        .then(res => {
          console.log(`[SmartHub] PUT /api/v1/project/${updateId} ←`, res);
          setSavedProjectId(updateId);
          addLog(setLogs, "success", "📁 Project updated in Project Centre");
        })
        .catch(err => console.warn('[SmartHub] Failed to update project:', err))
        .finally(() => setSaving(false));
      return;
    }
    savedProjectIdRef.current = "pending"; // block any concurrent save calls
    setSaving(true);
    const firstUserPrompt = messages.find(m => m.role === 'user')?.content || '';
    const savePayload = {
      projectName: name || deployProjName || projName || "SmartHub Project",
      projectType: tech || selectedTech || "mule",
      projectArchetype: 'smarthub-template',
      cloudType: 'not-mentioned',
      sessionId,
      technology: tech || selectedTech,
      plantumlCode: pumlCode || plantumlCodeRef.current || plantumlCode || '',
      projectPath: projPath || projectPath || deriveRootFromApis(apisToSave),
      generatedApis: apisToSave,
      userPrompt: firstUserPrompt,
    };
    console.log('[SmartHub] POST /api/v1/project → saving project:', savePayload);
    fetch(`${domain}/api/v1/project`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'x-access-token': localStorage.getItem('token') },
      body: JSON.stringify(savePayload),
    })
      .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then(res => {
        console.log('[SmartHub] POST /api/v1/project ←', res);
        const id = res._id || res.id || res.projectId || res.data?._id;
        const savedId = id || `saved_${Date.now()}`;
        if (!res.error) {
          savedProjectIdRef.current = savedId;
          setSavedProjectId(savedId);
          addLog(setLogs, "success", "📁 Project saved to Project Centre");
        } else {
          savedProjectIdRef.current = null;
          addLog(setLogs, "error", `❌ Failed to save project: ${res.error || "Unknown error"}`);
        }
      })
      .catch(err => {
        console.warn('[SmartHub] Failed to save project:', err);
        savedProjectIdRef.current = null; // reset so user can retry
      })
      .finally(() => setSaving(false));
  }, [sessionId, generatedApis, projName, deployProjName, selectedTech, plantumlCode, projectPath, artifacts]);

  const checkDuplicateName = useCallback(async (name) => {
    try {
      const r = await fetch(`${domain}/api/v1/project`, {
        headers: { 'x-access-token': localStorage.getItem('token') },
      });
      const data = await r.json();
      const projects = data.projects || data || [];
      return projects.some(p => (p.projectName || "").toLowerCase() === name.toLowerCase());
    } catch (e) {
      return false;
    }
  }, [domain]);

  // ── Save design (PlantUML) project ───────────────────────────
  const designProjectIdRef = useRef(null);
  const lastSyncedApisSigRef = useRef(null); // signature of generatedApis last synced to the saved project
  const suppressAutoSyncRef = useRef(false);  // set on project LOAD so restoring APIs doesn't trigger a wipe-sync
  const saveDesignProject = useCallback((pumlCode, nameOverride) => {
    if (designProjectIdRef.current === "pending") return; // save already in flight
    if (designProjectIdRef.current) {
      // Already saved — update plantuml in place
      console.log(`[SmartHub] PUT /api/v1/project/${designProjectIdRef.current} → updating plantumlCode`);
      fetch(`${domain}/api/v1/project/${designProjectIdRef.current}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'x-access-token': localStorage.getItem('token') },
        body: JSON.stringify({ plantumlCode: pumlCode }),
      })
        .then(r => r.json())
        .then(d => console.log(`[SmartHub] PUT /api/v1/project/${designProjectIdRef.current} ←`, d))
        .catch(err => console.warn('[SmartHub] Failed to update design project:', err));
      return;
    }
    designProjectIdRef.current = "pending"; // block concurrent calls
    const firstUserPrompt = messages.find(m => m.role === 'user')?.content || '';
    const payload = {
      projectName: nameOverride || designProjectName || projName || "Design Project",
      projectType: 'design',
      projectArchetype: 'smarthub-template',
      cloudType: 'not-mentioned',
      sessionId,
      technology: 'design',
      plantumlCode: pumlCode,
      generatedApis: [],
      userPrompt: firstUserPrompt,
    };
    console.log('[SmartHub] POST /api/v1/project → request payload:', payload);
    fetch(`${domain}/api/v1/project`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'x-access-token': localStorage.getItem('token') },
      body: JSON.stringify(payload),
    })
      .then(r => r.json())
      .then(res => {
        console.log('[SmartHub] POST /api/v1/project ← response:', res);
        const saved = res._id ? res : res.createProject?.createProject || res.createProject;
        if (saved?._id) {
          designProjectIdRef.current = saved._id;
          setDesignSaved(true);
          addLog(setLogs, "success", "📐 Design saved to Project Centre");
        } else {
          designProjectIdRef.current = null;
          addLog(setLogs, "error", `❌ Failed to save design: ${res.error || res.message || "Unknown error"}`);
        }
      })
      .catch(err => {
        console.warn('[SmartHub] Failed to save design project:', err);
        designProjectIdRef.current = null;
        addLog(setLogs, "error", `❌ Failed to save design: ${err.message || "Unknown error"}`);
      });
  }, [sessionId, projName, designProjectName]);

  // Auto-sync generated APIs into an ALREADY-SAVED project (design or integration) so a
  // saved record never goes stale when the user forgets to re-save after generating.
  // Update-only — never auto-creates a project (nothing happens if none was saved).
  useEffect(() => {
    if (!generatedApis || generatedApis.length === 0) return;
    // Which saved doc belongs to this session? Prefer an integration id; else a saved design.
    let existingId = (savedProjectIdRef.current && savedProjectIdRef.current !== 'pending')
      ? savedProjectIdRef.current
      : (designSaved && designProjectIdRef.current && designProjectIdRef.current !== 'pending'
        ? designProjectIdRef.current
        : null);
    if (!existingId) return; // no saved record for this session → do nothing (no auto-create)

    // Only sync when the API set actually changed (prevents loops / redundant PUTs).
    const sig = JSON.stringify(generatedApis.map(a => a.projectPath || a.id || a.name));
    if (sig === lastSyncedApisSigRef.current) return;
    lastSyncedApisSigRef.current = sig;

    // Suppress the sync that a project LOAD triggers (setGeneratedApis while restoring a
    // saved project). Syncing here is pointless — nothing changed — and dangerous: the
    // restored diagram ref may lag, so the PUT could overwrite the saved plantumlCode with
    // an empty string. Record the signature (above) but skip the write exactly once.
    if (suppressAutoSyncRef.current) {
      suppressAutoSyncRef.current = false;
      console.log('[SmartHub] skipping auto-sync for restored project (load), no wipe');
      return;
    }

    // Promote a saved-design id into the integration slot so the UPDATE branch targets it
    // (and the "Saved to Project Centre" badge reflects the now-saved generated project).
    if (!savedProjectIdRef.current) {
      savedProjectIdRef.current = existingId;
      setSavedProjectId(existingId);
    }
    console.log('[SmartHub] auto-syncing generated APIs into saved project', existingId);
    saveSmartHubProject(null, selectedTech, plantumlCodeRef.current, projectPath, generatedApis, false);
    addLog(setLogs, "success", "🔄 Generated APIs synced to your saved project");
  }, [generatedApis]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Load existing project when opened from Project Centre ─────
  useEffect(() => {
    if (!projId) return;
    // Clear stale session so a page refresh while viewing a project also starts clean
    localStorage.removeItem(STORAGE_KEY);
    savedProjectIdRef.current = projId;
    setSavedProjectId(projId);
    lastSyncedApisSigRef.current = null;
    const loadHeaders = { 'x-access-token': localStorage.getItem('token') };
    // Try detail endpoint first; fall back to list endpoint (same as multiProjectDetails uses)
    const fetchProject = () => {
      console.log(`[SmartHub] GET /api/detail/project?id=${projId} → loading project`);
      // Detail endpoint lives on the controller (domain, :8081) and takes ?id=, not a path
      // segment — hitting the process backend (backendUrl) or a /:id path 404s and forces
      // the whole-list fallback below on every open.
      return fetch(`${domain}/api/detail/project?id=${encodeURIComponent(projId)}`, { headers: loadHeaders })
        .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
        .then(raw => {
          console.log(`[SmartHub] GET /api/detail/project?id=${projId} ← response:`, raw);
          // Controller wraps as { data: { projects: [doc] } }; be tolerant of every shape.
          const container = raw?.data || raw;
          const p = Array.isArray(container?.projects) ? container.projects[0]
            : Array.isArray(container) ? container[0]
            : (container?.project || container);
          if (p?._id) return p;
          throw new Error('no _id in detail response');
        })
        .catch(() => {
          console.log(`[SmartHub] Fallback: GET /api/v1/project → loading project list`);
          return fetch(`${domain}/api/v1/project`, { headers: loadHeaders })
            .then(r => r.json())
            .then(data => {
              console.log("[SmartHub] GET /api/v1/project ← response:", data);
              const list = data?.projects || [];
              const found = list.find(p => p._id === projId);
              if (!found) throw new Error('project not found in list');
              return found;
            });
        });
    };

    fetchProject()
      .then(proj => {
        console.log('[SmartHub] Loaded project:', proj);
        if (!proj?._id) return;
        // Restore the design's ORIGINAL session so continuing a saved project reuses its
        // own sessionId (not the current/New-Session id). Keeps all of one design's token
        // usage under one "task" instead of splitting/merging it with other sessions.
        if (proj.sessionId) {
          setSessionId(proj.sessionId);
          sessionIdRef.current = proj.sessionId;
          localStorage.setItem("langraph_session_id", proj.sessionId);
          if (socketRef.current?.connected) {
            console.log('[SmartHub] re-join restored project session →', proj.sessionId);
            socketRef.current.emit("join-session", proj.sessionId);
          }
        }
        if (proj.technology) setSelectedTech(proj.technology);
        if (proj.projectPath) setProjectPath(proj.projectPath);

        // Load file contents from server using saved projectPath per API
        if (proj.generatedApis?.length) {
          console.log('[SmartHub] generatedApis found:', proj.generatedApis, '| plantumlCode present:', !!proj.plantumlCode);
          // Restoring APIs below will call setGeneratedApis → fire the auto-sync effect.
          // Flag it so that sync is skipped (see suppressAutoSyncRef) and can't overwrite
          // the saved diagram with an empty string on open.
          suppressAutoSyncRef.current = true;
          // Show the saved design diagram IMMEDIATELY (if present) — independent of the
          // per-API file fetch below, so the diagram appears even if file loading is slow
          // or fails. handleApiResponse later re-adds the puml artifact alongside the files.
          if (proj.plantumlCode) {
            setPlantumlCode(proj.plantumlCode);
            plantumlCodeRef.current = proj.plantumlCode;
            setArtifacts([{ id: "puml", icon: "📐", name: "flow-diagram.puml", meta: `${proj.plantumlCode.split("\n").length} lines`, badge: "Design", content: proj.plantumlCode }]);
          }
          // NOTE: the diagram is rendered once, inline with the integration-result message
          // below (handleApiResponse adds artifactId:"puml"). We only prime state/artifact
          // here — no separate diagram message — so it doesn't show twice.
          setMessages([
            { ...INIT_MESSAGE, time: "" },
            { role: "bot", time: now(), content: `📂 **${proj.projectName}** — fetching files from server...` },
          ]);
          const token = localStorage.getItem('token');
          const isSpring = proj.technology === 'springboot';
          const getFileIcon = (name) =>
            name.endsWith('.java') ? "☕"
            : name.endsWith('.xml') ? "📦"
            : (name.endsWith('.yaml') || name.endsWith('.yml')) ? "⚙️"
            : name.endsWith('.properties') ? "⚙️"
            : name === 'Dockerfile' ? "🐳"
            : name.endsWith('.json') ? "🔧"
            : name.endsWith('.md') ? "📝"
            : "📄";
          Promise.all(
            proj.generatedApis.map(api => {
              console.log(`[SmartHub] GET /api/v1/project/load → projectPath: ${api.projectPath}`);
              return fetch(`${backendUrl}/api/v1/project/load?projectPath=${encodeURIComponent(api.projectPath)}`, {
                headers: { 'x-access-token': token },
              })
                .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
                .then(data => {
                  console.log(`[SmartHub] GET /api/v1/project/load ← response (${api.name}):`, data);
                  const files = (data.files || []).map(f => ({
                    relativePath: f.relativePath || f.path || '',
                    name: f.name || f.fileName || (f.relativePath || f.path || '').split(/[\\/]/).pop() || 'file',
                    content: f.content || '',
                  }));
                  return { ...api, generated: { files } };
                });
            })
          )
            .then(apisWithFiles => {
              console.log('[SmartHub] load-project response:', apisWithFiles);
              if (isSpring) {
                setSelectedTech('springboot');
                setActiveAgent('integration');
                if (proj.projectPath) setProjectPath(proj.projectPath);
                if (proj.projectName) setDeployProjName(proj.projectName);
                const newArtifacts = [];
                const fileList = [];
                // Show the saved design diagram alongside the Spring Boot files.
                if (proj.plantumlCode) {
                  newArtifacts.push({ id: "puml", icon: "📐", name: "flow-diagram.puml", meta: `${proj.plantumlCode.split("\n").length} lines`, badge: "Design", content: proj.plantumlCode });
                  fileList.push({ id: "puml", name: "flow-diagram.puml", icon: "📐", group: "Design" });
                }
                apisWithFiles.forEach(api => {
                  (api.generated?.files || []).forEach(file => {
                    const id = `sb-load-${api.id}-${file.relativePath}`;
                    const icon = getFileIcon(file.name);
                    newArtifacts.push({
                      id,
                      icon,
                      name: file.name,
                      badge: api.name,
                      group: api.name,
                      svcGroup: api.name,
                      svcProjectPath: api.projectPath,
                      content: file.content,
                      relativePath: file.relativePath,
                    });
                    fileList.push({ id, name: file.name, icon, group: api.name, apiProjectPath: api.projectPath, relativePath: file.relativePath });
                  });
                });
                setArtifacts(newArtifacts);
                setMessages([
                  { ...INIT_MESSAGE, time: "" },
                  {
                    role: "bot", time: now(),
                    content: `📂 **${proj.projectName}** loaded — ${proj.generatedApis.length} service(s).`,
                    fileList,
                    ...(proj.plantumlCode ? { artifactId: "puml" } : {}),
                    actions: [{ label: "🚀 Deploy", action: "proceed_to_deployment" }],
                  },
                ]);
              } else {
                handleApiResponse({ apis: apisWithFiles, agentUsed: proj.technology || 'mule', projectPath: proj.projectPath, plantumlCode: proj.plantumlCode || '' });
              }
            })
            .catch(err => {
              console.warn('[SmartHub] load-project failed:', err.message);
              // Fallback: show file tree with placeholder content
              setActiveAgent("integration");
              setGeneratedApis(proj.generatedApis);
              const fallbackArtifacts = proj.generatedApis.map(api => ({
                id: api.id, icon: layerIcon[api.layer] || "📁",
                name: `${api.name}.xml`,
                badge: api.layer ? api.layer.charAt(0).toUpperCase() + api.layer.slice(1) : "",
                layer: api.layer,
                content: `<!-- Files stored at: ${api.projectPath} -->\n<!-- Backend load-project endpoint not available -->`,
              }));
              // Still surface the saved design diagram even when file contents are unavailable.
              if (proj.plantumlCode) {
                fallbackArtifacts.unshift({ id: "puml", icon: "📐", name: "flow-diagram.puml", meta: `${proj.plantumlCode.split("\n").length} lines`, badge: "Design", content: proj.plantumlCode });
              }
              setArtifacts(fallbackArtifacts);
              setMessages([
                { ...INIT_MESSAGE, time: "" },
                {
                  role: "bot", time: now(),
                  content: `📂 **${proj.projectName}** loaded — ${proj.generatedApis.length} API(s).\n\n⚠️ Could not fetch file contents (${err.message}). Files listed in sidebar.`,
                  ...(proj.plantumlCode ? { artifactId: "puml" } : {}),
                  actions: [{ label: "🔄 Start Fresh", action: "new_session" }],
                },
              ]);
            });
          if (proj.plantumlCode) {
            setPlantumlCode(proj.plantumlCode);
            plantumlCodeRef.current = proj.plantumlCode;
          }
          return;
        }

        // Design-template project — restore PlantUML and allow editing
        if (proj.projectArchetype === 'smarthub-template' && proj.plantumlCode && !proj.generatedApis?.length) {
          designProjectIdRef.current = proj._id;
          setDesignSaved(true);
          // Mint a FRESH session for this load (override the restore above). The saved design's
          // own sessionId has a checkpoint that already holds javaVersion / gatewayChoice /
          // deploymentSetup from its first generation; reusing it makes the hub's setup nodes
          // short-circuit so a re-generation skips those questions. Regenerating from a saved
          // design is a new build → a clean session re-asks java version / gateway / CI-CD. The
          // design itself carries over via plantumlCode below.
          const freshSid = uuidv4();
          setSessionId(freshSid);
          sessionIdRef.current = freshSid;
          localStorage.setItem("langraph_session_id", freshSid);
          if (socketRef.current?.connected) {
            console.log('[SmartHub] fresh session for design-template load →', freshSid);
            socketRef.current.emit("join-session", freshSid);
          }
          setPlantumlCode(proj.plantumlCode);
          plantumlCodeRef.current = proj.plantumlCode;
          setActiveAgent("design");
          setGeneratedApis([]);
          setArtifacts([{ id: "puml", icon: "📐", name: "flow-diagram.puml", meta: `${proj.plantumlCode.split("\n").length} lines`, badge: "Design", content: proj.plantumlCode }]);
          setActiveArtifact(null);
          savedProjectIdRef.current = null;   // design project ID must not carry over to integration
          setSavedProjectId(null);            // clears "Saved to Project Centre" badge for new integration
          setMessages([
            { ...INIT_MESSAGE, time: "" },
            {
              role: "bot", time: now(),
              content: `📐 **${proj.projectName}** loaded. Your design diagram is ready — you can edit it or proceed to integration.`,
              artifactId: "puml",
              actions: [
                { label: "✅ Yes, proceed to Integration", action: "proceed_to_integration" },
                { label: "✏️ Edit Diagram",                action: "edit_diagram"           },
                { label: "💾 Save Design",                 action: "save_design"            },
              ],
            },
          ]);
          return;
        }

        // No generatedApis — just show loaded message
        console.warn('[SmartHub] No generatedApis in project:', proj._id);
        setMessages([
          { ...INIT_MESSAGE, time: "" },
          {
            role: "bot", time: now(),
            content: `📂 **${proj.projectName}** loaded.\nTechnology: **${proj.technology || "unknown"}** · No generated files found.\n\nStart a new prompt to generate integration files.`,
            actions: [{ label: "🔄 Start Fresh", action: "new_session" }],
          },
        ]);
      })
      .catch(err => console.warn('[SmartHub] Failed to load project:', err));
  }, [projId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Shared response handler ───────────────────────────────────
  const handleApiResponse = (data) => {
    if (data && data.recoverable && data.status === "running") {
      recoverGeneration();
      return;
    }
    if (!data) {
      console.error("[SmartHub] handleApiResponse called with no data");
      setLoading(false);
      return;
    }
    setLoading(false);
    addLog(setLogs, "success", "✅ Response received");
    fetchUsage();
    console.log("[SmartHub] API response:", JSON.stringify(data, null, 2));

    // ── Backend/LLM error (e.g. 429 quota exceeded) ──────────────
    if (data.error) {
      const errMsg = typeof data.error === "string" ? data.error : JSON.stringify(data.error);
      addLog(setLogs, "error", `❌ ${errMsg}`);
      setMessages(prev => [...prev, { role: "bot", time: now(), content: `❌ ${errMsg}`, retryable: true }]);
      return;
    }

    const rawText = data.message || data.response || data.text || data.data?.message || "";

    // Extract plantuml from dedicated fields OR from markdown code block in message text
    const extractPuml = (str) => {
      if (!str) return "";
      // ```plantuml ... ``` block
      const fenced = str.match(/```plantuml\s*([\s\S]*?)```/i);
      if (fenced) return fenced[1].trim();
      // bare @startuml ... @enduml block
      const bare = str.match(/(@startuml[\s\S]*?@enduml)/i);
      if (bare) return bare[1].trim();
      return "";
    };

    const pumlFromText = extractPuml(rawText);
    const puml  = data.data?.plantumlCode || data.plantumlCode || pumlFromText;

    // Strip the plantuml block from the displayed message text
    const botText = pumlFromText
      ? rawText.replace(/```plantuml[\s\S]*?```/gi, "").replace(/@startuml[\s\S]*?@enduml/gi, "").trim()
      : rawText;
    const raml  = data.data?.raml || data.generated?.raml || "";
    const xml   = data.generated?.source || data.generated?.application || data.xml || "";
    const rawDeps = data.generated?.dependencies;
    const deps  = rawDeps
      ? (typeof rawDeps === "string" ? rawDeps : `<dependencies>\n${rawDeps.map(d => `  <dependency>\n    <groupId>${d.groupId}</groupId>\n    <artifactId>${d.artifactId}</artifactId>\n    <version>${d.version}</version>\n  </dependency>`).join("\n")}\n</dependencies>`)
      : "";

    // ── Architectural review pending (Mule) ──────────────────────
    if (data.type === "architectural_review_pending") {
      setMessages(prev => [...prev, {
        role: "bot",
        time: now(),
        content: data.message || "Please review the proposed architecture.",
        architecturalReview: {
          apis:               data.apis               || [],
          detectedConnectors: data.detectedConnectors || [],
          round:              data.round              ?? 0,
          canSkip:            data.canSkip            !== false,
          tech:               data.tech               || "mule",
        },
      }]);
      return;
    }

    // ── API selection pending ─────────────────────────────────────
    if (data.type === "api_selection_pending") {
      setMessages(prev => [...prev, {
        role: "bot",
        time: now(),
        content: data.message || "Please select the APIs to generate.",
        apiSelection: { apis: data.apis || [] },
      }]);
      return;
    }

    // ── Standalone-app confirmation pending ───────────────────────
    // Some selected APIs have no REST endpoints (e.g. scheduler-triggered). Ask whether
    // to build them as standalone background apps vs. normal governed APIs.
    if (data.type === "standalone_confirmation_pending") {
      setMessages(prev => [...prev, {
        role: "bot",
        time: now(),
        content: data.message || "Some APIs have no REST endpoints — treat them as standalone apps?",
        standaloneConfirm: { apis: data.apis || [], canSkip: data.canSkip !== false, skipHint: data.skipHint || "" },
      }]);
      return;
    }

    // ── Integration-testing approach (Spring Boot) ───────────────
    // Renders the 3 options as clickable buttons + a Skip(default) — otherwise the
    // question shows as plain text with no way to reply. Backend parses the value
    // (restassured / mockmvc / testcontainers) via parseTestingApproachNode.
    if (data.type === "testing_approach_pending") {
      setMessages(prev => [...prev, {
        role: "bot", time: now(),
        content: (data.message && data.message.trim()) || "Which integration-testing approach should I generate for your Spring Boot service(s)?",
        actions: [
          ...(data.options || []).map(o => ({ label: (o.label || o.value).split("—")[0].trim(), action: `inline_choice__${o.value}` })),
          ...(data.canSkip ? [{ label: `⏭ Skip (${data.default || 'restassured'})`, action: `inline_choice__${data.default || 'restassured'}` }] : []),
        ],
      }]);
      return;
    }

    // ── Transport (TLS) selection pending ────────────────────────
    if (data.type === "transport_selection_pending") {
      setMessages(prev => [...prev, {
        role: "bot", time: now(),
        content: (data.message && data.message.trim()) || "How should your Mule API handle TLS?",
        actions: [
          ...(data.options || []).map(o => ({ label: o.label, action: `inline_choice__${o.value}` })),
          ...(data.canSkip ? [{ label: `⏭ Skip (${data.default || 'http'})`, action: `inline_choice__${data.default || 'http'}` }] : []),
        ],
      }]);
      return;
    }

    // ── Schema elicitation pending ───────────────────────────────
    if (data.type === "schema_elicitation_pending") {
      setMessages(prev => [...prev, {
        role: "bot", time: now(),
        content: (data.message && data.message.trim()) || "Before generating, I have a few schema questions about your APIs:",
        schemaElicitation: {
          apis: data.apis || [],
          canSkip: data.canSkip !== false,
        },
      }]);
      return;
    }

    // ── Existing API review pending ──────────────────────────────
    if (data.type === "existing_api_review_pending") {
      setMessages(prev => [...prev, {
        role: "bot", time: now(),
        content: data.message,
        actions: [
          { label: "✅ Proceed",          action: "inline_choice__proceed"        },
          { label: "📁 Upload another",   action: "inline_choice__upload another" },
        ],
      }]);
      return;
    }

    // ── Custom Azure pipeline upload pending (Mule only) ─────────
    if (data.type === "pipeline_upload_pending") {
      setMessages(prev => [...prev, {
        role: "bot", time: now(),
        content: data.message,
        pipelineUpload: {
          uploadEndpoint: data.uploadEndpoint || "/api/v1/hub/upload-pipeline",
          options:        data.options        || [],
          canSkip:        data.canSkip !== false,
          skipHint:       data.skipHint || "Type 'skip' for the built-in pipeline",
        },
      }]);
      return;
    }

    // ── CI/CD setup pending (deployment_setup_pending) ───────────
    // Asked once after the technology is picked: where the pipeline runs (Azure / GitHub /
    // upload-own for Mule). Update the open (unanswered) card in place so stage transitions
    // (awaiting_upload / awaiting_type) and re-asks morph one card instead of stacking.
    if (data.type === "deployment_setup_pending") {
      setMessages(prev => {
        const next = prev.slice();
        const card = {
          role: "bot", time: now(),
          content: (data.message && data.message.trim()) || "Where should the CI/CD pipeline run?",
          deploymentSetup: {
            technology:     data.technology,
            stage:          data.stage || null,
            message:        data.message,
            options:        data.options || [],
            deployTarget:   data.deployTarget || null,
            default:        data.default || null,
            canSkip:        data.canSkip !== false,
            skipHint:       data.skipHint || "",
            uploadEndpoint: data.uploadEndpoint || "/api/v1/hub/upload-pipeline",
            note:           data.note || "",
          },
        };
        let idx = -1;
        for (let k = next.length - 1; k >= 0; k--) {
          if (next[k] && next[k].deploymentSetup && !next[k].deploymentSetupAnswered) { idx = k; break; }
        }
        if (idx >= 0) next[idx] = { ...next[idx], ...card }; else next.push(card);
        return next;
      });
      return;
    }

    // ── Existing API upload pending ──────────────────────────────
    if (data.type === "existing_api_upload_pending") {
      setMessages(prev => [...prev, {
        role: "bot", time: now(),
        content: data.message,
        existingApiUpload: {
          uploadEndpoint: data.uploadEndpoint || "/api/v1/hub/upload-existing-api",
          accepts:        data.accepts        || ["jar", "github"],
          canSkip:        data.canSkip        !== false,
          skipHint:       data.skipHint       || "Type 'skip' to generate without it",
          declaredLayers: data.declaredLayers || [],
        },
      }]);
      return;
    }

    // ── Validation agent response ────────────────────────────────
    if (data.type === "validation_questions") {
      setMessages(prev => [...prev, {
        role: "bot", time: now(),
        content: (data.message && data.message.trim()) || "I need a few more details before proceeding.",
        validation: {
          questions: data.questions || [],
          gaps: data.gapsIdentified || [],
        },
      }]);
      return;
    }

    // ── Design plan pending (review/approve before the diagram is drawn) ──
    // Rendered as a full-width "plan window" card in the center chat column.
    // Across edit rounds the backend returns a fresh design_plan_pending each
    // time; we update the SAME card in place instead of stacking new ones.
    if (data.type === "design_plan_pending") {
      setMessages(prev => {
        const next = prev.slice();
        const designPlanMsg = {
          role: "bot", time: now(),
          content: (data.message && data.message.trim()) || "Design plan ready — review and approve.",
          designPlan: {
            plan:          data.plan || null,
            round:         data.round ?? 0,
            maxRounds:     data.maxRounds ?? null,
            openDecisions: data.openDecisions || [],
            lastEdit:      data.lastEdit || null,
            canEdit:       data.canEdit !== false,
            editHint:      data.editHint || "",
          },
        };
        // Update the last existing plan card (not yet approved) in place.
        let idx = -1;
        for (let k = next.length - 1; k >= 0; k--) {
          if (next[k] && next[k].designPlan && !next[k].designPlanAnswered) { idx = k; break; }
        }
        if (idx >= 0) next[idx] = { ...next[idx], ...designPlanMsg };
        else next.push(designPlanMsg);
        return next;
      });
      return;
    }

    // ── Implementation plan pending (review/approve AFTER pipeline upload,
    //    JUST BEFORE project generation) ───────────────────────────────────
    if (data.type === "implementation_plan_pending") {
      setMessages(prev => {
        const next = prev.slice();
        const implPlanMsg = {
          role: "bot", time: now(),
          content: (data.message && data.message.trim()) || "Implementation plan ready — review and approve.",
          implementationPlan: {
            plan:          data.plan || null,
            round:         data.round ?? 0,
            maxRounds:     data.maxRounds ?? null,
            openDecisions: data.openDecisions || [],
            lastEdit:      data.lastEdit || null,
            canEdit:       data.canEdit !== false,
            editHint:      data.editHint || "",
            diagrams:      data.diagrams || (data.plan && data.plan.diagrams) || null,
          },
        };
        let idx = -1;
        for (let k = next.length - 1; k >= 0; k--) {
          if (next[k] && next[k].implementationPlan && !next[k].implementationPlanAnswered) { idx = k; break; }
        }
        if (idx >= 0) next[idx] = { ...next[idx], ...implPlanMsg };
        else next.push(implPlanMsg);
        return next;
      });
      return;
    }

    // The approved implementation plan leads here (generation / next phase).
    // Any result-bearing response past the pending-type gates means generation
    // has started, so collapse an open implementation-plan card.
    setMessages(prev => (
      prev.some(m => m && m.implementationPlan && !m.implementationPlanAnswered)
        ? prev.map(m => (m && m.implementationPlan && !m.implementationPlanAnswered) ? { ...m, implementationPlanAnswered: true } : m)
        : prev
    ));

    // ── .NET response ────────────────────────────────────────────
    if (data.agentUsed === "dotnet") {
      setActiveAgent("integration");
      setSelectedTech("dotnet");
      if (data.projectName) setDeployProjName(data.projectName);
      var dotnetServices = (data.projects || data.apis || []).map(function(p, i) {
        var p_path = (p.path || p.projectPath || '').replace(/\\/g, '/');
        var p_name = p.name || p_path.split('/').filter(Boolean).pop() || ('Service' + (i + 1));
        return { id: 'dotnet-' + i, name: p_name, displayName: p.displayName || p_name, layer: 'service', projectPath: p_path };
      });
      if (dotnetServices.length > 0) setGeneratedApis(dotnetServices);
      const dotnetProjectPath = data.projectPath || (data.projects && data.projects[0]?.path) || data.apis?.[0]?.projectPath || "";

      const getDotnetIcon = (name) =>
        name.endsWith('.cs')        ? "🔷"
        : name.endsWith('.sln')     ? "🧩"
        : name.endsWith('.csproj')  ? "📦"
        : (name.endsWith('.yaml') || name.endsWith('.yml')) ? "⚙️"
        : name === 'Dockerfile'     ? "🐳"
        : name.endsWith('.json')    ? "🔧"
        : name.endsWith('.md')      ? "📝"
        : "📄";

      const addBotMsg = (fileEntries, mdDocs) => {
        setMessages(prev => [...prev, {
          role: "bot", time: now(),
          content: `✅ .NET project **${data.projectName || ""}** generated successfully!`,
          fileList: fileEntries,
          actions: [
            { label: "✏️ Edit Integration", action: "edit_integration" },
            { label: "💾 Save to repository", action: "save_repository" },
            { label: "✕ Not now",           action: "dismiss" },
            ...(mdDocs.length > 0 ? [{ label: "📄 Push to Confluence", action: "push_confluence" }] : []),
          ],
        }]);
      };

      // Determine the root upload folder (contains service subfolder, shared/, root-level files).
      // Root folders are named with a timestamp suffix (_YYYYMMDD_HHMMSS); service subfolders are not.
      const token = localStorage.getItem("token");
      const dpNorm = (data.projectPath || "").replace(/\\/g, '/');
      const lastSeg = dpNorm.split('/').filter(Boolean).pop() || '';
      const isTimestampRoot = /\d{8}_\d{6}/.test(lastSeg);
      const serviceSubPath = ((data.projects?.[0]?.path) || data.apis?.[0]?.projectPath || "").replace(/\\/g, '/');
      let loadPath;
      if (serviceSubPath) {
        // Strip service subfolder → parent = root
        loadPath = serviceSubPath.includes('/') ? serviceSubPath.substring(0, serviceSubPath.lastIndexOf('/')) : serviceSubPath;
      } else if (isTimestampRoot) {
        // data.projectPath already looks like the root upload folder
        loadPath = dpNorm;
      } else {
        // data.projectPath looks like a service subfolder (no timestamp) — strip one level
        loadPath = dpNorm.includes('/') ? dpNorm.substring(0, dpNorm.lastIndexOf('/')) : dpNorm;
      }
      loadPath = loadPath || dotnetProjectPath.replace(/\\/g, '/');
      if (loadPath) setProjectPath(loadPath);

      if (loadPath) {
        console.log(`[SmartHub] GET /api/v1/project/load → .NET root: ${loadPath}`);
        fetch(`${backendUrl}/api/v1/project/load?projectPath=${encodeURIComponent(loadPath)}`, {
          headers: { "x-access-token": token },
        })
          .then(r => r.ok ? r.json() : null)
          .then(res => {
            console.log(`[SmartHub] GET /api/v1/project/load ← .NET root: ${res?.files?.length ?? 0} files`);
            const allFiles = res?.files || [];
            const merged = allFiles.map(file => {
              const relPath = (file.relativePath || file.name).replace(/\\/g, "/");
              // First path segment = group (HelloWorld, shared, etc.)
              // Root-level files (no sub-folder) go into "Project Files" group
              const groupName = relPath.includes("/") ? relPath.split("/")[0] : "Project Files";
              // Strip the group prefix so relativePath is relative to the group subfolder
              const relativeToGroup = relPath.includes("/") ? relPath.slice(groupName.length + 1) : relPath;
              // groupPath = absolute path to the group subfolder (for editing)
              const groupPath = groupName === "Project Files"
                ? loadPath
                : `${loadPath}/${groupName}`;
              return {
                id: `dotnet-${groupName}-${relativeToGroup}`,
                icon: getDotnetIcon(file.name),
                name: file.name,
                badge: groupName,
                group: groupName,
                svcGroup: groupName,
                svcProjectPath: groupPath,
                content: file.content || "",
                relativePath: relativeToGroup,
              };
            });
            if (merged.length > 0) setArtifacts(merged);
            // Derive service list from actual discovered groups (more reliable than data.projects)
            // Exclude well-known non-service folders
            var NON_SVC = /^(shared|common|infrastructure|contracts|models|utilities|utils|project files)$/i;
            var svcGroups = merged
              .map(function(a) { return a.group; })
              .filter(function(g, i, arr) { return g && !NON_SVC.test(g) && arr.indexOf(g) === i; });
            if (svcGroups.length > 0) {
              setGeneratedApis(svcGroups.map(function(g, i) {
                return { id: 'dotnet-' + i, name: g, displayName: g, layer: 'service', projectPath: loadPath ? loadPath + '/' + g : '' };
              }));
            }
            const mdDocs = merged.filter(a => a.name.endsWith('.md') && a.content)
              .map(a => ({ title: a.name.replace(/\.md$/i, ''), markdown: a.content }));
            if (mdDocs.length > 0) setConfluenceDocs(mdDocs);
            addBotMsg(merged, mdDocs);
          })
          .catch(() => addBotMsg([], []));
      } else {
        addBotMsg([], []);
      }
      return;
    }

    // ── Spring Boot response ─────────────────────────────────────
    if (data.agentUsed === "springboot") {
      setActiveAgent("integration");
      setSelectedTech("springboot");
      if (data.projectPath) setProjectPath(data.projectPath);
      if (data.projectName) setDeployProjName(data.projectName);

      const microservices = data.ast?.microservices || [];
      const isMicroservices = microservices.length > 0;
      if (isMicroservices) {
        setGeneratedApis(microservices.map(function(svc, i) {
          var svcPath = data.projectPath ? (data.projectPath + '/' + svc.name) : '';
          return { id: 'spring-' + i, name: svc.name, displayName: svc.displayName || svc.name, layer: 'service', projectPath: svcPath };
        }));
      }
      const chatFileEntries = [];

      if (isMicroservices) {
        // Microservices format: build artifacts from each service's code
        if (microservices[0]?.packageName) setPackageName(microservices[0].packageName);

        microservices.forEach(svc => {
          const badge = svc.name;
          const svcPath = data.projectPath ? `${data.projectPath}/${svc.name}` : "";
          (svc.services || []).forEach(s => {
            const id = `sb-${svc.name}-svc-${s.name}`;
            pushArtifact({ id, icon: "☕", name: `${s.name}.java`, badge, svcGroup: svc.name, svcProjectPath: svcPath, content: s.fullCode });
            chatFileEntries.push({ id, name: `${s.name}.java`, icon: "☕", group: badge, apiProjectPath: svcPath });
          });
          (svc.entities || []).forEach(e => {
            const id = `sb-${svc.name}-entity-${e.name}`;
            pushArtifact({ id, icon: "🗄️", name: `${e.name}.java`, badge, svcGroup: svc.name, svcProjectPath: svcPath, content: e.fullCode });
            chatFileEntries.push({ id, name: `${e.name}.java`, icon: "🗄️", group: badge, apiProjectPath: svcPath });
          });
          (svc.repositories || []).forEach(r => {
            const id = `sb-${svc.name}-repo-${r.name}`;
            pushArtifact({ id, icon: "📁", name: `${r.name}.java`, badge, svcGroup: svc.name, svcProjectPath: svcPath, content: r.fullCode });
            chatFileEntries.push({ id, name: `${r.name}.java`, icon: "📁", group: badge, apiProjectPath: svcPath });
          });
          (svc.configs || []).forEach(c => {
            const id = `sb-${svc.name}-cfg-${c.name}`;
            pushArtifact({ id, icon: "⚙️", name: `${c.name}.java`, badge, svcGroup: svc.name, svcProjectPath: svcPath, content: c.fullCode });
            chatFileEntries.push({ id, name: `${c.name}.java`, icon: "⚙️", group: badge, apiProjectPath: svcPath });
          });
        });
      } else {
        // Single-service format: use data.generated
        if (data.ast?.packageName) setPackageName(data.ast.packageName);
        const IMPORTANT_FILES = [
          { key: "pom",                   name: "pom.xml",                icon: "📦", badge: "Config" },
          { key: "application",           name: "Application.java",       icon: "☕", badge: "Java"   },
          { key: "controller",            name: "Controller.java",        icon: "☕", badge: "Java"   },
          { key: "applicationProperties", name: "application.properties", icon: "⚙️", badge: "Config" },
          { key: "dockerfile",            name: "Dockerfile",             icon: "🐳", badge: "Docker" },
        ];
        if (data.generated) {
          IMPORTANT_FILES.forEach(({ key, name, icon, badge }) => {
            const content = data.generated[key];
            if (!content) return;
            const id = `sb-gen-${key}`;
            pushArtifact({ id, icon, name, badge, content });
            chatFileEntries.push({ id, name, icon });
          });
        }
      }

      const addSpringBotMessage = (fileList, showConfluence = false) => {
        setMessages(prev => [...prev, {
          role: "bot", time: now(),
          content: "",
          fileList,
          actions: [
            { label: "💾 Save to repository", action: "save_repository" },
            { label: "✕ Not now",          action: "dismiss"               },
            ...(showConfluence ? [{ label: "📄 Push to Confluence", action: "push_confluence" }] : []),
          ],
        }]);
      };

      const getFileIcon = (name) =>
        name.endsWith('.java') ? "☕"
        : name.endsWith('.xml') ? "📦"
        : (name.endsWith('.yaml') || name.endsWith('.yml')) ? "⚙️"
        : name.endsWith('.properties') ? "⚙️"
        : name === 'Dockerfile' ? "🐳"
        : name.endsWith('.json') ? "🔧"
        : name.endsWith('.md') ? "📝"
        : "📄";

      if (isMicroservices && data.projectPath) {
        // Load each microservice folder from disk separately so sidebar SvcFolder grouping works
        const token = localStorage.getItem('token');
        Promise.all(
          microservices.map(svc => {
            const svcPath = `${data.projectPath}/${svc.name}`;
            console.log(`[SmartHub] GET /api/v1/project/load → Spring svc: ${svc.name}`);
            return fetch(`${backendUrl}/api/v1/project/load?projectPath=${encodeURIComponent(svcPath)}`, {
              headers: { 'x-access-token': token },
            })
              .then(r => r.ok ? r.json() : null)
              .then(res => {
                console.log(`[SmartHub] GET /api/v1/project/load ← svc: ${svc.name}, files: ${res?.files?.length ?? 0}`);
                return res?.files?.length ? { svc, svcPath, files: res.files } : null;
              })
              .catch(() => null);
          })
        ).then(results => {
          const loaded = results.filter(Boolean);
          if (loaded.length > 0) {
            const newArtifacts = [];
            const newChatFileEntries = [];
            loaded.forEach(({ svc, svcPath, files }) => {
              files.forEach(file => {
                const icon = getFileIcon(file.name);
                const id = `sb-disk-${svc.name}-${file.relativePath}`;
                newArtifacts.push({
                  id, icon, name: file.name,
                  badge: svc.name,
                  group: svc.name,
                  svcGroup: svc.name,
                  svcProjectPath: svcPath,
                  content: file.content,
                  relativePath: file.relativePath,
                });
                newChatFileEntries.push({ id, name: file.name, icon, group: svc.name, apiProjectPath: svcPath });
              });
            });
            setArtifacts(newArtifacts);
            const mdDocs = [];
            loaded.forEach(({ files }) => {
              files.filter(f => f.name.endsWith('.md') && f.content).forEach(f => {
                mdDocs.push({ title: f.name.replace(/\.md$/i, ''), markdown: f.content });
              });
            });
            if (mdDocs.length > 0) setConfluenceDocs(mdDocs);
            addSpringBotMessage(newChatFileEntries, mdDocs.length > 0);
          } else {
            addSpringBotMessage(chatFileEntries, false);
          }
        });
      } else if (data.projectPath) {
        // Single service: load root path
        const token = localStorage.getItem('token');
        const groupName = data.projectName || projName || "Spring Boot Project";
        console.log(`[SmartHub] GET /api/v1/project/load → Spring single-service: ${data.projectPath}`);
        fetch(`${backendUrl}/api/v1/project/load?projectPath=${encodeURIComponent(data.projectPath)}`, {
          headers: { 'x-access-token': token },
        })
          .then(r => r.ok ? r.json() : null)
          .then(res => {
            console.log(`[SmartHub] GET /api/v1/project/load ← Spring single-service files: ${res?.files?.length ?? 0}`);
            if (res?.files?.length) {
              const newArtifacts = res.files.map(file => ({
                id: `sb-disk-${file.relativePath}`,
                icon: getFileIcon(file.name),
                name: file.name,
                badge: groupName,
                content: file.content,
                relativePath: file.relativePath,
                group: groupName,
                svcProjectPath: data.projectPath || '',
              }));
              setArtifacts(newArtifacts);
              const mdDocs = newArtifacts
                .filter(a => a.name.endsWith('.md') && a.content)
                .map(a => ({ title: a.name.replace(/\.md$/i, ''), markdown: a.content }));
              if (mdDocs.length > 0) setConfluenceDocs(mdDocs);
              addSpringBotMessage(chatFileEntries, mdDocs.length > 0);
            } else {
              addSpringBotMessage(chatFileEntries, false);
            }
          })
          .catch(() => { addSpringBotMessage(chatFileEntries, false); });
      } else {
        addSpringBotMessage(chatFileEntries);
      }
      return;
    }

    // ── Mule integration response: data.apis[] ───────────────────
    const apis = data.apis || [];

    if (apis.length > 0) {
      setActiveAgent("integration");
      setSelectedTech("mule");
      setGeneratedApis(apis.map(({ id, name, displayName, layer, projectPath }) => ({ id, name, displayName, layer, projectPath })));
      // Mule responses carry no top-level projectPath — derive the root from the first API's
      // folder (strip its service subfolder) so the project saves/loads with a usable path.
      const firstApiPath = (apis[0]?.projectPath || '').replace(/\\/g, '/');
      const muleRoot = firstApiPath.includes('/') ? firstApiPath.slice(0, firstApiPath.lastIndexOf('/')) : firstApiPath;
      if (muleRoot) setProjectPath(muleRoot);
      const secs = data.metrics?.totalTime ? Math.round(data.metrics.totalTime / 1000) : null;

      // Keep the design diagram alongside the generated files: restore the PlantUML
      // into state and surface it as a "flow-diagram.puml" artifact so re-opening a
      // saved project shows BOTH the diagram and the generated code.
      if (puml) {
        setPlantumlCode(puml);
        plantumlCodeRef.current = puml;
      }
      const pumlArtifact = puml
        ? { id: "puml", icon: "📐", name: "flow-diagram.puml", meta: `${puml.split("\n").length} lines`, badge: "Design", content: puml }
        : null;
      const pumlFileItem = puml
        ? { id: "puml", name: "flow-diagram.puml", icon: "📐", group: "Design" }
        : null;

      // Collect documentation from each API for Confluence push
      const docs = apis
        .filter(api => api.documentation?.plainText)
        .map(api => ({
          title: api.documentation.summary?.projectName || api.displayName || api.name,
          markdown: api.documentation.plainText,
        }));
      if (docs.length > 0) setConfluenceDocs(docs);

      const buildArtifactsFromFiles = (loaded) => {
        const newArtifacts = pumlArtifact ? [pumlArtifact] : [];
        const newFileList = pumlFileItem ? [pumlFileItem] : [];
        loaded.forEach(({ api, files }) => {
          const layerBadge = api.layer ? api.layer.charAt(0).toUpperCase() + api.layer.slice(1) : '';
          const icon = layerIcon[api.layer] || "📁";
          files.forEach(file => {
            const fileIcon = file.name.endsWith('.xml') ? icon
              : file.name.endsWith('.raml') ? "📄"
              : file.name.endsWith('.md') ? "📝"
              : file.name.endsWith('.yaml') || file.name.endsWith('.yml') ? "⚙️"
              : file.name.endsWith('.json') ? "🔧"
              : "📦";
            const artId = `${api.id}-${file.relativePath}`;
            newArtifacts.push({
              id: artId, icon: fileIcon, name: file.name,
              badge: layerBadge, layer: api.layer,
              content: file.content, relativePath: file.relativePath,
              group: api.displayName || api.name,
              svcProjectPath: api.projectPath || '',
            });
            newFileList.push({ id: artId, name: file.name, icon: fileIcon, group: api.displayName || api.name, apiProjectPath: api.projectPath, relativePath: file.relativePath });
          });
        });
        return { newArtifacts, newFileList };
      };

      const buildArtifactsFromResponse = () => {
        const muleFileList = [];
        if (pumlArtifact) { pushArtifact(pumlArtifact); muleFileList.push(pumlFileItem); }
        apis.forEach(api => {
          const layerBadge = api.layer.charAt(0).toUpperCase() + api.layer.slice(1);
          const icon = layerIcon[api.layer] || "📁";
          const xmlId = api.id;
          pushArtifact({ id: xmlId, icon, name: `${api.name}.xml`, badge: layerBadge, layer: api.layer, content: api.generated?.source || '' });
          muleFileList.push({ id: xmlId, name: `${api.name}.xml`, icon, group: `${layerBadge} Layer` });
          if (api.generated?.raml) {
            const ramlId = `${api.id}-raml`;
            pushArtifact({ id: ramlId, icon: "📄", name: `${api.name}.raml`, badge: layerBadge, layer: api.layer, content: api.generated.raml });
            muleFileList.push({ id: ramlId, name: `${api.name}.raml`, icon: "📄", group: `${layerBadge} Layer` });
          }
        });
        return muleFileList;
      };

      // Try to load full folder structure from disk first
      const apisWithPath = apis.filter(api => api.projectPath);
      if (apisWithPath.length > 0) {
        const token = localStorage.getItem('token');
        Promise.all(
          apisWithPath.map(api => {
            console.log(`[SmartHub] GET /api/v1/project/load → Mule API: ${api.name}, path: ${api.projectPath}`);
            return fetch(`${backendUrl}/api/v1/project/load?projectPath=${encodeURIComponent(api.projectPath)}`, {
              headers: { 'x-access-token': token },
            })
              .then(r => r.ok ? r.json() : null)
              .then(res => {
                console.log(`[SmartHub] GET /api/v1/project/load ← Mule API: ${api.name}, files: ${res?.files?.length ?? 0}`);
                return res?.files?.length ? { api, files: res.files } : null;
              })
              .catch(() => null);
          }
          )
        ).then(results => {
          const loaded = results.filter(Boolean);
          let fileList;
          if (loaded.length > 0) {
            const { newArtifacts, newFileList } = buildArtifactsFromFiles(loaded);
            setArtifacts(newArtifacts);
            fileList = newFileList;
            // Prefer .md files from disk over plainText
            const diskMdDocs = [];
            loaded.forEach(({ files }) => {
              files.filter(f => f.name.endsWith('.md') && f.content).forEach(f => {
                diskMdDocs.push({ title: f.name.replace(/\.md$/i, ''), markdown: f.content });
              });
            });
            if (diskMdDocs.length > 0) setConfluenceDocs(diskMdDocs);
          } else {
            fileList = buildArtifactsFromResponse();
          }
          setMessages(prev => [...prev, {
            role: "bot", time: now(),
            content: data.diagramSummary || "MuleSoft integration generated successfully.",
            fileList, secs,
            ...(pumlArtifact ? { artifactId: "puml" } : {}),
            actions: [
              { label: "🛡 Compliance Check", action: "compliance_check" },
              { label: "💾 Save to repository", action: "save_repository" },
              ...(docs.length > 0 ? [{ label: "📄 Push to Confluence", action: "push_confluence" }] : []),
            ],
          }]);
        });
      } else {
        const fileList = buildArtifactsFromResponse();
        setMessages(prev => [...prev, {
          role: "bot", time: now(),
          content: data.diagramSummary || "MuleSoft integration generated successfully.",
          fileList, secs,
          ...(pumlArtifact ? { artifactId: "puml" } : {}),
          actions: [
            { label: "✏️ Edit Integration", action: "edit_integration" },
            { label: "🛡 Compliance Check", action: "compliance_check" },
            { label: "💾 Save to repository", action: "save_repository" },
            ...(docs.length > 0 ? [{ label: "📄 Push to Confluence", action: "push_confluence" }] : []),
          ],
        }]);
      }
      return;
    }

    if (puml) {
      setPlantumlCode(puml);
      plantumlCodeRef.current = puml;
      setActiveAgent("design");
      pushArtifact({ id: "puml", icon: "📐", name: "flow-diagram.puml", meta: `${puml.split("\n").length} lines`, badge: "Design", content: puml });
      if (data.data?.raml) pushArtifact({ id: "raml-d", icon: "📄", name: "api-design.raml", badge: "Design", content: data.data.raml });
      // The approved plan produced this diagram — collapse the plan card.
      setMessages(prev => {
        if (!prev.some(m => m && m.designPlan && !m.designPlanAnswered)) return prev;
        return prev.map(m => (m && m.designPlan && !m.designPlanAnswered) ? { ...m, designPlanAnswered: true } : m);
      });
    }
    if (xml) {
      setActiveAgent("integration");
      pushArtifact({ id: "xml", icon: "⚡", name: selectedTech === "mule" ? "mule-config.xml" : selectedTech === "dotnet" ? "Integration.cs" : "Application.java", badge: "Integration", content: xml });
    }
    if (raml && !puml) pushArtifact({ id: "raml", icon: "📄", name: "api.raml", badge: "Integration", content: raml });
    if (deps)           pushArtifact({ id: "deps", icon: "📦", name: "pom.xml",  badge: "Integration", content: deps });

    const componentCount = data.data?.components?.length;
    const procSecs       = data.processingTime ? Math.round(data.processingTime / 1000) : null;

    // ── Numbered-choice question detection ───────────────────────
    // Detects backend questions like "Which X? 1. Option A 2. Option B"
    // and converts them into clickable action buttons.
    if (!puml && !xml && botText) {
      const optionMatches = [...botText.matchAll(/\b(\d+)\.\s+([^\d\n][^\n]*?)(?=\s+\d+\.|$)/g)];
      if (optionMatches.length >= 2) {
        const parsedActions = optionMatches.map(m => ({
          label: m[2].trim(),
          action: `inline_choice__${m[2].trim()}`,
        }));
        setMessages(prev => [...prev, { role: "bot", time: now(), content: botText, actions: parsedActions }]);
        return;
      }
    }

    let replyContent;
    if (puml) {
      replyContent = `Design generated${componentCount ? ` with ${componentCount} components` : ""}${procSecs ? ` in ${procSecs}s` : ""}. Are you happy with this design?`;
    } else if (xml) {
      replyContent = `Integration generated successfully!${raml ? " RAML spec included." : ""}${deps ? " Dependencies ready." : ""} Check the artifacts panel.`;
    } else {
      const isEditAction =
        lastActionRef.current === 'file_edit' ||
        lastHubBodyRef.current?.action === 'edit_integration';

      if (isEditAction) {
        const prevWithFiles = [...messages].reverse().find(m => m.fileList && m.fileList.length > 0);
        if (prevWithFiles) {
          setMessages(prev => [...prev, {
            role: "bot",
            time: now(),
            content: botText || "✅ Edit applied. Review the updated files below.",
            fileList: prevWithFiles.fileList,
            actions: [
              { label: "✏️ Edit Again",           action: "edit_integration" },
              { label: "💾 Save to repository", action: "save_repository" },
            ],
          }]);
          return;
        }
      }
      replyContent = botText || "Done.";
    }

    const actions = puml ? [
      { label: "✅ Yes, proceed to Integration", action: "proceed_to_integration" },
      { label: "✏️ Edit Diagram",                action: "edit_diagram"           },
      { label: "💾 Save Design",                 action: "save_design"            },
    ] : undefined;

    const inlineArtifactId = puml ? "puml" : xml ? "xml" : null;
    const analysis = puml && (data.data?.components?.length || data.data?.generatedApis?.length)
      ? { components: data.data.components || [], interactions: data.data.interactions || [], generatedApis: data.data.generatedApis || [] }
      : null;
    setMessages(prev => [...prev, { role: "bot", time: now(), content: replyContent, actions, artifactId: inlineArtifactId, analysis }]);
  };

  // ── Token helpers ─────────────────────────────────────────────
  function formatTokenCount(n) {
    if (n >= 1000000) return (n / 1000000).toFixed(1) + 'M';
    if (n >= 1000)    return Math.round(n / 1000) + 'K';
    return String(n);
  }

  function checkTokensBeforeGenerate(body) {
    if (!liveTokens) {
      sendHubRequest(body);
      return;
    }
    // Unlimited plans (e.g. super admins) have no daily cap — the backend reports
    // limit/remaining as 0 with strategy 'unlimited', so never gate on `remaining`.
    if (liveTokens.strategy === 'unlimited') {
      sendHubRequest(body);
      return;
    }
    var serviceCount    = generatedApis.length || 1;
    var estimatedNeeded = serviceCount * TOKENS_PER_SERVICE_ESTIMATE;
    if (liveTokens.remaining >= estimatedNeeded) {
      sendHubRequest(body);
      return;
    }
    setLoading(false);
    setTokenCheckModal({
      estimatedNeeded: estimatedNeeded,
      remaining:       liveTokens.remaining,
      percentUsed:     liveTokens.percentUsed,
      serviceCount:    serviceCount,
      limitReached:    liveTokens.remaining <= 0,
    });
  }

  // ── Centralised hub request (mock-aware) ─────────────────────
  // ── Auto-recover a generation after a client network drop ──────────────────
  // The server keeps generating even if our socket died; it persists the finished
  // result. Poll /api/v1/hub/result and render it when ready — no re-run/re-bill.
  const recoverGeneration = () => {
    if (recoveringRef.current) return;              // already polling
    if (!loadingRef.current) return;                // nothing in flight
    const sid = sessionIdRef.current || localStorage.getItem("langraph_session_id");
    if (!sid) return;
    recoveringRef.current = true;
    const startedAt = Date.now();
    // Code generation can run 30–40+ min (see integration-agent logs). The cap must
    // exceed that or recovery quits before the server finishes and the result is lost.
    // We keep polling as long as the server says 'running'; the cap is a hard ceiling
    // (measured from the LAST time we saw 'running', so a genuinely-running long job
    // never times out prematurely).
    const MAX_IDLE_MS = 60 * 60 * 1000;                // 60 min without a 'running' heartbeat
    let lastRunningAt = Date.now();
    let failCount = 0;                                 // consecutive unreachable-server polls
    addLog(setLogs, "info", "Checking whether your generation finished on the server…");

    const stop = () => {
      recoveringRef.current = false;
      if (recoverPollRef.current) { clearInterval(recoverPollRef.current); recoverPollRef.current = null; }
    };
    const poll = () => {
      if (!loadingRef.current) { stop(); return; }   // original response already arrived
      if (Date.now() - lastRunningAt > MAX_IDLE_MS) {
        stop();
        setLoading(false);
        setMessages(m => [...m, { role: "bot", time: now(), content: "⚠️ Couldn't recover this run in time. Please Retry.", retryable: true }]);
        return;
      }
      fetch(`${backendUrl}/api/v1/hub/result?sessionId=${encodeURIComponent(sid)}&clientTurnId=${encodeURIComponent(lastHubBodyRef.current?.clientTurnId || "")}`, {
        cache: "no-store",
        headers: { "x-access-token": localStorage.getItem("token") },
      })
        .then(r => (r.ok ? r.json() : null))
        .then(data => {
          if (!data || !loadingRef.current) return;
          failCount = 0;                              // server reachable again
          if (data.status === "ready") {
            stop();
            // Abort the interrupted original POST so its late/duplicate response can't
            // land a stale "couldn't generate design" card on top of the recovered result.
            try { if (abortRef.current) abortRef.current.abort(); } catch (_) {}
            setMessages(m => m.filter(x => !x.connLost));  // drop the "offline" note now that it's recovered
            handleApiResponse(data.result || {});    // render exactly as if the response had arrived
          } else if (data.status === "running") {
            // healthy but still generating — keep the spinner + keep polling, no alarm.
            // Reset the idle deadline so a long-but-active job never times out.
            lastRunningAt = Date.now();
          } else if (data.status === "none") {       // explicitly absent, not a store outage
            stop();
            setLoading(false);
            setMessages(m => [...m, { role: "bot", time: now(), content: "⚠️ The connection dropped and this run couldn't be recovered. Please Retry.", retryable: true }]);
          }
        })
        .catch(() => {
          // Server unreachable — the client's network is likely down. After a couple
          // of failed polls, surface it once (this is the "no feedback for minutes" fix).
          failCount += 1;
          if (failCount >= 2 && loadingRef.current) {
            setMessages(m => m.some(x => x.connLost) ? m : [...m, {
              role: "bot", time: now(), connLost: true,
              content: "⚠️ You appear to be offline — your generation is still running on the server. It'll recover automatically when your connection returns.",
            }]);
          }
        });
    };
    poll();                                           // immediate check
    recoverPollRef.current = setInterval(poll, 4000);
  };
  recoverRef.current = recoverGeneration;

  // Async-hub: poll GET /api/v1/jobs/:jobId until the background generation finishes,
  // then render its result exactly as if the sync POST had returned it. Mirrors
  // recoverGeneration. `controller` (optional) lets a New Session / recovery abort
  // supersede an in-flight wait.
  const waitForJob = (jobId, controller) => {
    if (!jobId) return;
    jobIdRef.current = jobId;
    if (jobPollRef.current) { clearInterval(jobPollRef.current); jobPollRef.current = null; }
    const startedAt = Date.now();
    const MAX_MS = 30 * 60 * 1000;
    let failCount = 0;                                 // consecutive 503s (queue unavailable)
    addLog(setLogs, "info", "Generation queued — waiting for it to finish on the server…");

    const stop = () => {
      if (jobPollRef.current) { clearInterval(jobPollRef.current); jobPollRef.current = null; }
    };
    const done = () => {
      stop();
      jobIdRef.current = null;
      localStorage.removeItem("langraph_job_id");
    };
    const poll = () => {
      if (controller && controller.signal.aborted) { stop(); return; }
      if (!loadingRef.current) { stop(); return; }     // turn already resolved elsewhere
      if (Date.now() - startedAt > MAX_MS) {
        done();
        setLoading(false);
        setMessages(m => [...m, { role: "bot", time: now(), content: "⚠️ This generation took too long to complete. Please Retry.", retryable: true }]);
        return;
      }
      fetch(`${backendUrl}/api/v1/jobs/${encodeURIComponent(jobId)}`, {
        headers: { "x-access-token": localStorage.getItem("token") },
      })
        .then(r => {
          if (r.status === 404) return { _http: 404 };
          if (r.status === 503) return { _http: 503 };
          return r.ok ? r.json() : null;
        })
        .then(body => {
          if (!body || (controller && controller.signal.aborted) || !loadingRef.current) return;
          if (body._http === 503) {                    // queue unavailable — transient
            failCount += 1;
            if (failCount >= 3) {
              done();
              setLoading(false);
              setMessages(m => [...m, { role: "bot", time: now(), content: "⚠️ The generation service is temporarily unavailable. Please try again in a moment.", retryable: true }]);
            }
            return;
          }
          failCount = 0;
          if (body._http === 404) {                    // evicted / unknown job — unrecoverable
            done();
            setLoading(false);
            setMessages(m => [...m, { role: "bot", time: now(), content: "⚠️ This run has expired and can't be recovered. Please start again.", retryable: true }]);
            return;
          }
          if (body.status === "completed") {
            done();
            handleApiResponse(body.result || {});      // render as if the sync POST returned it
          } else if (body.status === "failed") {
            done();
            setLoading(false);
            setMessages(m => [...m, { role: "bot", time: now(), content: `❌ ${body.error || "Generation failed."}`, retryable: true }]);
          }
          // waiting | active | delayed → keep the spinner + keep polling
        })
        .catch(() => { /* transient network error — next tick retries */ });
    };
    poll();                                             // immediate check
    jobPollRef.current = setInterval(poll, 4000);
  };
  waitForJobRef.current = waitForJob;

  // Recover on browser online/offline too (covers drops without a socket event).
  useEffect(() => {
    const onOffline = () => {
      if (loadingRef.current) {
        setMessages(m => m.some(x => x.connLost) ? m : [...m, {
          role: "bot", time: now(), connLost: true,
          content: "⚠️ You're offline — your generation is still running on the server. It'll recover automatically when you reconnect.",
        }]);
      }
    };
    // Revive a dead/idle socket the moment connectivity returns (or the tab regains
    // focus) — the socket.io manager can stay idle after a long outage. The existing
    // `connect` handler then clears the banner, rejoins the room, and recovers.
    const reviveSocket = () => {
      const s = socketRef.current;
      if (s && !s.connected) { try { s.connect(); } catch (_) {} }
    };
    const onOnline = () => { reviveSocket(); if (loadingRef.current) recoverGeneration(); };
    const onFocus = () => reviveSocket();
    const onVisible = () => { if (document.visibilityState === "visible") reviveSocket(); };
    window.addEventListener("offline", onOffline);
    window.addEventListener("online", onOnline);
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisible);
    // Watchdog: while online but disconnected, keep nudging a reconnect (covers flaky
    // Wi-Fi where neither `online` nor socket.io's own retry fires).
    const watchdog = setInterval(() => {
      if (typeof navigator !== "undefined" && navigator.onLine === false) return;
      reviveSocket();
    }, 5000);
    return () => {
      window.removeEventListener("offline", onOffline);
      window.removeEventListener("online", onOnline);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisible);
      clearInterval(watchdog);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const sendHubRequest = (hubBody) => {
    // Tag the turn so the backend can apply the right min-token buffer
    // (code generation needs more headroom than diagram generation).
    if (hubBody && !hubBody.opType) hubBody.opType = 'design';
    // Stable per-turn id so a Retry replays the SAME turn (backend returns the prior
    // result instead of re-running — prevents a dropped validation-answer from
    // restarting the whole Q&A). Only assigned for a NEW action; a replayed
    // lastHubBodyRef already carries its id, so retries reuse it.
    if (hubBody && !hubBody.clientTurnId) hubBody.clientTurnId = uuidv4();
    lastHubBodyRef.current = hubBody;
    lastActionRef.current  = 'hub';
    // Arm the watchdog: if no response lands within WATCHDOG_MS, SILENTLY start
    // polling /hub/result (the offline/socket events are unreliable). A healthy but
    // slow generation keeps returning 'running' → no message, spinner continues.
    // Only if the poll can't reach the server does recoverGeneration surface the
    // "connection lost" message — so we never false-alarm on a legitimately long run.
    if (watchdogRef.current) clearTimeout(watchdogRef.current);
    watchdogRef.current = setTimeout(() => {
      watchdogRef.current = null;
      if (!loadingRef.current) return;              // already completed
      if (recoverRef.current) recoverRef.current();  // polls /hub/result; messages only on unreachable server
    }, WATCHDOG_MS);
    if (USE_MOCK) {
      const key = USE_MOCK === "sequence"
        ? MOCK_SEQUENCE[Math.min(_mockSeqIdx++, MOCK_SEQUENCE.length - 1)]
        : USE_MOCK;
      const mockData = MOCK_RESPONSES[key];
      if (!mockData) {
        console.warn(`[SmartHub] Unknown mock key: "${key}". Available keys:`, Object.keys(MOCK_RESPONSES));
        setLoading(false);
        addLog(setLogs, "error", `❌ Unknown mock key: "${key}"`);
        return;
      }
      addLog(setLogs, "info", `🧪 Mock [${key}] (step ${_mockSeqIdx}/${MOCK_SEQUENCE.length})`);
      setTimeout(() => handleApiResponse(mockData), 600);
      return;
    }
    const controller = new AbortController();
    abortRef.current = controller;
    console.log("[SmartHub] POST /api/v1/hub → request:", hubBody);
    fetch(`${backendUrl}/api/v1/hub`, {
      method: "POST",
      headers: { "Content-Type": "application/json", "x-access-token": localStorage.getItem("token") },
      body: JSON.stringify(hubBody),
      signal: abortRef.current.signal,
    })
      .then(async r => {
        if (!r.ok) {
          // Read the response body so real server messages (e.g. token/pool
          // limit reached) surface instead of a generic "HTTP 429".
          let body = null;
          try { body = await r.json(); } catch (_) {}
          const err = new Error((body && (body.error || body.message)) || `HTTP ${r.status}`);
          err.status = r.status;
          err.body = body;
          throw err;
        }
        return r.json();
      })
      .then(data => {
        // If recovery (or New Session) already resolved this turn, the request was
        // aborted — ignore this now-stale response so it can't post a duplicate card.
        if (controller.signal.aborted) return;
        console.log("[SmartHub] POST /api/v1/hub ← response:", data);
        // Async-hub contract (HUB_ASYNC_ENABLED): the POST returns a {jobId} receipt
        // instead of the answer. Persist it and poll the job until it finishes.
        // Sync fallback: no jobId → the answer is already here (works before/after 4C).
        if (data && data.jobId) {
          localStorage.setItem("langraph_job_id", data.jobId);
          waitForJob(data.jobId, controller);
          return;
        }
        // Idempotent-retry: the backend recognized this as a replay of a turn that's
        // still running — don't render anything; poll /hub/result for the real result.
        if (data && data.type === 'in_progress') {
          if (recoverRef.current) recoverRef.current();
          return;
        }
        handleApiResponse(data);
      })
      .catch(err => {
        if (err.name === "AbortError" || controller.signal.aborted) return;
        addLog(setLogs, "error", `❌ ${err.message}`);
        const is401        = err.status === 401 || err.message.includes("401");
        const is429        = err.status === 429 || err.message.includes("429");
        const isTimeout    = err.message.includes("504") || err.message.includes("503") || err.message.includes("timeout");
        const isNetworkErr = err.message === "Failed to fetch" || err.message === "NetworkError when attempting to fetch resource." || err.message.includes("network");
        // A client-side network/timeout drop does NOT mean the turn failed — the server
        // keeps running and stores its result. Route through recovery (poll /hub/result)
        // instead of a premature "connection lost" card that would (a) be stale once the
        // real result lands and (b) leave the retry pointing at a consumed pre-flight body.
        if (isTimeout || isNetworkErr) {
          if (recoverRef.current) recoverRef.current();   // keeps the spinner; renders when the server result lands
          return;
        }
        setLoading(false);
        if (is401) {
          setMessages(prev => [...prev, {
            role: "bot", time: now(),
            content: "🔒 Your session has expired. Please log in again to continue.",
            actions: [{ label: "🔑 Log In", action: "session_expired_logout" }],
          }]);
        } else if (is429) {
          // Distinguish OUR token/pool-limit 429 (has a strategy/limit body)
          // from a transient AI-provider overload 429.
          const b = err.body;
          const isTokenLimit = b && (b.strategy || b.insufficientBuffer || b.limit != null);
          if (isTokenLimit) {
            setMessages(prev => [...prev, {
              role: "bot", time: now(),
              content: `⚠️ ${b.error || err.message}`,
              retryable: true,
            }]);
          } else {
            setMessages(prev => [...prev, {
              role: "bot", time: now(),
              content: "⚠️ Rate limit reached (429 Too Many Requests). The AI service is receiving too many requests — this usually clears within 15–30 seconds. Please wait a moment and retry.",
              retryable: true,
            }]);
          }
        } else {
          setMessages(prev => [...prev, { role: "bot", time: now(), content: `❌ Failed: ${err.message}`, retryable: true }]);
        }
      });
  };

  // ── Submit message ────────────────────────────────────────────
  const handleValidationSubmit = (combinedAnswers, qaItems) => {
    setMessages(prev => {
      const updated = [...prev];
      for (let i = updated.length - 1; i >= 0; i--) {
        if (updated[i].validation && !updated[i].validationAnswered) {
          updated[i] = { ...updated[i], validationAnswered: true };
          break;
        }
      }
      return [...updated, { role: "user", time: now(), content: combinedAnswers, validationQA: qaItems }];
    });
    setLoading(true);
    addLog(setLogs, "info", "📤 Submitting validation answers...");
    sendHubRequest({ prompt: combinedAnswers, sessionId, projectName: projName, technology: selectedTech });
  };

  const handleExistingApiSubmit = (result, msgIdx) => {
    setMessages(prev => prev.map((m, i) =>
      i === msgIdx ? { ...m, existingApiAnswered: true } : m
    ));
    setLoading(true);
    // Integration pre-flight turns — tag opType:'integration' so the backend applies
    // the code-generation token buffer (this step leads into code generation).
    if (result.type === 'skip') {
      addLog(setLogs, "info", "⏭ Skipping existing API upload...");
      sendHubRequest({ prompt: 'skip', sessionId, projectName: projName, technology: selectedTech, opType: 'integration' });
    } else if (result.type === 'file') {
      addLog(setLogs, "info", "📤 Submitting existing API project path...");
      sendHubRequest({ prompt: 'done', sessionId, projectName: projName, technology: selectedTech, opType: 'integration',
        existingApiProjectPath: result.projectPath });
    } else if (result.type === 'github') {
      addLog(setLogs, "info", "📤 Submitting existing API GitHub repo...");
      const ghPayload = { repo: result.repo, branch: result.branch || 'main' };
      if (result.token) ghPayload.token = result.token;
      sendHubRequest({ prompt: 'use my repo', sessionId, projectName: projName, technology: selectedTech, opType: 'integration',
        existingApiGithub: ghPayload });
    }
  };

  const handlePipelineSubmit = (result, msgIdx) => {
    setMessages(prev => prev.map((m, i) => i === msgIdx ? { ...m, pipelineAnswered: true } : m));
    setLoading(true);
    if (result.type === 'uploaded') {
      // File already uploaded via the card's multipart call — continue with "done"
      addLog(setLogs, "info", "📤 Using your uploaded Azure pipeline...");
      sendHubRequest({ prompt: 'done', sessionId, projectName: projName, technology: selectedTech, opType: 'integration' });
    } else {
      // Built-in pipeline
      addLog(setLogs, "info", "⚙️ Using the built-in Azure pipeline...");
      sendHubRequest({ prompt: 'skip', sessionId, projectName: projName, technology: selectedTech, opType: 'integration' });
    }
  };

  // CI/CD setup answer. payload = { deploymentSetup: {...} } or { prompt: "..." } from the card.
  const handleDeploymentSetupSubmit = (payload, msgIdx) => {
    setMessages(prev => prev.map((m, i) => i === msgIdx ? { ...m, deploymentSetupAnswered: true } : m));
    setLoading(true);
    sendHubRequest({ ...payload, sessionId, projectName: projName, technology: selectedTech, opType: 'integration' });
  };

  const handleSchemaSubmit = (combinedText, schemaItems) => {
    const isSkip = combinedText === "skip";
    const displayContent = isSkip ? "skip" : combinedText;
    setMessages(prev => [...prev, {
      role: "user", time: now(),
      content: displayContent,
      ...(schemaItems ? { schemaQA: schemaItems } : {}),
    }]);
    setLoading(true);
    addLog(setLogs, "info", isSkip ? "⏭ Skipping schema — letting AI decide..." : "📤 Submitting schema fields...");
    sendHubRequest({ prompt: displayContent, sessionId, projectName: projName, technology: selectedTech, opType: 'integration' });
  };

  const handleApiSelectionSubmit = (selectedApis, all) => {
    const prompt = all
      ? "generate all"
      : `generate only: ${selectedApis.map(a => a.name).join(", ")}`;
    setMessages(prev => [...prev, { role: "user", time: now(), content: prompt }]);
    setLoading(true);
    addLog(setLogs, "info", "📤 Submitting API selection...");
    sendHubRequest({ prompt, sessionId, projectName: projName, technology: selectedTech, opType: 'integration' });
  };

  const handleStandaloneConfirmSubmit = (replyPrompt, label) => {
    setMessages(prev => [...prev, { role: "user", time: now(), content: label || replyPrompt }]);
    setLoading(true);
    addLog(setLogs, "info", "📤 Submitting standalone-app confirmation...");
    sendHubRequest({ prompt: replyPrompt, sessionId, projectName: projName, technology: selectedTech, opType: 'integration' });
  };

  const retryLastRequest = () => {
    addLog(setLogs, "info", "🔄 Retrying...");
    const actionType = lastActionRef.current;

    // For deployment retries, re-run handleDeployAction so the full request is
    // rebuilt from current state rather than replaying a stale cached body.
    if (actionType === 'mule_deploy') {
      handleDeployAction('confirm_deploy_mule');
      return;
    }
    if (actionType === 'spring_deploy') {
      handleDeployAction('confirm_deploy_spring');
      return;
    }
    if (actionType === 'dotnet_deploy') {
      handleDeployAction('confirm_deploy_dotnet');
      return;
    }

    // Fallback after page reload (lastActionRef is null): infer from message content
    // so retryable: true messages from old sessions still open the deploy terminal.
    const lastBotErr = [...messages].reverse().find(function(m) { return m.role === 'bot' && m.retryable; });
    if (lastBotErr && /deployment failed|deployment error/i.test(lastBotErr.content || '')) {
      var tech = selectedTechRef.current || selectedTech || 'mule';
      var deployRetryAction = tech === 'dotnet' ? 'confirm_deploy_dotnet' : tech === 'springboot' ? 'confirm_deploy_spring' : 'confirm_deploy_mule';
      handleDeployAction(deployRetryAction);
      return;
    }

    setLoading(true);
    if (actionType === 'file_edit') {
      const last = lastFileEditRef.current;
      if (!last) { setLoading(false); return; }
      console.log(`[SmartHub] Retry file edit POST ${last.url} → request:`, last.body);
      fetch(last.url, {
        method: "POST",
        headers: { "Content-Type": "application/json", "x-access-token": localStorage.getItem("token") },
        body: JSON.stringify(last.body),
      })
        .then(r => r.json())
        .then(data => {
          setLoading(false);
          console.log(`[SmartHub] Retry file edit ← response:`, data);
          try { handleApiResponse(data); } catch(e) {}
        })
        .catch(err => {
          setLoading(false);
          setMessages(prev => [...prev, {
            role: "bot", time: now(),
            content: `❌ Edit failed: ${err.message}`,
            retryable: true,
          }]);
        });
    } else {
      if (!lastHubBodyRef.current) { setLoading(false); return; }
      sendHubRequest(lastHubBodyRef.current);
    }
  };

  const stopGeneration = () => {
    if (abortRef.current) abortRef.current.abort();
    abortRef.current = null;
    setLoading(false);
    setMessages(prev => [...prev, { role: "bot", time: now(), content: "⏹ Generation stopped." }]);
  };

  const startEdit = (msgIndex) => {
    setEditingMsgIdx(msgIndex);
    setEditingText(messages[msgIndex].content);
  };

  const cancelEdit = () => {
    setEditingMsgIdx(null);
    setEditingText("");
  };

  const saveEdit = (msgIndex) => {
    const text = editingText.trim();
    if (!text) return;
    setEditingMsgIdx(null);
    setEditingText("");
    // Remove this message and everything after, then re-submit
    setMessages(prev => prev.slice(0, msgIndex));
    setInput(text);
    // trigger submit after state settles
    setTimeout(() => {
      setInput("");
      setLoading(true);
      addLog(setLogs, "info", `📤 Sending request...`);
      setMessages(prev => [...prev, { role: "user", time: now(), content: text }]);
      const isFirst = !messages.some(m => m.role === "user");
      const editPrompt = isFirst ? `## Project Description\n\n${text}` : `## Follow-up Request\n\n${text}`;
      const hubBody = { prompt: editPrompt, sessionId, projectName: projName, technology: selectedTech };
      sendHubRequest(hubBody);
    }, 0);
  };

  // Once the current project has finished (generated/saved), a new top-level prompt in the
  // same session would make the hub reuse the old approved design/plan and redraw the
  // previous architecture. So we LOCK the main prompt box at that point and steer the user
  // to "Start new project" (a fresh session). Follow-ups (plan edits, validation answers,
  // Update Project, per-file Agent Edit) are not driven by this box, so they stay available.
  const isProjectFinished = () => {
    const projectCompleted =
      (savedProjectIdRef.current && savedProjectIdRef.current !== "pending") ||
      designSaved || (generatedApis && generatedApis.length > 0);
    if (!projectCompleted) return false;
    if (awaitingInput) return false;
    // Don't lock while a card is awaiting the user's input.
    const pendingCard = messages.some(m =>
      (m.designPlan && !m.designPlanAnswered) ||
      (m.implementationPlan && !m.implementationPlanAnswered) ||
      (m.deploymentSetup && !m.deploymentSetupAnswered));
    if (pendingCard) return false;
    return true;
  };
  // Re-evaluated every render: locks the main prompt box once the project is finished.
  const promptLocked = isProjectFinished();

  const submitMessage = () => {
    const msg = input.trim();
    if (!msg || loading || bf.bfLoading) return;

    // ── Handle awaiting project name for deployment ───────────
    if (awaitingInput === "deployProjectName") {
      setMessages(prev => [...prev, { role: "user", time: now(), content: msg }]);
      setInput("");
      setDeployProjName(msg);
      setAwaitingInput(null);
      handleDeployAction("__continue_after_projname__");
      return;
    }

    // ── Project finished: the box is locked — use "Start new project" instead ──
    if (isProjectFinished()) return;

    setMessages(prev => [...prev, { role: "user", time: now(), content: msg }]);
    if (!designProjectName) setDesignProjectName(msg.slice(0, 60));
    setInput("");
    setLoading(true);
    addLog(setLogs, "info", `📤 Sending request...`);

    const isFirstPrompt = !messages.some(m => m.role === "user");
    const promptToSend = isFirstPrompt ? `## Project Description\n\n${msg}` : msg;

    let finalPrompt = promptToSend;
    if (pendingAttachment) {
      finalPrompt = `[Attached file: ${pendingAttachment.name}]\n${pendingAttachment.content}\n\n---\n\n${promptToSend}`;
      setPendingAttachment(null);
    }

    const hubBody = { prompt: finalPrompt, sessionId, projectName: projName, technology: selectedTech };
    if (plantumlCodeRef.current) hubBody.plantumlCode = plantumlCodeRef.current;
    if (attachedImageRef.current) { hubBody.imageBase64 = attachedImageRef.current; attachedImageRef.current = null; }
    console.log("[SmartHub] POST /api/v1/hub →", hubBody);

    sendHubRequest(hubBody);
  };

  // ── Diagram edit submit ───────────────────────────────────────
  const handleDiagramEditSubmit = () => {
    if (!diagramEditText.trim()) return;
    const prompt = diagramEditText.trim();
    setDiagramEditMsgIdx(null);
    setDiagramEditText("");
    setMessages(prev => [...prev, { role: "user", time: now(), content: prompt }]);
    const diagramEditPayload = { prompt, sessionId, editEnabled: true, plantumlCode };
    console.log("[SmartHub] Diagram edit payload →", diagramEditPayload);
    setLoading(true);
    sendHubRequest(diagramEditPayload);
  };

  // ── Deployment pause — emit confirmation to resume pipeline ──
  const handleProceedDeployment = () => {
    if (!deploymentConfirmation) return;
    if (socketRef.current) {
      const emitPayload = { phase: deploymentConfirmation.phase, sessionId };
      console.log('[SmartHub] socket.emit deployment:user_confirmed →', emitPayload);
      socketRef.current.emit("deployment:user_confirmed", emitPayload);
    }
    addLog(setLogs, "info", "▶️ Proceeding with deployment...");
    setDeploymentConfirmation(null);
    deploymentPausedRef.current = false;
    setLoading(true);
  };

  // ── Confluence push ──────────────────────────────────────────
  // Push ONE page. Returns { success, pageUrl?, message? } and mirrors the outcome
  // to the logs + a success chat bubble. Failures are shown inline in the modal
  // (which stays open so the user can rename and retry), so we don't spam the chat
  // with a bubble per failed attempt — only a success bubble and the final summary.
  const pushConfluenceDoc = async ({ confluenceAcct, spaceKey, title, markdown }) => {
    const token = localStorage.getItem("token");
    try {
      console.log(`[SmartHub] POST /api/v1/confluence/createPage → title: "${title}", space: ${spaceKey}`);
      const res = await fetch(`${domain}/api/v1/confluence/createPage`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "x-access-token": token },
        body: JSON.stringify({
          confluenceEmail:   confluenceAcct.confluenceEmail,
          confluencePat:     confluenceAcct.confluencePat,
          confluenceBaseUrl: confluenceAcct.confluenceBaseUrl,
          spaceKey,
          title,
          markdown,
        }),
      });
      const d = await res.json();
      console.log(`[SmartHub] POST /api/v1/confluence/createPage ← title: "${title}"`, d);
      if (res.ok && d.success) {
        addLog(setLogs, "success", `✅ "${title}" pushed`);
        setMessages(prev => [...prev, {
          role: "bot", time: now(),
          content: `✅ **${title}** published to Confluence`,
          confluenceLink: d.pageUrl,
        }]);
        return { success: true, pageUrl: d.pageUrl };
      }
      const errMsg = d.message || `HTTP ${res.status}`;
      addLog(setLogs, "error", `❌ "${title}": ${errMsg}`);
      return { success: false, message: errMsg };
    } catch (err) {
      addLog(setLogs, "error", `❌ "${title}": ${err.message}`);
      return { success: false, message: err.message };
    }
  };

  // Called by the modal when the user is done (all pages succeeded, or they closed
  // after a partial push). Posts the summary + deployment prompt, then closes.
  const finishConfluencePush = (pushed, total) => {
    setShowConfluenceModal(false);
    if (!total) return; // nothing was attempted — just a plain close
    if (pushed > 0) addLog(setLogs, "success", `📄 ${pushed}/${total} page(s) pushed to Confluence`);
    setMessages(prev => [...prev, {
      role: "bot", time: now(),
      content: pushed === total
        ? `📄 All ${pushed} page(s) published to Confluence.`
        : pushed > 0
          ? `📄 ${pushed} of ${total} page(s) published. Some pages failed — see errors above.`
          : `Confluence push failed for all pages.`,
      actions: [{ label: "🚀 Review Complete?", action: "proceed_to_deployment" }],
    }]);
  };

  // ── Integration edit submit ───────────────────────────────────
  const handleIntegrationEditSubmit = () => {
    if (!integrationEditText.trim()) return;
    const prompt = integrationEditText.trim();
    setIntegrationEditMsgIdx(null);
    setIntegrationEditText("");
    setMessages(prev => [...prev, { role: "user", time: now(), content: prompt }]);

    if (selectedTech === "dotnet") {
      const editPayload = { projectPath, prompt, sessionId };
      lastFileEditRef.current = { url: `${backendUrl}/api/v1/dotnet/edit`, body: editPayload };
      lastActionRef.current = 'file_edit';
      setLoading(true);
      addLog(setLogs, "info", "📤 Sending .NET edit request...");
      console.log("[SmartHub] POST /api/v1/dotnet/edit → request:", editPayload);
      fetch(`${backendUrl}/api/v1/dotnet/edit`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "x-access-token": localStorage.getItem("token") },
        body: JSON.stringify(editPayload),
      })
        .then(r => r.json())
        .then(data => {
          setLoading(false);
          try { handleApiResponse(data); } catch(e) { console.warn('[SmartHub] handleApiResponse skipped after dotnet edit:', e.message); }
        })
        .catch(err => {
          setLoading(false);
          addLog(setLogs, "error", `❌ ${err.message}`);
          setMessages(prev => [...prev, { role: "bot", time: now(), content: `❌ Edit failed: ${err.message}`, retryable: true }]);
        });
      return;
    }

    sendHubRequest({
      sessionId,
      projectPath,
      editPrompt: prompt,
      technology: selectedTech,
      action: "edit_integration",
    });
  };

  const updateDeployCard = (updates) =>
    setMessages(prev => prev.map(m =>
      m.deploymentCard ? { ...m, deploymentCard: { ...m.deploymentCard, ...updates } } : m
    ));

  const addDeployLog = (text, type) => {
    if (!text) return;
    var ts = new Date().toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' });
    setDeployLogs(prev => [...prev, { text: String(text), type: type || 'info', timestamp: ts }]);
  };

  const advanceDeployStage = (text) => {
    var t = (text || '').toLowerCase();
    setDeployStages(function(prev) {
      var idx = prev.findIndex(function(s) { return s.status === 'active'; });
      if (idx < 0) return prev;
      // Advance by detecting the NEXT stage's keywords — the current stage only
      // becomes ✅ when the stage after it begins, not on its own first log line.
      var next = prev[idx + 1];
      if (!next) return prev; // last stage — only integration:complete marks it done
      var nextName = next.name.toLowerCase();
      var advance = false;
      // Mule: next = GitHub
      if (nextName === 'github' && (t.includes('github') || t.includes('git push') || t.includes('pushing to') || t.includes('push code'))) advance = true;
      // Mule: next = Azure Pipeline
      if ((nextName.includes('azure') || nextName.includes('pipeline')) && (t.includes('azure') || t.includes('pipeline') || t.includes('trigger') || t.includes('devops'))) advance = true;
      // Generic: next = Build
      if (nextName.includes('build') && (t.includes('build') || t.includes('compil') || t.includes('maven') || t.includes('mvn'))) advance = true;
      // Generic: next = Docker
      if (nextName.includes('docker') && (t.includes('docker') || t.includes('image') || t.includes('container'))) advance = true;
      // Generic: next = Deploy (non-Azure)
      if ((nextName.includes('deploy') && !nextName.includes('azure') && !nextName.includes('pipeline')) && (t.includes('deploy') || t.includes('cloudhub') || t.includes('trigger'))) advance = true;
      if (!advance) return prev;
      return prev.map(function(s, i) {
        if (i === idx)     return { ...s, status: 'done' };
        if (i === idx + 1) return { ...s, status: 'active' };
        return s;
      });
    });
  };

  function buildApiStatuses(apis, stageMap) {
    return apis.map(function(a, i) {
      return {
        name: a.name,
        displayName: a.displayName || a.name,
        status: i === 0 ? 'active' : 'pending',
        stages: stageMap.map(function(n, j) {
          return { name: n, status: i === 0 && j === 0 ? 'active' : 'pending' };
        })
      };
    });
  }

  function advanceActiveApiStage(msg) {
    var t = (msg || '').toLowerCase();
    setDeployApiStatuses(function(prev) {
      var activeIdx = deployCurrentApiRef.current;
      if (activeIdx >= prev.length) return prev;
      var api = prev[activeIdx];
      var stages = api.stages;
      if (!stages) return prev;
      var idx = stages.findIndex(function(s) { return s.status === 'active'; });
      if (idx < 0 || idx >= stages.length - 1) return prev;
      var next = stages[idx + 1];
      var nextName = next.name.toLowerCase();
      var advance = false;
      // Mule
      if (nextName === 'github' && (t.includes('github') || t.includes('git push') || t.includes('pushing to') || t.includes('push code'))) advance = true;
      if ((nextName.includes('azure') || nextName.includes('pipeline')) && (t.includes('azure') || t.includes('pipeline') || t.includes('trigger') || t.includes('devops'))) advance = true;
      // Spring Boot / .NET
      if (nextName.includes('build') && (t.includes('build') || t.includes('compil') || t.includes('maven') || t.includes('mvn'))) advance = true;
      if (nextName.includes('docker') && (t.includes('docker') || t.includes('image') || t.includes('container'))) advance = true;
      if ((nextName.includes('deploy') && !nextName.includes('azure') && !nextName.includes('pipeline')) && (t.includes('deploy') || t.includes('cloudhub') || t.includes('trigger'))) advance = true;
      if (!advance) return prev;
      var newStages = stages.map(function(s, i) {
        if (i === idx)     return { ...s, status: 'done' };
        if (i === idx + 1) return { ...s, status: 'active' };
        return s;
      });
      return prev.map(function(a, i) {
        return i === activeIdx ? { ...a, stages: newStages } : a;
      });
    });
  }

  // Return from the Enhance Existing compliance flow back to New Integrations to deploy
  // the now-fixed project. Greenfield state (generatedApis/projectPath/plantumlCode) is
  // untouched by the tab switch, so deployment ships the corrected on-disk files.
  const handleReturnToDeployment = () => {
    setActiveTab("greenfield");
    setMessages(prev => [...prev, {
      role: "bot", time: now(),
      content: "✅ Compliance fixes applied. Ready to deploy the updated project.",
      actions: [{ label: "🚀 Proceed to Deployment", action: "proceed_to_deployment" }],
    }]);
  };

  // ── Action button handler ─────────────────────────────────────
  const handleMsgAction = (action, msgIndex) => {
    // edit_diagram: open inline input without clearing buttons
    if (action === "edit_diagram") {
      setDiagramEditMsgIdx(msgIndex);
      setDiagramEditText("");
      return;
    }
    // edit_integration: open inline input without clearing buttons
    if (action === "edit_integration") {
      setIntegrationEditMsgIdx(msgIndex);
      setIntegrationEditText("");
      return;
    }
    // save_design: open name input without clearing buttons (so cancel restores them)
    if (action === "save_design") {
      setDesignNameMsgIdx(msgIndex);
      setDesignNameText("");
      return;
    }
    // session_expired_logout: token expired — send user to login but preserve conversation state.
    // State is keyed by userId so it restores on next login for the same account.
    if (action === "session_expired_logout") {
      localStorage.removeItem('langraph_session_id');
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/';
      return;
    }
    // open_connected_apps: navigate away without clearing buttons so Refresh remains on return
    if (action === "open_connected_apps") { window.location.href = "/settings"; return; }
    // push_confluence: keep buttons so user can reopen modal after navigating away and back
    if (action === "push_confluence") { setShowConfluenceModal(true); return; }
    // save_repository: push the generated project to a new GitHub/Azure repo (no deploy).
    if (action === "save_repository") { setShowSaveRepoModal(true); return; }
    // compliance_check: register the generated project folder and hand off to the
    // Enhance Existing (brownfield) compliance scan — no re-upload. Keeps buttons.
    if (action === "compliance_check") {
      // Prefer the top-level Mule root; else derive it from the first API's on-disk path (its parent).
      const firstApi = ((generatedApis[0]?.projectPath) || "").replace(/\\/g, "/");
      const derivedRoot = firstApi.includes("/") ? firstApi.slice(0, firstApi.lastIndexOf("/")) : firstApi;
      const folder = (projectPath || derivedRoot || "").trim();
      if (!folder) {
        setMessages(prev => [...prev, { role: "bot", time: now(), content: "⚠️ No generated project folder found to scan — please generate an integration first." }]);
        return;
      }
      addLog(setLogs, "info", "🛡 Registering generated project for compliance scan…");
      console.log('%c[Compliance handoff] click → folder=', 'color:#a78bfa;font-weight:700', folder, '| sessionId=', sessionId);
      // Clear any prior brownfield result IMMEDIATELY on click (entry-agnostic: works even
      // when a saved project was loaded from Project Centre and its persisted scan is showing).
      resetBrownfieldSession();
      fetch(`${backendUrl}/api/v1/brownfield/register-project`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "x-access-token": localStorage.getItem("token") },
        body: JSON.stringify({ sessionId, projectPath: folder }),
      })
        .then(r => r.json())
        .then(res => {
          console.log('%c[Compliance handoff] register-project ←', 'color:#a78bfa;font-weight:700', res);
          if (!res?.success) throw new Error(res?.error || "register-project failed");
          // Re-assert fresh (in case a late socket/poll touched state between click and now),
          // then hand off to the auto-drive compliance scan on the generated folder.
          resetBrownfieldSession();
          setPendingBfJob({ mode: "compliance", projectPath: folder });
          setActiveTab("brownfield");
        })
        .catch(err => {
          console.warn("[SmartHub] register-project failed:", err.message);
          setMessages(prev => [...prev, { role: "bot", time: now(), content: `⚠️ Couldn't start the compliance check: ${err.message}` }]);
        });
      return;
    }
    setMessages(prev => prev.map((m, i) => {
      if (i !== msgIndex) return m;
      const selectedLabel = m.actions?.find(a => a.action === action)?.label || null;
      // After proceeding to integration, keep the "Save Design" button available if the
      // design hasn't been saved yet — so the user can still save it from here.
      const keepSave = action === "proceed_to_integration" && !designSaved
        && m.actions?.some(a => a.action === "save_design");
      if (keepSave) {
        return { ...m, actions: m.actions.filter(a => a.action === "save_design"), selectedLabel };
      }
      return { ...m, actions: null, selectedLabel };
    }));
    // Route deployment actions to dedicated handler
    if (action === "proceed_to_deployment") {
      // Ask for review confirmation before starting deployment
      setMessages(prev => [...prev, {
        role: "bot", time: now(),
        content: "🔍 **Review complete?**\n\nPlease confirm you have reviewed the generated files before proceeding to deployment.",
        actions: [
          { label: "✅ Yes, proceed to deployment", action: "review_complete_yes" },
          { label: "✕ Not yet",                     action: "dismiss"             },
        ],
      }]);
      return;
    }
    if (action === "review_complete_yes") {
      handleDeployAction("proceed_to_deployment");
      return;
    }
    if (
      action.startsWith("deploy_") ||
      action.startsWith("edit_deploy_") ||
      action.startsWith("refresh_and_retry_cloud__") ||
      action === "back_to_cloud_provider" ||
      action === "confirm_deploy_mule" ||
      action === "confirm_deploy_spring" ||
      action === "confirm_deploy_dotnet" ||
      action === "refresh_accounts" ||
      action === "refresh_repo" ||
      action === "refresh_sonar"
    ) {
      handleDeployAction(action);
      return;
    }
    if (action === "dismiss") return;
    if (action.startsWith("inline_choice__")) {
      const choice = action.slice("inline_choice__".length);
      setMessages(prev => [...prev, { role: "user", time: now(), content: choice }]);
      setLoading(true);
      addLog(setLogs, "info", `📤 Sending choice: ${choice}`);
      sendHubRequest({ prompt: choice, sessionId, projectName: projName, technology: selectedTech });
      return;
    }
    if (action === "new_session") { handleNewSession(); return; }
    if (action === "retry") {
      setMessages(prev => prev.slice(0, -1)); // remove the error bot message
      setLoading(true);

      // The actual re-run (only used if there's nothing recoverable on the server).
      const doRerun = () => {
        addLog(setLogs, "info", "🔄 Retrying last request…");
        // A post-design integration pre-flight answer (schema skip/submit, existing-api,
        // pipeline, api-selection) is a bare prompt with NO plantumlCode. Once its pending
        // flag is consumed on the backend, replaying it mis-routes to a design turn
        // ("couldn't generate design"). Detect that from LIVE state — we're in the
        // integration phase iff a technology was chosen AND a diagram exists — and re-enter
        // that tech's integration flow via the router's plantumlCode+technology gate.
        const lastBody = lastHubBodyRef.current;
        // Use the STATE (empty until a tech is chosen), NOT selectedTechRef (defaults to
        // "mule") — otherwise a design-phase skip could be misread as an integration turn.
        const tech = selectedTech;
        const puml = plantumlCodeRef.current;
        const isPreflightAnswer = lastBody && typeof lastBody.prompt === 'string' &&
          !lastBody.plantumlCode &&
          /^(skip|done|approve|use my repo|generate)/i.test(lastBody.prompt.trim());
        if (isPreflightAnswer && tech && puml) {
          addLog(setLogs, "info", `↻ Resuming ${tech} integration (re-entering from the diagram)…`);
          sendHubRequest({
            prompt: 'generate', sessionId, projectName: projName,
            plantumlCode: puml, technology: tech, opType: 'integration',
          });
        } else if (lastBody) {
          // Re-send the EXACT last hub body — it carries plantumlCode, technology and
          // the correct opType, so a failed generation retries the SAME operation.
          sendHubRequest(lastBody);
        } else {
          const lastUserMsg = [...messages].reverse().find(m => m.role === "user");
          if (lastUserMsg && lastUserMsg.content) {
            sendHubRequest({ prompt: lastUserMsg.content, sessionId, projectName: projName, technology: selectedTech });
          } else {
            setLoading(false);
          }
        }
      };

      // Belt-and-suspenders: before spending tokens on a re-run, check whether the
      // server ALREADY has a finished result for this session (e.g. a long generation
      // that completed after recovery gave up). If so, retrieve it instead of
      // regenerating — no double token spend, no buffer block.
      const sid = sessionIdRef.current || localStorage.getItem("langraph_session_id");
      if (sid) {
        addLog(setLogs, "info", "Checking for an already-finished result before re-running…");
        fetch(`${backendUrl}/api/v1/hub/result?sessionId=${encodeURIComponent(sid)}&clientTurnId=${encodeURIComponent(lastHubBodyRef.current?.clientTurnId || "")}`, {
          cache: "no-store",
          headers: { "x-access-token": localStorage.getItem("token") },
        })
          .then(r => (r.ok ? r.json() : null))
          .then(data => {
            if (retryRecoveryAction(data) === "render") {
              addLog(setLogs, "success", "✅ Recovered the completed result — no re-run needed.");
              handleApiResponse(data.result || {});
            } else if (retryRecoveryAction(data) === "rerun") {
              doRerun();
            } else {
              // Running or temporarily unavailable: keep recovering the same
              // turn instead of replaying a POST and hitting duplicate detection.
              recoverGeneration();
            }
          })
          .catch(() => recoverGeneration());   // an outage must not replay the POST
      } else {
        doRerun();
      }
      return;
    }
    if (action === "view_logs_error") { setLogsModalFilter("error"); setShowLogsModal(true); return; }
    if (action === "proceed_to_integration") {
      setActiveAgent("integration");
      setMessages(prev => [...prev, {
        role: "bot", time: now(),
        content: "Great! Which technology would you like to generate the integration for?",
        actions: [
          ...(allowedTechs.includes('mule')       ? [{ label: "⚡ MuleSoft",    action: "tech_mule"   }] : []),
          ...(allowedTechs.includes('springboot') ? [{ label: "🍃 Spring Boot", action: "tech_spring" }] : []),
          ...(allowedTechs.includes('dotnet')     ? [{ label: "🔷 .NET",        action: "tech_dotnet" }] : []),
        ],
      }]);
    } else if (action === "tech_mule") {
      setSelectedTech("mule");
      setMessages(prev => [...prev, {
        role: "bot", time: now(),
        content: "MuleSoft selected. Click below to generate the integration from your design, or describe any customisations first.",
        actions: [
          { label: "⚡ Generate Mule Integration", action: "generate_mule" },
          { label: "← Change Technology", action: "change_tech" },
        ],
      }]);
    } else if (action === "change_tech") {
      setMessages(prev => [...prev, {
        role: "bot", time: now(),
        content: "Which technology would you like to generate the integration for?",
        actions: [
          ...(allowedTechs.includes('mule')       ? [{ label: "⚡ MuleSoft",    action: "tech_mule"   }] : []),
          ...(allowedTechs.includes('springboot') ? [{ label: "🍃 Spring Boot", action: "tech_spring" }] : []),
          ...(allowedTechs.includes('dotnet')     ? [{ label: "🔷 .NET",        action: "tech_dotnet" }] : []),
        ],
      }]);
    } else if (action === "generate_mule") {
      setLoading(true);
      addLog(setLogs, "info", "📤 Generating MuleSoft integration...");
      const muleBody = { sessionId, plantumlCode: plantumlCodeRef.current || plantumlCode, technology: "mule", opType: "integration" };
      console.log("[SmartHub] POST /api/v1/hub (Mule) →", muleBody);
      checkTokensBeforeGenerate(muleBody);

    } else if (action === "tech_spring") {
      setSelectedTech("springboot");
      setMessages(prev => [...prev, {
        role: "bot", time: now(),
        content: "Spring Boot selected. Click below to generate the integration from your design, or describe any customisations first.",
        actions: [
          { label: "🍃 Generate Spring Boot Integration", action: "generate_spring" },
          { label: "← Change Technology", action: "change_tech" },
        ],
      }]);
    } else if (action === "generate_spring") {
      setSelectedTech("springboot");
      setLoading(true);
      addLog(setLogs, "info", "📤 Generating Spring Boot integration...");
      const springBody = { sessionId, plantumlCode: plantumlCodeRef.current || plantumlCode, technology: "springboot", opType: "integration" };
      console.log("[SmartHub] POST /api/v1/hub (Spring Boot) →", springBody);
      checkTokensBeforeGenerate(springBody);

    } else if (action === "tech_dotnet") {
      setSelectedTech("dotnet");
      setMessages(prev => [...prev, {
        role: "bot", time: now(),
        content: ".NET selected. Click below to generate the integration from your design, or describe any customisations first.",
        actions: [
          { label: "🔷 Generate .NET Integration", action: "generate_dotnet" },
          { label: "← Change Technology", action: "change_tech" },
        ],
      }]);
    } else if (action === "generate_dotnet") {
      setLoading(true);
      addLog(setLogs, "info", "📤 Generating .NET integration...");
      const dotnetBody = { sessionId, plantumlCode: plantumlCodeRef.current || plantumlCode, technology: "dotnet", opType: "integration" };
      console.log("[SmartHub] POST /api/v1/hub (.NET) →", dotnetBody);
      checkTokensBeforeGenerate(dotnetBody);

    }
  };

  // Helper to show repo selection step
  const showDeployRepoStep = (botMsg, overrideRepo, overrideGitlab) => {
    const repos   = overrideRepo   ?? repoAccounts;
    const gitlabs = overrideGitlab ?? gitlabAccounts;
    const repoChips = repos.map(r => ({ label: `GitHub: ${r.gitUsername || r.name || r._id}`, action: `deploy_repo__github__${r._id}` }))
      .concat(gitlabs.map(r => ({ label: `GitLab: ${r.gitUsername || r.name || r._id}`, action: `deploy_repo__gitlab__${r._id}` })));
    if (!repoChips.length) {
      botMsg(
        "No repository account found. Add one in Connected Apps or skip.",
        [
          { label: "⚙️ Go to Connected Apps", action: "open_connected_apps" },
          { label: "🔄 Refresh", action: "refresh_repo" },
          { label: "⏭ Skip", action: "deploy_skip_repo" },
        ]
      );
    } else {
      botMsg(
        "Select repository account (or skip):",
        [...repoChips, { label: "⏭ Skip", action: "deploy_skip_repo" }]
      );
    }
  };

  // Helper to show SonarQube selection step
  const showDeploySonarStep = (botMsg, overrideSonar) => {
    const sonars = overrideSonar ?? sonarAccounts;
    const sonarChips = sonars.map((s, idx) => ({
      label: `🔍 ${s.name || s.sonarName || (sonars.length > 1 ? `SonarQube ${idx + 1}` : "SonarQube")}`,
      action: `deploy_sonar__${s._id}`,
    }));
    if (!sonarChips.length) {
      botMsg(
        "No SonarQube account found. Add one in Connected Apps or skip.",
        [
          { label: "⚙️ Go to Connected Apps", action: "open_connected_apps" },
          { label: "🔄 Refresh", action: "refresh_sonar" },
          { label: "⏭ Skip SonarQube", action: "deploy_skip_sonar" },
        ]
      );
    } else {
      botMsg(
        "Select SonarQube account for code quality (or skip):",
        [...sonarChips, { label: "⏭ Skip SonarQube", action: "deploy_skip_sonar" }]
      );
    }
  };

  // Helper to show Key Vault selection step
  const showDeployKeyVaultStep = (botMsg) => {
    botMsg(
      "Do you want to use a Key Vault to manage secrets?",
      [
        { label: "🔐 Yes, use Key Vault", action: "deploy_use_keyvault" },
        { label: "⏭ Skip",               action: "deploy_skip_keyvault" },
      ]
    );
  };

  // ── Deployment flow ───────────────────────────────────────────
  const handleDeployAction = (action) => {
    const botMsg = (content, actions) =>
      setMessages(prev => [...prev, { role: "bot", time: now(), content, actions }]);

    // ── Refresh connected accounts ────────────────────────────
    if (action === "refresh_accounts") {
      botMsg("Refreshing connected accounts...");
      reloadCloudResources().then(fresh => {
        if (selectedTech === "mule") {
          if (!fresh.muleAccounts.length) {
            botMsg("Still no Anypoint accounts found. Please add one in Connected Apps.", [
              { label: "⚙️ Go to Connected Apps", action: "open_connected_apps" },
              { label: "🔄 Refresh Again", action: "refresh_accounts" },
            ]);
          } else {
            botMsg(
              "Accounts refreshed! Select your Anypoint Platform account:",
              fresh.muleAccounts.map(a => ({ label: a.muleOrg || a.name || a._id, action: `deploy_mule_acc__${a._id}` }))
            );
          }
        }
      });
      return;
    }

    // ── Refresh repo accounts ─────────────────────────────────
    if (action === "refresh_repo") {
      botMsg("Refreshing repository accounts...");
      reloadCloudResources().then(fresh => {
        showDeployRepoStep(botMsg, fresh.repoAccounts, fresh.gitlabAccounts);
      });
      return;
    }

    // ── Refresh SonarQube accounts ────────────────────────────
    if (action === "refresh_sonar") {
      botMsg("Refreshing SonarQube accounts...");
      reloadCloudResources().then(fresh => {
        showDeploySonarStep(botMsg, fresh.sonarAccounts);
      });
      return;
    }

    // ── Deployment card: edit individual fields (popup) ──────────
    if (action === 'edit_deploy_acct') {
      deployEditModeRef.current = 'acct';
      setDeployEditModal({
        title: 'Select Anypoint Account',
        options: muleAccounts.map(function(a) { return { label: a.muleOrg || a.name || a._id, action: 'deploy_mule_acc__' + a._id }; }),
      });
      return;
    }
    if (action === 'edit_deploy_env') {
      if (!deployEnvs.length) { botMsg("No environments loaded. Please select the account again."); return; }
      deployEditModeRef.current = 'env';
      setDeployEditModal({
        title: 'Select Environment',
        options: deployEnvs.map(function(e) { return { label: e.name, action: 'deploy_env__' + e.id + '__' + encodeURIComponent(e.name) }; }),
      });
      return;
    }
    if (action === 'edit_deploy_repo') {
      deployEditModeRef.current = 'repo';
      const repoOpts = repoAccounts.map(function(r) { return { label: 'GitHub: ' + (r.gitUsername || r.name || r._id), action: 'deploy_repo__github__' + r._id }; })
        .concat(gitlabAccounts.map(function(r) { return { label: 'GitLab: ' + (r.gitUsername || r.name || r._id), action: 'deploy_repo__gitlab__' + r._id }; }));
      setDeployEditModal({
        title: 'Select Repository',
        options: repoOpts.length
          ? [...repoOpts, { label: '⏭ Skip Repository', action: 'deploy_skip_repo', isSkip: true }]
          : [{ label: '⏭ Skip Repository', action: 'deploy_skip_repo', isSkip: true }],
      });
      return;
    }
    if (action === 'edit_deploy_cicd') {
      deployEditModeRef.current = 'cicd';
      const cicdOpts = azureAccounts.filter(function(a) { return a.azDevopsPat || a.organization; }).map(function(a) { return { label: 'Azure DevOps: ' + (a.organizationName || a.organization || a._id), action: 'deploy_devops__azure__' + a._id }; });
      setDeployEditModal({
        title: 'Select CI/CD',
        options: [...cicdOpts, { label: '⏭ Skip CI/CD', action: 'deploy_skip_devops', isSkip: true }],
      });
      return;
    }
    if (action === 'edit_deploy_sonar') {
      deployEditModeRef.current = 'sonar';
      const sonarOpts = sonarAccounts.map(function(s, idx) { return { label: '🔍 ' + (s.name || s.sonarName || (sonarAccounts.length > 1 ? 'SonarQube ' + (idx + 1) : 'SonarQube')), action: 'deploy_sonar__' + s._id }; });
      setDeployEditModal({
        title: 'Select Code Analysis',
        options: [...sonarOpts, { label: '⏭ Skip SonarQube', action: 'deploy_skip_sonar', isSkip: true }],
      });
      return;
    }
    if (action === 'edit_deploy_kv') {
      deployEditModeRef.current = 'kv';
      const azureKvOpts = azureVaultAccounts.map(function(v) { return { label: '🔷 ' + (v.keyVaultName || v.subscriptionName || v._id), action: 'deploy_kv_select__' + v._id }; });
      const awsKvOpts   = awsAcctList.map(function(a) { return { label: '🟠 ' + (a.awsaccountid || a.awsaccesskeyid || a._id), action: 'deploy_kv_select__aws__' + a._id }; });
      setDeployEditModal({
        title: 'Select Key Vault',
        options: [...azureKvOpts, ...awsKvOpts, { label: '⏭ Skip Key Vault', action: 'deploy_skip_keyvault', isSkip: true }],
      });
      return;
    }

    // ── Step 1: Start deployment ──────────────────────────────
    if (action === "proceed_to_deployment") {
      setActiveAgent("deployment");
      // Add the persistent deployment review card first
      setMessages(prev => [...prev, {
        role: "bot", time: now(),
        deploymentCard: {
          tech: selectedTech,
          accountLabel: null, envName: null, repoLabel: null,
          devOpsLabel: null,
          sonarLabel: selectedTech === 'mule' ? null : 'N/A',
          kvLabel: null, deployAction: null,
        },
      }]);
      if (selectedTech === "mule") {
        // Mule: projects are already known — skip project name, go straight to account
        if (!muleAccounts.length) {
          botMsg("No Anypoint accounts found. Please add one in Connected Apps first.", [
            { label: "⚙️ Go to Connected Apps", action: "open_connected_apps" },
            { label: "🔄 Refresh", action: "refresh_accounts" },
          ]);
          return;
        }
        botMsg(
          "Select your Anypoint Platform account:",
          muleAccounts.map(a => ({ label: a.muleOrg || a.name || a._id, action: `deploy_mule_acc__${a._id}` }))
        );
      } else {
        // Spring Boot: go straight to cloud provider selection
        botMsg(
          "Select your cloud provider for deployment:",
          [
            { label: "☁️ AWS (EKS)",   action: "deploy_cloud__aws"   },
            { label: "🔵 Azure (AKS)", action: "deploy_cloud__azure" },
            { label: "🟢 GCP (GKE)",   action: "deploy_cloud__gcp"   },
          ]
        );
      }
      return;
    }

    // ── MuleSoft: account selected → fetch envs ───────────────
    if (action.startsWith("deploy_mule_acc__")) {
      const accId = action.replace("deploy_mule_acc__", "");
      // Use live state first; fall back to a fresh fetch in case state is stale after refresh
      const resolveAcc = () => {
        const fromState = muleAccounts.find(a => a._id === accId);
        if (fromState) return Promise.resolve(fromState);
        return loadResource("mule", setMuleAccounts, "mule")
          .then(fresh => fresh.find(a => a._id === accId));
      };
      resolveAcc().then(acc => {
        if (!acc) { botMsg("Could not find the selected account. Please refresh and try again."); return; }
        setDeployConfig(prev => ({ ...prev, muleAccountId: accId, muleAccount: acc }));
        updateDeployCard({ accountLabel: acc.muleOrg || acc.name || acc._id });
        const creds = {
          connAppClientId:     acc.connAppClientId || "",
          connAppClientSecret: acc.connAppClientSecret || "",
          muleClientId:        acc.muleClientId || "",
          muleClientSecret:    acc.muleClientSecret || "",
          muleOrgId:           acc.muleOrgId || "",
        };
        console.log("[SmartHub] POST /api/v1/mule/org/environments → request:", creds);
        setMessages(prev => [...prev, { role: "bot", time: now(), content: "⏳ Loading environments…", envLoading: true }]);
        fetch(`${domain}/api/v1/mule/org/environments`, {
          method: "POST",
          headers: { "Content-Type": "application/json", "x-access-token": localStorage.getItem("token") },
          body: JSON.stringify(creds),
        })
          .then(r => r.json())
          .then(res => {
            console.log("[SmartHub] Environments response:", res);
            setMessages(prev => prev.filter(m => !m.envLoading));
            const envs = res.data || [];
            setDeployEnvs(envs);
            if (!envs.length) {
              deployEditModeRef.current = null;
              botMsg(res.error || res.message || "No environments found for this account.");
              return;
            }
            if (deployEditModeRef.current === 'acct') {
              deployEditModeRef.current = 'env'; // env step will stop after selection
              setDeployEditModal({
                title: 'Select Environment',
                options: envs.map(function(e) { return { label: e.name, action: 'deploy_env__' + e.id + '__' + encodeURIComponent(e.name) }; }),
              });
              return;
            }
            botMsg(
              "Select the deployment environment:",
              envs.map(e => ({ label: e.name, action: `deploy_env__${e.id}__${encodeURIComponent(e.name)}` }))
            );
          })
          .catch(err => {
            setMessages(prev => prev.filter(m => !m.envLoading));
            botMsg(`Failed to fetch environments: ${err.message}`);
          });
      });
      return;
    }

    // ── MuleSoft: env selected → ask repo ─────────────────────
    if (action.startsWith("deploy_env__")) {
      const [, envId, envNameEnc] = action.split("__");
      const envName = decodeURIComponent(envNameEnc);
      setDeployConfig(prev => ({ ...prev, envId, envName }));
      updateDeployCard({ envName });
      if (deployEditModeRef.current === 'env' || deployEditModeRef.current === 'acct') {
        deployEditModeRef.current = null;
        return;
      }
      showDeployRepoStep(botMsg);
      return;
    }

    // ── Back to cloud provider selection ──────────────────────
    if (action === "back_to_cloud_provider") {
      botMsg("Select your cloud provider for deployment:", [
        { label: "☁️ AWS (EKS)",   action: "deploy_cloud__aws"   },
        { label: "🔵 Azure (AKS)", action: "deploy_cloud__azure" },
        { label: "🟢 GCP (GKE)",   action: "deploy_cloud__gcp"   },
      ]);
      return;
    }

    // ── Refresh cloud resources and retry provider ─────────────
    if (action.startsWith("refresh_and_retry_cloud__")) {
      const provider = action.replace("refresh_and_retry_cloud__", "");
      botMsg("🔄 Refreshing resources...");
      reloadCloudResources().then((fresh) => {
        let accounts = [];
        if (provider === "aws")   accounts = fresh.awsClusters.map(a => ({ label: `${a.awscluster || a.name || a._id} (${a.location || ""})`, action: `deploy_cloud_acc__${a._id}` }));
        if (provider === "azure") accounts = fresh.azureAccounts.map(a => ({ label: a.name || a._id, action: `deploy_cloud_acc__${a._id}` }));
        if (provider === "gcp")   accounts = fresh.gkeAccounts.map(a => ({ label: a.name || a._id, action: `deploy_cloud_acc__${a._id}` }));
        if (!accounts.length) {
          botMsg(
            `No ${provider.toUpperCase()} resources found. Please add one in Connected Apps and try again.`,
            [
              { label: "⚙️ Go to Connected Apps", action: "open_connected_apps" },
              { label: "🔄 Refresh Again",         action: `refresh_and_retry_cloud__${provider}` },
              { label: "← Change Provider",        action: "back_to_cloud_provider" },
            ]
          );
          return;
        }
        const label = provider === "aws" ? "Select your EKS cluster:" : `Select your ${provider.toUpperCase()} account:`;
        botMsg(label, accounts);
      });
      return;
    }

    // ── Spring Boot: cloud provider selected ──────────────────
    if (action.startsWith("deploy_cloud__")) {
      const provider = action.replace("deploy_cloud__", "");
      setDeployConfig(prev => ({ ...prev, cloudProvider: provider }));
      let accounts = [];
      if (provider === "aws")   accounts = awsClusters.map(a => ({ label: `${a.awscluster || a.name || a._id} (${a.location || ""})`, action: `deploy_cloud_acc__${a._id}` }));
      if (provider === "azure") accounts = azureAccounts.map(a => ({ label: a.name || a._id, action: `deploy_cloud_acc__${a._id}` }));
      if (provider === "gcp")   accounts = gkeAccounts.map(a => ({ label: a.name || a._id, action: `deploy_cloud_acc__${a._id}` }));
      if (!accounts.length) {
        botMsg("🔄 Loading resources...");
        reloadCloudResources().then(function(fresh) {
          var freshAccounts = [];
          if (provider === "aws")   freshAccounts = fresh.awsClusters.map(function(a) { return { label: (a.awscluster || a.name || a._id) + ' (' + (a.location || '') + ')', action: 'deploy_cloud_acc__' + a._id }; });
          if (provider === "azure") freshAccounts = fresh.azureAccounts.map(function(a) { return { label: a.name || a._id, action: 'deploy_cloud_acc__' + a._id }; });
          if (provider === "gcp")   freshAccounts = fresh.gkeAccounts.map(function(a)   { return { label: a.name || a._id, action: 'deploy_cloud_acc__' + a._id }; });
          if (!freshAccounts.length) {
            botMsg(
              'No ' + provider.toUpperCase() + ' resources found. Add one in Connected Apps, then try again.',
              [
                { label: "⚙️ Go to Connected Apps", action: "open_connected_apps" },
                { label: "🔄 Refresh Again",         action: 'refresh_and_retry_cloud__' + provider },
                { label: "← Change Provider",        action: "back_to_cloud_provider" },
              ]
            );
            return;
          }
          var freshLabel = provider === "aws" ? "Select your EKS cluster:" : ("Select your " + provider.toUpperCase() + " account:");
          botMsg(freshLabel, freshAccounts);
        });
        return;
      }
      const label = provider === "aws" ? "Select your EKS cluster:" : `Select your ${provider.toUpperCase()} account:`;
      botMsg(label, accounts);
      return;
    }

    // ── Spring Boot: cloud account / cluster selected ──────────
    if (action.startsWith("deploy_cloud_acc__")) {
      const accId = action.replace("deploy_cloud_acc__", "");
      const { cloudProvider } = deployConfig;
      let acc = null;
      // AWS: awsClusters item has both credentials (accessKeyId, secretAccessKey) and cluster info
      if (cloudProvider === "aws")   acc = awsClusters.find(a => a._id === accId);
      if (cloudProvider === "azure") acc = azureAccounts.find(a => a._id === accId);
      if (cloudProvider === "gcp")   acc = gkeAccounts.find(a => a._id === accId);
      // For AWS set both cloudAccount and cluster to same item (it has all we need)
      setDeployConfig(prev => ({
        ...prev,
        cloudAccountId: accId,
        cloudAccount: acc,
        cluster: cloudProvider === "aws" ? acc : prev.cluster,
      }));
      // AWS: no separate cluster step needed; Azure/GCP: go to repo
      showDeployRepoStep(botMsg);
      return;
    }

    // ── Spring Boot: cluster selected → ask repo ─────────────
    if (action.startsWith("deploy_cluster__")) {
      const clusterId = action.replace("deploy_cluster__", "");
      const cluster = awsClusters.find(c => c._id === clusterId);
      setDeployConfig(prev => ({ ...prev, clusterId, cluster }));
      showDeployRepoStep(botMsg);
      return;
    }

    // ── Repo selected (both flows) → ask DevOps ───────────────
    if (action.startsWith("deploy_repo__") || action === "deploy_skip_repo") {
      let repoLabel = 'None';
      if (action !== "deploy_skip_repo") {
        const parts = action.replace("deploy_repo__", "").split("__");
        const repoType = parts[0];
        const repoId   = parts[1];
        const list = repoType === "github" ? repoAccounts : gitlabAccounts;
        const repo = list.find(r => r._id === repoId);
        setDeployConfig(prev => ({ ...prev, repoType, repoId, repo }));
        const provider = repoType === 'github' ? 'GitHub' : 'GitLab';
        const owner = repo ? (repo.gitUsername || repo.username || '') : '';
        repoLabel = `${provider}${owner ? ' (' + owner + ')' : ''}`;
      }
      updateDeployCard({ repoLabel });
      if (deployEditModeRef.current === 'repo') {
        deployEditModeRef.current = null;
        return;
      }
      const devOpsChips = azureAccounts
        .filter(a => a.azDevopsPat || a.organization)
        .map(a => ({ label: `Azure DevOps: ${a.organizationName || a.organization || a._id}`, action: `deploy_devops__azure__${a._id}` }));
      botMsg(
        "Select Azure DevOps account for CI/CD pipeline (or skip):",
        [...devOpsChips, { label: "⏭ Skip CI/CD", action: "deploy_skip_devops" }]
      );
      return;
    }

    // ── DevOps selected (both flows) → sonarqube (mule) or confirm (spring) ──
    if (action.startsWith("deploy_devops__") || action === "deploy_skip_devops") {
      let devOpsLabel = 'None';
      if (action !== "deploy_skip_devops") {
        const parts = action.replace("deploy_devops__", "").split("__");
        const devOpsType = parts[0];   // "azure"
        const devOpsId   = parts[1];
        const devOpsAcc  = azureAccounts.find(a => a._id === devOpsId);
        setDeployConfig(prev => ({ ...prev, devOpsType, devOpsAcc }));
        devOpsLabel = `Azure DevOps${devOpsAcc ? ' (' + (devOpsAcc.organization || devOpsAcc.name || '') + ')' : ''}`;
      }
      updateDeployCard({ devOpsLabel });
      if (deployEditModeRef.current === 'cicd') {
        deployEditModeRef.current = null;
        return;
      }
      if (selectedTech === "mule") {
        showDeploySonarStep(botMsg);
      } else {
        showDeployKeyVaultStep(botMsg);
      }
      return;
    }

    // ── Mule: sonarqube selected → key vault ───────────────────
    if (action.startsWith("deploy_sonar__") || action === "deploy_skip_sonar") {
      let sonarLabel = 'None';
      if (action !== "deploy_skip_sonar") {
        const sonarId  = action.replace("deploy_sonar__", "");
        const sonarAcc = sonarAccounts.find(s => s._id === sonarId);
        setDeployConfig(prev => ({ ...prev, sonarAcc }));
        sonarLabel = sonarAcc ? (sonarAcc.sonarServer || 'SonarQube') : 'None';
      }
      updateDeployCard({ sonarLabel });
      if (deployEditModeRef.current === 'sonar') {
        deployEditModeRef.current = null;
        return;
      }
      showDeployKeyVaultStep(botMsg);
      return;
    }

    // ── Key Vault: skip → confirm ──────────────────────────────
    if (action === "deploy_skip_keyvault") {
      const confirmAction = selectedTech === "mule" ? "confirm_deploy_mule" : selectedTech === "dotnet" ? "confirm_deploy_dotnet" : "confirm_deploy_spring";
      updateDeployCard({ kvLabel: 'None', deployAction: confirmAction });
      return;
    }

    // ── Key Vault: use → ask provider ──────────────────────────
    if (action === "deploy_use_keyvault") {
      botMsg(
        "Select Key Vault provider:",
        [
          { label: "🔷 Azure Key Vault", action: "deploy_kv_provider__azure" },
          { label: "🟠 AWS Secrets Manager", action: "deploy_kv_provider__aws" },
        ]
      );
      return;
    }

    // ── Key Vault: provider selected → show vaults ─────────────
    if (action.startsWith("deploy_kv_provider__")) {
      const provider = action.replace("deploy_kv_provider__", "");
      setDeployConfig(prev => ({ ...prev, kvProvider: provider }));

      if (provider === "azure") {
        if (!azureVaultAccounts.length) {
          botMsg(
            "No Azure Key Vaults found. Add one in Connected Apps → Azure Settings.",
            [
              { label: "⚙️ Go to Connected Apps", action: "open_connected_apps" },
              { label: "⏭ Skip Key Vault",        action: "deploy_skip_keyvault" },
            ]
          );
        } else {
          const chips = azureVaultAccounts.map(v => ({
            label: `🔐 ${v.keyVaultName || v.subscriptionName}`,
            action: `deploy_kv_select__${v._id}`,
          }));
          botMsg("Select Azure Key Vault:", [...chips, { label: "⏭ Skip", action: "deploy_skip_keyvault" }]);
        }
      } else if (provider === "aws") {
        if (!awsAcctList.length) {
          botMsg(
            "No AWS accounts found. Add one in Connected Apps.",
            [
              { label: "⚙️ Go to Connected Apps", action: "open_connected_apps" },
              { label: "⏭ Skip Key Vault",        action: "deploy_skip_keyvault" },
            ]
          );
        } else {
          const chips = awsAcctList.map(a => ({
            label: `🟠 ${a.awsaccountid || a.awsaccesskeyid}`,
            action: `deploy_kv_select__aws__${a._id}`,
          }));
          botMsg("Select AWS account for Secrets Manager:", [...chips, { label: "⏭ Skip", action: "deploy_skip_keyvault" }]);
        }
      }
      return;
    }

    // ── Key Vault: vault selected → confirm ────────────────────
    if (action.startsWith("deploy_kv_select__")) {
      const id = action.replace("deploy_kv_select__", "").replace(/^aws__/, "");
      const isAws = action.includes("__aws__");
      let kvAccount = null;
      if (isAws) {
        kvAccount = awsAcctList.find(a => a._id === id);
      } else {
        kvAccount = azureVaultAccounts.find(v => v._id === id);
      }
      setDeployConfig(prev => ({ ...prev, kvAccount }));
      const confirmAction = selectedTech === "mule" ? "confirm_deploy_mule" : selectedTech === "dotnet" ? "confirm_deploy_dotnet" : "confirm_deploy_spring";
      const kvLabel = kvAccount ? (kvAccount.keyVaultName || kvAccount.awsaccountid || '') : '';
      updateDeployCard({ kvLabel, deployAction: confirmAction });
      return;
    }

    // ── Confirm MuleSoft deploy ────────────────────────────────
    if (action === "confirm_deploy_mule") {
      const cfg = deployConfig;
      const acc = cfg.muleAccount;

      const deploymentConfig = {
        anypoint: {
          enabled: true,
          version: "1.0.0-SNAPSHOT",
          orgName: acc.muleOrg || acc.name || "",
          credentials: {
            clientId:             acc.connAppClientId  || "",
            clientSecret:         acc.connAppClientSecret || "",
            platformClientId:     acc.muleClientId     || "",
            platformClientSecret: acc.muleClientSecret || "",
          },
          envName: cfg.envName || "",
        },
        governance: {},
      };

      if (cfg.repoType === "github" && cfg.repo) {
        deploymentConfig.github = {
          enabled: true,
          token: cfg.repo.gitPat || cfg.repo.token || "",
          owner: cfg.repo.gitUsername || cfg.repo.username || "",
        };
      } else if (cfg.repoType === "gitlab" && cfg.repo) {
        deploymentConfig.gitlab = {
          enabled: true,
          token: cfg.repo.gitlabPat || cfg.repo.gitPat || cfg.repo.token || "",
          owner: cfg.repo.gitUsername || cfg.repo.username || "",
        };
      }

      if (cfg.devOpsType === "azure" && cfg.devOpsAcc) {
        deploymentConfig.azure = {
          enabled: true,
          credentials: {
            pat:    cfg.devOpsAcc.azDevopsPat  || "",
            orgUrl: cfg.devOpsAcc.organization || "",
          },
          triggerBuild: true,
        };
      }

      if (cfg.sonarAcc) {
        deploymentConfig.sonarqube = {
          enabled: true,
          serverUrl: cfg.sonarAcc.sonarServer   || "",
          token:     cfg.sonarAcc.sonarPassword || "",
        };
      }

      if (cfg.kvAccount && cfg.kvProvider) {
        if (cfg.kvProvider === "azure") {
          deploymentConfig.azure = {
            ...deploymentConfig.azure,
            keyVault: {
              enabled:          true,
              subscriptionName: cfg.kvAccount.subscriptionName || "",
              servicePrincipal: {
                clientId:       cfg.kvAccount.serviceprincipalid  || "",
                clientSecret:   cfg.kvAccount.serviceprincipalkey || "",
                tenantId:       cfg.kvAccount.tenantId            || "",
                subscriptionId: cfg.kvAccount.subscriptionId      || "",
              },
            },
          };
        } else {
          deploymentConfig.keyVault = {
            enabled:     true,
            provider:    "aws",
            awsAccountId: cfg.kvAccount.awsaccountid   || "",
            region:       cfg.kvAccount.awsregion      || "",
            accessKeyId:  cfg.kvAccount.awsaccesskeyid || "",
          };
        }
      }

      const body = {
        projects:      generatedApis,
        projectConfig: { version: "1.0.0" },
        deploymentConfig,
        sessionId,
      };

      lastDeployBodyRef.current = { type: 'mule', body };
      lastActionRef.current = 'mule_deploy';
      deploymentErrorShown.current = false;
      setDeploymentConfirmation(null);
      deploymentPausedRef.current = false;
      deploymentErrorLogs.current = [];
      lastDeployErrMsg.current = "";
      deployCurrentApiRef.current = 0;
      deployingSeenCountRef.current = 0;
      validatingCountRef.current = 0;
      setDeployLogs([]);
      setPipelineStages([]);
      var muleApiStatuses = buildApiStatuses(generatedApis, DEPLOY_STAGE_MAP.mule);
      setDeployApiStatuses(muleApiStatuses);
      deployApiStatusesRef.current = muleApiStatuses;
      deployApiCountRef.current = muleApiStatuses.length;
      setDeployStages(muleApiStatuses.length === 0 ? DEPLOY_STAGE_MAP.mule.map(function(n, i) { return { name: n, status: i === 0 ? 'active' : 'pending' }; }) : []);
      setShowDeployWindow(true);
      setDeployWindowMin(false);
      setLoading(true);
      addLog(setLogs, "info", "📤 Starting MuleSoft deployment...");
      console.log("[SmartHub] POST /api/v1/plantuml-deploy →", body);
      fetch(`${backendUrl}/api/v1/plantuml-deploy`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "x-access-token": localStorage.getItem("token") },
        body: JSON.stringify(body),
      })
        .then(r => { const ok = r.ok; return r.json().then(d => ({ ok, d })); })
        .then(({ ok, d }) => {
          console.log("[SmartHub] Deployment response:", d);
          if (ok || d.success) {
            deploymentErrorShown.current = false;
            addLog(setLogs, "info", "🚀 Deployment running — streaming live progress...");
          } else if (!deploymentPausedRef.current && !deploymentErrorShown.current) {
            deploymentErrorShown.current = true;
            setTimeout(() => {
              setLoading(false);
              const rawErrMsg = d.message || d.error || "";
              const summary = deploymentErrorLogs.current[0] || (rawErrMsg !== "Unknown error" ? rawErrMsg : "") || "Deployment failed.";
              const isK8sVersion = /kubernetes version|no longer supported|upgrade your kubernetes|supported version/i.test(summary);
              const hint = getDeployErrorHint(summary, "mule");
              if (isK8sVersion) {
                setMessages(prev => [...prev, {
                  role: "bot", time: now(),
                  content: `❌ Deployment failed: Your EKS cluster is running an unsupported Kubernetes version.\n\nPlease upgrade your cluster in the AWS console, then retry the deployment.`,
                  actions: [{ label: "🔄 Retry Deployment", action: "confirm_deploy_mule" }],
                }]);
              } else {
                const content = hint
                  ? `❌ Deployment failed: ${summary}\n\n${hint}`
                  : `❌ Deployment failed: ${summary}`;
                setMessages(prev => [...prev, { role: "bot", time: now(), content, retryable: true }]);
              }
            }, 800);
          }
        })
        .catch(err => {
          if (!deploymentPausedRef.current) {
            setLoading(false);
            addDeployLog('❌ Could not reach backend: ' + err.message + '. Restart the backend and click Retry.', 'error');
            setDeployStages(function(prev) { return prev.map(function(s) { return s.status === 'active' ? { ...s, status: 'error' } : s; }); });
            setDeployApiStatuses(function(prev) {
              var activeIdx = deployCurrentApiRef.current;
              return prev.map(function(a, i) {
                if (i !== activeIdx) return a;
                return { ...a, status: 'error', stages: (a.stages || []).map(function(s) { return s.status === 'active' ? { ...s, status: 'error' } : s; }) };
              });
            });
            setMessages(prev => [...prev, {
              role: "bot", time: now(),
              content: `❌ Deployment failed: Could not reach the backend server. Please ensure the backend is running, then retry.`,
              actions: [{ label: "🔄 Retry Deployment", action: "confirm_deploy_mule" }],
            }]);
          }
        });
      return;
    }

    // ── Confirm .NET deploy ────────────────────────────────────
    if (action === "confirm_deploy_dotnet") {
      const cfg     = deployConfig;
      const cluster = cfg.cluster;
      const cloud   = cfg.cloudAccount;

      const awsAcct = awsAcctList.find(a => a.awsaccesskeyid === cloud?.awsaccesskeyid);
      const awsAccountId = awsAcct?.awsaccountid || "";

      const payload = {
        projectPath,
        projectName: deployProjName || projName,
        applicationType: "dotnet",
        dotnet: { projectName: deployProjName || projName },
      };

      // ── Repository ──────────────────────────────────────────
      if (cfg.repoType === "github" && cfg.repo) {
        payload.github = {
          enabled: true,
          token: cfg.repo.gitPat || cfg.repo.token || "",
          owner: cfg.repo.gitUsername || cfg.repo.username || "",
        };
      } else if (cfg.repoType === "gitlab" && cfg.repo) {
        payload.gitlab = {
          enabled: true,
          token: cfg.repo.gitlabPat || cfg.repo.gitPat || cfg.repo.token || "",
          owner: cfg.repo.gitUsername || cfg.repo.username || "",
        };
      }

      // ── DevOps ───────────────────────────────────────────────
      if (cfg.devOpsType === "azure" && cfg.devOpsAcc) {
        payload.azure = {
          enabled: true,
          credentials: {
            pat:    cfg.devOpsAcc.azDevopsPat  || "",
            orgUrl: cfg.devOpsAcc.organization || "",
          },
          variableGroupName: "build.variables",
          variables: {
            awsRegion:      cluster?.location   || "",
            awsAccountId:   awsAccountId,
            eksClusterName: cluster?.awscluster || "",
            ARTIFACTS_FEED_NAME: (cfg.devOpsAcc.organization || "").replace(/\/+$/, "").split("/").pop() || "",
          },
          secretVariables: {
            awsSecretAccessKey: cloud?.awsaccesskey   || "",
            awsAccessKeyId:     cloud?.awsaccesskeyid || "",
            awsClusterName:     cluster?.awscluster   || "",
            awsRegion:          cluster?.location     || "",
          },
          triggerBuild: true,
        };
      }

      // ── Cloud platform ───────────────────────────────────────
      if (cfg.cloudProvider === "aws" && cloud) {
        payload.aws = {
          enabled: true,
          credentials: {
            accessKeyId:     cloud.awsaccesskeyid || "",
            secretAccessKey: cloud.awsaccesskey   || "",
            region:          cluster?.location || cloud.location || "us-east-1",
          },
          deploymentTarget: "eks",
          clusterName: cluster?.awscluster || "",
          environment: "development",
        };
      } else if (cfg.cloudProvider === "azure" && cloud) {
        payload.azure = {
          ...payload.azure,
          enabled: true,
          credentials: {
            subscriptionId: cloud.subscriptionId || "",
            tenantId:       cloud.tenantId       || "",
            clientId:       cloud.clientId       || "",
            clientSecret:   cloud.clientSecret   || "",
          },
          variableGroupName: "build.variables",
          variables: {
            awsRegion:      cluster?.location || "",
            awsAccountId:   "",
            eksClusterName: cluster?.awscluster || "",
          },
          secretVariables: {
            awsAccessKeyId:     cloud.awsaccesskeyid || "",
            awsSecretAccessKey: cloud.awsaccesskey   || "",
          },
          triggerBuild: true,
          deploymentTarget: "aks",
          resourceGroup: "rg-dotnet-dev",
        };
      } else if (cfg.cloudProvider === "gcp" && cloud) {
        payload.gcp = {
          enabled: true,
          credentials: {
            projectId:         cloud.projectId         || "",
            serviceAccountKey: cloud.serviceAccountKey || "",
          },
          deploymentTarget: "gke",
          clusterName: cloud.clusterName || "",
        };
      }

      if (cfg.kvAccount && cfg.kvProvider) {
        if (cfg.kvProvider === "azure") {
          payload.azure = {
            ...payload.azure,
            keyVault: {
              enabled:          true,
              subscriptionName: cfg.kvAccount.subscriptionName || "",
              servicePrincipal: {
                clientId:       cfg.kvAccount.serviceprincipalid  || "",
                clientSecret:   cfg.kvAccount.serviceprincipalkey || "",
                tenantId:       cfg.kvAccount.tenantId            || "",
                subscriptionId: cfg.kvAccount.subscriptionId      || "",
              },
            },
          };
        } else {
          payload.keyVault = {
            enabled:      true,
            provider:     "aws",
            awsAccountId: cfg.kvAccount.awsaccountid   || "",
            region:       cfg.kvAccount.awsregion      || "",
            accessKeyId:  cfg.kvAccount.awsaccesskeyid || "",
          };
        }
      }

      lastDeployBodyRef.current = { type: 'dotnet', body: { config: payload, sessionId } };
      lastActionRef.current = 'dotnet_deploy';
      deploymentErrorShown.current = false;
      setDeploymentConfirmation(null);
      deploymentPausedRef.current = false;
      deploymentErrorLogs.current = [];
      lastDeployErrMsg.current = "";
      deployCurrentApiRef.current = 0;
      deployingSeenCountRef.current = 0;
      validatingCountRef.current = 0;
      setDeployLogs([]);
      setPipelineStages([]);
      var dotnetApiStatuses = buildApiStatuses(generatedApis, DEPLOY_STAGE_MAP.dotnet);
      setDeployApiStatuses(dotnetApiStatuses);
      deployApiStatusesRef.current = dotnetApiStatuses;
      deployApiCountRef.current = dotnetApiStatuses.length;
      setDeployStages(dotnetApiStatuses.length === 0 ? DEPLOY_STAGE_MAP.dotnet.map(function(n, i) { return { name: n, status: i === 0 ? 'active' : 'pending' }; }) : []);
      setShowDeployWindow(true);
      setDeployWindowMin(false);
      setLoading(true);
      addLog(setLogs, "info", "📤 Starting .NET deployment...");
      console.log("[SmartHub] POST /api/v1/dotnet-deployment →", payload);
      fetch(`${domain}/api/v1/dotnet-deployment`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "x-access-token": localStorage.getItem("token") },
        body: JSON.stringify({ config: payload, sessionId }),
      })
        .then(r => { const ok = r.ok; return r.json().then(d => ({ ok, d })); })
        .then(({ ok, d }) => {
          console.log("[SmartHub] .NET deployment response:", d);
          if (ok || d.success) {
            deploymentErrorShown.current = false;
            addLog(setLogs, "info", "🚀 Deployment running — streaming live progress...");
          } else if (!deploymentPausedRef.current && !deploymentErrorShown.current) {
            deploymentErrorShown.current = true;
            setTimeout(() => {
              setLoading(false);
              const rawErrMsg = d.message || d.error || "";
              const summary = deploymentErrorLogs.current[0] || (rawErrMsg !== "Unknown error" ? rawErrMsg : "") || "Deployment failed.";
              const isK8sVersion = /kubernetes version|no longer supported|upgrade your kubernetes|supported version/i.test(summary);
              const hint = getDeployErrorHint(summary, "dotnet");
              if (isK8sVersion) {
                setMessages(prev => [...prev, {
                  role: "bot", time: now(),
                  content: `❌ Deployment failed: Your EKS cluster is running an unsupported Kubernetes version.\n\nPlease upgrade your cluster in the AWS console, then retry the deployment.`,
                  actions: [{ label: "🔄 Retry Deployment", action: "confirm_deploy_dotnet" }],
                }]);
              } else {
                const content = hint
                  ? `❌ Deployment failed: ${summary}\n\n${hint}`
                  : `❌ Deployment failed: ${summary}`;
                setMessages(prev => [...prev, { role: "bot", time: now(), content, actions: [{ label: "🔄 Retry Deployment", action: "confirm_deploy_dotnet" }] }]);
              }
            }, 800);
          }
        })
        .catch(err => {
          if (!deploymentPausedRef.current) {
            setLoading(false);
            addDeployLog('❌ Could not reach backend: ' + err.message + '. Restart the backend and click Retry.', 'error');
            setDeployStages(function(prev) { return prev.map(function(s) { return s.status === 'active' ? { ...s, status: 'error' } : s; }); });
            setDeployApiStatuses(function(prev) {
              var activeIdx = deployCurrentApiRef.current;
              return prev.map(function(a, i) {
                if (i !== activeIdx) return a;
                return { ...a, status: 'error', stages: (a.stages || []).map(function(s) { return s.status === 'active' ? { ...s, status: 'error' } : s; }) };
              });
            });
            setMessages(prev => [...prev, {
              role: "bot", time: now(),
              content: `❌ Deployment failed: Could not reach the backend server. Please ensure the backend is running, then retry.`,
              actions: [{ label: "🔄 Retry Deployment", action: "confirm_deploy_dotnet" }],
            }]);
          }
        });
      return;
    }

    // ── Confirm Spring Boot deploy ─────────────────────────────
    if (action === "confirm_deploy_spring") {
      const cfg     = deployConfig;
      const cluster = cfg.cluster;
      const cloud   = cfg.cloudAccount;


      // Match awsaccount by awsaccesskeyid to get awsaccountid
      const awsAcct = awsAcctList.find(a => a.awsaccesskeyid === cloud?.awsaccesskeyid);
      const awsAccountId = awsAcct?.awsaccountid || "";

      const payload = {
        projectPath,
        projectName: deployProjName || projName,
        applicationType: "springboot",
        springboot: { projectName: deployProjName || projName, packageName },
      };

      // ── Repository ──────────────────────────────────────────
      if (cfg.repoType === "github" && cfg.repo) {
        payload.github = {
          enabled: true,
          token: cfg.repo.gitPat || cfg.repo.token || "",
          owner: cfg.repo.gitUsername || cfg.repo.username || "",
        };
      } else if (cfg.repoType === "gitlab" && cfg.repo) {
        payload.gitlab = {
          enabled: true,
          token: cfg.repo.gitlabPat || cfg.repo.gitPat || cfg.repo.token || "",
          owner: cfg.repo.gitUsername || cfg.repo.username || "",
        };
      }

      // ── DevOps ───────────────────────────────────────────────
      if (cfg.devOpsType === "azure" && cfg.devOpsAcc) {
        payload.azure = {
          enabled: true,
          credentials: {
            pat:    cfg.devOpsAcc.azDevopsPat  || "",
            orgUrl: cfg.devOpsAcc.organization || "",
          },
          variableGroupName: "build.variables",
          variables: {
            awsRegion:      cluster?.location   || "",
            awsAccountId:   awsAccountId,
            eksClusterName: cluster?.awscluster || "",
            ARTIFACTS_FEED_NAME: (cfg.devOpsAcc.organization || "").replace(/\/+$/, "").split("/").pop() || "",
          },
          secretVariables: {
            awsSecretAccessKey: cloud?.awsaccesskey   || "",
            awsAccessKeyId:     cloud?.awsaccesskeyid || "",
            awsClusterName:     cluster?.awscluster   || "",
            awsRegion:          cluster?.location     || "",
          },
          triggerBuild: true,
        };
      }

      // ── Cloud platform ───────────────────────────────────────
      if (cfg.cloudProvider === "aws" && cloud) {
        payload.aws = {
          enabled: true,
          credentials: {
            accessKeyId:     cloud.awsaccesskeyid || "",
            secretAccessKey: cloud.awsaccesskey   || "",
            region:          cluster?.location || cloud.location || "us-east-1",
          },
          deploymentTarget: "eks",
          clusterName: cluster?.awscluster || "",
          environment: "development",
        };
      } else if (cfg.cloudProvider === "azure" && cloud) {
        payload.azure = {
          ...payload.azure,           // preserve devops azure fields if already set
          enabled: true,
          credentials: {
            subscriptionId: cloud.subscriptionId || "",
            tenantId:       cloud.tenantId       || "",
            clientId:       cloud.clientId       || "",
            clientSecret:   cloud.clientSecret   || "",
          },
          variableGroupName: "build.variables",
          variables: {
            awsRegion:      cluster?.location || "",
            awsAccountId:   "",
            eksClusterName: cluster?.awscluster || "",
          },
          secretVariables: {
            awsAccessKeyId:     cloud.awsaccesskeyid || "",
            awsSecretAccessKey: cloud.awsaccesskey   || "",
          },
          triggerBuild: true,
          deploymentTarget: "aks",
          resourceGroup: "rg-springboot-dev",
        };
      } else if (cfg.cloudProvider === "gcp" && cloud) {
        payload.gcp = {
          enabled: true,
          credentials: {
            projectId:         cloud.projectId         || "",
            serviceAccountKey: cloud.serviceAccountKey || "",
          },
          deploymentTarget: "gke",
          clusterName: cloud.clusterName || "",
        };
      }

      if (cfg.kvAccount && cfg.kvProvider) {
        if (cfg.kvProvider === "azure") {
          payload.azure = {
            ...payload.azure,
            keyVault: {
              enabled:          true,
              subscriptionName: cfg.kvAccount.subscriptionName || "",
              servicePrincipal: {
                clientId:       cfg.kvAccount.serviceprincipalid  || "",
                clientSecret:   cfg.kvAccount.serviceprincipalkey || "",
                tenantId:       cfg.kvAccount.tenantId            || "",
                subscriptionId: cfg.kvAccount.subscriptionId      || "",
              },
            },
          };
        } else {
          payload.keyVault = {
            enabled:      true,
            provider:     "aws",
            awsAccountId: cfg.kvAccount.awsaccountid   || "",
            region:       cfg.kvAccount.awsregion      || "",
            accessKeyId:  cfg.kvAccount.awsaccesskeyid || "",
          };
        }
      }

      lastDeployBodyRef.current = { type: 'spring', body: { config: payload, sessionId } };
      lastActionRef.current = 'spring_deploy';
      deploymentErrorShown.current = false;
      setDeploymentConfirmation(null);
      deploymentPausedRef.current = false;
      deploymentErrorLogs.current = [];
      lastDeployErrMsg.current = "";
      deployCurrentApiRef.current = 0;
      deployingSeenCountRef.current = 0;
      validatingCountRef.current = 0;
      setDeployLogs([]);
      setPipelineStages([]);
      var springApiStatuses = buildApiStatuses(generatedApis, DEPLOY_STAGE_MAP.springboot);
      setDeployApiStatuses(springApiStatuses);
      deployApiStatusesRef.current = springApiStatuses;
      deployApiCountRef.current = springApiStatuses.length;
      setDeployStages(springApiStatuses.length === 0 ? DEPLOY_STAGE_MAP.springboot.map(function(n, i) { return { name: n, status: i === 0 ? 'active' : 'pending' }; }) : []);
      setShowDeployWindow(true);
      setDeployWindowMin(false);
      setLoading(true);
      addLog(setLogs, "info", "📤 Starting Spring Boot deployment...");
      console.log("[SmartHub] POST /api/v1/springboot-deployment →", payload);
      fetch(`${domain}/api/v1/springboot-deployment`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "x-access-token": localStorage.getItem("token") },
        body: JSON.stringify({ config: payload, sessionId }),
      })
        .then(r => { const ok = r.ok; return r.json().then(d => ({ ok, d })); })
        .then(({ ok, d }) => {
          console.log("[SmartHub] Spring Boot deployment response:", d);
          if (ok || d.success) {
            deploymentErrorShown.current = false;
            addLog(setLogs, "info", "🚀 Deployment running — streaming live progress...");
          } else if (!deploymentPausedRef.current && !deploymentErrorShown.current) {
            deploymentErrorShown.current = true;
            setTimeout(() => {
              setLoading(false);
              const rawErrMsg = d.message || d.error || "";
              const summary = deploymentErrorLogs.current[0] || (rawErrMsg !== "Unknown error" ? rawErrMsg : "") || "Deployment failed.";
              const isK8sVersion = /kubernetes version|no longer supported|upgrade your kubernetes|supported version/i.test(summary);
              const hint = getDeployErrorHint(summary, "spring");
              if (isK8sVersion) {
                setMessages(prev => [...prev, {
                  role: "bot", time: now(),
                  content: `❌ Deployment failed: Your EKS cluster is running an unsupported Kubernetes version.\n\nPlease upgrade your cluster in the AWS console, then retry the deployment.`,
                  actions: [{ label: "🔄 Retry Deployment", action: "confirm_deploy_spring" }],
                }]);
              } else {
                const content = hint
                  ? `❌ Deployment failed: ${summary}\n\n${hint}`
                  : `❌ Deployment failed: ${summary}`;
                setMessages(prev => [...prev, { role: "bot", time: now(), content, actions: [{ label: "🔄 Retry Deployment", action: "confirm_deploy_spring" }] }]);
              }
            }, 800);
          }
        })
        .catch(err => {
          if (!deploymentPausedRef.current) {
            setLoading(false);
            addDeployLog('❌ Could not reach backend: ' + err.message + '. Restart the backend and click Retry.', 'error');
            setDeployStages(function(prev) { return prev.map(function(s) { return s.status === 'active' ? { ...s, status: 'error' } : s; }); });
            setDeployApiStatuses(function(prev) {
              var activeIdx = deployCurrentApiRef.current;
              return prev.map(function(a, i) {
                if (i !== activeIdx) return a;
                return { ...a, status: 'error', stages: (a.stages || []).map(function(s) { return s.status === 'active' ? { ...s, status: 'error' } : s; }) };
              });
            });
            setMessages(prev => [...prev, {
              role: "bot", time: now(),
              content: `❌ Deployment failed: Could not reach the backend server. Please ensure the backend is running, then retry.`,
              actions: [{ label: "🔄 Retry Deployment", action: "confirm_deploy_spring" }],
            }]);
          }
        });
      return;
    }
  };

  // ── File upload helpers ───────────────────────────────────────
  const readFile = async (file) => {
    if (!file) return;
    const ALLOWED_EXTS = ['.puml', '.txt', '.pdf', '.docx', '.zip'];
    const ext = '.' + file.name.split('.').pop().toLowerCase();
    if (!ALLOWED_EXTS.includes(ext)) {
      setUploadedFile({ name: file.name, error: `Unsupported file type "${ext}". Only .puml, .txt, .pdf, .docx, and .zip files are accepted.` });
      return;
    }
    const MAX_BYTES = 5 * 1024 * 1024;
    if (file.size > MAX_BYTES) {
      setUploadedFile({ name: file.name, error: `File exceeds the 5 MB limit (${(file.size / 1024 / 1024).toFixed(1)} MB).` });
      return;
    }
    if (ext === '.pdf') {
      const arrayBuffer = await file.arrayBuffer();
      try {
        // Load pdfjs-dist from public/ at runtime to bypass CRA Babel transpilation of node_modules
        if (!window.pdfjsLib) {
          await new Promise((resolve, reject) => {
            const script = document.createElement('script');
            script.src = `${process.env.PUBLIC_URL}/pdfjs.min.js`;
            script.onload = resolve;
            script.onerror = reject;
            document.head.appendChild(script);
          });
        }
        const pdfjs = window.pdfjsLib;
        pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.js`;
        const pdf = await pdfjs.getDocument({ data: arrayBuffer }).promise;
        const pages = await Promise.all(
          Array.from({ length: pdf.numPages }, (_, i) =>
            pdf.getPage(i + 1).then(p => p.getTextContent()).then(tc => tc.items.map(it => it.str).join(" "))
          )
        );
        const text = pages.join("\n\n").trim();
        if (!text) { setUploadedFile({ name: file.name, error: "No readable text found in this PDF." }); return; }
        setUploadedFile({ name: file.name, content: text, pageCount: pdf.numPages });
      } catch {
        setUploadedFile({ name: file.name, error: "Could not read PDF. Make sure it is not password-protected." });
      }
      return;
    }
    if (ext === '.docx') {
      const arrayBuffer = await file.arrayBuffer();
      try {
        const zip = await JSZip.loadAsync(arrayBuffer);
        const xmlFile = zip.file('word/document.xml');
        if (!xmlFile) { setUploadedFile({ name: file.name, error: "Could not read DOCX file." }); return; }
        const xml = await xmlFile.async('string');
        const text = xml.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
        if (!text) { setUploadedFile({ name: file.name, error: "No readable text found in this DOCX." }); return; }
        setUploadedFile({ name: file.name, content: text });
      } catch {
        setUploadedFile({ name: file.name, error: "Could not read DOCX file." });
      }
      return;
    }
    if (ext === '.zip') {
      const arrayBuffer = await file.arrayBuffer();
      try {
        const zip = await JSZip.loadAsync(arrayBuffer);
        const TEXT_EXT = /\.(xml|java|cs|raml|dwl|ya?ml|json|properties|js|ts|py|gradle|md|sql|txt|csproj|sln|xsd|wsdl|html|css|sh)$/i;
        const SKIP_DIR = /(^|\/)(node_modules|target|bin|obj|dist|build|\.git)\//i;
        const MAX_TOTAL = 200 * 1024; // cap combined context so the prompt stays sane
        const entries = Object.values(zip.files)
          .filter(f => !f.dir && TEXT_EXT.test(f.name) && !SKIP_DIR.test(f.name));
        const parts = [];
        let total = 0, fileCount = 0, skipped = 0;
        for (const entry of entries) {
          if (total >= MAX_TOTAL) { skipped++; continue; }
          let content;
          try { content = await entry.async('string'); } catch { continue; }
          if (!content) continue;
          const remaining = MAX_TOTAL - total;
          const clipped = content.length > remaining
            ? content.slice(0, remaining) + '\n…(truncated)…'
            : content;
          parts.push(`--- ${entry.name} ---\n${clipped}`);
          total += clipped.length;
          fileCount++;
        }
        if (!fileCount) { setUploadedFile({ name: file.name, error: "No readable text/code files found in this ZIP." }); return; }
        let combined = parts.join('\n\n');
        if (skipped) combined += `\n\n…(${skipped} more file(s) omitted to stay within the size limit)…`;
        setUploadedFile({ name: file.name, content: combined, fileCount });
      } catch {
        setUploadedFile({ name: file.name, error: "Could not read the ZIP file." });
      }
      return;
    }
    const reader = new FileReader();
    reader.onload = e => setUploadedFile({ name: file.name, content: e.target.result });
    reader.readAsText(file);
  };

  const handleFileInput = (e) => readFile(e.target.files[0]);
  const handleDrop = (e) => { e.preventDefault(); setDragOver(false); readFile(e.dataTransfer.files[0]).catch(() => {}); };

  const handleAttachPuml = () => {
    if (!uploadedFile || !uploadedFile.content?.trim()) return;
    const name = uploadedFile.name;
    const code = uploadedFile.content.trim();

    // For .puml files — still render the diagram so the user can preview it
    if (name?.endsWith('.puml')) {
      setPlantumlCode(code);
      plantumlCodeRef.current = code;
      setActiveAgent("design");
      pushArtifact({ id: "puml", icon: "📐", name: name || "flow-diagram.puml", meta: `${code.split("\n").length} lines`, badge: "Design", content: code });
    }

    // Store as pending attachment — sent with the next user message
    setPendingAttachment({ name, content: code });
    setUploadedFile(null);
    setShowPumlPanel(false);
    if (reopenPromptModal) {
      setReopenPromptModal(false);
      setShowPromptModal(true);
    }
  };

  // ── New Session ───────────────────────────────────────────────
  // Fully reset the brownfield (Enhance Existing) session: clear persisted keys,
  // remount the panel fresh (new key), and clear hook state. Used by both New Session
  // and the Compliance Check handoff (which must always start clean).
  const resetBrownfieldSession = () => {
    try {
      const _bfUid = JSON.parse(localStorage.getItem('user') || '{}').id || 'default';
      localStorage.removeItem(`bf_state_${_bfUid}`);
      localStorage.removeItem(`bf_feature_${_bfUid}`);
      localStorage.removeItem(`bf_scan_${_bfUid}`); // never resume a prior scan on New Session / handoff
    } catch (_) {}
    setBfSessionKey(k => k + 1);
    bfScanActiveRef.current = false;
    bf.setBfMessages([]);
    bf.setBfLoading(false);
    bf.setBfAnalysisDone(false);
    bf.setBfResults(null);
    bf.setBfLogs([]);
    if (bf.setBfChangedFiles) bf.setBfChangedFiles([]);
    // Session is now fresh — dismiss the "context may have expired" stale banner, which
    // otherwise stays latched from the previously-persisted (old) session.
    if (bf.setShowBfStaleBanner) bf.setShowBfStaleBanner(false);
  };

  const handleNewSession = () => {
    _mockSeqIdx = 0;
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem("langraph_session_id");
    // Cancel any in-flight async-hub job poll and clear its persisted id.
    if (jobPollRef.current) { clearInterval(jobPollRef.current); jobPollRef.current = null; }
    jobIdRef.current = null;
    localStorage.removeItem("langraph_job_id");
    // clear migration tab session
    const _mgUserId = (() => { try { return JSON.parse(localStorage.getItem('user')||'{}').id||'anon'; } catch { return 'anon'; } })();
    localStorage.removeItem(`migration_tab_state_${_mgUserId}`);
    localStorage.removeItem('migration_session_id');
    createSession(true);  // forceNew=true — intentional reset, bypass active-conversation guard
    setShowStaleBanner(false);  // a fresh session is never stale — hide the "Session from earlier" banner
    setMessages([{ ...INIT_MESSAGE, time: "" }]);
    setInput("");
    setLoading(false);
    setLogs([]);
    setArtifacts([]);
    setActiveArtifact(null);
    setPlantumlCode("");
    setActiveAgent(null);
    savedProjectIdRef.current = null;
    setSavedProjectId(null);
    lastSyncedApisSigRef.current = null;
    designProjectIdRef.current = null;
    setDesignSaved(false);
    setPendingAttachment(null);
    // refs
    plantumlCodeRef.current = "";
    attachedImageRef.current = null;
    lastHubBodyRef.current    = null;
    lastDeployBodyRef.current = null;
    lastFileEditRef.current   = null;
    lastActionRef.current     = null;
    // project data
    setGeneratedApis([]);
    setProjectPath("");
    setPackageName("");
    setDeployProjName("");
    setDesignProjectName("");
    setSelectedTech("");
    setAwaitingInput(null);
    setSaving(false);
    // inline editors
    setEditingMsgIdx(null);
    setEditingText("");
    setIntegrationEditMsgIdx(null);
    setIntegrationEditText("");
    setDiagramEditMsgIdx(null);
    setDiagramEditText("");
    deploymentPausedRef.current = false;
    setDeploymentConfirmation(null);
    setDesignNameMsgIdx(null);
    setDesignNameText("");
    setIntegrationNameMsgIdx(null);
    setIntegrationNameText("");
    // file upload panel
    setUploadedFile(null);
    setShowPumlPanel(false);
    // brownfield + migration reset — increment key forces remount; also clear hook state.
    resetBrownfieldSession();
    setMigrationKey(k => k + 1);
  };

  // ── Download helpers ─────────────────────────────────────────
  const handleDownloadPuml = () => {
    const blob = new Blob([plantumlCode], { type: "text/plain" });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement("a");
    a.href     = url;
    a.download = "diagram.puml";
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleDownloadPng = async () => {
    try {
      console.log('[SmartHub] POSTing to backend proxy → downloading diagram PNG');
      const resp = await fetch(`${domain}/api/v1/plantuml/render`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-access-token': localStorage.getItem('token'),
        },
        body: JSON.stringify({ code: plantumlCode }),
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      const src = data.imageData || data.imageUrl;
      if (!src) throw new Error('No image data in response');

      let blob;
      if (src.startsWith('data:')) {
        // base64 data URI → convert to Blob
        const base64 = src.split(',')[1];
        const bytes = Uint8Array.from(atob(base64), c => c.charCodeAt(0));
        blob = new Blob([bytes], { type: 'image/png' });
      } else {
        // imageUrl — fetch it (same-origin or backend-proxied, no CORS issue)
        const imgResp = await fetch(src);
        blob = await imgResp.blob();
      }

      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'diagram.png';
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('[Download PNG] failed:', e);
    }
  };

  // ─── RENDER ──────────────────────────────────────────────────
  return (
    <div className="sh-root">

      {/* ══ SIDEBAR ══ */}
      <aside
        className={`sh-sidebar${sidebarCollapsed ? " sh-sidebar--collapsed" : ""}`}
        style={sidebarCollapsed ? {} : { width: sidebarWidth }}
      >
        <button className="sh-sidebar-toggle" onClick={() => setSidebarCollapsed(p => !p)} title={sidebarCollapsed ? "Expand" : "Collapse"}>
          {sidebarCollapsed ? "›" : "‹"}
        </button>
        {/* Resize handle */}
        {!sidebarCollapsed && (
          <div
            className="sh-sidebar-resizer"
            onMouseDown={e => {
              e.preventDefault();
              sidebarResizing.current = true;
              const startX = e.clientX;
              const startW = sidebarWidth;
              const onMove = ev => {
                if (!sidebarResizing.current) return;
                const newW = Math.min(520, Math.max(180, startW + ev.clientX - startX));
                setSidebarWidth(newW);
              };
              const onUp = () => {
                sidebarResizing.current = false;
                window.removeEventListener("mousemove", onMove);
                window.removeEventListener("mouseup", onUp);
              };
              window.addEventListener("mousemove", onMove);
              window.addEventListener("mouseup", onUp);
            }}
          />
        )}
        <div className="sh-sidebar-top">

          {/* Brand */}
          <div className="sh-brand">
             <img
            className="intelog-icon components--logo-img__3108zO"
            alt="INTELOG"
            src={inteloglogo}
          />
            {/* <img src={whiteLogo} alt="Intelog" className="sh-brand-logo" />
            <div>
              <div className="sh-brand-name">intelog<span>.ai</span></div>
              <div className="sh-brand-plan">{projName}</div>
            </div> */}
          </div>

          {/* Back */}
          <nav className="sh-nav">
            <button
              ref={backBtnRef}
              className="sh-nav-item sh-back-btn"
              disabled={loading || bf.bfLoading}
              title={(loading || bf.bfLoading) ? "Please wait — agent is still thinking…" : undefined}
              onClick={() => !(loading || bf.bfLoading) && navigate("/dashboard")}
              style={(loading || bf.bfLoading) ? { opacity: 0.4, cursor: 'not-allowed', pointerEvents: 'all' } : undefined}
            >
              <span className="sh-nav-icon">←</span> Back
            </button>

            {/* Progress stepper — greenfield only. Read-only status, not selectable. */}
            {activeTab === "greenfield" && (
              <div className="sh-steps">
                <div className="sh-steps-head">Progress</div>
                {AGENTS.map((a, i) => {
                  const done = a.key === "design" ? hasDesign : a.key === "integration" ? hasIntegration : false;
                  const active = activeAgent === a.key && !done;
                  const state = done ? "done" : active ? "active" : "pending";
                  return (
                    <div
                      key={a.key}
                      className={`sh-step sh-step--${state} ${i === AGENTS.length - 1 ? "sh-step--last" : ""}`}
                    >
                      <span className="sh-step-marker" aria-hidden="true">
                        {done ? "✓" : active ? "⟳" : "○"}
                      </span>
                      <span className="sh-step-label">{a.label}</span>
                      {done && <span className="sh-nav-status">DONE</span>}
                      {active && <span className="sh-nav-status">ACTIVE</span>}
                    </div>
                  );
                })}
              </div>
            )}
          </nav>

          {/* Migration sidebar portal target */}
          <div
            id="mg-sidebar-portal"
            style={{ display: activeTab === 'migration' ? 'flex' : 'none', flexDirection: 'column', flex: 1, overflow: 'hidden' }}
          />

          {/* Artifacts — folder tree — greenfield only */}
          {activeTab === "greenfield" && artifacts.length > 0 && (() => {
            // ── Disk-loaded: use ApiFileTree (relativePath present) ──
            const diskArts = artifacts.filter(a => a.relativePath);
            if (diskArts.length > 0) {
              const apiGroups = {};
              diskArts.forEach(art => {
                const g = art.group || 'Files';
                if (!apiGroups[g]) apiGroups[g] = [];
                apiGroups[g].push(art);
              });
              // Non-disk artifacts (no relativePath) — e.g. the design flow-diagram.puml —
              // aren't part of an API folder; render them as standalone files at the top so
              // the diagram is visible alongside the generated code.
              const looseArts = artifacts.filter(a => !a.relativePath);
              return (
                <div className="sh-tree-root">
                  <div className="sh-tree-section-label">Generated Files</div>
                  {looseArts.map(art => (
                    <button
                      key={art.id}
                      className={`sh-nav-item sh-tree-file ${activeArtifact?.id === art.id ? "active" : ""}`}
                      onClick={() => setActiveArtifact(art)}
                    >
                      <span>{art.icon}</span>
                      <span className="sh-tree-file-name">{art.name}</span>
                      <span className="sh-tree-badge">{art.badge}</span>
                    </button>
                  ))}
                  {Object.entries(apiGroups).map(([apiName, files]) => (
                    <ApiFileTree
                      key={apiName}
                      apiName={apiName}
                      artifacts={files}
                      activeArtifact={activeArtifact}
                      setActiveArtifact={setActiveArtifact}
                    />
                  ))}
                </div>
              );
            }

            // ── Generated: original layer/svc grouping ────────────
            const flatArts  = artifacts.filter(a => !a.layer && !a.svcGroup);
            const svcArts   = artifacts.filter(a => !a.layer && a.svcGroup);
            const layerArts = artifacts.filter(a => a.layer);

            const svcMap = {};
            svcArts.forEach(art => {
              if (!svcMap[art.svcGroup]) svcMap[art.svcGroup] = [];
              svcMap[art.svcGroup].push(art);
            });

            const getApiBase = (name) => name.replace(/\.(xml|raml)$/, "").replace(/-pom$/, "");
            const layerMap = {};
            layerArts.forEach(art => {
              const l = art.layer;
              const base = getApiBase(art.name);
              if (!layerMap[l]) layerMap[l] = {};
              if (!layerMap[l][base]) layerMap[l][base] = [];
              layerMap[l][base].push(art);
            });

            const LAYER_ORDER = ["experience", "process", "system"];

            return (
              <div className="sh-tree-root">
                <div className="sh-tree-section-label">Generated Files</div>

                {flatArts.map(art => (
                  <button
                    key={art.id}
                    className={`sh-nav-item sh-tree-file ${activeArtifact?.id === art.id ? "active" : ""}`}
                    onClick={() => setActiveArtifact(art)}
                  >
                    <span>{art.icon}</span>
                    <span className="sh-tree-file-name">{art.name}</span>
                    <span className="sh-tree-badge">{art.badge}</span>
                  </button>
                ))}

                {Object.entries(svcMap).map(([svcName, files]) => (
                  <SvcFolder
                    key={svcName}
                    name={svcName}
                    files={files}
                    activeArtifact={activeArtifact}
                    setActiveArtifact={setActiveArtifact}
                  />
                ))}

                {LAYER_ORDER.map(layer => {
                  const apiGroups = layerMap[layer];
                  if (!apiGroups) return null;
                  return (
                    <div key={layer} className="sh-tree-layer-group">
                      <div className="sh-tree-layer-label">
                        <span>{layerIcon[layer]}</span>
                        <span>{layer.charAt(0).toUpperCase() + layer.slice(1)} Layer</span>
                      </div>
                      {Object.entries(apiGroups).map(([apiBase, files]) => (
                        <div key={apiBase} className="sh-tree-api-group">
                          <div className="sh-tree-api-label">
                            <span>📂</span>
                            <span className="sh-tree-api-name">{apiBase}</span>
                          </div>
                          {files.map(art => (
                            <button
                              key={art.id}
                              className={`sh-nav-item sh-tree-file sh-tree-file--nested ${activeArtifact?.id === art.id ? "active" : ""}`}
                              onClick={() => setActiveArtifact(art)}
                            >
                              <span>{art.icon}</span>
                              <span className="sh-tree-file-name">
                                {art.name.replace(apiBase, "").replace(/^[-_]/, "") || art.name}
                              </span>
                            </button>
                          ))}
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>
            );
          })()}
        </div>

      </aside>

      {/* ══ MAIN ══ */}
      <main className="sh-main">

        {/* Header */}
        <header className="sh-header">
          <div className="sh-header-left">
            <span className={`sh-conn-dot ${connected ? "pulse" : "off"}`} />
            <div className="sh-header-tabs" ref={tabsRef}>
              {(hasPermission('smartHub') || hasPermission('greenfieldAgent')) && (
                <button
                  className={`sh-header-tab${activeTab === "greenfield" ? " sh-header-tab--active sh-header-tab--green" : ""}`}
                  onClick={() => setActiveTab("greenfield")}
                >🌱 New Integrations</button>
              )}
              {(hasPermission('smartHub') || hasPermission('brownfieldAgent')) && (
                <button
                  className={`sh-header-tab${activeTab === "brownfield" ? " sh-header-tab--active sh-header-tab--brown" : ""}`}
                  onClick={() => setActiveTab("brownfield")}
                >🏗 Enhance Existing</button>
              )}
              {(hasPermission('migrationAgent') || hasPermission('migrationDotnet') || hasPermission('migrationJitterbit')) && (
                <button
                  className={`sh-header-tab${activeTab === "migration" ? " sh-header-tab--active sh-header-tab--migration" : ""}`}
                  onClick={() => setActiveTab("migration")}
                >⬆️ Migration</button>
              )}
            </div>
            {savedProjectId && (
              <span className="sh-header-badge" style={{ background: "rgba(0,200,100,0.15)", color: "#00c864" }}>
                ✅ Saved to Projects
              </span>
            )}
          </div>
          <div className="sh-header-right">
            <button className="sh-hdr-btn sh-hdr-tour" title="Take a guided tour" onClick={() => { setShowHelp(false); setTourStep(1); }}>🧭 Tour</button>
            <div style={{ position: "relative" }}>
              <button className="sh-hdr-btn" title="How to use intelog AI Hub" onClick={() => setShowHelp(p => !p)}>?</button>
              {showHelp && (
                <div className="sh-help-popover" onClick={e => e.stopPropagation()}>
                  <div className="sh-help-popover-header">
                    <span>{activeTab === 'brownfield' ? 'How to use Enhance Existing' : 'How to use intelog AI Hub'}</span>
                    <button onClick={() => setShowHelp(false)}>✕</button>
                  </div>
                  {activeTab === 'brownfield' ? (
                    <div className="sh-help-steps">
                      <div className="sh-help-step">
                        <span className="sh-help-step-num">1</span>
                        <div>
                          <strong>Describe your project</strong>
                          <p>Tell the agent what you'd like to analyse — e.g. "Check my MuleSoft repo for naming and error-handling violations".</p>
                        </div>
                      </div>
                      <div className="sh-help-step">
                        <span className="sh-help-step-num">2</span>
                        <div>
                          <strong>Choose a compliance standard</strong>
                          <p>Connect a Confluence space, upload a PDF/Word document, or use the built-in Intelog standard. The agent will ask which to use.</p>
                        </div>
                      </div>
                      <div className="sh-help-step">
                        <span className="sh-help-step-num">3</span>
                        <div>
                          <strong>Connect your repository</strong>
                          <p>Select a GitHub/GitLab/Bitbucket repository and branch. The agent will scan every file against the standard.</p>
                        </div>
                      </div>
                      <div className="sh-help-step">
                        <span className="sh-help-step-num">4</span>
                        <div>
                          <strong>Review &amp; fix violations</strong>
                          <p>See errors and warnings per file. Apply AI-suggested fixes with one click and commit the changes directly from here.</p>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="sh-help-steps">
                      <div className="sh-help-step">
                        <span className="sh-help-step-num">1</span>
                        <div>
                          <strong>Describe your integration</strong>
                          <p>Type a plain-English description of what you want to build — e.g. "Sync Salesforce contacts to a MySQL database every hour".</p>
                        </div>
                      </div>
                      <div className="sh-help-step">
                        <span className="sh-help-step-num">2</span>
                        <div>
                          <strong>Design &amp; generate code</strong>
                          <p>The AI will ask clarifying questions, produce a sequence diagram, then generate integration code for your chosen technology (MuleSoft, Spring Boot, or .NET).</p>
                        </div>
                      </div>
                      <div className="sh-help-step">
                        <span className="sh-help-step-num">3</span>
                        <div>
                          <strong>Review artifacts</strong>
                          <p>Open generated files in the Artifacts panel on the right. You can edit, refine, or ask the AI to update any file inline.</p>
                        </div>
                      </div>
                      <div className="sh-help-step">
                        <span className="sh-help-step-num">4</span>
                        <div>
                          <strong>Deploy</strong>
                          <p>Ask to deploy — e.g. "Deploy to CloudHub" or "Push to GitHub". Connect your accounts first in <em>Connected Apps</em> settings.</p>
                        </div>
                      </div>
                    </div>
                  )}
                  <div className="sh-help-popover-footer">
                    {activeTab !== 'brownfield' && (
                      <button className="sh-help-examples-link" onClick={() => { setShowHelp(false); setShowExamples(true); }}>💡 See example prompts</button>
                    )}
                  </div>
                </div>
              )}
            </div>
            <div className="sh-hdr-divider" />
            <button ref={newSessionRef} className="sh-hdr-new-btn" onClick={handleNewSession}>➕ New Session</button>
            {/* Shared profile avatar + dropdown — right of New Session */}
            <ProfileMenu onLogout={smartHubLogout} />
          </div>
        </header>

        {/* Token warning / limit banner */}
        {tokenWarning && (
          <div className={`sh-token-banner ${tokenWarning.limitReached ? "sh-token-banner--error" : "sh-token-banner--warn"}`}>
            <span>
              {tokenWarning.limitReached
                ? `🚫 Daily token limit reached — ${(tokenWarning.used || tokenWarning.totalUsed || 0).toLocaleString()} / ${(tokenWarning.limit || 0).toLocaleString()} tokens used. Try again tomorrow or extend your limit.`
                : `⚠️ ${tokenWarning.percentUsed}% of daily tokens used — ${(tokenWarning.remaining || 0).toLocaleString()} tokens remaining today.`
              }
            </span>
            <button className="sh-token-banner-close" onClick={() => setTokenWarning(null)}>✕</button>
          </div>
        )}

        {/* Content */}
        {activeTab === "migration" && (
          <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <MigrationTab key={migrationKey} backendDomain={domain} processUrl={backendUrl} onNewSession={() => setMigrationKey(k => k + 1)}
              canDotnet={hasPermission('migrationAgent') || hasPermission('migrationDotnet')}
              canJitterbit={hasPermission('migrationAgent') || hasPermission('migrationJitterbit')}
              usageData={usageData} liveTokens={liveTokens} extensionStatus={extensionStatus} onExtend={handleExtendUsage} onUsageRefresh={fetchUsage} />
          </div>
        )}
        {activeTab === "brownfield" && <BrownfieldPanel
          key={bfSessionKey}
          sessionId={sessionId}
          createSession={createSession}
          {...bf}
          greenfieldLoading={loading}
          usageData={usageData}
          liveTokens={liveTokens}
          extendingUsage={extendingUsage}
          extensionStatus={extensionStatus}
          handleExtendUsage={handleExtendUsage}
          onUsageRefresh={fetchUsage}
          pendingBfJob={pendingBfJob}
          onPendingBfJobConsumed={() => setPendingBfJob(null)}
          onScanStart={() => { bfScanActiveRef.current = true; }}
          onReturnToDeployment={handleReturnToDeployment}
        />}
        {activeTab === "greenfield" && <div className="sh-content">

          {/* ── Chat Column ── */}
          <div className="sh-chat-col">
            <div className="sh-messages">
              {showStaleBanner && (
                <StaleBanner
                  lastActive={_persisted.lastActive}
                  onNewSession={() => { handleNewSession(); setShowStaleBanner(false); }}
                  onDismiss={() => setShowStaleBanner(false)}
                />
              )}
              {messages.map((msg, i) => {
                if (msg.role === "bot") {
                  const textContent = typeof msg.content === 'string' ? msg.content.trim() : '';
                  const hasText = textContent.length > 0 && !msg.validation && !msg.architecturalReview && !msg.apiSelection && !msg.selectedLabel && !msg.designPlan && !msg.implementationPlan && !msg.deploymentSetup;
                  const hasCard =
                    msg.designPlan ||
                    msg.implementationPlan ||
                    msg.deploymentSetup ||
                    msg.validationQA || msg.schemaQA ||
                    msg.validation ||
                    msg.architecturalReview || msg.apiSelection ||
                    (msg.schemaElicitation && !msg.schemaAnswered) ||
                    (msg.fileList && msg.fileList.length > 0) ||
                    (msg.actions && msg.actions.length > 0) ||
                    msg.artifactId ||
                    (msg.analysis && (
                      (msg.analysis.components && msg.analysis.components.length > 0) ||
                      (msg.analysis.interactions && msg.analysis.interactions.length > 0) ||
                      (msg.analysis.generatedApis && msg.analysis.generatedApis.length > 0)
                    )) ||
                    msg.streaming || msg.confluenceLink || msg.retryable || msg.deploymentCard ||
                    msg.standaloneConfirm;
                  if (!hasText && !hasCard) return null;
                }
                return (
                <div key={i} className={`sh-msg ${msg.role}`}>
                  <div className={`sh-msg-avatar ${msg.role === "bot" ? "bot-avatar" : ""}`}>
                    {msg.role === "bot" ? <img src={whiteLogo} alt="intelog" /> : "👤"}
                  </div>
                  <div className="sh-msg-body">
                    {editingMsgIdx === i ? (
                      <div className="sh-inline-edit">
                        <textarea
                          className="sh-inline-edit-textarea"
                          value={editingText}
                          onChange={e => setEditingText(e.target.value)}
                          onKeyDown={e => {
                            if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); saveEdit(i); }
                            if (e.key === "Escape") cancelEdit();
                          }}
                          autoFocus
                        />
                        <div className="sh-inline-edit-actions">
                          <span className="sh-inline-edit-hint">Enter to send · Esc to cancel</span>
                          <button className="sh-inline-edit-cancel" onClick={cancelEdit}>Cancel</button>
                          <button className="sh-inline-edit-save" onClick={() => saveEdit(i)}>Send ↑</button>
                        </div>
                      </div>
                    ) : (
                      <>
                    <div className="sh-msg-meta">
                      <span className="sh-msg-name">{msg.role === "bot" ? "intelog AI" : "You"}</span>
                      {msg.time && <span className="sh-msg-time">{msg.time}</span>}
                      {msg.role === "user" && !loading && (
                        <button className="sh-msg-edit-btn" title="Edit message" onClick={() => startEdit(i)}>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                          </svg>
                        </button>
                      )}
                    </div>
                    <div className={`sh-msg-bubble ${msg.role}`}>
                        {msg.validationQA ? (
                          <div className="sh-qa-summary">
                            {msg.validationQA.map((item, qi) => (
                              <div key={qi} className="sh-qa-row">
                                <div className="sh-qa-q"><span className="sh-qa-label">Q{qi + 1}</span>{item.q}</div>
                                <div className="sh-qa-a"><span className="sh-qa-label sh-qa-label--a">A</span>{item.a}</div>
                              </div>
                            ))}
                          </div>
                        ) : msg.schemaQA ? (
                          <div className="sh-qa-summary">
                            {msg.schemaQA.map((item, si) => {
                              const mc = METHOD_COLOR[item.method] || { bg: "rgba(255,255,255,0.08)", color: "#ccc" };
                              return (
                                <div key={si} className="sh-qa-row">
                                  <div className="sh-qa-q">
                                    <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                                      <span style={{ fontSize: 9, fontWeight: 700, padding: "1px 5px", borderRadius: 3, background: mc.bg, color: mc.color, textTransform: "uppercase" }}>{item.method}</span>
                                      <span style={{ fontFamily: "monospace", fontSize: 12, color: "#c0c8d8" }}>{item.path}</span>
                                    </span>
                                    <span style={{ fontSize: 11, color: "rgba(255,255,255,0.35)", marginLeft: 6 }}>{item.api}</span>
                                  </div>
                                  <div className="sh-qa-a" style={{ flexDirection: "column", gap: 2 }}>
                                    <span><span className="sh-qa-label sh-qa-label--a">REQ</span> {item.request}</span>
                                    <span><span className="sh-qa-label sh-qa-label--a">RES</span> {item.response}</span>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        ) : (!msg.validation && !msg.architecturalReview && !msg.apiSelection && !msg.selectedLabel && !msg.designPlan && !msg.implementationPlan && msg.content && (
                          msg.role === 'bot'
                            ? <div style={{ lineHeight: 1.7 }}>{renderMdText(msg.content)}</div>
                            : <p>{msg.content}</p>
                        ))}
                      {msg.confluenceLink && (
                        <a
                          href={msg.confluenceLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="sh-confluence-page-link"
                        >
                          📄 View in Confluence →
                        </a>
                      )}
                      {msg.retryable && !loading && (
                        <button
                          className="sh-retry-btn"
                          onClick={retryLastRequest}
                          title="Retry this request"
                        >
                          🔄 Retry
                        </button>
                      )}
                      {msg.checks && (
                        <ul className="sh-check-list">
                          {msg.checks.map((c, ci) => (
                            <li key={ci}><span className="sh-check-icon">✓</span>{c}</li>
                          ))}
                        </ul>
                      )}
                      {/* API list card — shown for integration responses */}
                      {msg.apiList && (
                        <div className="sh-api-list">
                          {["experience", "process", "system"].map(layer => {
                            const layerApis = msg.apiList.filter(a => a.layer === layer);
                            if (!layerApis.length) return null;
                            return (
                              <div key={layer} className="sh-api-layer">
                                <div className="sh-api-layer-label">
                                  {layerIcon[layer]} {layer.charAt(0).toUpperCase() + layer.slice(1)} Layer
                                </div>
                                {layerApis.map(api => (
                                  <button
                                    key={api.id}
                                    className="sh-api-item"
                                    onClick={() => setActiveArtifact(artifacts.find(a => a.id === api.id))}
                                  >
                                    <span className="sh-api-item-name">{api.displayName}</span>
                                    <span className={`sh-api-item-status ${api.generated ? "ok" : "err"}`}>
                                      {api.generated ? "Generated" : "Failed"}
                                    </span>
                                  </button>
                                ))}
                              </div>
                            );
                          })}
                          {msg.secs && <div className="sh-api-meta">⏱ Generated in {msg.secs}s</div>}
                        </div>
                      )}

                      {/* File list — collapsible folder tree grouped by API */}
                      {msg.fileList && (() => {
                        const groups = {};
                        msg.fileList.forEach(f => {
                          const g = f.group || "Other";
                          if (!groups[g]) groups[g] = [];
                          groups[g].push(f);
                        });
                        return (
                          <div className="sh-api-list">
                            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                              <div className="sh-api-meta" style={{ flex: 1, marginBottom: 0 }}>
                                📁 {msg.fileList.length} files generated
                              </div>
                              <button
                                onClick={() => downloadAllFiles(groups, projectPath)}
                                disabled={downloadingAll}
                                title="Download all files as a .zip"
                                style={{
                                  padding: "4px 10px",
                                  background: "rgba(59,127,255,0.15)",
                                  border: "1px solid rgba(59,127,255,0.5)",
                                  borderRadius: 5, color: "#6aa3ff",
                                  fontSize: 11, fontWeight: 600,
                                  cursor: downloadingAll ? "default" : "pointer",
                                  display: "flex", alignItems: "center", gap: 4,
                                  opacity: downloadingAll ? 0.6 : 1,
                                }}
                              >
                                <span style={{ fontSize: 13 }}>{downloadingAll ? "⏳" : "⬇"}</span> {downloadingAll ? "Zipping…" : "Download All"}
                              </button>
                              <button
                                onClick={() => setIdeModalGroups({ groups, projectPath })}
                                title="Open in IDE view"
                                style={{
                                  padding: "4px 10px",
                                  background: "rgba(46,191,165,0.15)",
                                  border: "1px solid rgba(46,191,165,0.5)",
                                  borderRadius: 5, color: "#2ebfa5",
                                  fontSize: 11, fontWeight: 600,
                                  cursor: "pointer",
                                  display: "flex", alignItems: "center", gap: 4,
                                  boxShadow: "0 0 6px rgba(46,191,165,0.2)",
                                }}
                              >
                                <span style={{ fontSize: 13 }}>⛶</span> IDE View
                              </button>
                            </div>
                            {Object.entries(groups).map(([group, files]) => (
                              <ChatGroupTree
                                key={group}
                                group={group}
                                files={files}
                                apiProjectPath={files[0]?.apiProjectPath || ""}
                                onFileClick={(f) => setActiveArtifact(artifacts.find(a => a.id === f.id))}
                                onEdit={({ projectPath: apiPath, editPrompt }) => {
                                  setMessages(prev => [...prev, { role: "user", time: now(), content: editPrompt }]);
                                  const editEndpoint = selectedTech === "dotnet" ? "/api/v1/dotnet/edit"
                                    : selectedTech === "springboot" ? "/api/v1/springboot-edit-file"
                                    : "/api/v1/mule/edit";
                                  const editUrl = `${backendUrl}${editEndpoint}`;
                                  const editPayload = { projectPath: apiPath, prompt: editPrompt, sessionId };
                                  lastFileEditRef.current = { url: editUrl, body: editPayload };
                                  lastActionRef.current = 'file_edit';
                                  console.log(`[SmartHub] POST ${editEndpoint} → request:`, editPayload);
                                  setLoading(true);
                                  fetch(editUrl, {
                                    method: "POST",
                                    headers: { "Content-Type": "application/json", "x-access-token": localStorage.getItem("token") },
                                    body: JSON.stringify(editPayload),
                                  })
                                    .then(r => r.json())
                                    .then(data => {
                                      setLoading(false);
                                      try { handleApiResponse(data); } catch(e) { console.warn('[SmartHub] handleApiResponse skipped after edit:', e.message); }
                                      // Reload updated files from disk for this API
                                      const token = localStorage.getItem('token');
                                      console.log(`[SmartHub] GET /api/v1/project/load → reload after edit: ${apiPath}`);
                                      fetch(`${backendUrl}/api/v1/project/load?projectPath=${encodeURIComponent(apiPath)}`, {
                                        headers: { 'x-access-token': token },
                                      })
                                        .then(r => r.ok ? r.json() : null)
                                        .then(res => {
                                          console.log(`[SmartHub] GET /api/v1/project/load ← reload after edit, files: ${res?.files?.length ?? 0}`);
                                          if (!res?.files?.length) return;
                                          const newFiles = res.files.map(file => {
                                            const fileIcon = file.name.endsWith('.xml') ? "⚡"
                                              : file.name.endsWith('.raml') ? "📄"
                                              : file.name.endsWith('.md') ? "📝"
                                              : file.name.endsWith('.yaml') || file.name.endsWith('.yml') ? "⚙️"
                                              : file.name.endsWith('.json') ? "🔧"
                                              : "📦";
                                            return {
                                              id: `${group}-${file.relativePath}`,
                                              icon: fileIcon, name: file.name,
                                              content: file.content, relativePath: file.relativePath,
                                              group,
                                            };
                                          });
                                          setArtifacts(prev => [
                                            ...prev.filter(a => a.group !== group),
                                            ...newFiles,
                                          ]);
                                          if (newFiles[0]) setActiveArtifact(newFiles[0]);
                                        })
                                        .catch(() => {});
                                    })
                                    .catch(err => {
                                      setLoading(false);
                                      setMessages(prev => [...prev, {
                                        role: "bot", time: now(),
                                        content: `❌ Edit failed: ${err.message}`,
                                        retryable: true,
                                      }]);
                                    });
                                }}
                                onDownload={() => {
                                  const fullFiles = files
                                    .map(f =>
                                      artifacts.find(a => a.id === f.id) ||
                                      artifacts.find(a => a.relativePath === f.relativePath && a.group === f.group)
                                    )
                                    .filter(a => a?.content);
                                  return downloadGroupAsZip(group, fullFiles);
                                }}
                                onUpload={({ files, projectPath }) =>
                                  handleFolderUpload({ files, projectPath })
                                    .then(() => reloadProjectAfterUpload(projectPath, group))
                                }
                                onUploadArchive={({ file, projectPath }) =>
                                  handleArchiveUpload({ file, projectPath })
                                    .then(() => reloadProjectAfterUpload(projectPath, group))
                                }
                              />
                            ))}
                            {msg.secs && <div className="sh-api-meta">⏱ Generated in {msg.secs}s</div>}
                            {!savedProjectId && (
                              <button
                                onClick={() => { setIntegrationNameMsgIdx(i); setIntegrationNameText(""); }}
                                disabled={saving}
                                style={{
                                  marginTop: 10, width: "100%", padding: "8px 0",
                                  background: "rgba(0,200,100,0.2)",
                                  border: "1px solid rgba(0,200,100,0.4)", borderRadius: 8,
                                  color: "#00c864", fontSize: 12, fontWeight: 600,
                                  cursor: saving ? "not-allowed" : "pointer",
                                }}
                              >
                                {saving ? "Saving..." : "💾 Save Project"}
                              </button>
                            )}
                            {savedProjectId && (
                              <div style={{ marginTop: 10, display: "flex", gap: 8, alignItems: "center" }}>
                                <div style={{ flex: 1, padding: "8px 0", textAlign: "center", background: "rgba(0,200,100,0.12)", border: "1px solid rgba(0,200,100,0.4)", borderRadius: 8, color: "#00c864", fontSize: 12, fontWeight: 600 }}>
                                  ✅ Saved to Project Centre
                                </div>
                                <button
                                  onClick={() => saveSmartHubProject(null, null, null, null, null, true)}
                                  disabled={saving}
                                  style={{ padding: "8px 14px", borderRadius: 8, background: "rgba(59,127,255,0.18)", border: "1px solid rgba(59,127,255,0.45)", color: "#6aa3ff", fontSize: 12, fontWeight: 600, cursor: saving ? "not-allowed" : "pointer", whiteSpace: "nowrap" }}
                                >
                                  {saving ? "Updating…" : "🔄 Update Project"}
                                </button>
                              </div>
                            )}
                          </div>
                        );
                      })()}

                      {/* Integration project name inline input */}
                      {integrationNameMsgIdx === i && (
                        <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 8 }}>
                          <input
                            autoFocus
                            type="text"
                            placeholder="Enter project name…"
                            value={integrationNameText}
                            onChange={e => { setIntegrationNameText(e.target.value); setIntegrationNameError(""); }}
                            onKeyDown={async e => {
                              if (e.key === "Enter" && integrationNameText.trim()) {
                                const name = integrationNameText.trim();
                                if (await checkDuplicateName(name)) {
                                  setIntegrationNameError(`A project named "${name}" already exists. Choose a different name.`);
                                  return;
                                }
                                setIntegrationNameMsgIdx(null);
                                setIntegrationNameText("");
                                setIntegrationNameError("");
                                saveSmartHubProject(name, null, null, null, null, true);
                              }
                              if (e.key === "Escape") { setIntegrationNameMsgIdx(null); setIntegrationNameText(""); setIntegrationNameError(""); }
                            }}
                            style={{ padding: "8px 12px", borderRadius: "6px", border: `1px solid ${integrationNameError ? "#ff6b6b" : "rgba(255,255,255,0.2)"}`, background: "rgba(255,255,255,0.05)", color: "#fff", fontSize: "13px", outline: "none" }}
                          />
                          {integrationNameError && (
                            <span style={{ color: "#ff6b6b", fontSize: "11px", marginTop: -4 }}>{integrationNameError}</span>
                          )}
                          <div style={{ display: "flex", gap: 6 }}>
                            <button
                              onClick={async () => {
                                const name = integrationNameText.trim();
                                if (!name) return;
                                if (await checkDuplicateName(name)) {
                                  setIntegrationNameError(`A project named "${name}" already exists. Choose a different name.`);
                                  return;
                                }
                                setIntegrationNameMsgIdx(null);
                                setIntegrationNameText("");
                                setIntegrationNameError("");
                                saveSmartHubProject(name, null, null, null, null, true);
                              }}
                              disabled={!integrationNameText.trim()}
                              style={{ padding: "7px 16px", borderRadius: "6px", background: "#2EBFA5", color: "#000", border: "none", cursor: "pointer", fontSize: "12px", fontWeight: 600 }}
                            >💾 Save</button>
                            <button
                              onClick={() => { setIntegrationNameMsgIdx(null); setIntegrationNameText(""); setIntegrationNameError(""); }}
                              style={{ padding: "7px 16px", borderRadius: "6px", background: "rgba(255,255,255,0.08)", color: "#aaa", border: "1px solid rgba(255,255,255,0.12)", cursor: "pointer", fontSize: "12px" }}
                            >Cancel</button>
                          </div>
                        </div>
                      )}

                      {/* Integration edit inline input */}
                      {integrationEditMsgIdx === i && (
                        <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 8 }}>
                          <textarea
                            autoFocus
                            rows={2}
                            placeholder="Describe your change… (e.g. 'add retry logic to the payment flow')"
                            value={integrationEditText}
                            onChange={e => setIntegrationEditText(e.target.value)}
                            onKeyDown={e => {
                              if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleIntegrationEditSubmit(); }
                              if (e.key === "Escape") { setIntegrationEditMsgIdx(null); setIntegrationEditText(""); }
                            }}
                            style={{ flex: 1, padding: "8px", borderRadius: "6px", border: "1px solid rgba(255,255,255,0.2)", background: "rgba(255,255,255,0.05)", color: "#fff", fontSize: "13px", resize: "none" }}
                          />
                          <div style={{ display: "flex", flexDirection: "row", gap: 6 }}>
                            <button
                              onClick={handleIntegrationEditSubmit}
                              disabled={!integrationEditText.trim() || loading}
                              style={{ padding: "7px 16px", borderRadius: "6px", background: "#4a90d9", color: "#fff", border: "none", cursor: "pointer", fontSize: "12px", fontWeight: 600 }}
                            >Apply</button>
                            <button
                              onClick={() => { setIntegrationEditMsgIdx(null); setIntegrationEditText(""); }}
                              style={{ padding: "7px 16px", borderRadius: "6px", background: "rgba(255,255,255,0.08)", color: "#aaa", border: "1px solid rgba(255,255,255,0.12)", cursor: "pointer", fontSize: "12px" }}
                            >Cancel</button>
                          </div>
                        </div>
                      )}

                      {/* Inline image attachment */}
                      {msg.attachedImage && (
                        <div style={{ marginTop: 8 }}>
                          <img src={msg.attachedImage} alt="Attached" style={{ maxWidth: "100%", maxHeight: 240, borderRadius: 8, objectFit: "contain", border: "1px solid rgba(255,255,255,0.1)" }} />
                        </div>
                      )}

                      {/* Inline artifact — rendered at the exact position in chat */}
                      {msg.artifactId && (() => {
                        const art = artifacts.find(a => a.id === msg.artifactId);
                        if (!art) return null;
                        return (
                          <div className="sh-inline-artifact">
                            <div className="sh-inline-artifact-header">
                              <span>{art.icon} {art.name}</span>
                              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                {art.id === "puml" && plantumlCode && (
                                  <>
                                    <button className="sh-inline-copy-btn" title="Download .puml" onClick={handleDownloadPuml}>⬇ PUML</button>
                                    {hasFlowSections(plantumlCode || art.content) && (
                                      <button
                                        className="sh-inline-copy-btn"
                                        title="Download one .puml per flow (split diagrams) as a .zip"
                                        onClick={() => downloadSplitPumls(plantumlCode || art.content, art.name || "flow-diagram")}
                                      >⬇ Split PUMLs</button>
                                    )}
                                    <button className="sh-inline-copy-btn" title="Download PNG" onClick={handleDownloadPng}>⬇ PNG</button>
                                    <button
                                      className="sh-inline-copy-btn"
                                      style={showPumlCode ? { background: "rgba(99,179,237,0.15)", color: "#63b3ed", borderColor: "rgba(99,179,237,0.4)" } : {}}
                                      onClick={() => setShowPumlCode(v => !v)}
                                    >{showPumlCode ? "⊞ Diagram" : "</> Code"}</button>
                                    {!showPumlCode && splitChoice !== null && hasFlowSections(plantumlCode || art.content) && (
                                      <button
                                        className="sh-inline-copy-btn"
                                        title={showSplit ? "Show the whole diagram" : "Split into one diagram per flow"}
                                        style={showSplit ? { background: "rgba(46,191,165,0.15)", color: "#2ebfa5", borderColor: "rgba(46,191,165,0.4)" } : {}}
                                        onClick={() => { splitTouchedRef.current = true; setShowSplit(v => !v); }}
                                      >{showSplit ? "🗎 Full diagram" : "🧩 Split by flow"}</button>
                                    )}
                                  </>
                                )}
                                <button
                                  className="sh-inline-copy-btn"
                                  onClick={() => navigator.clipboard.writeText(art.content || "")}
                                >📋 Copy</button>
                              </div>
                            </div>
                            {art.id === "puml" && (plantumlCode || art.content) ? (
                              showPumlCode ? (
                                <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
                                  <div className="sh-puml-edit-hint">✏️ You can edit the PlantUML code directly — switch to <strong>Diagram</strong> to see your changes reflected.</div>
                                  <textarea
                                    className="sh-code-pre sh-puml-editor"
                                    spellCheck={false}
                                    value={plantumlCode || art.content}
                                    onChange={e => {
                                      const val = e.target.value;
                                      setPlantumlCode(val);
                                      plantumlCodeRef.current = val;
                                      setArtifacts(prev => prev.map(a =>
                                        a.id === "puml" ? { ...a, content: val } : a
                                      ));
                                    }}
                                    style={{ margin: 0, padding: 16, color: "#e6edf3", fontSize: 12, fontFamily: "monospace", whiteSpace: "pre", wordBreak: "break-word", background: "#0d1117", borderRadius: 8, maxHeight: 400, overflowY: "auto", width: "100%", border: "none", outline: "none", resize: "vertical", boxSizing: "border-box" }}
                                  />
                                </div>
                              ) : (
                              // Large diagram, not yet decided → ASK before splitting.
                              (splitChoice === null && isLargePuml(plantumlCode || art.content) && hasFlowSections(plantumlCode || art.content)) ? (
                                <div className="sh-split-prompt" style={{ border: "1px solid rgba(99,179,237,0.3)", background: "rgba(99,179,237,0.06)", borderRadius: 8, padding: 16 }}>
                                  <div style={{ fontWeight: 600, color: "#dce8ff", fontSize: 13, marginBottom: 6 }}>
                                    📐 This diagram is large ({(plantumlCode || art.content).split("\n").length} lines, {countFlowSections(plantumlCode || art.content)} flows).
                                  </div>
                                  <div style={{ color: "#9db4d6", fontSize: 12.5, marginBottom: 12 }}>
                                    Split it into one diagram per flow for readability, or view the whole diagram as-is?
                                  </div>
                                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                                    <button
                                      className="sh-inline-copy-btn"
                                      style={{ background: "rgba(46,191,165,0.15)", color: "#2ebfa5", borderColor: "rgba(46,191,165,0.4)", padding: "7px 14px", fontWeight: 600 }}
                                      onClick={() => { splitTouchedRef.current = true; setSplitChoice('split'); setShowSplit(true); }}
                                    >🧩 Yes, split by flow</button>
                                    <button
                                      className="sh-inline-copy-btn"
                                      style={{ padding: "7px 14px" }}
                                      onClick={() => { splitTouchedRef.current = true; setSplitChoice('full'); setShowSplit(false); }}
                                    >🗎 Show full diagram</button>
                                  </div>
                                </div>
                              ) : (
                              showSplit && hasFlowSections(plantumlCode || art.content) ? (
                                <SplitDiagramView
                                  plantumlCode={plantumlCode || art.content}
                                  plantumlEncoder={plantumlEncoder}
                                />
                              ) : (
                              <div style={{ border: "1px solid #2a2a2a", borderRadius: 8, overflow: "hidden", background: "#1a1a1a", minHeight: 260 }}>
                                <ZoomableDiagram
                                  plantumlCode={plantumlCode || art.content}
                                  plantumlEncoder={plantumlEncoder}
                                  onCodeUpdate={code => setPlantumlCode(code)}
                                  onSplit={hasFlowSections(plantumlCode || art.content)
                                    ? () => { splitTouchedRef.current = true; setSplitChoice('split'); setShowSplit(true); }
                                    : null}
                                />
                              </div>
                              )
                              )
                              )
                            ) : (
                              <pre className="sh-inline-artifact-pre">
                                <code>{art.content || "No content"}</code>
                              </pre>
                            )}
                          </div>
                        );
                      })()}

                      {/* Diagram edit inline input — after diagram */}
                      {diagramEditMsgIdx === i && (
                        <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 8 }}>
                          <textarea
                            autoFocus
                            rows={2}
                            placeholder="Describe your change… (e.g. 'add a Dead Letter Queue after the error handler')"
                            value={diagramEditText}
                            onChange={e => setDiagramEditText(e.target.value)}
                            onKeyDown={e => {
                              if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleDiagramEditSubmit(); }
                              if (e.key === "Escape") { setDiagramEditMsgIdx(null); setDiagramEditText(""); }
                            }}
                            style={{ padding: "8px", borderRadius: "6px", border: "1px solid rgba(255,255,255,0.2)", background: "rgba(255,255,255,0.05)", color: "#fff", fontSize: "13px", resize: "none" }}
                          />
                          <div style={{ display: "flex", flexDirection: "row", gap: 6 }}>
                            <button
                              onClick={handleDiagramEditSubmit}
                              disabled={!diagramEditText.trim() || loading}
                              style={{ padding: "7px 16px", borderRadius: "6px", background: "#4a90d9", color: "#fff", border: "none", cursor: "pointer", fontSize: "12px", fontWeight: 600 }}
                            >Apply</button>
                            <button
                              onClick={() => { setDiagramEditMsgIdx(null); setDiagramEditText(""); }}
                              style={{ padding: "7px 16px", borderRadius: "6px", background: "rgba(255,255,255,0.08)", color: "#aaa", border: "1px solid rgba(255,255,255,0.12)", cursor: "pointer", fontSize: "12px" }}
                            >Cancel</button>
                          </div>
                        </div>
                      )}

                      {/* Design project name inline input */}
                      {designNameMsgIdx === i && (
                        <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 8 }}>
                          <input
                            autoFocus
                            type="text"
                            placeholder="Enter project name…"
                            value={designNameText}
                            onChange={e => { setDesignNameText(e.target.value); setDesignNameError(""); }}
                            onKeyDown={async e => {
                              if (e.key === "Enter" && designNameText.trim()) {
                                const name = designNameText.trim();
                                if (await checkDuplicateName(name)) {
                                  setDesignNameError(`A project named "${name}" already exists. Choose a different name.`);
                                  return;
                                }
                                setDesignNameMsgIdx(null);
                                setDesignNameError("");
                                setDesignProjectName(name);
                                saveDesignProject(plantumlCode, name);
                                setActiveAgent("integration");
                                setMessages(prev => [...prev, {
                                  role: "bot", time: now(),
                                  content: `💾 Design saved as **${name}**. Select your technology to generate the integration.`,
                                  actions: [
                                    { label: "⚡ Generate Mule",        action: "tech_mule"   },
                                    { label: "🍃 Generate Spring Boot", action: "tech_spring" },
                                    { label: "🔷 Generate .NET",        action: "tech_dotnet" },
                                  ],
                                }]);
                              }
                              if (e.key === "Escape") { setDesignNameMsgIdx(null); setDesignNameText(""); setDesignNameError(""); }
                            }}
                            style={{ padding: "8px 12px", borderRadius: "6px", border: `1px solid ${designNameError ? "#ff6b6b" : "rgba(255,255,255,0.2)"}`, background: "rgba(255,255,255,0.05)", color: "#fff", fontSize: "13px", outline: "none" }}
                          />
                          {designNameError && (
                            <span style={{ color: "#ff6b6b", fontSize: "11px", marginTop: -4 }}>{designNameError}</span>
                          )}
                          <div style={{ display: "flex", gap: 6 }}>
                            <button
                              onClick={async () => {
                                const name = designNameText.trim();
                                if (!name) return;
                                if (await checkDuplicateName(name)) {
                                  setDesignNameError(`A project named "${name}" already exists. Choose a different name.`);
                                  return;
                                }
                                setDesignNameMsgIdx(null);
                                setDesignNameError("");
                                setDesignProjectName(name);
                                saveDesignProject(plantumlCode, name);
                                setActiveAgent("integration");
                                setMessages(prev => [...prev, {
                                  role: "bot", time: now(),
                                  content: `💾 Design saved as **${name}**. Select your technology to generate the integration.`,
                                  actions: [
                                    { label: "⚡ Generate Mule",        action: "tech_mule"   },
                                    { label: "🍃 Generate Spring Boot", action: "tech_spring" },
                                    { label: "🔷 Generate .NET",        action: "tech_dotnet" },
                                  ],
                                }]);
                              }}
                              disabled={!designNameText.trim()}
                              style={{ padding: "7px 16px", borderRadius: "6px", background: "#2EBFA5", color: "#000", border: "none", cursor: "pointer", fontSize: "12px", fontWeight: 600 }}
                            >Save</button>
                            <button
                              onClick={() => { setDesignNameMsgIdx(null); setDesignNameText(""); setDesignNameError(""); }}
                              style={{ padding: "7px 16px", borderRadius: "6px", background: "rgba(255,255,255,0.08)", color: "#aaa", border: "1px solid rgba(255,255,255,0.12)", cursor: "pointer", fontSize: "12px" }}
                            >Cancel</button>
                          </div>
                        </div>
                      )}

                      {/* Deployment review card */}
                      {msg.deploymentCard && (
                        <DeploymentReviewCard
                          deploymentCard={msg.deploymentCard}
                          onAction={function(a) { handleMsgAction(a, i); }}
                          loading={loading}
                        />
                      )}

                      {/* Architectural review card */}
                      {msg.architecturalReview && (
                        <ArchitecturalReviewCard
                          architecturalReview={msg.architecturalReview}
                          message={msg.content}
                          onSubmit={(text) => {
                            const isApproveOrSkip = text === 'approve' || text === 'skip';
                            // Only show a user bubble for change requests, not for approve/skip
                            if (!isApproveOrSkip) {
                              setMessages(prev => [...prev, { role: "user", time: now(), content: text }]);
                            }
                            setLoading(true);
                            if (USE_MOCK) {
                              // In mock mode, bypass the sequence counter and serve the correct next mock directly
                              const nextMock = isApproveOrSkip
                                ? MOCK_RESPONSES['api_selection']
                                : MOCK_RESPONSES['arch_review_mule_updated'];
                              setTimeout(() => handleApiResponse(nextMock), 600);
                              return;
                            }
                            sendHubRequest({ prompt: text, sessionId, projectName: projName, technology: selectedTech });
                          }}
                          loading={loading}
                          selectedTech={selectedTech}
                        />
                      )}

                      {/* Design plan window (review/approve before the diagram) */}
                      {msg.designPlan && !msg.designPlanAnswered && (
                        <DesignPlanWindow
                          data={msg.designPlan}
                          fallbackMd={msg.content}
                          renderMd={renderMdText}
                          loading={loading}
                          onAction={(text) => {
                            // Plan actions (approve / undo / show / question / change) stay
                            // inside the plan window — no chat bubble leaks into the thread.
                            setLoading(true);
                            sendHubRequest({ prompt: text, sessionId, projectName: projName, technology: selectedTech });
                          }}
                        />
                      )}
                      {msg.designPlan && msg.designPlanAnswered && (
                        <div style={{ fontSize: 13, opacity: 0.7 }}>✅ Plan approved</div>
                      )}
                      {msg.implementationPlan && !msg.implementationPlanAnswered && (
                        <ImplementationPlanWindow
                          data={msg.implementationPlan}
                          fallbackMd={msg.content}
                          renderMd={renderMdText}
                          loading={loading}
                          plantumlEncoder={plantumlEncoder}
                          onAction={(payload) => {
                            // Plan actions stay inside the plan window — no chat bubble leaks.
                            // A string is a free-text turn (approve/undo/show/question/change);
                            // an object is a structured batch (e.g. implementationPlanEdit.ops from
                            // the "Set answers" button — all decisions resolved in one request).
                            setLoading(true);
                            const base = { sessionId, projectName: projName, technology: selectedTech };
                            const body = typeof payload === "string" ? { ...base, prompt: payload } : { ...base, ...payload };
                            sendHubRequest(body);
                          }}
                        />
                      )}
                      {msg.implementationPlan && msg.implementationPlanAnswered && (
                        <div style={{ fontSize: 13, opacity: 0.7 }}>✅ Implementation plan approved</div>
                      )}

                      {/* Schema elicitation card */}
                      {msg.schemaElicitation && (
                        <SchemaElicitationCard schemaElicitation={msg.schemaElicitation} onSubmit={handleSchemaSubmit} />
                      )}

                      {/* Existing API upload card */}
                      {msg.existingApiUpload && !msg.existingApiAnswered && (
                        <ExistingApiUploadCard
                          data={msg.existingApiUpload}
                          sessionId={sessionId}
                          backendUrl={backendUrl}
                          onSubmit={(result) => handleExistingApiSubmit(result, i)}
                        />
                      )}
                      {msg.existingApiUpload && msg.existingApiAnswered && (
                        <div style={{ fontSize: 13, opacity: 0.7 }}>✅ Submitted</div>
                      )}

                      {/* Custom Azure pipeline upload card (Mule) */}
                      {msg.pipelineUpload && !msg.pipelineAnswered && (
                        <PipelineUploadCard
                          data={msg.pipelineUpload}
                          sessionId={sessionId}
                          backendUrl={backendUrl}
                          onSubmit={(result) => handlePipelineSubmit(result, i)}
                        />
                      )}
                      {msg.pipelineUpload && msg.pipelineAnswered && (
                        <div style={{ fontSize: 13, opacity: 0.7 }}>✅ Submitted</div>
                      )}

                      {/* CI/CD setup card (deployment_setup_pending) */}
                      {msg.deploymentSetup && !msg.deploymentSetupAnswered && (
                        <DeploymentSetupCard
                          data={msg.deploymentSetup}
                          sessionId={sessionId}
                          backendUrl={backendUrl}
                          onSubmit={(payload) => handleDeploymentSetupSubmit(payload, i)}
                        />
                      )}
                      {msg.deploymentSetup && msg.deploymentSetupAnswered && (
                        <div style={{ fontSize: 13, opacity: 0.7 }}>✅ CI/CD setup submitted</div>
                      )}

                      {/* Validation agent card — hidden once answered (persisted via validationAnswered flag) */}
                      {msg.validation && !msg.validationAnswered && (
                        <ValidationCard validation={msg.validation} onSubmit={handleValidationSubmit} />
                      )}
                      {msg.validation && msg.validationAnswered && (
                        <div style={{ fontSize: 13, opacity: 0.7 }}>✅ Requirements submitted</div>
                      )}

                      {/* API selection card */}
                      {msg.apiSelection && (
                        <ApiSelectionCard
                          apiSelection={msg.apiSelection}
                          onSubmit={handleApiSelectionSubmit}
                          loading={loading}
                        />
                      )}

                      {/* Standalone-app confirmation card */}
                      {msg.standaloneConfirm && (
                        <StandaloneConfirmationCard
                          standaloneConfirm={msg.standaloneConfirm}
                          onSubmit={handleStandaloneConfirmSubmit}
                          loading={loading}
                        />
                      )}

                      {msg.actions && (
                        <div className="sh-msg-actions">
                          {msg.actions
                            .filter(a => !(a.action === "save_design" && designSaved))
                            .map((a, ai) => (
                              <button
                                key={ai}
                                className={`sh-action-chip ${a.action.startsWith('inline_choice__') ? 'inline_choice' : a.action}`}
                                onClick={() => handleMsgAction(a.action, i)}
                              >
                                {a.label}
                              </button>
                            ))}
                          {designSaved && msg.actions.some(a => a.action === "save_design") && (
                            <span style={{ fontSize: 12, color: "#00c864", fontWeight: 600, padding: "4px 8px", background: "rgba(0,200,100,0.12)", border: "1px solid rgba(0,200,100,0.3)", borderRadius: 6 }}>
                              ✅ Design Saved
                            </span>
                          )}
                        </div>
                      )}
                      {/* After a save, offer a fresh session below the Save-to-repository options. */}
                      {savedProjectId && msg.actions && msg.actions.some(a => a.action === "save_repository") && (
                        <button
                          onClick={() => handleNewSession()}
                          style={{ marginTop: 10, width: "100%", padding: "8px 0", borderRadius: 8, background: "rgba(0,200,100,0.12)", border: "1px solid rgba(0,200,100,0.4)", color: "#00c864", fontSize: 12, fontWeight: 600, cursor: "pointer" }}
                        >
                          ➕ Start new project
                        </button>
                      )}
                      {!msg.actions && msg.selectedLabel && (
                        <div style={{ marginTop: 6, display: "inline-flex", alignItems: "center", gap: 6, padding: "5px 10px", borderRadius: 6, background: "rgba(0,200,100,0.12)", border: "1px solid rgba(0,200,100,0.35)", color: "#00c864", fontSize: 12, fontWeight: 600 }}>
                          ✓ {msg.selectedLabel}
                        </div>
                      )}
                    </div>
                    </>
                  )}
                  </div>
                </div>
                );
              })}

              {loading && (
                <div className="sh-msg bot">
                  <div className="sh-msg-avatar bot-avatar"><img src={whiteLogo} alt="intelog" /></div>
                  <div className="sh-msg-body">
                    <div className="sh-msg-meta"><span className="sh-msg-name">{selectedTech === 'mule' ? 'Generating MuleSoft…' : selectedTech === 'springboot' ? 'Generating Spring Boot…' : selectedTech === 'dotnet' ? 'Generating .NET…' : 'Thinking…'}</span></div>
                    <div className="sh-msg-bubble bot">
                      <div className="sh-loading-bubble"><span /><span /><span /></div>
                      {(() => {
                        const lastInfo = [...logs].reverse().find(e => e.level === "info");
                        return lastInfo ? (
                          <div className="sh-thinking-live-logs">
                            <div className="sh-thinking-log-line sh-thinking-log-info">{lastInfo.msg}</div>
                          </div>
                        ) : null;
                      })()}
                    </div>
                  </div>
                </div>
              )}
              <div ref={chatEndRef} />
            </div>

            {/* Deployment window — slide-up terminal drawer */}
            <DeploymentWindow
              show={showDeployWindow}
              minimised={deployWindowMin}
              logs={deployLogs}
              stages={deployStages}
              pipelineStages={pipelineStages}
              confirmation={deploymentConfirmation}
              tech={selectedTech}
              onMinimise={function() { setDeployWindowMin(function(v) { return !v; }); }}
              apis={deployApiStatuses}
              onClose={function() { setShowDeployWindow(false); setDeployLogs([]); setPipelineStages([]); setDeploymentConfirmation(null); }}
              onProceed={handleProceedDeployment}
              onCancel={function() {
                deploymentPausedRef.current = false;
                setDeploymentConfirmation(null);
                setLoading(false);
                if (socketRef.current && socketRef.current.connected) {
                  socketRef.current.emit('deployment:cancel', { sessionId });
                }
                addDeployLog('⚠️ Deployment cancelled by user.', 'warning');
                addLog(setLogs, 'warning', '⚠️ Deployment cancelled by user.');
              }}
              onRetry={function() {
                var action = selectedTech === 'mule' ? 'confirm_deploy_mule' : selectedTech === 'dotnet' ? 'confirm_deploy_dotnet' : 'confirm_deploy_spring';
                handleDeployAction(action);
              }}
            />

            {/* First-time tip card */}
            {showTipCard && messages.length === 0 && (
              <div className="sh-tip-card">
                <div className="sh-tip-card-body">
                  <span className="sh-tip-card-icon">💡</span>
                  <div className="sh-tip-card-text">
                    <strong>Getting started</strong>
                    <span>Describe an integration in plain English — the AI will design, generate code, and help you deploy. Try: <em>"Sync orders from Shopify to SAP every 15 minutes"</em></span>
                  </div>
                </div>
                <div className="sh-tip-card-actions">
                  <button className="sh-tip-card-help-btn" onClick={() => setShowHelp(true)}>View guide</button>
                  <button className="sh-tip-card-dismiss" onClick={() => { setShowTipCard(false); setShowHelp(false); localStorage.setItem("smarthub_tip_dismissed", "1"); }}>✕</button>
                </div>
              </div>
            )}

            {/* Input */}
            {bf.bfLoading && (
              <div className="sh-cross-tab-warn">
                ⚠️ Brownfield analysis is running — Greenfield is paused until it completes.
              </div>
            )}
            <div className="sh-input-area">
              {/* PlantUML context pill */}
              {plantumlCode && (
                <div className="sh-puml-pill">
                  <span>📐 Design context attached</span>
                  <button className="sh-puml-pill-remove" title="Remove" onClick={() => setPlantumlCode("")}>✕</button>
                </div>
              )}
              {pendingAttachment && (
                <div className="sh-attachment-chip">
                  <span>📎 {pendingAttachment.name} · will send with message</span>
                  <button onClick={() => setPendingAttachment(null)}>✕</button>
                </div>
              )}
              <div className="sh-input-row">
                {/* Left: Examples */}
                <div className="sh-input-side-actions">
                  <button ref={examplesRef} className="sh-input-action-btn" onClick={() => setShowExamples(p => !p)}>💡 Examples</button>
                </div>

                {/* Right: text input */}
                <div className="sh-input-wrap" ref={inputWrapRef}>
                  <button className="sh-attach-btn" title="Attach PlantUML file" onClick={() => setShowPumlPanel(true)} disabled={promptLocked}>+</button>
                  <input
                    className="sh-input"
                    style={{ paddingLeft: 46, opacity: promptLocked ? 0.5 : 1, cursor: promptLocked ? "not-allowed" : "text" }}
                    placeholder={promptLocked
                      ? "Project finished — click “➕ Start new project” to begin another"
                      : "Ask intelog AI anything — design, integrate, or deploy..."}
                    value={input}
                    onChange={e => setInput(e.target.value)}
                    onKeyDown={e => { if (e.key === "Enter" && !loading && !promptLocked) submitMessage(); }}
                    disabled={promptLocked}
                  />
                  <button ref={expandBtnRef} className="sh-expand-btn" title="Expand prompt" onClick={() => setShowPromptModal(true)} disabled={promptLocked}>⤢</button>
                  {loading && (
                    <button className="sh-stop-btn" onClick={stopGeneration} title="Stop generation">⏹</button>
                  )}
                  <button className="sh-send-btn" onClick={submitMessage} disabled={!input.trim() || bf.bfLoading || loading || promptLocked}>↑</button>
                </div>
              </div>
            </div>
          </div>

          {/* ── Dashboard Column ── */}
          <div className="sh-dashboard-col" ref={artifactsRef}>

            <SharedLogsPanel
              logs={logs}
              usageData={usageData}
              liveTokens={liveTokens}
              extensionStatus={extensionStatus}
              onExtend={handleExtendUsage}
              onExpand={() => { setLogsModalFilter("all"); setShowLogsModal(true); }}
              logsRef={logsRef}
            />

          </div>
        </div>}
      </main>

      {/* Deployment field edit popup */}
      {deployEditModal && (
        <DeployEditModal
          modal={deployEditModal}
          onSelect={function(a) { setDeployEditModal(null); handleDeployAction(a); }}
          onClose={function() { deployEditModeRef.current = null; setDeployEditModal(null); }}
        />
      )}

      {/* Confluence push modal */}
      {showConfluenceModal && (
        <ConfluenceModal
          docs={confluenceDocs}
          domain={domain}
          onClose={() => setShowConfluenceModal(false)}
          onPushDoc={pushConfluenceDoc}
          onComplete={finishConfluencePush}
        />
      )}

      {/* Save-to-repository modal (push generated code to a new GitHub/Azure repo) */}
      {showSaveRepoModal && (() => {
        const perProject = (generatedApis || []).map(a => a.projectPath).filter(Boolean);
        const root = (projectPath || "").trim();
        // Mule APIs are separate projects (per-API paths). Spring Boot / .NET are one
        // multi-module root (api-gateway + services) — send the ROOT so analyze discovers
        // EVERY module; the per-module generatedApis list omits api-gateway (it isn't in
        // data.ast.microservices), which is why Save used to list only the main service.
        const repoSavePaths = selectedTech === "mule"
          ? (perProject.length ? perProject : (root ? [root] : []))
          : (root ? [root] : perProject);
        return (
          <SaveRepositoryModal
            projectPaths={repoSavePaths}
            technology={selectedTech}
            sessionId={sessionId}
            backendUrl={backendUrl}
            token={localStorage.getItem("token")}
            socketRef={socketRef}
            projName={deployProjName || projName || (generatedApis[0] && generatedApis[0].name) || "generated-project"}
            githubAccounts={repoAccounts}
            azureAccounts={azureAccounts}
            connectHref="/settings"
            onClose={() => setShowSaveRepoModal(false)}
            onComplete={(res) => setSavedRepoResult(res)}
          />
        );
      })()}

      {/* IDE fullscreen modal */}
      {ideModalGroups && (
        <IdeModal
          groups={ideModalGroups.groups}
          projectPath={ideModalGroups.projectPath}
          artifacts={artifacts}
          onClose={() => setIdeModalGroups(null)}
          onContentSaved={(id, newContent) => {
            setArtifacts(prev => prev.map(a => a.id === id ? { ...a, content: newContent } : a));
          }}
        />
      )}

      {/* Prompt modal */}
      {showPromptModal && (
        <div className="sh-overlay" onClick={() => setShowPromptModal(false)}>
          <div className="sh-prompt-modal" onClick={e => e.stopPropagation()}>
            <div className="sh-prompt-modal-header">
              <span>Ask intelog AI</span>
              <button className="sh-prompt-modal-close" onClick={() => setShowPromptModal(false)}>✕</button>
            </div>
            <textarea
              className="sh-prompt-modal-textarea"
              autoFocus
              placeholder="Ask intelog AI anything — design, integrate, or deploy..."
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  setShowPromptModal(false);
                  submitMessage();
                }
              }}
            />
            {pendingAttachment && (
              <div className="sh-attachment-chip" style={{ margin: '0 0 8px 0' }}>
                <span>📎 {pendingAttachment.name} · will send with message</span>
                <button onClick={() => setPendingAttachment(null)}>✕</button>
              </div>
            )}
            <div className="sh-prompt-modal-footer">
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <button className="sh-prompt-modal-examples" onClick={() => setShowExamples(true)}>💡 Examples</button>
                <button
                  onClick={() => { setShowPromptModal(false); setReopenPromptModal(true); setShowPumlPanel(true); }}
                  title="Attach a file (.puml, .txt, .pdf, .docx, .zip)"
                  className="sh-prompt-modal-examples"
                >📎 Attach</button>
                <span className="sh-prompt-modal-hint">Enter to send · Shift+Enter for new line</span>
              </div>
              <button
                className="sh-prompt-modal-send"
                disabled={!input.trim() || loading || bf.bfLoading}
                onClick={() => { setShowPromptModal(false); submitMessage(); }}
              >
                {loading ? "Generating..." : "Send ↑"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Token pre-flight check modal */}
      {tokenCheckModal && (
        <div className="sh-tcm-overlay" onClick={function() { setTokenCheckModal(null); }}>
          <div className="sh-tcm-modal" onClick={function(e) { e.stopPropagation(); }}>
            <div className="sh-tcm-header">
              <span className="sh-tcm-icon">🚫</span>
              <span className="sh-tcm-title">
                {tokenCheckModal.limitReached ? 'Daily Token Limit Reached' : 'Insufficient Tokens'}
              </span>
            </div>
            <div className="sh-tcm-body">
              <p className="sh-tcm-desc">
                {tokenCheckModal.limitReached
                  ? 'You have used 100% of your daily token quota. Integration generation cannot proceed today.'
                  : ('Thank you for using Intelog! It looks like generating ' + tokenCheckModal.serviceCount + ' service' + (tokenCheckModal.serviceCount !== 1 ? 's' : '') + ' requires approximately ' + formatTokenCount(tokenCheckModal.estimatedNeeded) + ' tokens, but only ' + formatTokenCount(tokenCheckModal.remaining) + ' remain in your daily quota. Your quota will reset tomorrow — we appreciate your patience!')}
              </p>
              <div className="sh-tcm-stats">
                <div className="sh-tcm-stat"><span>Remaining</span><strong>{formatTokenCount(tokenCheckModal.remaining)}</strong></div>
                <div className="sh-tcm-stat"><span>Estimated needed</span><strong>{'~' + formatTokenCount(tokenCheckModal.estimatedNeeded)}</strong></div>
                <div className="sh-tcm-stat"><span>Services</span><strong>{tokenCheckModal.serviceCount}</strong></div>
                <div className="sh-tcm-stat"><span>Used today</span><strong>{Math.round(tokenCheckModal.percentUsed) + '%'}</strong></div>
              </div>
            </div>
            <div className="sh-tcm-actions">
              <button className="sh-tcm-cancel-btn" onClick={function() { setTokenCheckModal(null); }}>OK</button>
            </div>
          </div>
        </div>
      )}

      {/* PlantUML attach panel */}
      {showPumlPanel && (
        <div className="sh-overlay" onClick={() => { setShowPumlPanel(false); setUploadedFile(null); }}>
          <div className="sh-puml-panel" onClick={e => e.stopPropagation()}>
            <div className="sh-puml-panel-header">
              <span>📎 Attach File</span>
              <button onClick={() => { setShowPumlPanel(false); setUploadedFile(null); }} className="sh-overlay-close">✕</button>
            </div>
            <p className="sh-puml-panel-hint">
              Supports <code>.puml</code>, <code>.txt</code>, <code>.pdf</code>, <code>.docx</code>, <code>.zip</code> · max 5 MB. Will be sent as context with your next message.
            </p>
            <input ref={fileInputRef} type="file" accept=".puml,.txt,.pdf,.docx,.zip" style={{ display: "none" }} onChange={handleFileInput} />
            {uploadedFile ? (
              <div className="sh-puml-file-preview">
                {uploadedFile.error ? (
                  <div className="sh-puml-file-error">⚠️ {uploadedFile.error}</div>
                ) : (
                  <div className="sh-puml-file-icon">📄</div>
                )}
                <div className="sh-puml-file-info">
                  <div className="sh-puml-file-name">{uploadedFile.name}</div>
                  {!uploadedFile.error && <div className="sh-puml-file-meta">{uploadedFile.fileCount ? `${uploadedFile.fileCount} files` : uploadedFile.pageCount ? `${uploadedFile.pageCount} pages` : `${uploadedFile.content?.split('\n').length} lines`}</div>}
                </div>
                <button className="sh-puml-pill-remove" onClick={() => setUploadedFile(null)} title="Remove">✕</button>
              </div>
            ) : (
              <div
                className={`sh-puml-dropzone${dragOver ? " drag-over" : ""}`}
                onClick={() => fileInputRef.current?.click()}
                onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleDrop}
              >
                <div className="sh-puml-dz-icon">☁️</div>
                <div className="sh-puml-dz-text">Drop file here or <span>click to browse</span></div>
                <div className="sh-puml-dz-hint">.puml · .txt · .pdf · .docx · .zip · max 5 MB</div>
              </div>
            )}
            <div className="sh-puml-panel-footer">
              <button className="sh-puml-cancel-btn" onClick={() => { setShowPumlPanel(false); setUploadedFile(null); }}>Cancel</button>
              <button className="sh-puml-attach-btn" onClick={handleAttachPuml} disabled={!uploadedFile || !!uploadedFile?.error}>Attach</button>
            </div>
          </div>
        </div>
      )}

      {/* Full Logs Modal */}
      {showLogsModal && (
        <div className="sh-overlay" onClick={() => setShowLogsModal(false)}>
          <div className="sh-logs-modal" onClick={e => e.stopPropagation()}>
            <div className="sh-logs-modal-header">
              <span>Live Logs</span>
              <div className="sh-logs-modal-actions">
                <button
                  className="sh-logs-modal-copy"
                  onClick={() => navigator.clipboard.writeText(
                    logs.map(l => `[${l.ts}] ${l.level.toUpperCase()} ${l.msg}`).join('\n')
                  )}
                  title="Copy all logs"
                >📋 Copy All</button>
                <button className="sh-logs-modal-close" onClick={() => setShowLogsModal(false)}>✕</button>
              </div>
            </div>
            <div className="sh-logs-modal-tabs">
              {[["all","All"], ["error","Errors"], ["info","Info"]].map(([f, label]) => (
                <button
                  key={f}
                  className={`sh-logs-modal-tab${logsModalFilter === f ? " active" : ""}`}
                  onClick={() => setLogsModalFilter(f)}
                >{label}</button>
              ))}
            </div>
            <div className="sh-logs-modal-body">
              {logs
                .filter(l =>
                  logsModalFilter === "all" ||
                  (logsModalFilter === "error" ? ["error","warning"].includes(l.level) : ["info","success"].includes(l.level))
                )
                .map((log, i) => (
                  <div key={i} className="sh-log-row">
                    <span className="sh-log-ts">[{log.ts}]</span>
                    <span className={`sh-log-lvl ${log.level}`}>{log.level.toUpperCase()}</span>
                    <span className="sh-log-msg">{log.msg}</span>
                  </div>
                ))}
            </div>
          </div>
        </div>
      )}

      {/* Examples overlay */}
      {showExamples && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 60, display: "flex", alignItems: "center", justifyContent: "center" }} onClick={() => setShowExamples(false)}>
          <div style={{ background: "#121212", border: "1px solid #2a2a2a", borderRadius: 12, width: 520, maxHeight: "70vh", overflow: "hidden", display: "flex", flexDirection: "column" }} onClick={e => e.stopPropagation()}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 18px", borderBottom: "1px solid #2a2a2a" }}>
              <span style={{ fontSize: 14, fontWeight: 600, color: "#fff" }}>💡 Example Prompts</span>
              <button onClick={() => setShowExamples(false)} style={{ background: "none", border: "none", color: "#737373", cursor: "pointer", fontSize: 16 }}>✕</button>
            </div>
            <div className="sh-examples-scroll" style={{ overflow: "auto", padding: 8 }}>
              {ALL_PROMPTS.map((ex, i) => (
                <div key={i} style={{ padding: "12px 14px", borderRadius: 8, cursor: "pointer", borderBottom: "1px solid #1a1a1a", color: "#a3a3a3", fontSize: 13, lineHeight: 1.6 }}
                  onMouseEnter={e => e.currentTarget.style.background = "#1a1a1a"}
                  onMouseLeave={e => e.currentTarget.style.background = "none"}
                  onClick={() => { setInput(ex); setShowExamples(false); }}>
                  {ex}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Guided product tour */}
      {tourStep > 0 && (
        <TourSpotlight
          step={tourStep}
          steps={activeTab === 'brownfield' ? TOUR_STEPS_BROWNFIELD : TOUR_STEPS}
          refs={{ inputWrapRef, expandBtnRef, examplesRef, logsRef, newSessionRef, backBtnRef, tabsRef, artifactsRef }}
          onNext={() => { const len = (activeTab === 'brownfield' ? TOUR_STEPS_BROWNFIELD : TOUR_STEPS).length; if (tourStep >= len) { setTourStep(0); localStorage.setItem("smarthub_tip_dismissed", "1"); } else { setTourStep(s => s + 1); } }}
          onClose={() => { setTourStep(0); localStorage.setItem("smarthub_tip_dismissed", "1"); }}
        />
      )}
    </div>
  );
}


